
/* ===== assets/js/app/core.jsx ===== */
// ============================================================================
// BLOX.HOT — core constants + pure helpers (split out of app.jsx).
// Loaded/concatenated BEFORE app.jsx (see build.mjs SOURCES + foot.php dev
// script order). Shares ONE global scope with app.jsx — no import/export.
// Everything here is leaf-level: constants, formatters, crypto utils. It must
// NOT reference React or anything defined later in app.jsx.
// ============================================================================

// Brand swatches — Orange is the DEFAULT (and the "Default" label in Settings
// reflects that), Emerald/Magenta/Blue are alt themes a logged-in user can
// pick from Settings → Appearance → Accent color. Persisted to localStorage
// (key: `bloxhot.accent`) so each visitor's choice survives reloads — we
// never override an explicit user pick with the global default.
// NOTE: the CSS variable is still named `--orange` (kept for backwards
// compatibility with hundreds of class references) but the *value* changes
// with the user's pick at runtime.
const SWATCHES = [
  { hex: '#FE9920', hover: '#FFA538', soft: 'rgba(254,153,32,0.22)', name: 'Orange'  },
  { hex: '#10B981', hover: '#14C795', soft: 'rgba(16,185,129,0.18)', name: 'Emerald' },
  { hex: '#FF3D9A', hover: '#FF5BAA', soft: '#FFD0E6',               name: 'Magenta' },
  { hex: '#2E7DFF', hover: '#5C9CFF', soft: '#C7DCFF',               name: 'Blue'    },
];

const AVATAR_PALETTES = ['purple','blue','green','pink','yellow','cyan'];
const COLOR_GRADIENTS = {
  purple: 'linear-gradient(135deg,#7C3AED,#A78BFA)',
  blue:   'linear-gradient(135deg,#1E63F0,#5C9CFF)',
  green:  'linear-gradient(135deg,#047857,#10B981)',
  pink:   'linear-gradient(135deg,#C2185B,#FF3D9A)',
  yellow: 'linear-gradient(135deg,#FFA538,#FFC960)',
  cyan:   'linear-gradient(135deg,#0891B2,#22D3EE)',
};

const NAMES = ['novaknight','glassowl','pixelmonk','mintsharkk','cryptofox','lunaSpark','duster','wraith42','tinywolf','b3rkay','eren.7','aleyna_xx','frostbyte','sageclaw','vexx','kaori','draven','phin','rune.22','obsidian'];
const randomName = () => NAMES[Math.floor(Math.random()*NAMES.length)] + (Math.random()<0.3 ? Math.floor(Math.random()*99) : '');
const randomColor = () => AVATAR_PALETTES[Math.floor(Math.random()*AVATAR_PALETTES.length)];
const fmt = (n) => {
  if (n >= 1000) {
    const s = (n/1000).toFixed(1);
    return (s.endsWith('.0') ? s.slice(0, -2) : s) + 'K';
  }
  return n.toLocaleString('en-US');
};
// Item value formatter (handles tiny prices like 0.05 and big like 1500)
const fmtVal = (n) => {
  // Coerce nulls / undefined / NaN to 0 so callers never crash with
  //   "Cannot read property 'toFixed' of null"
  // (this was the root cause of the admin Detail white-screen — the
  //  backend can legitimately return NULL for total_value, biggest_win,
  //  etc., and calling n.toFixed() on null throws synchronously).
  const v = Number(n);
  if (!isFinite(v)) return '0';
  if (v >= 1000) return (v / 1000).toFixed(2) + 'K';
  if (v < 1)    return v.toFixed(2);
  if (v < 100)  return v.toFixed(1);
  return Math.round(v).toString();
};

// Items pool loaded from PHP-injected window.FLIPLINE_ITEMS (values.json)
const ALL_ITEMS = Object.entries(window.FLIPLINE_ITEMS || {}).map(([name, data]) => ({
  name, price: data.price, rarity: data.rarity || 'godly',
}));
const itemImg = (name) => `assets/images/${encodeURIComponent(name)}.webp`;

const pickRandomItems = (count, opts = {}) => {
  const { minPrice = 0, maxPrice = Infinity } = opts;
  const pool = ALL_ITEMS.filter(i => i.price >= minPrice && i.price <= maxPrice);
  if (pool.length === 0) return [];
  const out = [];
  for (let i = 0; i < count; i++) out.push(pool[Math.floor(Math.random() * pool.length)]);
  return out;
};

const flipValue = (items) => items.reduce((s, it) => s + (it?.price || 0), 0);

// ============================================
// PROVABLY FAIR — crypto utilities
// ============================================
const randomHex = (bytes = 32) => {
  const arr = new Uint8Array(bytes);
  (crypto || window.crypto).getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
};

const sha256Hex = async (text) => {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
};

const hmacSha256Hex = async (key, text) => {
  const keyData = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(key),
    { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', keyData, new TextEncoder().encode(text));
  return Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
};

;

/* ===== assets/js/app.jsx ===== */
// ============================================
// FLIPLINE — App
// ============================================

const TWEAKS_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": "#FE9920",
  "showFeatured": true,
  "showHero": true
}/*EDITMODE-END*/;

// NOTE: core constants + pure helpers (SWATCHES, AVATAR_PALETTES,
// COLOR_GRADIENTS, NAMES, fmt, fmtVal, ALL_ITEMS, itemImg, pickRandomItems,
// flipValue, randomHex, sha256Hex, hmacSha256Hex) were moved to
// assets/js/app/core.jsx — concatenated BEFORE this file by build.mjs, so
// they're in the same global scope and used exactly as before.

const computeFairResult = async (serverSeed, creatorSeed, joinerSeed, nonce) => {
  const msg = `${creatorSeed}:${joinerSeed}:${nonce}`;
  const h = await hmacSha256Hex(serverSeed, msg);
  // Use first 8 hex chars (32 bits) as integer, mod 2 → side
  const n = parseInt(h.slice(0, 8), 16);
  return { side: n % 2 === 0 ? 'heads' : 'tails', proof: h };
};

let _nonceCounter = Math.floor(Math.random() * 100000);

// Cache-busted logo URL. PHP injects window.BLOXHOT_LOGO_VER = filemtime() in
// foot.php, so when you replace the SVG the URL changes and every browser
// fetches the new file. Without this, .htaccess caches images for 1 month
// and users see the old logo for weeks.
const LOGO_SVG = 'assets/images/bloxhotlogothirtyone.svg' +
  (typeof window !== 'undefined' && window.BLOXHOT_LOGO_VER
    ? '?v=' + window.BLOXHOT_LOGO_VER
    : '');

// ============================================
// API CLIENT — backend (PHP) integration
// ============================================
// Empty stub kept so any leftover legacy callers don't throw. Sound removed.
const noop = new Proxy({}, { get: () => () => {} });

// Session token persistence — cookies are the primary mechanism but some
// browsers / hosting setups (mobile Safari ITP, in-app browsers, Hostinger
// quirks) silently drop them. We also persist the token in localStorage and
// send it as Authorization: Bearer so login survives even if the cookie is
// evicted between requests.
const TOKEN_KEY = 'bloxhot.session_token';
const authToken = {
  get() { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (_) { return ''; } },
  set(v) { try { v ? localStorage.setItem(TOKEN_KEY, v) : localStorage.removeItem(TOKEN_KEY); } catch (_) {} },
  clear() { try { localStorage.removeItem(TOKEN_KEY); } catch (_) {} },
};

const _apiFetchOnce = async (path, opts, { omitCookies = false } = {}) => {
  const isFormData = opts.body instanceof FormData;
  const baseHeaders = opts.body && !isFormData
    ? { 'Content-Type': 'application/json', ...(opts.headers || {}) }
    : (opts.headers || {});
  const tok = authToken.get();
  const headers = tok ? { ...baseHeaders, Authorization: `Bearer ${tok}` } : baseHeaders;
  // SECURITY: the ?_t= URL fallback was REMOVED. Tokens leaked via Apache
  // access logs, Referer headers and shared links. Cookie + Authorization
  // header are the only token transports now.
  const res = await fetch(path, {
    method: opts.method || 'GET',
    credentials: omitCookies ? 'omit' : 'same-origin',
    headers,
    body: opts.body
      ? (isFormData ? opts.body : JSON.stringify(opts.body))
      : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) {}
  return { res, data };
};

const apiFetch = async (path, opts = {}) => {
  // Try once; retry once on a transient Apache 404 or 5xx (no JSON body =
  // server didn't run PHP at all). This smooths over Hostinger shared-hosting
  // worker contention that occasionally returns a bare 404 page.
  let { res, data } = await _apiFetchOnce(path, opts);
  const transient = !res.ok && (res.status === 404 || res.status >= 500) && data === null;
  if (transient) {
    await new Promise(r => setTimeout(r, 600));
    ({ res, data } = await _apiFetchOnce(path, opts));
  }
  // MOBILE FIX: 401 with a Bearer token in localStorage usually means the
  // *cookie* is stale (browser hasn't garbage-collected an expired
  // bh_session yet) and the server preferred it over our fresh token.
  // Retry once with cookies omitted — server falls back to Bearer. Only
  // clear the local token if THAT also CONFIRMS the token is invalid
  // (returns `not_authenticated` again, not just any 401). A network blip
  // / proxy 401 / WAF reject would also be a 401 and would incorrectly
  // wipe a valid session on refresh — that's the "rastgele atıyor"
  // (random logout) the user hit.
  if (!res.ok && res.status === 401 && data && data.error === 'not_authenticated' && authToken.get()) {
    const retry = await _apiFetchOnce(path, opts, { omitCookies: true });
    if (retry.res.ok) {
      res = retry.res; data = retry.data;
    } else if (retry.res.status === 401 && retry.data && retry.data.error === 'not_authenticated') {
      // Both attempts confirmed "not authenticated" — token really is dead.
      authToken.clear();
    }
    // Any other failure (network, 5xx, WAF) → leave the token intact and
    // surface the original 401 to the caller. Next page load will retry.
  }
  if (!res.ok) {
    // User-facing message ONLY — never includes the /api/path so when this
    // bubbles up into a toast there's no "(/api/whatever)" leak. The endpoint
    // path is still available as err.path for callers that want to log it.
    const baseMsg = (data && (data.message || data.error)) || `HTTP ${res.status}`;
    const err = new Error(baseMsg);
    err.code = data && data.error;
    err.status = res.status;
    err.path = path;
    throw err;
  }
  return data;
};

// Stable color from a user id so the same user gets the same avatar tint everywhere.
const deterministicColor = (id) => {
  const n = Math.abs(Number(id) || 0);
  return AVATAR_PALETTES[n % AVATAR_PALETTES.length];
};

const synthAvatar = (name) => (name || '??').replace(/[^a-zA-Z0-9]/g, '').substring(0, 2).toUpperCase() || '??';

// ============================================================
// useDragMultiSelect — drag-to-select for inventory pickers.
// Desktop: click + drag across items to toggle them in bulk.
// Mobile : long-press an item (~280ms, with haptic) to enter drag
//          mode, then slide your finger across items to toggle.
// Single tap/click still works as before for one-item toggle.
// ============================================================
function useDragMultiSelect(toggle, isSelected) {
  const stateRef = React.useRef({ active: false, mode: null, seen: new Set(), suppressClick: new Set() });
  const longPressRef = React.useRef(null);
  const startPosRef = React.useRef({ x: 0, y: 0 });

  const beginDrag = (id) => {
    const mode = isSelected(id) ? 'deselect' : 'select';
    stateRef.current = {
      active: true,
      mode,
      seen: new Set([id]),
      suppressClick: new Set([id]),
    };
    toggle(id);
    try { navigator.vibrate && navigator.vibrate(28); } catch (_) {}
  };

  const enterId = (id) => {
    if (!stateRef.current.active) return;
    if (stateRef.current.seen.has(id)) return;
    stateRef.current.seen.add(id);
    stateRef.current.suppressClick.add(id);
    const desired = stateRef.current.mode === 'select';
    if (isSelected(id) !== desired) toggle(id);
  };

  // ---- Mouse (desktop) ----
  const onItemMouseDown = (id) => (e) => {
    if (e.button !== 0) return;
    // Defer activation until pointer moves to another item — so a plain
    // click is still a single-item toggle handled by onClick.
    stateRef.current.pendingStart = id;
  };
  const onItemMouseEnter = (id) => () => {
    const pending = stateRef.current.pendingStart;
    if (pending !== undefined && pending !== null && pending !== id) {
      beginDrag(pending);
      stateRef.current.pendingStart = null;
      enterId(id);
      return;
    }
    enterId(id);
  };

  // ---- Touch (mobile) ----
  const onItemTouchStart = (id) => (e) => {
    const t = e.touches[0];
    startPosRef.current = { x: t.clientX, y: t.clientY };
    clearTimeout(longPressRef.current);
    longPressRef.current = setTimeout(() => beginDrag(id), 280);
  };
  const onItemTouchMove = (e) => {
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - startPosRef.current.x);
    const dy = Math.abs(t.clientY - startPosRef.current.y);
    // Moved before long-press triggered → it's a scroll, cancel.
    if (longPressRef.current && (dx > 12 || dy > 12)) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
    if (!stateRef.current.active) return;
    e.preventDefault();
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const target = el && el.closest && el.closest('[data-pickable-id]');
    if (!target) return;
    const id = target.dataset.pickableId;
    if (id !== undefined) enterId(id);
  };
  const onItemTouchEnd = () => {
    clearTimeout(longPressRef.current);
    longPressRef.current = null;
  };

  // Suppress the click that follows a drag-touched item (otherwise the
  // browser's synthetic click would un-toggle it).
  const shouldSuppressClick = (id) => stateRef.current.suppressClick.has(String(id)) || stateRef.current.suppressClick.has(id);

  // Global pointerup ends drag mode.
  React.useEffect(() => {
    const end = () => {
      stateRef.current.active = false;
      stateRef.current.mode = null;
      stateRef.current.seen = new Set();
      stateRef.current.pendingStart = null;
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
      // Clear suppress set on next tick so the click event has time to fire
      setTimeout(() => { stateRef.current.suppressClick = new Set(); }, 0);
    };
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);
    window.addEventListener('touchcancel', end);
    return () => {
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchend', end);
      window.removeEventListener('touchcancel', end);
    };
  }, []);

  // Per-item props builder
  const itemProps = (id) => ({
    'data-pickable-id': id,
    onMouseDown: onItemMouseDown(id),
    onMouseEnter: onItemMouseEnter(id),
    onTouchStart: onItemTouchStart(id),
    onTouchEnd: onItemTouchEnd,
  });

  // Grid-level prop (needed for touchmove to track across items)
  const gridProps = { onTouchMove };

  function onTouchMove(e) { onItemTouchMove(e); }

  return { itemProps, gridProps, shouldSuppressClick };
}

// CountUp — animates from previous to new value over `dur` ms. Used for live stats.
function CountUp({ to, dur = 800, fmt }) {
  const [v, setV] = React.useState(to);
  const fromRef = React.useRef(to);
  React.useEffect(() => {
    const from = fromRef.current;
    const target = Number(to) || 0;
    if (Math.abs(target - from) < 0.001) { setV(target); return; }
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [to, dur]);
  return <>{typeof fmt === 'function' ? fmt(v) : Math.round(v).toLocaleString('en-US')}</>;
}

// Pick an avatar-border class from a user's level. Falls through to no class.
const levelBorder = (level) => {
  const l = Number(level) || 0;
  if (l >= 60) return 'lvl-diamond';
  if (l >= 40) return 'lvl-gold';
  if (l >= 20) return 'lvl-silver';
  if (l >= 10) return 'lvl-bronze';
  return '';
};

// Map a backend flip JSON → the frontend flip shape the existing UI components expect.
const mapApiFlip = (api, currentUserId) => {
  const mkUser = (u) => ({
    id: u.id,
    name: u.username || u.username,
    username: u.username,
    lvl: Math.max(1, Math.floor((u.total_flips || 0) / 10) + 1),
    av: synthAvatar(u.username || u.username),
    color: deterministicColor(u.id),
    avatarUrl: u.avatar_url || '',
  });
  const cItems = (api.creator_items || []).map((it, i) => ({ ...it, id: `srv-${api.id}-c-${i}` }));
  const oItems = (api.opponent_items || []).map((it, i) => ({ ...it, id: `srv-${api.id}-o-${i}` }));
  return {
    id: `srv-${api.id}`,
    srvId: api.id,
    c: mkUser(api.creator),
    o: api.opponent ? mkUser(api.opponent) : undefined,
    side: api.side,
    items: cItems,
    oItems,
    state: api.state,
    teamSize:  typeof api.team_size   === 'number' ? api.team_size   : 1,
    teamSizeB: typeof api.team_size_b === 'number' ? api.team_size_b : (typeof api.team_size === 'number' ? api.team_size : 1),
    participants: Array.isArray(api.participants) ? api.participants : [],
    matchRange: typeof api.match_range === 'number' ? api.match_range : 0.05,
    mine: currentUserId != null && api.creator.id === currentUserId,
    serverSeed: api.server_seed,        // null until resolved
    serverSeedHash: api.server_seed_hash,
    creatorSeed: api.creator_seed,
    joinerSeed: api.joiner_seed,
    nonce: api.nonce,
    result: api.result,
    winnerId: api.winner_id,
    createdAt: api.created_at,
    joinedAt: api.joined_at,
    resolvedAt: api.resolved_at,
  };
};

// Map a backend chat message → frontend chat shape.
const mapApiChat = (m) => {
  if (m.user_id == null) {
    return { id: `srv-c-${m.id}`, srvId: m.id, type: 'system', text: m.text };
  }
  const name = m.username || m.username || 'user';
  return {
    id: `srv-c-${m.id}`,
    srvId: m.id,
    type: 'user',
    name,
    username: m.username,
    avatarUrl: m.avatar_url || '',
    lvl: Math.max(1, Number(m.level) || 1),
    is_admin: !!m.is_admin,
    color: deterministicColor(m.user_id),
    av: synthAvatar(name),
    text: m.text,
    userId: m.user_id,
  };
};

const makeFlip = (overrides = {}) => {
  const palette = AVATAR_PALETTES[Math.floor(Math.random()*AVATAR_PALETTES.length)];
  const name = randomName();
  const count = 1 + Math.floor(Math.random() * 24);
  const items = pickRandomItems(count, { maxPrice: 800 });
  return {
    id: 'f-' + Date.now() + '-' + Math.floor(Math.random()*10000),
    c: { name, lvl: Math.floor(Math.random()*60)+1, av: name.substring(0,2), color: palette },
    side: Math.random() < 0.5 ? 'heads' : 'tails',
    items,
    state: 'open',
    // Provably fair seeds — server seed is committed (hash shown), revealed after flip
    serverSeed: randomHex(32),
    creatorSeed: randomHex(16),
    nonce: ++_nonceCounter,
    ...overrides,
  };
};

const initialFlips = [
  makeFlip({ id: 'f1',  c: { name: 'NovaKnight',  lvl: 42, av: 'NK', color: 'purple' }, side: 'heads', items: pickRandomItems(4,  { maxPrice: 200 }) }),
  makeFlip({ id: 'f2',  c: { name: 'cryptofox',   lvl: 18, av: 'cf', color: 'blue'   }, side: 'tails', items: pickRandomItems(2,  { maxPrice: 50 }) }),
  makeFlip({ id: 'f3',  c: { name: 'mintsharkk',  lvl: 7,  av: 'ms', color: 'green'  }, side: 'heads', items: pickRandomItems(8,  { maxPrice: 10 }) }),
  makeFlip({ id: 'f4',  c: { name: 'aleyna_xx',   lvl: 27, av: 'ax', color: 'pink'   }, o: { name: 'wraith42', lvl: 31, av: 'w4', color: 'yellow' }, side: 'tails',  items: pickRandomItems(6,  { maxPrice: 100 }), state: 'rolling' }),
  makeFlip({ id: 'f5',  c: { name: 'PixelMonk',   lvl: 55, av: 'PM', color: 'yellow' }, side: 'tails', items: pickRandomItems(12, { maxPrice: 500 }) }),
  makeFlip({ id: 'f6',  c: { name: 'glassowl',    lvl: 12, av: 'go', color: 'purple' }, side: 'heads', items: pickRandomItems(3,  { maxPrice: 30 }) }),
  makeFlip({ id: 'f7',  c: { name: 'b3rkay',      lvl: 23, av: 'b3', color: 'blue'   }, side: 'tails', items: pickRandomItems(5,  { maxPrice: 80 }) }),
  makeFlip({ id: 'f8',  c: { name: 'lunaSpark',   lvl: 9,  av: 'ls', color: 'pink'   }, o: { name: 'duster', lvl: 14, av: 'du', color: 'cyan' }, side: 'heads', items: pickRandomItems(2, { maxPrice: 20 }), state: 'rolling' }),
  makeFlip({ id: 'f9',  c: { name: 'eren.7',      lvl: 36, av: 'e7', color: 'green'  }, side: 'heads', items: pickRandomItems(7,  { maxPrice: 300 }) }),
  makeFlip({ id: 'f10', c: { name: 'tinywolf',    lvl: 4,  av: 'tw', color: 'cyan'   }, side: 'tails', items: pickRandomItems(1,  { maxPrice: 5 }) }),
];

// TRANSLATIONS moved to assets/js/app/translations.js (loaded BEFORE this file).
// Use window.TRANSLATIONS — kept as a local alias for backward compat.
const TRANSLATIONS = window.TRANSLATIONS || {};

// Global translation lookup — works from anywhere without prop-drilling `t`.
// Reads the active language from localStorage (the same place the React
// state mirrors it). Pattern: tt('keyName', 'English fallback', { n: 5 })
// — placeholders {n} {name} {amount} {side} {time} {q} get substituted.
function tt(key, fallback, vars) {
  let lang = 'en';
  try { lang = localStorage.getItem('flipline.lang') || 'en'; } catch (_) {}
  const dict = TRANSLATIONS[lang] || {};
  const enDict = TRANSLATIONS.en || {};
  let s = dict[key] || enDict[key] || fallback || key;
  if (vars && typeof s === 'string') {
    for (const k in vars) s = s.split('{' + k + '}').join(String(vars[k]));
  }
  return s;
}

const initialChat = [
  { id: 1, type: 'user',   name: 'cryptofox',  lvl: 18, color: 'blue',   av: 'cf', text: 'gg nova bu inanılmazdı' },
  { id: 2, type: 'system', text: 'NovaKnight won 25K from cryptofox' },
  { id: 3, type: 'user',   name: 'mintsharkk', lvl: 7,  color: 'green',  av: 'ms', text: 'bir tane daha açıyorum 1k heads' },
  { id: 4, type: 'user',   name: 'b3rkay',     lvl: 23, color: 'blue',   av: 'b3', text: 'türkçe yazan var mı knk' },
  { id: 5, type: 'user',   name: 'eren.7',     lvl: 36, color: 'green',  av: 'e7', text: 'selam berkay buradayım' },
  { id: 6, type: 'user',   name: 'wraith42',   lvl: 31, color: 'yellow', av: 'w4', text: 'streak 4 baby' },
  { id: 7, type: 'system', text: 'PixelMonk won 48K' },
  { id: 8, type: 'user',   name: 'aleyna_xx',  lvl: 27, color: 'pink',   av: 'ax', text: 'kim 5k matchler' },
  { id: 9, type: 'user',   name: 'glassowl',   lvl: 12, color: 'purple', av: 'go', text: 'tilt etmeyin abi' },
  { id:10, type: 'user',   name: 'duster',     lvl: 14, color: 'cyan',   av: 'du', text: 'lfg' },
];

// ============================================
// ICONS (FontAwesome wrapper)
// ============================================
const ICON_MAP = {
  search:   'fa-solid fa-magnifying-glass',
  bell:     'fa-solid fa-bell',
  sun:      'fa-solid fa-sun',
  moon:     'fa-solid fa-moon',
  warning:  'fa-solid fa-triangle-exclamation',
  fire:     'fa-solid fa-fire',
  plus:     'fa-solid fa-plus',
  arrow:    'fa-solid fa-arrow-right',
  close:    'fa-solid fa-xmark',
  chev:     'fa-solid fa-chevron-down',
  shield:   'fa-solid fa-shield-halved',
  trophy:   'fa-solid fa-trophy',
  bolt:     'fa-solid fa-bolt',
  sort:     'fa-solid fa-arrow-down-wide-short',
  sliders:  'fa-solid fa-sliders',
  eye:      'fa-solid fa-eye',
  check:    'fa-solid fa-check',
  send:     'fa-solid fa-paper-plane',
  chat:     'fa-solid fa-comment-dots',
  smile:    'fa-regular fa-face-smile',
  globe:    'fa-solid fa-globe',
  user:     'fa-solid fa-user',
  wallet:   'fa-solid fa-wallet',
  robot:    'fa-solid fa-robot',
  download: 'fa-solid fa-arrow-down',
  upload:   'fa-solid fa-arrow-up',
  trash:    'fa-solid fa-trash',
  twitter:  'fa-brands fa-x-twitter',
  discord:  'fa-brands fa-discord',
  telegram: 'fa-brands fa-telegram',
};
const I = ({ name, size = 16, color = 'currentColor' }) => {
  const cls = ICON_MAP[name] || 'fa-solid fa-circle';
  return <i className={cls} style={{ fontSize: size, color, lineHeight: 1, display: 'inline-block' }}/>;
};

// ============================================
// COMPONENTS
// ============================================
const LANGUAGES = [
  { code: 'tr', flag: 'tr', label: 'Türkçe' },
  { code: 'en', flag: 'us', label: 'English' },
  { code: 'ru', flag: 'ru', label: 'Русский' },
  { code: 'es', flag: 'es', label: 'Español' },
  { code: 'it', flag: 'it', label: 'Italiano' },
  { code: 'fr', flag: 'fr', label: 'Français' },
  { code: 'de', flag: 'de', label: 'Deutsch' },
  { code: 'ph', flag: 'ph', label: 'Filipino' },
  { code: 'zh', flag: 'cn', label: '中文' },
  { code: 'ja', flag: 'jp', label: '日本語' },
  { code: 'uk', flag: 'ua', label: 'Українська' },
  { code: 'ar', flag: 'sa', label: 'العربية' },
  { code: 'ur', flag: 'pk', label: 'اردو' },
];

function LangSwitcher({ lang, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const current = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="lang-switch" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)} aria-label="language">
        <img src={`assets/images/flag-${current.flag}.webp`} className="lang-flag" alt={current.code} loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }}/>
      </button>
      {open && (
        <div className="lang-menu">
          {LANGUAGES.map(l => (
            <button key={l.code} className={`lang-item ${l.code===lang?'is-active':''}`} onClick={() => { onChange(l.code); setOpen(false); }}>
              <img src={`assets/images/flag-${l.flag}.webp`} className="lang-flag" alt={l.code} loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }}/>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Mobile search popup — opens from the header search icon. Full-screen
// sheet so on tiny phones the input gets the whole width to itself.
// On desktop we never mount this (inline search lives in the header).
function MobileSearchPopup({ value, onChange, onClose, placeholder }) {
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    // Auto-focus the input on open. setTimeout because iOS Safari ignores
    // focus() calls made synchronously during a state-change render.
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div className="msp-overlay" onClick={onClose}>
      <div className="msp-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="msp-row">
          <I name="search" size={18} color="#838383"/>
          <input
            ref={inputRef}
            className="msp-input"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onClose(); }}
          />
          {value && (
            <button className="msp-clear" onClick={() => onChange('')} aria-label="clear">
              <I name="close" size={14}/>
            </button>
          )}
          <button className="msp-close" onClick={onClose} aria-label="close search">Cancel</button>
        </div>
        <div className="msp-hint">Search players, items, or flip codes</div>
      </div>
    </div>
  );
}

function Header({ balance, onWallet, lang, onLang, t, chatOpen, onChatToggle, searchQuery, onSearch, profileOpen, onProfileToggle, profileMenu, onHome, currentUser, onAuthOpen, notifications, notifOpen, onNotifToggle, onNotifsRead, onClearNotifs, bootLoading, subNav, theme, onThemeToggle, onMenu, onFairness }) {
  const unreadCount = (notifications || []).filter(n => !n.read).length;
  const handle = currentUser ? (currentUser.username || currentUser.username) : '';
  const initials = currentUser
    ? (handle.replace(/[^a-zA-Z0-9]/g, '').substring(0,2).toUpperCase() || '??')
    : '';
  return (
    <header className="header">
      <div className="header-row">
        <div className="h-left">
          {/* Hamburger — only visible on tablet/small-laptop range (981–1450px)
              via CSS. Opens the left side-drawer with nav links + fairness +
              settings. On PC (>1450px) the header SubNav is wide enough so
              the burger hides. On mobile (≤980px) the bottom mobile-nav is
              the primary nav, so the burger also hides. */}
          {onMenu && (
            <button
              type="button"
              className="hdr-burger"
              aria-label="Menu"
              onClick={onMenu}
            >
              <span className="hdr-burger-line"/>
              <span className="hdr-burger-line"/>
              <span className="hdr-burger-line"/>
            </button>
          )}
          <a className="logo brandmark brandmark--header" href="/" onClick={(e) => { e.preventDefault(); onHome && onHome(); }} aria-label="BLOX.HOT home">
            <span className="brandmark-icon" aria-hidden="true"/>
            <span className="brandmark-text">BLOX<span className="brandmark-dot">.</span><span className="brandmark-hot">HOT</span></span>
          </a>
          {/* Chat button removed — chat is in mobile navbar already, and PC has
              the side panel always open. No need for a header chat icon. */}
        </div>
        <div className="h-center">
          {/* SubNav lives inside the header on desktop. Search moved out to
              the lobby's FilterBar so the topbar isn't fighting for space.
              SubNav itself is hidden on mobile via CSS — phone users get the
              page-level <SubNav> below the header. */}
          {subNav && <div className="h-subnav">{subNav}</div>}
        </div>
        <div className="h-right">
          {/* Fairness — opens the provably-fair modal. Tooltip below on hover
              tells the user what the icon does without having to click. PC-only
              (≥1451) shows it in the header; tablet uses the drawer entry,
              mobile uses the account sheet / footer. */}
          {onFairness && (
            <button
              className="h-icon-btn h-fairness-btn"
              aria-label="provably fair"
              onClick={onFairness}
              data-tip={t?.fairTitle || 'Fairness'}
            >
              <i className="fa-solid fa-scale-balanced" style={{fontSize:15}}/>
            </button>
          )}
          {/* Tablet-only chat trigger (981-1450px) — PC has the sidebar
              always reachable via a different toggle, mobile has the
              bottom-nav chat icon. On tablet the chat is an overlay (not
              a sidebar push) so users need a way to open it from the top. */}
          {onChatToggle && (
            <button
              className={`h-icon-btn h-chat-tablet-btn ${chatOpen ? 'is-active' : ''}`}
              aria-label="chat"
              onClick={onChatToggle}
              title="Chat"
            >
              <i className="fa-solid fa-comment-dots" style={{fontSize:15}}/>
            </button>
          )}
          {/* Theme toggle — visible at all viewports so the user can flip
              dark/light from anywhere without diving into menus. */}
          {onThemeToggle && (
            <button
              className="h-icon-btn h-theme-mobile-btn"
              aria-label="toggle theme"
              onClick={onThemeToggle}
              title={theme === 'dark' ? (t?.pmLightTheme || 'Light theme') : (t?.pmDarkTheme || 'Dark theme')}
            >
              <i className={`fa-solid fa-${theme === 'dark' ? 'sun' : 'moon'}`} style={{fontSize:16}}/>
            </button>
          )}
          <div className="h-notif-wrap">
            <button className={`h-icon-btn ${notifOpen ? 'is-active' : ''}`} aria-label="notifications" onClick={onNotifToggle}>
              <I name="bell" size={16} />
              {unreadCount > 0 && <span className="h-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>
            {notifOpen && (
              <NotificationsDropdown
                items={notifications || []}
                onClose={() => onNotifToggle()}
                onClear={onClearNotifs}
                t={t}
              />
            )}
          </div>
          <LangSwitcher lang={lang} onChange={onLang}/>
          {currentUser ? (
            <>
              {/* Two-column wallet card — left = value (gem + amount) over
                  a small "Value" label, right = square action chip with
                  wallet icon. Mirrors the StarPets reference but uses our
                  bloxhotgem.svg and our brand orange action. The whole
                  card is one button so clicking anywhere opens the
                  inventory / wallet sheet. */}
              <button className="h-wallet-cta" onClick={onWallet} aria-label="wallet">
                <span className="h-wallet-info">
                  <span className="h-wallet-amt">
                    <i className="fa-solid fa-gem gem-i" aria-hidden="true"/>
                    {bootLoading
                      ? <Skeleton w={56} h={16} radius={4}/>
                      : <span className="h-wallet-num">{fmtVal(balance)}</span>}
                  </span>
                  <span className="h-wallet-lbl">{t?.headerValueLabel || 'Value'}</span>
                </span>
                <span className="h-wallet-action" aria-hidden="true">
                  <i className="fa-solid fa-wallet"/>
                </span>
              </button>
              <div className="h-avatar-wrap">
                <button className="h-profile" onClick={onProfileToggle} aria-label="profile">
                  <div className="h-profile-info">
                    <div className="h-profile-name">{rankEmojiForLevel(currentUser.level)} @{currentUser.username}</div>
                    <div className="h-profile-sub">Lv {currentUser.level || 1}</div>
                  </div>
                  <div className="h-profile-avatar">
                    {currentUser.avatar_url
                      ? <img src={currentUser.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'inherit'}}/>
                      : initials}
                  </div>
                </button>
                {profileOpen && profileMenu}
              </div>
            </>
          ) : (
            <div className="h-auth">
              <button className="h-auth-btn h-auth-register" onClick={() => onAuthOpen('login')}>
                {t?.loginTabLogin || 'Login'}
                <i className="fa-solid fa-arrow-right" style={{fontSize: 11, marginLeft: 6}}/>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function MobileNav({ onWallet, chatOpen, onChatToggle, onCreate, onHome, onProfile, activeNav, t, currentUser }) {
  // Profile button: when logged in, show the user's Roblox avatar instead of
  // the generic person icon. Falls back to the icon if the image is missing
  // or the user isn't authenticated yet.
  const avatarUrl = currentUser && currentUser.avatar_url;
  // Center "+" button context-swap: on the Dice page show "Dice" + open the
  // dice create modal; everywhere else show "Flip" + open the coinflip
  // create modal. The label is a tiny UX nicety so the user knows what the
  // button is about to create without an icon swap (the + stays the same).
  const onDice    = activeNav === 'dice';
  const centerLbl = onDice ? (t?.mnDice || 'Dice') : (t?.mnFlip || 'Flip');
  const handleCenter = () => {
    if (onDice) {
      // The DicePage listens for this event (see useEffect inside DicePage)
      // and opens its local create modal. Decouples App from dice state.
      window.dispatchEvent(new CustomEvent('blox:open-dice-create'));
      return;
    }
    onCreate && onCreate();
  };
  return (
    <nav className="mobile-nav">
      <button className={`mn-btn ${activeNav === 'coinflip' ? 'is-active' : ''}`} onClick={onHome} aria-label="home">
        <i className="fa-solid fa-house"/>
        <span className="mn-label">{t?.mnHome || 'Home'}</span>
      </button>
      <button className="mn-btn" onClick={onWallet} aria-label="wallet">
        <i className="fa-solid fa-wallet"/>
        <span className="mn-label">{t?.mnWallet || 'Wallet'}</span>
      </button>
      <button className="mn-btn center" onClick={handleCenter} aria-label={`create ${centerLbl.toLowerCase()}`}>
        <div className="mn-center-bg"><i className="fa-solid fa-plus"/></div>
        <span className="mn-label">{centerLbl}</span>
      </button>
      <button className={`mn-btn ${chatOpen ? 'is-active' : ''}`} onClick={onChatToggle} aria-label="chat">
        <i className="fa-solid fa-comment-dots"/>
        <span className="mn-label">{t?.mnChat || 'Chat'}</span>
      </button>
      <button className="mn-btn mn-btn--profile" onClick={onProfile} aria-label="profile">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="mn-avatar"
            draggable={false}
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'inline-block'); }}
          />
        ) : null}
        <i className="fa-solid fa-user" style={{ display: avatarUrl ? 'none' : undefined }}/>
        <span className="mn-label">{t?.mnProfile || 'Profile'}</span>
      </button>
    </nav>
  );
}

function MobileAccountSheet({ onClose, currentUser, balance, theme, onThemeToggle, lang, onLang, onWallet, onPage, onLogout, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);
  const handle = currentUser ? currentUser.username : 'guest';
  const initials = currentUser
    ? ((currentUser.username || currentUser.username || '??').replace(/[^a-zA-Z0-9]/g, '').substring(0,2).toUpperCase() || '??')
    : '??';
  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal mas" ref={sheetRef} style={{maxWidth: 520}}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>{t?.masTitle || 'My Account'}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body mas-body">
          {currentUser && (
            <div className="mas-card">
              <div className="mas-av">
                {currentUser.avatar_url
                  ? <img src={currentUser.avatar_url} alt=""/>
                  : <div className="mas-av-fallback">{initials}</div>}
              </div>
              <div className="mas-meta">
                <div className="mas-handle">@{handle}</div>
                <div className="mas-sub"><i className="fa-solid fa-bolt"/> Lv {currentUser.level || 1} · {currentUser.total_flips || 0} flips</div>
              </div>
            </div>
          )}

          <div className="mas-balance">
            <div className="mas-balance-lbl"><i className="fa-solid fa-wallet"/> {t?.masBalance || 'Balance'}</div>
            <div className="mas-balance-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(balance)}</div>
          </div>

          <div className="mas-row-grid">
            <button className="mas-pill" onClick={onThemeToggle}>
              <i className={`fa-solid fa-${theme === 'dark' ? 'sun' : 'moon'}`}/>
              <span>{theme === 'dark' ? (t?.pmLightTheme || 'Light') : (t?.pmDarkTheme || 'Dark')}</span>
            </button>
            <div className="mas-pill">
              <CustomSelect
                value={lang}
                onChange={(v) => { onLang(v); }}
                ariaLabel="language"
                options={LANGUAGES.map(l => ({
                  value: l.code,
                  label: l.label,
                  icon: <img src={`assets/images/flag-${l.flag}.webp`} className="lang-flag" alt={l.code} loading="lazy"/>,
                }))}
              />
            </div>
          </div>

          <button className="mas-row primary" onClick={() => { close(); onWallet && onWallet(); }}>
            <span className="mas-row-icon"><i className="fa-solid fa-upload"/></span>
            <span className="mas-row-label">{t?.masWallet || 'Wallet / Withdraw'}</span>
            <i className="fa-solid fa-chevron-right"/>
          </button>
          <button className="mas-row" onClick={() => { close(); onPage && onPage('profile'); }}>
            <span className="mas-row-icon"><i className="fa-solid fa-user"/></span>
            <span className="mas-row-label">{t?.masProfile || 'My profile'}</span>
            <i className="fa-solid fa-chevron-right"/>
          </button>
          <button className="mas-row" onClick={() => { close(); onPage && onPage('settings'); }}>
            <span className="mas-row-icon"><i className="fa-solid fa-cog"/></span>
            <span className="mas-row-label">{t?.masSettings || 'Settings'}</span>
            <i className="fa-solid fa-chevron-right"/>
          </button>
          <button className="mas-row danger" onClick={() => { close(); onLogout && onLogout(); }}>
            <span className="mas-row-icon"><i className="fa-solid fa-right-from-bracket"/></span>
            <span className="mas-row-label">{t?.pmLogout || 'Log out'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function SubNav({ active, onChange, t, stats, variant }) {
  // Live countdown for the Events pill — tiny secondary label under "Events".
  // Reads from REWARDS_RACE.endsAt (admin can replace this later with a
  // setting). Re-renders every second.
  const cd = useCountdown(REWARDS_RACE.endsAt);
  const eventCountdown = cd.days > 0
    ? `${cd.days}d ${cd.hours}h`
    : cd.hours > 0
      ? `${cd.hours}h ${cd.mins}m`
      : `${cd.mins}m ${cd.secs}s`;
  // Active pot value (sum of all open + rolling flips). Just the value +
  // gem icon — no "live" text per user request, keeps the pill compact.
  const activePotLabel = stats && stats.totalValue > 0
    ? fmtVal(stats.totalValue)
    : null;
  // Dice pill mirrors Coinflip — shows the value at stake (sum of all
  // open dice matches' creator_value) when there's action, blank otherwise.
  // The dice-stats are fetched separately because the lobby is only loaded
  // when the user enters the Dice page.
  const activeDiceLabel = stats && stats.diceValue > 0
    ? fmtVal(stats.diceValue)
    : null;
  // Order: Coinflip → Dice → Events → Leaderboard
  const items = [
    { id: 'coinflip',    label: t.coinflipTab || 'Coinflip',    icon: 'fa-solid fa-coins',  subLabel: activePotLabel,  subKind: 'gem' },
    { id: 'dice',        label: t.diceTab     || 'Dice',        icon: 'fa-solid fa-dice',   subLabel: activeDiceLabel, subKind: 'gem' },
    { id: 'rewards',     label: t.events      || 'Events',      icon: 'fa-solid fa-trophy', subLabel: eventCountdown,  subKind: 'time' },
    { id: 'leaderboard', label: t.leaderboard || 'Leaderboard', icon: 'fa-solid fa-medal',  subLabel: null },
  ];
  return (
    <nav className={`subnav-wrap subnav-wrap--${variant || 'page'}`}>
      <div className="subnav">
        {items.map(it => (
          <button
            key={it.id}
            className={`subnav-pill ${active===it.id?'is-active':''} ${it.subLabel ? 'has-sub' : ''}`}
            onClick={() => onChange(it.id)}
            /* Prefetch on hover/focus — warms the API cache for the
               target page so the actual click feels instant. Safe to
               call repeatedly: apiFetch + browser HTTP cache dedupe. */
            onMouseEnter={() => {
              if (it.id === 'leaderboard') apiFetch('/api/leaderboard?period=week&limit=50').catch(() => {});
              if (it.id === 'rewards')     apiFetch('/api/leaderboard?period=month&limit=10').catch(() => {});
            }}
            onFocus={() => {
              if (it.id === 'leaderboard') apiFetch('/api/leaderboard?period=week&limit=50').catch(() => {});
              if (it.id === 'rewards')     apiFetch('/api/leaderboard?period=month&limit=10').catch(() => {});
            }}
            aria-label={`${it.label} tab${it.subLabel ? ' — ' + it.subLabel : ''}`}
          >
            <i className={it.icon} style={{fontSize: 12}}/>
            <span className="subnav-label-wrap">
              <span className="subnav-label">{it.label}</span>
              {it.subLabel && (
                <span className={`subnav-sub subnav-sub--${it.subKind || 'time'}`}>
                  {it.subKind === 'gem' && <i className="fa-solid fa-gem gem-i" style={{fontSize:9}}/>}
                  {it.subKind === 'online' && <span className="subnav-sub-dot"/>}
                  {it.subLabel}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
}

const USER_RANKS = [
  { min: 10000, name: 'Whale',    cls: 'whale',    icon: 'fa-solid fa-water',          emoji: '🐳' },
  { min: 5000,  name: 'Diamond',  cls: 'diamond',  icon: 'fa-solid fa-gem',            emoji: '💎' },
  { min: 2500,  name: 'Platinum', cls: 'platinum', icon: 'fa-solid fa-star',           emoji: '⭐' },
  { min: 1000,  name: 'Gold',     cls: 'gold',     icon: 'fa-solid fa-medal',          emoji: '🥇' },
  { min: 500,   name: 'Silver',   cls: 'silver',   icon: 'fa-solid fa-shield',         emoji: '🥈' },
  { min: 100,   name: 'Bronze',   cls: 'bronze',   icon: 'fa-solid fa-shield-halved',  emoji: '🥉' },
  { min: 0,     name: 'Newcomer', cls: 'rookie',   icon: 'fa-solid fa-seedling',       emoji: '🌱' },
];
const getUserRank = (value) => USER_RANKS.find(r => value >= r.min) || USER_RANKS[USER_RANKS.length - 1];
// Rank emoji derived from numeric level. Lifetime value also works (USER_RANKS thresholds).
const rankEmojiForLevel = (level) => {
  const lvl = Number(level) || 1;
  if (lvl >= 60) return '🐳';
  if (lvl >= 40) return '💎';
  if (lvl >= 25) return '⭐';
  if (lvl >= 15) return '🥇';
  if (lvl >= 8)  return '🥈';
  if (lvl >= 3)  return '🥉';
  return '🌱';
};

function NotificationsDropdown({ items, onClose, onClear, t }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const handler = (e) => {
      if (e.target.closest('.h-notif-wrap')) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const fmtTime = (ts) => {
    const diff = Date.now() - ts;
    const m = Math.floor(diff / 60000);
    if (m < 1) return t?.notifNow || 'now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d`;
  };
  return (
    <div className="notif-menu" ref={ref}>
      <div className="notif-head">
        <span className="notif-title">{t?.notifTitle || 'Notifications'}</span>
        {items.length > 0 && (
          <button className="notif-clear" onClick={onClear}>{t?.notifClear || 'Clear all'}</button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="notif-empty">{t?.notifEmpty || 'No notifications yet.'}</div>
      ) : (
        <div className="notif-list">
          {items.map(n => (
            <div key={n.id} className={`notif-item kind-${n.kind || 'info'} ${n.read ? 'read' : 'unread'}`}>
              <span className="notif-dot"/>
              <div className="notif-body">
                <div className="notif-row">
                  <span className="notif-text-title">{n.title}</span>
                  <span className="notif-time">{fmtTime(n.ts)}</span>
                </div>
                {n.text && <div className="notif-text">{n.text}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ balance, onWallet, onClose, onPage, theme, onThemeToggle, t, currentUser, onLogout, onQuests, onFriends }) {
  const ref = React.useRef(null);
  const rank = getUserRank(balance);
  React.useEffect(() => {
    const handler = (e) => {
      if (e.target.closest('.h-avatar-wrap')) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const dispName = currentUser ? (currentUser.username || currentUser.username) : 'user';
  const handle = currentUser ? currentUser.username : 'guest';
  const initials = (dispName || '??').replace(/[^a-zA-Z0-9]/g, '').substring(0,2).toUpperCase() || '??';
  return (
    <div className="profile-menu" ref={ref}>
      <div className="pm-user">
        <div className="pm-avatar">
          {currentUser?.avatar_url
            ? <img src={currentUser.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'inherit'}}/>
            : initials}
        </div>
        <div className="pm-info">
          <div className="pm-name">@{handle}</div>
          <div className={`pm-rank rank-${rank.cls}`}>
            <i className={rank.icon}/> {rank.name}
          </div>
        </div>
      </div>
      <div className="pm-balance">
        <div className="pm-balance-lbl"><I name="wallet" size={12}/> VALUE</div>
        <div className="pm-balance-val">{fmtVal(balance)}</div>
      </div>
      {currentUser && (
        <div className="pm-xp">
          <div className="pm-xp-head">
            <span className="pm-xp-lvl">Level {currentUser.level || 1}</span>
            <span className="pm-xp-num">{currentUser.xp_in_level || 0} / {currentUser.xp_to_next || 0} XP</span>
          </div>
          <div className="pm-xp-bar"><div className="pm-xp-fill" style={{width: `${Math.min(100, currentUser.xp_pct || 0)}%`}}/></div>
        </div>
      )}
      <button className="pm-row pm-primary" onClick={() => { onClose(); onPage('profile'); }}>
        <span className="pm-icon"><I name="user" size={13} color="white"/></span>
        <span className="pm-label">{t?.pmProfile || 'Profile'}</span>
        <I name="arrow" size={12} color="white"/>
      </button>
      <button className="pm-row" onClick={() => { onClose(); onWallet(); }}>
        <span className="pm-icon"><I name="wallet" size={13}/></span>
        <span className="pm-label">{t?.pmInventory || 'Inventory'}</span>
        <I name="arrow" size={11} color="#838383"/>
      </button>
      <button className="pm-row" onClick={() => { onClose(); onQuests && onQuests(); }}>
        <span className="pm-icon"><i className="fa-solid fa-medal" style={{fontSize:13}}/></span>
        <span className="pm-label">{t?.pmLevelRewards || 'Level Rewards'}</span>
        <I name="arrow" size={11} color="#838383"/>
      </button>
      <button className="pm-row" onClick={() => { onClose(); onPage('affiliates'); }}>
        <span className="pm-icon"><i className="fa-solid fa-users" style={{fontSize:13}}/></span>
        <span className="pm-label">{t?.pmAffiliates || 'Affiliates'}</span>
        <I name="arrow" size={11} color="#838383"/>
      </button>
      <button className="pm-row" onClick={() => { onClose(); onPage('rakeback'); }}>
        <span className="pm-icon"><i className="fa-solid fa-rotate-left" style={{fontSize:13}}/></span>
        <span className="pm-label">{t?.pmRakeback || 'Rakeback'}</span>
        <I name="arrow" size={11} color="#838383"/>
      </button>
      <button className="pm-row" onClick={() => { onClose(); onPage('settings'); }}>
        <span className="pm-icon"><i className="fa-solid fa-gear" style={{fontSize:13}}/></span>
        <span className="pm-label">{t?.pmSettings || 'Settings'}</span>
        <I name="arrow" size={11} color="#838383"/>
      </button>
      {/* Theme toggle moved out of profile menu — lives in Settings page now */}
      {currentUser?.is_value_manager && (
        <button className="pm-row" onClick={() => { onClose(); window.dispatchEvent(new Event('blox:open-value-manager')); }}>
          <span className="pm-icon"><i className="fa-solid fa-gem" style={{fontSize:13}}/></span>
          <span className="pm-label">Value Manager</span>
          <I name="arrow" size={11} color="#838383"/>
        </button>
      )}
      {currentUser?.is_admin && (
        <button className="pm-row" onClick={() => { onClose(); onPage('admin'); }}>
          <span className="pm-icon"><i className="fa-solid fa-shield-halved" style={{fontSize:13}}/></span>
          <span className="pm-label">Admin panel</span>
          <I name="arrow" size={11} color="#838383"/>
        </button>
      )}
      <button className="pm-row pm-danger" onClick={() => { onClose(); onLogout && onLogout(); }}>
        <span className="pm-icon"><I name="close" size={13}/></span>
        <span className="pm-label">{t?.pmLogout || 'Log out'}</span>
      </button>
    </div>
  );
}

// ============================================
// BANNER CAROUSEL — pure image slots, admin-controlled later
// Each slot can hold an image (banner.image = "assets/images/banners/..webp")
// Click destination is configurable per banner (link → page name)
// ============================================
const BANNERS = [
  {
    id: 'b1',
    image: 'assets/images/eventbanner1.webp',
    title: 'Chroma Evergreen Race',
    subtitle: 'Top 10 wagerers this month win exclusive Chroma items',
    cta: 'View leaderboard',
    link: 'rewards',
    accent: 'purple',
  },
];

// ============================================
// EVENT CALLOUT — text-based event banner with CTA → leaderboard / rewards
// ============================================
function EventCallout({ onGoLeaderboard, onGoRewards }) {
  const target = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
  const { days } = useCountdown(target);
  const topPrize = REWARDS_RACE.prizes[0];
  return (
    <button className="event-callout" onClick={onGoLeaderboard}>
      <span className="ec-pill"><i className="fa-solid fa-gift"/></span>
      <span className="ec-text">
        <strong>{REWARDS_RACE.title}</strong>
        <span className="ec-sep">·</span>
        <span className="ec-time">Ends in {days}d</span>
        <span className="ec-sep">·</span>
        <span>#1: <em>{topPrize.item}</em></span>
      </span>
      <span className="ec-cta">
        Leaderboard <I name="arrow" size={11}/>
      </span>
    </button>
  );
}

// Activity ticker — scrolling band of last 30 events (wins, tips, big new flips).
// Polls /api/activity every 12s. Cached for 8s server-side so polling is cheap.
function ActivityTicker({ onOpenFlip }) {
  const [events, setEvents] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiFetch('/api/activity').then(res => {
        if (!cancelled) setEvents(res.events || []);
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (events.length === 0) return null;

  // Duplicate the list so the marquee can loop seamlessly (translate -50%)
  const doubled = [...events, ...events];

  const renderEvent = (e, i) => {
    if (e.kind === 'tip') {
      return (
        <span key={`tip-${i}`} className="ticker-event ticker-tip">
          <i className="fa-solid fa-gift"/>
          <b>@{e.sender}</b> tipped <i>{e.item}</i> to <b>@{e.receiver}</b>
        </span>
      );
    }
    if (e.kind === 'whale_win' || e.kind === 'big_win') {
      return (
        <button
          key={`win-${i}-${e.flip_id}`}
          className={`ticker-event ticker-win ${e.kind === 'whale_win' ? 'is-whale' : ''}`}
          onClick={() => onOpenFlip && onOpenFlip(e.flip_id)}
        >
          <i className="fa-solid fa-trophy"/>
          <b>@{e.winner}</b> won {fmtVal(e.value)} from @{e.loser}
          {e.kind === 'whale_win' && <span className="ticker-tag-whale">🐋 WHALE</span>}
        </button>
      );
    }
    if (e.kind === 'win') {
      return (
        <button
          key={`w-${i}-${e.flip_id}`}
          className="ticker-event ticker-win-small"
          onClick={() => onOpenFlip && onOpenFlip(e.flip_id)}
        >
          <i className="fa-solid fa-circle-check"/>
          <b>@{e.winner}</b> {fmtVal(e.value)}
        </button>
      );
    }
    if (e.kind === 'big_open') {
      return (
        <button
          key={`o-${i}-${e.flip_id}`}
          className="ticker-event ticker-big-open"
          onClick={() => onOpenFlip && onOpenFlip(e.flip_id)}
        >
          <i className="fa-solid fa-bolt"/>
          <b>@{e.creator}</b> opened a {fmtVal(e.value)} flip
        </button>
      );
    }
    return null;
  };

  return (
    <div className="activity-ticker">
      <div className="ticker-live"><span className="ticker-pulse-dot"/>LIVE</div>
      <div className="ticker-viewport">
        <div className="ticker-track">
          {doubled.map(renderEvent)}
        </div>
      </div>
    </div>
  );
}

function BannerCarousel({ onGo }) {
  // Dismiss state — if user clicked × within last 2 hours, don't render banner.
  // Stored in localStorage as a timestamp.
  const [dismissed, setDismissed] = React.useState(() => {
    try {
      const ts = Number(localStorage.getItem('bloxhot.bannerDismissedAt') || 0);
      return ts && (Date.now() - ts) < 2 * 60 * 60 * 1000;
    } catch (_) { return false; }
  });
  // Track which banner image URLs failed to load. If a banner's image is
  // missing on the server (404) we hide the whole banner rather than show
  // an empty white box with just a dismiss × button.
  const [brokenImages, setBrokenImages] = React.useState(() => new Set());
  const markBroken = React.useCallback((src) => {
    setBrokenImages(prev => {
      if (prev.has(src)) return prev;
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  }, []);
  const dismiss = (e) => {
    if (e) e.stopPropagation();
    try { localStorage.setItem('bloxhot.bannerDismissedAt', String(Date.now())); } catch (_) {}
    setDismissed(true);
  };
  // INFINITE LOOP CAROUSEL — prepend the last slide + append the first
  // so the track is [last-clone, ...real, first-clone]. virtualPos walks
  // through this extended array (0..N+1). When animation lands on a clone
  // position, snap to the real equivalent without animation. That gives
  // the illusion of seamless wraparound: swiping past the last banner
  // continues smoothly into the first, never teleporting backward.
  // Skip banners whose image 404'd so we never show an empty white box.
  const ACTIVE_BANNERS = React.useMemo(
    () => BANNERS.filter(b => !b.image || !brokenImages.has(b.image)),
    [brokenImages]
  );
  const N = ACTIVE_BANNERS.length;
  const [idx, setIdx] = React.useState(0);           // real banner 0..N-1
  const [virtualPos, setVirtualPos] = React.useState(1); // position in extended array 0..N+1
  const [noTransition, setNoTransition] = React.useState(false);
  const [dragX, setDragX] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const dragStart = React.useRef({ x: 0, t: 0, moved: false });
  const carouselRef = React.useRef(null);
  const intervalRef = React.useRef(null);

  // NOTE: previous version had `if (dismissed) return null` HERE — before the
  // useEffect below. That's a Rules-of-Hooks violation: clicking dismiss caused
  // the component to render fewer hooks than before, which React detects and
  // crashes → the entire page goes white. Early return moved to the END so
  // every render path has the same hook count.

  // Ignore nav calls during a mid-snap state — rapid swipes were skipping
  // virtualPos past the clone slots and leaving the carousel showing
  // empty space beyond the array.
  const snapLockRef = React.useRef(false);
  const goNext = () => {
    if (snapLockRef.current) return;
    setIdx(i => (i + 1) % N);
    setVirtualPos(p => Math.min(p + 1, N + 1));
  };
  const goPrev = () => {
    if (snapLockRef.current) return;
    setIdx(i => (i - 1 + N) % N);
    setVirtualPos(p => Math.max(p - 1, 0));
  };
  const jumpTo = (i) => {
    setIdx(i);
    setVirtualPos(i + 1); // +1 for prepended clone
  };

  const startTimer = () => {
    if (N <= 1) return;
    stopTimer();
    intervalRef.current = setInterval(goNext, 5000);
  };
  const stopTimer = () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  };

  React.useEffect(() => {
    if (dismissed) return; // skip timer when hidden, but still register the hook
    startTimer();
    return stopTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed]);

  // After a slide animation lands on a clone, snap to the real position
  // with transitions disabled so the snap is invisible.
  const onTrackTransitionEnd = () => {
    if (virtualPos === 0) {
      snapLockRef.current = true;
      setNoTransition(true);
      setVirtualPos(N);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setNoTransition(false);
        snapLockRef.current = false;
      }));
    } else if (virtualPos === N + 1) {
      snapLockRef.current = true;
      setNoTransition(true);
      setVirtualPos(1);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setNoTransition(false);
        snapLockRef.current = false;
      }));
    }
  };

  // Safe early return — all hooks above this line have been called.
  // No active banners (all images broken, or BANNERS empty) → render nothing
  // so we don't leave an empty white box with just a dismiss × button.
  if (dismissed) return null;
  if (N === 0)   return null;

  const stop = (e) => { e.stopPropagation(); };
  const prev = (e) => { stop(e); goPrev(); };
  const next = (e) => { stop(e); goNext(); };
  const goTo = (i) => (e) => { stop(e); jumpTo(i); };
  const onClickBanner = () => {
    if (dragStart.current.moved) { dragStart.current.moved = false; return; }
    const b = ACTIVE_BANNERS[idx];
    if (b && b.link && onGo) onGo(b.link);
  };

  // ---- Drag / swipe handlers ----
  const getX = (e) => (e.touches ? e.touches[0].clientX : e.clientX);
  const onDragStart = (e) => {
    if (N <= 1) return;
    stopTimer();
    setDragging(true);
    setDragX(0);
    dragStart.current = { x: getX(e), t: Date.now(), moved: false };
  };
  const onDragMove = (e) => {
    if (!dragging) return;
    let dx = getX(e) - dragStart.current.x;
    if (Math.abs(dx) > 5) dragStart.current.moved = true;
    // Clamp drag to ±90% of the carousel width — prevents the user from
    // pulling the track past the cloned slides (which would expose the
    // empty space beyond and show a white area).
    const width = (carouselRef.current && carouselRef.current.offsetWidth) || window.innerWidth;
    const max = width * 0.9;
    if (dx > max)  dx = max;
    if (dx < -max) dx = -max;
    setDragX(dx);
  };
  const onDragEnd = () => {
    if (!dragging) return;
    const dt = Date.now() - dragStart.current.t;
    const dx = dragX;
    const width = carouselRef.current ? carouselRef.current.offsetWidth : window.innerWidth;
    const threshold = Math.min(80, width * 0.2);
    const fast = dt < 250 && Math.abs(dx) > 30;
    if      (dx < -threshold || (fast && dx < 0)) goNext();
    else if (dx >  threshold || (fast && dx > 0)) goPrev();
    setDragX(0);
    setDragging(false);
    startTimer();
  };

  // Render with cloned slides at the ends for seamless wraparound — but ONLY
  // when there are 2+ banners. In single-banner mode there are no clones, so
  // we must use trackOffset = 0 (not -virtualPos * 100, which would push the
  // sole slide 100% off-screen leaving an empty grey rectangle visible —
  // that was the "banner empty white box with nothing inside" bug).
  const isSingle = N <= 1;
  const slides = isSingle
    ? ACTIVE_BANNERS
    : [ACTIVE_BANNERS[N - 1], ...ACTIVE_BANNERS, ACTIVE_BANNERS[0]];
  const trackOffset = isSingle ? 0 : -virtualPos * 100;
  const dragPct = carouselRef.current ? (dragX / carouselRef.current.offsetWidth) * 100 : 0;

  return (
    <section className="hero-wrap">
      {/* Dismiss button — hides the carousel for 2h on click */}
      <button className="bc-dismiss" onClick={dismiss} aria-label="dismiss banner">
        <i className="fa-solid fa-xmark"/>
      </button>
      <div
        ref={carouselRef}
        className={`banner-carousel ${dragging ? 'is-dragging' : ''} ${N <= 1 ? 'is-single' : ''}`}
        onMouseDown={N > 1 ? onDragStart : undefined}
        onMouseMove={dragging ? onDragMove : null}
        onMouseUp={onDragEnd}
        onMouseLeave={dragging ? onDragEnd : null}
        onTouchStart={N > 1 ? onDragStart : undefined}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
      >
        <div
          className="bc-track"
          onTransitionEnd={onTrackTransitionEnd}
          style={{
            // translate3d (not translateX) forces GPU compositing — smoother
            // animation on mobile, no per-frame repaint of the big images.
            transform: `translate3d(${trackOffset + dragPct}%, 0, 0)`,
            transition: (dragging || noTransition) ? 'none' : undefined,
          }}
        >
          {slides.map((b, i) => (
            <div className={`bc-slide bc-accent-${b.accent || 'purple'} ${b.image ? 'has-bg' : ''}`} key={`${b.id}-${i}`}>
              {b.image && <img
                className="bc-bg"
                src={b.image + (typeof window !== 'undefined' && window.BLOXHOT_BANNER_VER ? '?v=' + window.BLOXHOT_BANNER_VER : '')}
                alt=""
                /* loading=lazy + fetchPriority=low so the banner artwork
                   never competes with critical content for bandwidth on
                   first paint. Mobile users on 3G feel this. */
                loading="lazy"
                fetchpriority="low"
                decoding="async"
                onError={() => markBroken(b.image)}
              />}
              <div className="bc-content">
                {b.link === 'rewards' ? (() => {
                  // Rewards banner content — instead of a static title we
                  // headline with the actual prize pool value. The race
                  // identity is communicated by the artwork in the
                  // background image; subtitle gives context.
                  const pool = REWARDS_RACE.prizes.reduce((s, p) => s + (Number(p.value) || 0), 0);
                  return (
                    <>
                      <div className="bc-eyebrow">{tt('rewardsPrizePool', 'Prize pool').toUpperCase()}</div>
                      <h2 className="bc-title bc-title--pool">
                        <i className="fa-solid fa-gem gem-i"/>{fmtVal(pool)}
                      </h2>
                      <p className="bc-subtitle">{tt('rewardsBannerSub', 'Top 10 wagerers this month win exclusive Chroma items')}</p>
                      {b.cta && (
                        <button
                          type="button"
                          className="bc-cta"
                          onClick={(e) => { e.stopPropagation(); if (b.link && onGo) onGo(b.link); }}
                        >
                          {b.cta} <i className="fa-solid fa-arrow-right"/>
                        </button>
                      )}
                    </>
                  );
                })() : (
                  <>
                    {b.title && <h2 className="bc-title">{b.title}</h2>}
                    {b.subtitle && <p className="bc-subtitle">{b.subtitle}</p>}
                    {b.cta && (
                      <button
                        type="button"
                        className="bc-cta"
                        onClick={(e) => { e.stopPropagation(); if (b.link && onGo) onGo(b.link); }}
                      >
                        {b.cta} <i className="fa-solid fa-arrow-right"/>
                      </button>
                    )}
                  </>
                )}
              </div>
              {/* Top-3 prize tiles — anchored to the right side of the banner.
                  Each tile shows the rank, item artwork and value. Provides
                  the "1st gets X, 2nd gets Y" visual the user asked for
                  without a separate ranking widget. Hidden on narrow phones. */}
              {b.link === 'rewards' && (
                <div className="bc-podium" aria-hidden="true">
                  {REWARDS_RACE.prizes.slice(0, 3).map((p) => (
                    <div key={p.rank} className={`bc-podium-tile rank-${p.rank}`}>
                      <span className="bc-podium-rank">#{p.rank}</span>
                      <div className="bc-podium-img">
                        <img
                          src={itemImg(p.item)}
                          alt=""
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                      <span className="bc-podium-val">
                        <i className="fa-solid fa-gem gem-i"/>{fmtVal(p.value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {N > 1 && (
          <>
            {/* Both arrows always visible — carousel is infinite */}
            <button className="bc-arrow bc-prev" onClick={prev} aria-label="prev">
              <i className="fa-solid fa-chevron-left"/>
            </button>
            <button className="bc-arrow bc-next" onClick={next} aria-label="next">
              <i className="fa-solid fa-chevron-right"/>
            </button>
            <div className="bc-dots" onClick={stop}>
              {ACTIVE_BANNERS.map((_, i) => (
                <button key={i} className={`bc-dot ${i===idx?'is-active':''}`} onClick={goTo(i)} aria-label={`banner ${i+1}`}/>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Tickers({ stats, t, loading }) {
  if (loading) {
    return (
      <div className="tickers tickers-3">
        {[0,1,2].map(i => (
          <div className="ticker" key={i}>
            <div className="lbl"><Skeleton w={26} h={26} radius={7}/><Skeleton w={90} h={11}/></div>
            <div className="val"><Skeleton w={70} h={22} radius={4}/></div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="tickers tickers-3">
      <div className="ticker">
        <div className="lbl"><i className="fa-solid fa-gem ticker-fa ticker-fa--orange"/>{t.totalValue}</div>
        <div className="val"><CountUp to={stats.totalValue} fmt={fmtVal}/></div>
      </div>
      <div className="ticker">
        <div className="lbl"><i className="fa-solid fa-cubes-stacked ticker-fa ticker-fa--blue"/>{t.totalItems}</div>
        <div className="val"><CountUp to={stats.totalItems}/></div>
      </div>
      <div className="ticker">
        <div className="lbl"><i className="fa-solid fa-bolt ticker-fa ticker-fa--green"/>{t.activeGames}</div>
        <div className="val"><CountUp to={stats.activeGames}/><span className="ticker-pulse"/></div>
      </div>
    </div>
  );
}

function Featured({ flip, onJoin, t }) {
  const total = flipValue(flip.items || []);
  return (
    <div className="featured">
      <div className="featured-text">
        <span className="featured-tag">⚡ {t.whaleFlip || 'Whale flip'}</span>
        <h3>{flip.c.name} · {flip.items?.length || 0} items</h3>
        <p>{t.whaleSub || 'Match this flip within 2 minutes to claim everything.'}</p>
      </div>
      <div style={{textAlign:'right'}}>
        <div className="featured-prize">{fmtVal(total)}</div>
        <div style={{fontSize:11,color:'#8C4500',marginTop:2}}>{t.totalValue || 'Total value'}</div>
      </div>
      <button className="featured-cta" onClick={() => onJoin(flip)}>
        {t.matchFlip || 'Match flip'} <I name="arrow" size={13} color="white"/>
      </button>
    </div>
  );
}

const FlipItem = React.memo(function FlipItem({ item, size = 50, noTooltip = false }) {
  // Portal-based hover tooltip so the full item name shows even when the
  // item lives inside a modal / picker grid with overflow:hidden|auto. The
  // tooltip is computed in fixed coordinates from the item's bounding box.
  // Previously the CSS-only tooltip got clipped for items like
  // "Chroma Constellation" in the inventory grid.
  //
  // noTooltip=true: skip the hover tooltip entirely. Use this when the
  // surrounding picker already shows the item name as a label below the
  // image (inventory page, create-flip picker, giveaway picker, tip flow) —
  // the tooltip is redundant noise there.
  const ref = React.useRef(null);
  const [tip, setTip] = React.useState(null); // null | { x, y }
  if (!item) return null;

  const show = () => {
    if (noTooltip) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setTip({ x: r.left + r.width / 2, y: r.top });
  };
  const hide = () => setTip(null);

  return (
    <div
      ref={ref}
      className={`fc-item r-${item.rarity}`}
      style={{ width: size, height: size }}
      onMouseEnter={noTooltip ? undefined : show}
      onMouseLeave={noTooltip ? undefined : hide}
      onTouchStart={noTooltip ? undefined : show}
      onTouchEnd={noTooltip ? undefined : hide}
    >
      <div className="inner">
        <img src={itemImg(item.name)} alt={item.name} loading="lazy" decoding="async"
             onLoad={(e) => { e.currentTarget.dataset.loaded = '1'; }}
             onError={(e) => { e.currentTarget.style.display='none'; }}/>
      </div>
      {tip && ReactDOM.createPortal(
        <span
          className="fc-item-tooltip is-portal"
          style={{ left: tip.x, top: tip.y, transform: 'translate(-50%, calc(-100% - 8px))' }}
        >{item.name}</span>,
        document.body
      )}
    </div>
  );
});

function UserPopover({ username, pos, onClose, onOpenProfile, onTip, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    apiFetch(`/api/user?username=${encodeURIComponent(username)}`)
      .then(res => { if (!cancelled) setData(res); })
      .catch(e => { if (!cancelled) setErr(e.message || 'Not found'); });
    return () => { cancelled = true; };
  }, [username]);

  const u = data?.user;
  const wishlist = data?.wishlist || [];
  const showcase = data?.showcase || [];

  return (
    <div className={`modal-overlay user-pop-overlay ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal user-pop-modal" onClick={(e) => e.stopPropagation()}>
      {err ? <div className="up-err">{err}</div> : !u ? (
        <div className="up-loading">{t?.loading || 'Loading…'}</div>
      ) : (
        <>
          {/* Banner header with avatar overlapping */}
          <div className={`up-banner prof-banner-${Number(data.profile_banner || 1)}`}>
            <button className="modal-close up-close-floating" onClick={close} aria-label="close"><I name="close" size={18}/></button>
          </div>
          <div className="up-head">
            <div className="up-av-wrap">
              {u.avatar_url
                ? <img src={u.avatar_url} alt="" className="up-av"/>
                : <div className="up-av up-av-fallback">{synthAvatar(u.username || u.username)}</div>}
              <span className="up-level">Lv {u.level || 1}</span>
            </div>
            <div className="up-meta">
              {/* User asked for ONLY the @handle to show — the duplicate plain
                  username above was redundant since username === handle in
                  Roblox auth (no separate display name). */}
              <div className="up-name">
                @{u.username}
                {data?.generous_active && <span className="generous-pill" title="Tipped someone in the last 24h">🎁 Generous</span>}
              </div>
              {(u.total_flips || 0) > 0 && <div className="up-handle">{u.win_rate}% win rate · {u.current_streak || 0}-flip streak</div>}
            </div>
          </div>
          <div className="up-stats">
            <div className="up-stat"><div className="up-stat-lbl">{t?.popFlips || 'Flips'}</div><div className="up-stat-val">{u.total_flips}</div></div>
            <div className="up-stat"><div className="up-stat-lbl">{t?.popWins || 'Wins'}</div><div className="up-stat-val">{u.total_wins}</div></div>
            <div className="up-stat"><div className="up-stat-lbl">{t?.popLosses || 'Losses'}</div><div className="up-stat-val">{Math.max(0, (u.total_flips || 0) - (u.total_wins || 0))}</div></div>
            <div className="up-stat"><div className="up-stat-lbl">{t?.popWagered || 'Total bet'}</div><div className="up-stat-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(u.total_value)}</div></div>
            <div className="up-stat"><div className="up-stat-lbl">{t?.popBiggest || 'Biggest win'}</div><div className="up-stat-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(u.biggest_win)}</div></div>
            <div className="up-stat"><div className="up-stat-lbl">{t?.popStreak || 'Current streak'}</div><div className="up-stat-val">{u.current_streak || 0}</div></div>
          </div>
          {showcase.length > 0 && (
            <div className="up-section">
              <div className="up-section-title">✨ Featured</div>
              <div className="up-mini-row">
                {showcase.slice(0, 6).map(it => (
                  <div key={it.id} className="up-mini-item" title={`${it.name} · ${fmtVal(it.price)}`}>
                    <img src={`assets/images/${it.name}.webp`} alt={it.name} onError={(e) => { e.currentTarget.style.opacity = 0; }}/>
                  </div>
                ))}
              </div>
            </div>
          )}
          {wishlist.length > 0 && (
            <div className="up-section">
              <div className="up-section-title">🌟 Wishlist</div>
              <div className="up-mini-row">
                {wishlist.slice(0, 6).map(name => (
                  <div key={name} className="up-mini-item up-mini-wish" title={name}>
                    <img src={`assets/images/${name}.webp`} alt={name} onError={(e) => { e.currentTarget.style.opacity = 0; }}/>
                  </div>
                ))}
                {/* "tip these for +XP bonus" hint removed per user request */}
              </div>
            </div>
          )}
          <div className="up-cta-row">
            <button className="up-cta" onClick={() => { close(); onOpenProfile && onOpenProfile(u.username); }}>
              {t?.popViewProfile || 'View full profile'} <i className="fa-solid fa-arrow-right"/>
            </button>
            {onTip && (
              <button className="up-tip-btn" onClick={() => { close(); onTip(u); }} title={t?.popTip || 'Send a tip'}>
                <i className="fa-solid fa-gift"/>
              </button>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
}

// Tip another user a single inventory item. Opens from the user popover.
// User picks one item from their (unlocked) inventory and confirms.
// Plays once when a tip is received — auto-dismisses after 2.6s.
// Renders a centered bouncing item image + 24 confetti pieces.
function TipReceivedAnimation({ tip, onDone }) {
  React.useEffect(() => {
    const t = setTimeout(() => onDone && onDone(), 2600);
    return () => clearTimeout(t);
  }, [onDone]);

  const sender = tip.sender_display || tip.sender_username || 'Someone';
  // 24 confetti chips with randomized angles + delays
  const chips = React.useMemo(() => Array.from({ length: 24 }).map((_, i) => ({
    angle: (i * 15) + (Math.random() * 10 - 5),
    delay: Math.random() * 200,
    color: ['#10B981','#FFD64A','#FF3D9A','#7C3AED','#34D9A0','#5C9CFF'][i % 6],
  })), []);

  return (
    <div className="tip-anim-overlay" onClick={onDone}>
      <div className="tip-anim-stage">
        {chips.map((c, i) => (
          <span
            key={i}
            className="tip-anim-chip"
            style={{
              '--ang': c.angle + 'deg',
              '--delay': c.delay + 'ms',
              background: c.color,
            }}
          />
        ))}
        <div className="tip-anim-card">
          <img
            src={`assets/images/${tip.item_name}.webp`}
            alt={tip.item_name}
            className="tip-anim-img"
            onError={(e) => { e.currentTarget.style.opacity = 0.4; }}
          />
          <div className="tip-anim-text">
            <div className="tip-anim-from">💝 from @{sender}</div>
            <div className="tip-anim-item">{tip.item_name}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TipModal({ receiver, userInventory, onClose, onDone, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);
  const [picked, setPicked] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const available = React.useMemo(() => userInventory.filter(it => !it.lockedFlipId), [userInventory]);

  // Send button now opens a confirm step first to prevent accidental gifts.
  const trySend = () => {
    if (!picked) return;
    setConfirming(true);
  };

  const doSend = async () => {
    if (busy || !picked) return;
    setBusy(true); setErr('');
    const sentInvId = picked.id;
    try {
      const res = await apiFetch('/api/tips', {
        method: 'POST',
        body: { receiver_id: receiver.id, inventory_id: sentInvId },
      });
      onDone && onDone(res, sentInvId);
      animateThen(onClose);
    } catch (e) {
      setErr(e.message || 'Tip failed');
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal tip-modal" ref={sheetRef} style={{maxWidth: 480}}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>🎁 {t?.tipTitle || 'Send a tip'}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body">
          <div className="tip-receiver">
            {receiver.avatar_url ? <img src={receiver.avatar_url} alt="" className="tip-recv-av"/> : <div className="tip-recv-av tip-recv-av-fallback">{synthAvatar(receiver.username || receiver.username)}</div>}
            <div>
              <div className="tip-recv-name">{receiver.username || receiver.username}</div>
              <div className="tip-recv-sub">@{receiver.username}</div>
            </div>
          </div>
          <div className="tip-sub">{t?.tipPick || 'Pick one item from your inventory to gift.'}</div>
          {available.length === 0 ? (
            <div className="tip-empty">{t?.tipEmpty || 'No unlocked items in your inventory.'}</div>
          ) : (
            <div className="tip-grid">
              {available.slice(0, 60).map(it => (
                <button
                  key={it.id}
                  className={`tip-cell ${picked?.id === it.id ? 'is-picked' : ''}`}
                  onClick={() => setPicked(it)}
                  title={it.name}
                >
                  <FlipItem item={it} size={48} noTooltip/>
                  <div className="tip-cell-name">{it.name}</div>
                  <div className="tip-cell-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                </button>
              ))}
            </div>
          )}
          {err && <div className="tip-err">{err}</div>}
          <button
            className="modal-cta"
            disabled={!picked || busy}
            onClick={trySend}
          >
            {picked ? `${t?.tipSend || 'Send'} ${picked.name}` : (t?.tipPickPrompt || 'Pick an item')}
          </button>
        </div>
        {confirming && picked && (
          <div className="tip-confirm">
            <div className="tip-confirm-box">
              <div className="tip-confirm-title">{t?.tipConfirmTitle || 'Send this tip?'}</div>
              <div className="tip-confirm-msg">
                {t?.tipConfirmMsg || 'Once you send, this item moves to'} <b>@{receiver.username}</b>.
                <br/>{t?.tipConfirmIrrev || 'This can\'t be undone.'}
              </div>
              <div className="tip-confirm-preview">
                <FlipItem item={picked} size={56} noTooltip/>
                <div>
                  <div className="tip-cell-name" style={{fontSize:13}}>{picked.name}</div>
                  <div className="tip-cell-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(picked.price)}</div>
                </div>
              </div>
              <div className="tip-confirm-actions">
                <button className="tip-confirm-cancel" onClick={() => setConfirming(false)} disabled={busy}>
                  {t?.cancel || 'Cancel'}
                </button>
                <button className="tip-confirm-send" onClick={doSend} disabled={busy}>
                  {busy ? (t?.tipSending || 'Sending…') : (t?.tipConfirmYes || 'Yes, send it')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FlipContextMenu({ flip, pos, onClose, onWatch, onViewProfile, onReport, t }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  // Clamp so the menu stays in viewport
  const w = 180, h = 132;
  const x = Math.min(pos.x, window.innerWidth - w - 8);
  const y = Math.min(pos.y, window.innerHeight - h - 8);

  return (
    <div ref={ref} className="fc-ctx" style={{ left: x, top: y, width: w }}>
      <button className="fc-ctx-item" onClick={() => { onClose(); onWatch(flip); }}>
        <i className="fa-solid fa-eye"/> {t?.ctxView || 'View flip'}
      </button>
      <button className="fc-ctx-item" onClick={() => { onClose(); onViewProfile(flip.c.username || flip.c.name); }}>
        <i className="fa-solid fa-user"/> {t?.ctxProfile || 'View creator'}
      </button>
      <button className="fc-ctx-item danger" onClick={() => { onClose(); onReport(flip); }}>
        <i className="fa-solid fa-flag"/> {t?.ctxReport || 'Report'}
      </button>
    </div>
  );
}

// Minimal virtualized grid — renders a windowed slice of items, plus spacer
// elements above/below so the scroll height is correct. Used only when the
// inventory is large enough (> 80) to actually need it.
function VirtualGrid({ items, rowH = 120, cols = 5, overscan = 2, renderItem }) {
  const ref = React.useRef(null);
  const [scroll, setScroll] = React.useState(0);
  const [vh, setVh] = React.useState(400);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => setScroll(el.scrollTop);
    const onResize = () => setVh(el.clientHeight || 400);
    onResize();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const rows = Math.ceil(items.length / cols);
  const totalH = rows * rowH;
  const startRow = Math.max(0, Math.floor(scroll / rowH) - overscan);
  const endRow = Math.min(rows, Math.ceil((scroll + vh) / rowH) + overscan);
  const topPad = startRow * rowH;
  const slice = items.slice(startRow * cols, endRow * cols);

  return (
    <div ref={ref} className="vgrid" style={{ overflowY: 'auto', height: '100%' }}>
      <div style={{ height: totalH, position: 'relative' }}>
        <div className="vgrid-inner" style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: 8,
          position: 'absolute', top: topPad, left: 0, right: 0,
        }}>
          {slice.map((it, i) => renderItem(it, startRow * cols + i))}
        </div>
      </div>
    </div>
  );
}

function FriendsModal({ onClose, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);
  const [data, setData] = React.useState({ friends: [], incoming: [], outgoing: [] });
  const [search, setSearch] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const reload = () => apiFetch('/api/friends').then(setData).catch(() => {});
  React.useEffect(() => { reload(); }, []);

  const send = async () => {
    if (!search.trim()) return;
    try {
      await apiFetch('/api/friends', { method: 'POST', body: { username: search.trim() } });
      setMsg(t?.friendsSent || 'Request sent');
      setSearch('');
      reload();
    } catch (err) { setMsg(err.message || 'Failed'); }
    setTimeout(() => setMsg(''), 2000);
  };
  const act = async (user_id, action) => {
    try { await apiFetch('/api/friends', { method: 'PUT', body: { user_id, action } }); reload(); } catch (_) {}
  };

  const row = (u, actions) => (
    <div className="fr-row" key={u.id}>
      <div className="fr-av-wrap">
        {u.avatar_url
          ? <img src={u.avatar_url} alt="" className="fr-av-img"/>
          : <div className="fr-av-fallback">{synthAvatar(u.username || u.username)}</div>}
      </div>
      <div className="fr-meta">
        <div className="fr-name">{u.username || u.username}</div>
        <div className="fr-handle">@{u.username}</div>
      </div>
      <div className="fr-actions">{actions}</div>
    </div>
  );

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal" ref={sheetRef} style={{ maxWidth: 520 }}>
        <div className="modal-head" {...sheetHandlers}>
          <h3><i className="fa-solid fa-user-group" style={{marginRight:8, color:'var(--orange)'}}/>{t?.friendsTitle || 'Friends'}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body">
          <div className="fr-add-row">
            <input className="settings-select" style={{flex:1}} placeholder={t?.friendsSearchPh || 'Roblox username…'} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}/>
            <button className="settings-btn" onClick={send}>{t?.friendsAdd || '+ Add'}</button>
          </div>
          {msg && <div className="ref-msg" style={{margin:'6px 0'}}>{msg}</div>}

          {data.incoming.length > 0 && (<>
            <div className="quests-section-label">{t?.friendsIncoming || 'Incoming requests'}</div>
            {data.incoming.map(u => row(u, <>
              <button className="settings-btn" onClick={() => act(u.id, 'accept')}>{t?.friendsAccept || 'Accept'}</button>
              <button className="settings-btn danger" onClick={() => act(u.id, 'decline')}>{t?.friendsDecline || 'Decline'}</button>
            </>))}
          </>)}

          {data.outgoing.length > 0 && (<>
            <div className="quests-section-label">{t?.friendsOutgoing || 'Outgoing requests'}</div>
            {data.outgoing.map(u => row(u, <>
              <button className="settings-btn danger" onClick={() => act(u.id, 'remove')}>{t?.friendsCancel || 'Cancel'}</button>
            </>))}
          </>)}

          <div className="quests-section-label">{t?.friendsList || `Friends (${data.friends.length})`}</div>
          {data.friends.length === 0
            ? <div className="page-state" style={{margin:0}}>{t?.friendsEmpty || 'No friends yet — invite someone above!'}</div>
            : data.friends.map(u => row(u, <button className="settings-btn danger" onClick={() => act(u.id, 'remove')}>{t?.friendsRemove || 'Remove'}</button>))
          }
        </div>
      </div>
    </div>
  );
}

// Level Rewards: every 1 unit of value flipped = 1 XP. Each "level" needs 100 XP.
// Tier table: 5/10/20/50/100/250/500/1000 — pickup-able rewards.
const LEVEL_REWARDS = [
  // Rank-only — no item drops anymore. The user wanted level rewards to
  // be purely a status / role progression (visible on profile + chat tag).
  { level: 5,    title: 'Bronze rank',  icon: '🥉', desc: 'Bronze rank unlocked' },
  { level: 10,   title: 'Silver rank',  icon: '🥈', desc: 'Silver rank unlocked' },
  { level: 20,   title: 'Gold rank',    icon: '🥇', desc: 'Gold rank unlocked' },
  { level: 50,   title: 'Platinum',     icon: '⭐', desc: 'Platinum rank unlocked' },
  { level: 100,  title: 'Diamond',      icon: '💎', desc: 'Diamond rank unlocked' },
  { level: 250,  title: 'Whale',        icon: '🐳', desc: 'Whale rank unlocked' },
  { level: 500,  title: 'Champion',     icon: '👑', desc: 'Champion rank unlocked' },
  { level: 1000, title: 'Legend',       icon: '🏆', desc: 'Legend rank unlocked' },
];

function LevelRewardsModal({ onClose, currentUser, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);
  const lvl = Number(currentUser?.level || 0);
  const xpInLevel = Number(currentUser?.xp_in_level || 0);
  const xpToNext = Number(currentUser?.xp_to_next || 100);
  const pct = Math.min(100, Math.round((xpInLevel / Math.max(1, xpToNext)) * 100));

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal lvr" ref={sheetRef} style={{maxWidth: 540}}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>{t?.lvlRewards || 'Level Rewards'}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body lvr-body">
          <div className="lvr-current">
            <div className="lvr-cur-num">{lvl}</div>
            <div className="lvr-cur-info">
              <div className="lvr-cur-label">{t?.currentLevel || 'Current level'}</div>
              <div className="lvr-cur-bar"><div className="lvr-cur-bar-fill" style={{width: pct + '%'}}/></div>
              <div className="lvr-cur-sub">{xpInLevel} / {xpToNext} XP · 1 value = 1 XP</div>
            </div>
          </div>
          <div className="lvr-grid">
            {LEVEL_REWARDS.map(r => {
              const unlocked = lvl >= r.level;
              return (
                <div key={r.level} className={`lvr-card ${unlocked ? 'is-unlocked' : ''}`}>
                  <div className="lvr-icon">{r.icon}</div>
                  <div className="lvr-info">
                    <div className="lvr-title">Lv {r.level} · {r.title}</div>
                    <div className="lvr-desc">{r.desc}</div>
                  </div>
                  <div className="lvr-state">{unlocked ? <i className="fa-solid fa-check"/> : <i className="fa-solid fa-lock"/>}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestsModal({ onClose, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);
  const [quests, setQuests] = React.useState([]);
  const [busy, setBusy] = React.useState(null);
  const reload = () => apiFetch('/api/quests').then(r => setQuests(r.quests || []));
  React.useEffect(() => { reload(); }, []);

  const claim = async (code) => {
    setBusy(code);
    try {
      const res = await apiFetch('/api/quests', { method: 'POST', body: { code } });
      reload();
    } catch (err) { /* swallow */ }
    finally { setBusy(null); }
  };

  const daily = quests.filter(q => q.type === 'daily');
  const weekly = quests.filter(q => q.type === 'weekly');

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal" ref={sheetRef} style={{ maxWidth: 480 }}>
        <div className="modal-head" {...sheetHandlers}>
          <h3><i className="fa-solid fa-compass" style={{marginRight:8, color:'var(--orange)'}}/>{t?.questsTitle || 'Quests'}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body">
          {[['Daily', daily], ['Weekly', weekly]].map(([lbl, list]) => list.length > 0 && (
            <div key={lbl}>
              <div className="quests-section-label">{lbl}</div>
              <div className="quests-list">
                {list.map(q => {
                  const pct = Math.min(100, (q.progress / q.goal) * 100);
                  return (
                    <div key={q.code} className={`quest-row ${q.completed ? 'done' : ''} ${q.claimed ? 'claimed' : ''}`}>
                      <div className="qr-meta">
                        <div className="qr-title">{q.title}</div>
                        <div className="qr-desc">{q.desc}</div>
                        <div className="qr-progress"><div className="qr-progress-fill" style={{width: pct + '%'}}/></div>
                        <div className="qr-progress-num">{q.progress} / {q.goal}</div>
                      </div>
                      <div className="qr-reward">
                        <div className="qr-xp">+{q.xp} XP</div>
                        {q.claimed
                          ? <button className="qr-claim claimed" disabled><i className="fa-solid fa-check"/></button>
                          : q.completed
                            ? <button className="qr-claim" disabled={busy === q.code} onClick={() => claim(q.code)}>{t?.questsClaim || 'Claim'}</button>
                            : <button className="qr-claim locked" disabled>{t?.questsLocked || 'In progress'}</button>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Cinematic takeover — fires on 500+ value wins. Full-screen black-radial
// overlay with the won item bouncing in + counter ticking up + confetti.
// Click anywhere or auto-dismiss after 3.5s.
function BigWinCinematic({ data, onEnd }) {
  React.useEffect(() => {
    const t = setTimeout(onEnd, 4200);
    return () => clearTimeout(t);
  }, [onEnd]);
  const total = Math.round(data?.total || 0);

  // CountUp from 0 → total over ~1.8s
  const [shown, setShown] = React.useState(0);
  React.useEffect(() => {
    const start = Date.now();
    const dur = 1800;
    const step = () => {
      const t = Math.min(1, (Date.now() - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(total * eased));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [total]);

  // Three layers of particles for a richer scatter:
  //   • coins (gold/orange small dots, simulate items burst)
  //   • rays  (long thin streaks, radial from center)
  //   • confetti (multi-color squares falling)
  const coins = React.useMemo(() => Array.from({ length: 40 }, (_, i) => {
    const angle = (i / 40) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
    const dist = 180 + Math.random() * 220;
    return {
      i,
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
      delay: Math.random() * 0.3,
      size: 8 + Math.random() * 6,
      rot: (Math.random() * 720 - 360),
    };
  }), []);
  const rays = React.useMemo(() => Array.from({ length: 18 }, (_, i) => ({
    i,
    angle: (i / 18) * 360 + (Math.random() * 8 - 4),
    delay: Math.random() * 0.25,
    length: 220 + Math.random() * 160,
  })), []);
  const confetti = React.useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    i,
    left: Math.random() * 100,
    delay: Math.random() * 1.2,
    duration: 2.5 + Math.random() * 1.8,
    color: ['#10B981','#FFD64A','#FF3D9A','#7C3AED','#34D9A0','#5C9CFF','#EF4444','#22D3EE'][i % 8],
    size: 6 + Math.random() * 6,
    rot: Math.random() * 720,
  })), []);

  return (
    <div className="big-win-overlay" onClick={onEnd}>
      {/* Falling confetti rain — fixed across the whole viewport */}
      <div className="bw-confetti-rain" aria-hidden="true">
        {confetti.map(c => (
          <span
            key={c.i}
            className="bw-confetti-piece"
            style={{
              left: `${c.left}%`,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              background: c.color,
              width: c.size, height: c.size,
              '--rot': `${c.rot}deg`,
            }}
          />
        ))}
      </div>

      <div className="bw-stage">
        {/* Radial rays — give that "spotlight bursting outward" feel */}
        <div className="bw-rays" aria-hidden="true">
          {rays.map(r => (
            <span
              key={r.i}
              className="bw-ray"
              style={{
                '--ang': `${r.angle}deg`,
                '--len': `${r.length}px`,
                animationDelay: `${r.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Golden coin particles bursting outward from center */}
        <div className="bw-coins" aria-hidden="true">
          {coins.map(c => (
            <span
              key={c.i}
              className="bw-coin-particle"
              style={{
                '--tx': `${c.tx}px`,
                '--ty': `${c.ty}px`,
                '--rot': `${c.rot}deg`,
                animationDelay: `${c.delay}s`,
                width: c.size, height: c.size,
              }}
            />
          ))}
        </div>

        <div className="bw-trophy">🏆</div>
        <div className="bw-title">BIG WIN</div>
        <div className="bw-counter"><i className="fa-solid fa-gem gem-i"/>+{shown.toLocaleString()}</div>
        <div className="bw-subtitle">You took the pot!</div>
      </div>
    </div>
  );
}

function BigWinBurst({ onEnd, count = 36 }) {
  React.useEffect(() => {
    const t = setTimeout(onEnd, 1800);
    return () => clearTimeout(t);
  }, [onEnd]);
  const particles = React.useMemo(() => Array.from({ length: count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
    const distance = 120 + Math.random() * 160;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance;
    const delay = Math.random() * 0.15;
    const size = 6 + Math.random() * 6;
    return { i, dx, dy, delay, size, hue: Math.random() < 0.6 ? 'orange' : 'gold' };
  }), [count]);
  return (
    <div className="bw-burst" aria-hidden="true">
      {particles.map(p => (
        <span
          key={p.i}
          className={`bw-particle ${p.hue}`}
          style={{
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--delay': `${p.delay}s`,
            width: p.size, height: p.size,
          }}
        />
      ))}
    </div>
  );
}

// Generic skeleton placeholder — animated shimmer block. Used everywhere
// a value is loading. Variants:
//   <Skeleton w="60%" h={20}/>            — text line
//   <Skeleton circle size={40}/>          — avatar
//   <Skeleton w={80} h={32} radius={8}/>  — chip / button
function Skeleton({ w = '100%', h = 14, circle = false, size, radius = 6, className = '', style = {} }) {
  const finalStyle = {
    width: circle ? size : w,
    height: circle ? size : h,
    borderRadius: circle ? '50%' : radius,
    ...style,
  };
  return <span className={`sk-block ${className}`} style={finalStyle} aria-hidden="true"/>;
}

function FlipCardSkeleton() {
  return (
    <div className="flip-card sk">
      <div className="fc-creator"><div className="sk-circle"/></div>
      <div className="fc-opponent"><div className="sk-circle"/></div>
      <div className="fc-items-wrap">
        <div className="fc-items">
          <div className="sk-item"/><div className="sk-item"/><div className="sk-item"/><div className="sk-item"/>
        </div>
      </div>
      <div className="fc-value"><div className="sk-line w60"/><div className="sk-line w40"/></div>
      <div className="fc-actions"><div className="sk-pill"/></div>
    </div>
  );
}

function FlipCardEqual(prev, next) {
  const a = prev.flip, b = next.flip;
  if (prev.currentUserId !== next.currentUserId) return false;
  if (a === b) return prev.t === next.t;
  if (a.id !== b.id) return false;
  if (a.state !== b.state) return false;
  if (a.side !== b.side) return false;
  if ((a.o?.id || null) !== (b.o?.id || null)) return false;
  if ((a.items?.length || 0) !== (b.items?.length || 0)) return false;
  if (a.result !== b.result) return false;
  if (a.winnerId !== b.winnerId) return false;
  // Participant set changes must invalidate so the rolling banner re-evaluates
  // is-participant when someone joins.
  if (((a.participants || []).length) !== ((b.participants || []).length)) return false;
  if (prev.t !== next.t) return false;
  return true;
}

// 3-second countdown ring overlay shown on a flip card while a battle is in
// progress. Anchored to the server's `joinedAt` (or first-seen-time as
// fallback) so all spectators tick down in lockstep. After the 3 seconds
// expire it auto-hides — the card naturally transitions to the resolved
// state and the winner highlight takes over.
// Parse a MySQL TIMESTAMP string "YYYY-MM-DD HH:MM:SS" as UTC (the server
// stores everything in UTC). Without the explicit 'Z', JavaScript treats
// "YYYY-MM-DD HH:MM:SS" as LOCAL time which produces a 3-hour offset for
// users in Turkey — the bug that was breaking every time-based check.
function parseServerTs(s) {
  if (!s) return 0;
  if (typeof s !== 'string') return new Date(s).getTime() || 0;
  // Already ISO with offset → trust it
  if (/[+-]\d{2}:?\d{2}$|Z$/.test(s)) return new Date(s).getTime() || 0;
  return new Date(s.replace(' ', 'T') + 'Z').getTime() || 0;
}

// ============================================================================
// SHARED SERVER CLOCK — the single source of truth for "what time is it".
// Every /api/flips poll returns `server_now` (UTC ms). We measure how far the
// server clock is ahead of / behind this device's clock and store the offset.
// `serverNow()` then gives us the server's wall-clock time on ANY device.
//
// Why this matters: the coin-flip result must be revealed at the SAME instant
// for everyone — players AND spectators, across phones with wrong clocks and
// laggy connections. By anchoring the reveal to a server timestamp
// (joined_at + animation duration) and converting it through this offset, all
// clients land on the result simultaneously instead of "first-seen + delay"
// which drifted per device.
// ============================================================================
let serverClockOffset = 0;       // serverTimeMs - localDateNow at last sync
let serverClockSynced = false;
function syncServerClock(serverNowMs) {
  const n = Number(serverNowMs);
  if (!Number.isFinite(n) || n <= 0) return;
  // Naive offset = serverTime(at send) - localTime(at receive). This is off by
  // up to ~½ RTT, but every client has a similar small RTT so they stay in
  // lockstep with each other — which is exactly what we need for a shared
  // reveal moment. (We don't need absolute UTC accuracy, just agreement.)
  serverClockOffset = n - Date.now();
  serverClockSynced = true;
}
// Server wall-clock time, in ms, as seen from this device.
function serverNow() {
  return Date.now() + serverClockOffset;
}

// How many ms from THIS device's local clock until the shared server instant
// `serverTargetMs` arrives. Always ≥ 0. Used to schedule synchronized reveals.
function localDelayUntilServerTime(serverTargetMs) {
  // serverTargetMs is in server time; convert to local: local = server - offset
  const localTarget = serverTargetMs - serverClockOffset;
  return Math.max(0, localTarget - Date.now());
}

function FlipCountdown({ flip, isParticipant, onWatch }) {
  const COUNTDOWN_MS = 3000;
  // Anchor to server's joinedAt — set the instant the opponent joined.
  const anchor = parseServerTs(flip.joinedAt);

  // CRITICAL: animationDelay must be set ONCE at mount and never recomputed.
  // Previously this was inlined as `style={{ animationDelay: \`-${elapsed}ms\` }}`
  // — every React render produced a new `elapsed` value, the inline style
  // changed, CSS treated it as a new animation, and the ring's stroke jumped
  // back to its starting position. That's what felt "laggy" / "choppy".
  // Lock it via useRef so the keyframe runs uninterrupted for the full 3s.
  const initialDelayRef = React.useRef(null);
  if (initialDelayRef.current === null) {
    // serverNow() (not Date.now()) so a phone with a wrong clock still ticks
    // in lockstep with everyone else — the anchor is in server time.
    initialDelayRef.current = anchor ? Math.max(0, serverNow() - anchor) : 0;
  }
  const lockedDelayMs = initialDelayRef.current;

  // ONLY the visible number is React state — the ring fills via pure CSS.
  const [num, setNum] = React.useState(() => {
    if (!anchor) return 0;
    // CLAMP elapsed to [0, COUNTDOWN_MS]. Without the lower clamp, a tiny
    // clock-offset measurement error can make (serverNow() - anchor) slightly
    // NEGATIVE right when a flip starts → rem > 3000 → ceil() shows "4", then
    // the timers skip straight to "2". Clamping pins the first number to 3.
    const elapsed = Math.min(COUNTDOWN_MS, Math.max(0, serverNow() - anchor));
    return Math.max(1, Math.ceil((COUNTDOWN_MS - elapsed) / 1000));
  });

  React.useEffect(() => {
    if (!anchor) return;
    const elapsed = Math.max(0, serverNow() - anchor);
    if (elapsed >= COUNTDOWN_MS) { setNum(0); return; }
    // Schedule exact updates at each second boundary — no per-frame polling.
    const timers = [];
    for (let sec = 1; sec <= 3; sec++) {
      const ms = sec * 1000 - elapsed;
      if (ms <= 0) continue;
      const targetNum = Math.max(0, 3 - sec);
      timers.push(setTimeout(() => setNum(targetNum), ms));
    }
    return () => timers.forEach(clearTimeout);
  }, [anchor]);

  const elapsedNow = anchor ? Math.max(0, serverNow() - anchor) : COUNTDOWN_MS;
  const countdownActive = anchor && elapsedNow < COUNTDOWN_MS;

  // After the 3s countdown finishes (or for any older resolved flip with no
  // anchor), keep the same circular slot but switch its content to the
  // coin-face result.
  if (!countdownActive) {
    if (flip.state !== 'resolved' || !flip.result) return null;
    return (
      <button
        type="button"
        className={`fc-countdown is-result ${flip.result === 'heads' ? 'heads' : 'tails'} ${isParticipant ? 'is-participant' : ''}`}
        onClick={(e) => { e.stopPropagation(); onWatch && onWatch(); }}
        title={`Result: ${flip.result}`}
      >
        <span className="fc-countdown-result" aria-hidden="true"/>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`fc-countdown ${isParticipant ? 'is-participant' : ''}`}
      onClick={(e) => { e.stopPropagation(); onWatch && onWatch(); }}
      title="Watch the flip"
    >
      <svg className="fc-cd-svg" viewBox="0 0 40 40" aria-hidden="true">
        <circle className="fc-cd-track"  cx="20" cy="20" r="17"/>
        <circle className="fc-cd-stroke" cx="20" cy="20" r="17"
                style={{ animationDelay: `-${lockedDelayMs}ms` }}/>
      </svg>
      <span className="fc-countdown-num">{num > 0 ? num : ''}</span>
    </button>
  );
}

// HoldButton — "Hold to cancel" pattern (Notion / iOS-style destructive
// confirm). Hold for `duration` ms while the fill bar sweeps; release
// early → reset, no action. Hold the full duration → fires onComplete.
// Works with mouse, touch and keyboard (Enter/Space behave as hold).
// Visual: an outer pill, an inner fill that grows from 0→100% width via
// CSS transition driven by a `--hold-progress` variable.
function HoldButton({
  onComplete,
  label = 'Hold to cancel',
  icon = 'fa-solid fa-xmark',
  duration = 1200,
  className = '',
  disabled = false,
}) {
  const [holding, setHolding] = React.useState(false);
  const [armed, setArmed] = React.useState(false);
  const timerRef = React.useRef(null);

  const start = React.useCallback((e) => {
    if (disabled) return;
    // Don't preventDefault on touch — that would block scroll. Only
    // suppress synthetic mouse from touch via the touch flag (browser
    // handles it). We DO call stopPropagation so parent click handlers
    // (e.g. the dice row context menu opener) don't react to the press.
    if (e && e.stopPropagation) e.stopPropagation();
    setHolding(true);
    setArmed(false);
    timerRef.current = setTimeout(() => {
      setArmed(true);
      setHolding(false);
      onComplete && onComplete();
    }, duration);
  }, [onComplete, duration, disabled]);

  const cancel = React.useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setHolding(false);
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <button
      type="button"
      className={`hold-btn ${holding ? 'is-holding' : ''} ${armed ? 'is-armed' : ''} ${className}`}
      style={{ '--hold-duration': `${duration}ms` }}
      disabled={disabled}
      onMouseDown={start}
      onMouseUp={cancel}
      onMouseLeave={cancel}
      onTouchStart={start}
      onTouchEnd={cancel}
      onTouchCancel={cancel}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !holding) start(e); }}
      onKeyUp={(e) => { if (e.key === 'Enter' || e.key === ' ') cancel(e); }}
      aria-label={label}
    >
      <span className="hold-btn-fill"/>
      <span className="hold-btn-content">
        <i className={icon}/>
        <span>{label}</span>
      </span>
    </button>
  );
}

const FlipCard = React.memo(function FlipCard({ flip, onJoin, onInstaJoin, onWatch, onCancel, onContext, onUserClick, currentUserId, t }) {
  const isMine = flip.mine || (currentUserId != null && flip.c && flip.c.id === currentUserId);
  const items = flip.items || [];
  const oppItems = flip.oItems || [];
  const total = flipValue(items) + flipValue(oppItems);
  const PER_SIDE = 2;
  const cShown = items.slice(0, PER_SIDE);
  const cExtra = items.length - PER_SIDE;
  const oShown = oppItems.slice(0, PER_SIDE);
  const oExtra = oppItems.length - PER_SIDE;
  // Legacy variables kept so existing references downstream still work.
  const shown = items.slice(0, 4);
  const extra = items.length - 4;
  // Per-flip match range (set by creator). Falls back to ±5% for legacy flips.
  const rangePct = typeof flip.matchRange === 'number' ? flip.matchRange : 0.05;
  const lo = total * (1 - rangePct);
  const hi = total * (1 + rangePct);

  // Hide the winner highlight (crown + orange ring + loser fade) until the
  // 3-second lobby countdown has finished. Otherwise spectators see the
  // result instantly while the ring is still ticking, which spoils the
  // suspense. After the countdown elapses the highlight pops on its own.
  // For older resolved flips (no joinedAt or joinedAt > 3s ago), reveal
  // immediately — the countdown wouldn't render anyway.
  const joinedAtMs = parseServerTs(flip.joinedAt);
  const countdownDone = !joinedAtMs || (Date.now() - joinedAtMs >= 3000);
  // Force a re-render exactly when the countdown lands so the highlight
  // appears even without another poll cycle landing right after.
  const [, _bumpForCountdown] = React.useState(0);
  React.useEffect(() => {
    if (flip.state !== 'resolved' || !joinedAtMs || countdownDone) return;
    const ms = 3000 - (Date.now() - joinedAtMs);
    if (ms <= 0) return;
    const t = setTimeout(() => _bumpForCountdown(n => n + 1), ms + 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flip.state, joinedAtMs, countdownDone]);

  const creatorIsWinner = flip.state === 'resolved' && countdownDone && flip.winnerId === flip.c.id;
  const opponentIsWinner = flip.state === 'resolved' && countdownDone && flip.o && flip.winnerId === flip.o.id;

  // Multi-team / asymmetric: derive team stacks from participants
  const teamSizeA = flip.teamSize  || 1;
  const teamSizeB = flip.teamSizeB || teamSizeA;
  const isTeamFlip = teamSizeA > 1 || teamSizeB > 1;
  const partsA = isTeamFlip ? (flip.participants || []).filter(p => p.team === 'a') : [];
  const partsB = isTeamFlip ? (flip.participants || []).filter(p => p.team === 'b') : [];

  const renderTeamSlot = (part, sideBadge, isWinSlot, isLossSlot, key) => (
    <div
      key={key}
      className={`fc-avatar ${deterministicColor(part.user_id)} has-img ${isWinSlot ? 'is-winner' : ''} ${isLossSlot ? 'is-loser' : ''} clickable`}
      title={part.username || part.username}
      onClick={(e) => { e.stopPropagation(); onUserClick && onUserClick(part.username, { x: e.clientX, y: e.clientY }); }}
    >
      {part.avatar_url
        ? <img src={part.avatar_url} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
        : synthAvatar(part.username || part.username)}
      {sideBadge && <span className={`fc-side-badge ${sideBadge}`} title={sideBadge==='h'?'heads':'tails'}/>}
    </div>
  );

  // Am I one of the participants? Used to colorize the rolling banner —
  // participants see a brighter, "you're in this" version of the indicator
  // (orange = main accent, my-flip-flavored). Spectators see a neutral version.
  const isParticipant = currentUserId != null && (
    (flip.c && flip.c.id === currentUserId) ||
    (flip.o && flip.o.id === currentUserId) ||
    (flip.participants || []).some(p => Number(p.user_id) === Number(currentUserId))
  );

  return (
    <div
      className={`flip-card ${flip.state} ${isMine ? 'mine' : ''} ${isTeamFlip ? 'is-team' : ''} ${isParticipant && flip.state === 'rolling' ? 'is-mine-rolling' : ''}`}
      onContextMenu={(e) => { if (onContext) { e.preventDefault(); onContext(flip, { x: e.clientX, y: e.clientY }); } }}
    >
      {/* Coin / countdown slot — ALWAYS rendered so every card has the
          same column structure. For OPEN flips an invisible placeholder
          keeps the items strip horizontally aligned across all rows;
          ROLLING shows the countdown ring, RESOLVED shows the result coin. */}
      {(flip.state === 'rolling' || flip.state === 'resolved') ? (
        <FlipCountdown
          flip={flip}
          isParticipant={isParticipant}
          onWatch={() => onWatch && onWatch(flip)}
        />
      ) : (
        <span className="fc-countdown fc-countdown--placeholder" aria-hidden="true"/>
      )}
      {!isTeamFlip && <span className="fc-vs" aria-hidden="true">VS</span>}
      {isTeamFlip ? (
        <>
          {/* TEAM A stack */}
          <div className="fc-team fc-team-a">
            <span className="fc-team-label">{teamSizeA}v{teamSizeB}</span>
            <div className="fc-team-stack">
              {partsA.map(p => renderTeamSlot(
                p,
                flip.side === 'tails' ? 't' : 'h',
                flip.state === 'resolved' && flip.result === flip.side,
                flip.state === 'resolved' && flip.result !== flip.side,
                'a-' + p.user_id
              ))}
              {Array.from({ length: Math.max(0, teamSizeA - partsA.length) }).map((_, i) => (
                <button
                  key={'a-empty-' + i}
                  className="fc-empty-join sm"
                  title="Join team A"
                  onClick={() => onJoin && onJoin({ ...flip, joinTeam: 'a' })}
                >
                  <i className="fa-solid fa-plus"/>
                </button>
              ))}
            </div>
          </div>

          {/* TEAM B stack */}
          <div className="fc-team fc-team-b">
            <div className="fc-team-stack">
              {partsB.map(p => renderTeamSlot(
                p,
                flip.side === 'heads' ? 't' : 'h',
                flip.state === 'resolved' && flip.result && flip.result !== flip.side,
                flip.state === 'resolved' && flip.result === flip.side,
                'b-' + p.user_id
              ))}
              {Array.from({ length: Math.max(0, teamSizeB - partsB.length) }).map((_, i) => (
                <button
                  key={'b-empty-' + i}
                  className="fc-empty-join sm"
                  title="Join team B"
                  onClick={() => onJoin && onJoin({ ...flip, joinTeam: 'b' })}
                >
                  <i className="fa-solid fa-plus"/>
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="fc-creator">
            <div
              className={`fc-avatar ${flip.c.color} ${flip.c.avatarUrl ? 'has-img' : ''} ${levelBorder(flip.c.lvl)} ${creatorIsWinner ? 'is-winner' : ''} ${opponentIsWinner ? 'is-loser' : ''} clickable`}
              title={flip.c.name}
              onClick={(e) => { e.stopPropagation(); onUserClick && onUserClick(flip.c.username || flip.c.name, { x: e.clientX, y: e.clientY }); }}
            >
              {flip.c.avatarUrl
                ? <img src={flip.c.avatarUrl} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                : flip.c.av}
              <span className={`fc-side-badge ${flip.side==='tails'?'t':'h'}`} title={flip.side}/>
              {creatorIsWinner && <span className="fc-winner-pill"><i className="fa-solid fa-crown"/></span>}
            </div>
            {/* Creator's items below their avatar */}
            <div className="fc-side-items">
              {cShown.map((it, i) => <FlipItem key={'c-'+i} item={it} size={42}/>)}
              {cExtra > 0 && <div className="fc-side-overflow">+{cExtra}</div>}
            </div>
          </div>

          <div className="fc-opponent">
            {flip.o ? (
              <div
                className={`fc-avatar ${flip.o.color} ${flip.o.avatarUrl ? 'has-img' : ''} ${levelBorder(flip.o.lvl)} ${opponentIsWinner ? 'is-winner' : ''} ${creatorIsWinner ? 'is-loser' : ''} clickable`}
                title={flip.o.name}
                onClick={(e) => { e.stopPropagation(); onUserClick && onUserClick(flip.o.username || flip.o.name, { x: e.clientX, y: e.clientY }); }}
              >
                {flip.o.avatarUrl
                  ? <img src={flip.o.avatarUrl} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : flip.o.av}
                <span className={`fc-side-badge ${flip.side==='heads'?'t':'h'}`} title={flip.side==='heads'?'tails':'heads'}/>
                {opponentIsWinner && <span className="fc-winner-pill"><i className="fa-solid fa-crown"/></span>}
              </div>
            ) : isMine ? (
              <div className="fc-avatar empty" title="Waiting for opponent">
                <i className="fa-solid fa-bolt"/>
              </div>
            ) : (
              <button
                className="fc-empty-join"
                onClick={() => onInstaJoin(flip)}
                title={`Quick join · take ${flip.side === 'heads' ? 'tails' : 'heads'}`}
                aria-label="quick join"
              >
                {/* Joiner takes the OPPOSITE side of whatever the creator
                    picked — render that coin face so the user instantly
                    sees which side they'll be on. */}
                <span className={`fc-empty-coin ${flip.side === 'heads' ? 't' : 'h'}`} aria-hidden="true"/>
              </button>
            )}
            {/* Opponent's items below their avatar — always rendered (empty
                placeholder if no opponent) so both columns have equal height. */}
            <div className="fc-side-items">
              {flip.o
                ? <>
                    {oShown.map((it, i) => <FlipItem key={'o-'+i} item={it} size={42}/>)}
                    {oExtra > 0 && <div className="fc-side-overflow">+{oExtra}</div>}
                  </>
                : <span className="fc-side-placeholder">·</span>}
            </div>
          </div>
        </>
      )}

      {/* Central items strip — shown only in LIST view (hidden in CARD via CSS) */}
      <div className="fc-items-wrap">
        <div className="fc-items">
          {shown.map((it, i) => <FlipItem key={i} item={it}/>)}
        </div>
        {extra > 0 && <div className="fc-overflow">+{extra}</div>}
      </div>

      <div className="fc-value">
        <div className="total"><i className="fa-solid fa-gem gem-i"/>{fmtVal(total)}</div>
        {/* Match-range range line removed per user request. */}
        {/* If the opposing side has any stake, show stake-weighted win chance. */}
        {(flip.creator_value || 0) > 0 && (flip.opponent_value || 0) > 0 && (() => {
          const cv = Number(flip.creator_value) || 0;
          const ov = Number(flip.opponent_value) || 0;
          const sum = cv + ov;
          const aPct = sum > 0 ? Math.round((cv / sum) * 100) : 50;
          return <div className="fc-odds">A {aPct}% · B {100 - aPct}%</div>;
        })()}
      </div>

      <div className="fc-actions">
        {flip.state === 'rolling' ? (
          // Everyone can spectate a rolling flip.
          <button className="fc-btn view" onClick={() => onWatch(flip)}>
            <span className="dot"/>{t.view || 'View'}
          </button>
        ) : flip.state === 'resolved' ? (
          <button className="fc-btn view" onClick={() => onWatch(flip)}>{t.view || 'View'}</button>
        ) : flip.state === 'cancelled' ? (
          <span className="fc-btn rolling" style={{opacity: 0.6}}>{t.cancelled || 'Cancelled'}</span>
        ) : isMine ? (
          /* Reverted: regular click-to-cancel per user request — the
             hold-to-cancel pattern was too friction-heavy for them.
             Design styling lives in .fc-btn.cancel. */
          <button className="fc-btn cancel" onClick={() => onCancel(flip)}>
            <i className="fa-solid fa-xmark"/> {t.cancel || 'Cancel'}
          </button>
        ) : isTeamFlip ? (
          <button className="fc-btn view" onClick={() => onWatch(flip)}>{t.view || 'View'}</button>
        ) : (
          <>
            <button className="fc-btn join" onClick={() => onJoin(flip)}>{t.join || 'Join'}</button>
            <button className="fc-btn view" onClick={() => onWatch(flip)}>{t.view || 'View'}</button>
          </>
        )}
      </div>
    </div>
  );
}, FlipCardEqual);

function FilterBar({ t, filter, onFilter, side, onSide, sort, onSortToggle, openN, rollN, onCreate, flipView, onFlipView, searchValue, onSearchChange }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!mobileOpen) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMobileOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [mobileOpen]);

  const sortLabel = sort === 'high' ? t.sortHigh : sort === 'low' ? t.sortLow : t.sortNewest;
  const sideLabel = side === 'heads' ? t.filterHeads : side === 'tails' ? t.filterTails : t.filterAnySide;
  const filterLabel = filter === 'open' ? t.filterOpen : filter === 'rolling' ? t.filterRolling : t.filterAll;
  const hasSearch = typeof onSearchChange === 'function';

  return (
    <div className={`filter-bar ${hasSearch ? 'has-search' : ''}`}>
      {/* Search input — first slot, grows to fill remaining width. Mirrors
          the lobby search that used to live in the header. Same controlled
          value/setter as before, just relocated where users actually look
          for filters. Hidden if the parent didn't wire onSearchChange. */}
      {hasSearch && (
        <div className="fb-search">
          <I name="search" size={15} color="#838383" />
          <input
            placeholder={t.searchPlaceholder || 'Search players, items…'}
            value={searchValue || ''}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label={t.searchPlaceholder || 'Search'}
          />
          {searchValue ? (
            <button className="fb-search-clear" onClick={() => onSearchChange('')} aria-label="clear search">
              <i className="fa-solid fa-xmark"/>
            </button>
          ) : null}
        </div>
      )}

      {/* Desktop pills — All/Open/Rolling tabs removed per user request,
          since the lobby header count already shows the open total and
          the resolved cap is server-enforced. */}
      <div className="fb-pills">
        <button className={`fp ${side==='any'?'is-active':''}`} onClick={() => onSide('any')}>{t.filterAnySide}</button>
        <button className={`fp ${side==='heads'?'is-active':''}`} onClick={() => onSide('heads')}><span className="coin-mini h"/>{t.filterHeads}</button>
        <button className={`fp ${side==='tails'?'is-active':''}`} onClick={() => onSide('tails')}><span className="coin-mini t"/>{t.filterTails}</button>
        <div className="spacer"/>
        <button className="fp fp-sort" onClick={onSortToggle}><I name="sort" size={13}/>{sortLabel}</button>
      </div>

      {/* Mobile single button: opens combined dropdown. Now reduced to JUST
          the sliders icon + chevron (no label text), per user request — the
          search bar to its left already fills the row, so the filter button
          stays compact and recognizable. */}
      <div className="fb-mobile" ref={ref}>
        <button
          className={`fb-mb-btn fb-mb-btn--icon ${mobileOpen ? 'is-open' : ''}`}
          onClick={() => setMobileOpen(o => !o)}
          aria-label={`${sideLabel} · ${sortLabel}`}
          title={`${sideLabel} · ${sortLabel}`}
        >
          <I name="sliders" size={15}/>
          <i className={`fa-solid fa-chevron-down fb-mb-chev ${mobileOpen ? 'is-up' : ''}`}/>
        </button>
        {mobileOpen && (
          <div className="fb-mb-menu">
            <div className="fb-mb-group">
              <div className="fb-mb-label">Side</div>
              <div className="fb-mb-row">
                <button className={`fp ${side==='any'?'is-active':''}`}   onClick={() => onSide('any')}>{t.filterAnySide}</button>
                <button className={`fp ${side==='heads'?'is-active':''}`} onClick={() => onSide('heads')}><span className="coin-mini h"/>{t.filterHeads}</button>
                <button className={`fp ${side==='tails'?'is-active':''}`} onClick={() => onSide('tails')}><span className="coin-mini t"/>{t.filterTails}</button>
              </div>
            </div>
            <div className="fb-mb-group">
              <div className="fb-mb-label">Sort</div>
              <div className="fb-mb-row">
                <button className={`fp ${sort==='newest'?'is-active':''}`} onClick={() => sort!=='newest' && onSortToggle()}>{t.sortNewest}</button>
                <button className={`fp ${sort==='high'?'is-active':''}`}   onClick={() => sort!=='high' && (sort==='newest' ? onSortToggle() : (onSortToggle(), onSortToggle()))}>{t.sortHigh}</button>
                <button className={`fp ${sort==='low'?'is-active':''}`}    onClick={() => sort!=='low' && (sort==='high' ? onSortToggle() : (onSortToggle(), onSortToggle()))}>{t.sortLow}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View toggle REMOVED per user request — desktop always list, mobile
          always card. CSS handles the switch via @media; React state stays
          a no-op constant 'list' (mobile @media overrides to card grid). */}
      <button className="create-btn" onClick={onCreate}><I name="plus" size={14} color="white"/>{t.createFlip}</button>
    </div>
  );
}

const COMMON_EMOJIS = ['😀','😂','🤣','😊','😍','🥰','😎','🤩','🤔','🙄','😏','😒','😢','😭','😡','🤯','💀','🤡','🥺','😴','🥶','🤤','😱','🤝','👍','👎','👌','🙏','💪','✋','👀','🫡','❤️','💔','💖','🔥','⚡','✨','🎉','🎊','💯','💰','💸','🍀','🚀','💎','👑','😈','🎯','🃏','🎲','🪙','🐺','🦊'];

// Searchable catalog — each entry has the glyph + space-separated keywords.
// Emoji category bar (Discord/Slack-style nav at the bottom of the picker).
// Each entry: { id, icon (FA class), label }. The grid is grouped by these
// categories; clicking a category icon scrolls the grid to that section.
const EMOJI_CATEGORIES = [
  { id: 'recent',   icon: 'fa-solid fa-clock-rotate-left', label: 'Recently used' },
  { id: 'people',   icon: 'fa-regular fa-face-smile',      label: 'Smileys & people' },
  { id: 'nature',   icon: 'fa-solid fa-paw',               label: 'Animals & nature' },
  { id: 'food',     icon: 'fa-solid fa-burger',            label: 'Food & drink' },
  { id: 'activity', icon: 'fa-solid fa-futbol',            label: 'Activities' },
  { id: 'travel',   icon: 'fa-solid fa-car',               label: 'Travel & places' },
  { id: 'objects',  icon: 'fa-solid fa-lightbulb',         label: 'Objects' },
  { id: 'symbols',  icon: 'fa-solid fa-bolt',              label: 'Symbols' },
  { id: 'flags',    icon: 'fa-solid fa-flag',              label: 'Flags & letters' },
];

// Best-effort classifier by codepoint. Doesn't need to be perfect — it's
// just to group the grid into navigable sections. Each emoji always gets
// SOME category; the fallback is 'symbols'.
function emojiCategoryFor(em) {
  if (!em) return 'symbols';
  const cp = em.codePointAt(0);
  // Regional indicators / keycaps / capital letters / arrows → flags & letters
  if ((cp >= 0x1F1E6 && cp <= 0x1F1FF) ||
      (cp >= 0x1F170 && cp <= 0x1F19A) ||  // 🅰️ 🅱️ 🆎 …
      (cp >= 0x1F520 && cp <= 0x1F524) ||  // 🔠 🔡 🔢 🔣 🔤
      (cp === 0x0030 || cp === 0x0023 || cp === 0x002A) || // 0️⃣ #️⃣ *️⃣
      (cp >= 0x2B05 && cp <= 0x2B07) ||                    // ⬅ ⬆ ⬇
      (cp >= 0x27A1 && cp <= 0x27BF) ||                    // ➡ etc
      (cp >= 0x2196 && cp <= 0x2199) ||                    // ↖↗↘↙
      (cp === 0x261D)) return 'flags';
  // Smileys & people
  if ((cp >= 0x1F600 && cp <= 0x1F64F) ||  // 😀 😂 🙏 …
      (cp >= 0x1F910 && cp <= 0x1F92F) ||  // 🤐 🤔 …
      (cp >= 0x1F970 && cp <= 0x1F97F) ||  // 🥰 🥺 …
      (cp >= 0x1F9D0 && cp <= 0x1F9D9) ||  // 🧐 🧑 …
      (cp >= 0x1F44A && cp <= 0x1F4A9) ||  // 👊 👋 ... 💩
      (cp >= 0x1F590 && cp <= 0x1F596) ||  // 🖐 🖕 🖖
      (cp >= 0x1F44B && cp <= 0x1F450) ||  // 👋 👌 …
      (cp === 0x270A || cp === 0x270B || cp === 0x270C || cp === 0x270D) ||
      (cp >= 0x1FAF0 && cp <= 0x1FAF8)) return 'people';
  // Animals & nature
  if ((cp >= 0x1F400 && cp <= 0x1F43F) ||  // 🐀 🐢 …
      (cp >= 0x1F980 && cp <= 0x1F9AE) ||  // 🦀 🦋 …
      (cp >= 0x1F330 && cp <= 0x1F344) ||  // 🌰 🍇 (border w food)
      (cp >= 0x1F648 && cp <= 0x1F64A) ||  // 🙈 🙉 🙊
      (cp >= 0x1F343 && cp <= 0x1F33F)) return 'nature';
  // Food & drink
  if ((cp >= 0x1F345 && cp <= 0x1F37F) ||
      (cp >= 0x1F950 && cp <= 0x1F96B) ||
      (cp >= 0x2615 && cp <= 0x2615)) return 'food';
  // Activities
  if ((cp >= 0x1F3A0 && cp <= 0x1F3FF) ||
      (cp >= 0x26BD && cp <= 0x26BE) ||
      (cp >= 0x1F3C0 && cp <= 0x1F3DD)) return 'activity';
  // Travel & places
  if ((cp >= 0x1F680 && cp <= 0x1F6FF) ||
      (cp >= 0x1F30D && cp <= 0x1F320) ||
      (cp === 0x2708 || cp === 0x26F5)) return 'travel';
  // Objects
  if ((cp >= 0x1F4A1 && cp <= 0x1F4FF) ||
      (cp >= 0x1F507 && cp <= 0x1F50F) ||
      (cp === 0x1F4DA)) return 'objects';
  // Default
  return 'symbols';
}

const EMOJI_CATALOG = [
  { e: '😀', k: 'smile happy grin face' },
  { e: '😂', k: 'lol laugh crying joy tears' },
  { e: '🤣', k: 'rofl laugh rolling floor' },
  { e: '😊', k: 'smile blush happy' },
  { e: '😍', k: 'love heart eyes adore' },
  { e: '🥰', k: 'love hearts blush' },
  { e: '😎', k: 'cool sunglasses chill' },
  { e: '🤩', k: 'star eyes wow excited' },
  { e: '🤔', k: 'think hmm thinking' },
  { e: '🙄', k: 'eye roll annoyed' },
  { e: '😏', k: 'smirk sly' },
  { e: '😒', k: 'meh unamused' },
  { e: '😢', k: 'sad cry tear' },
  { e: '😭', k: 'crying sob weep sad' },
  { e: '😡', k: 'angry mad rage' },
  { e: '🤯', k: 'mind blown shocked' },
  { e: '💀', k: 'skull dead lol' },
  { e: '🤡', k: 'clown joke' },
  { e: '🥺', k: 'plead beg sad' },
  { e: '😴', k: 'sleep tired zzz' },
  { e: '🥶', k: 'cold freeze ice' },
  { e: '🤤', k: 'drool want' },
  { e: '😱', k: 'scream shock' },
  { e: '🤝', k: 'handshake deal' },
  { e: '👍', k: 'thumbs up like good yes' },
  { e: '👎', k: 'thumbs down dislike no bad' },
  { e: '👌', k: 'ok perfect' },
  { e: '🙏', k: 'pray thanks please' },
  { e: '💪', k: 'flex strong muscle' },
  { e: '✋', k: 'hand stop' },
  { e: '👀', k: 'eyes looking watch' },
  { e: '🫡', k: 'salute respect' },
  { e: '❤️', k: 'heart love red' },
  { e: '🧡', k: 'orange heart' },
  { e: '💛', k: 'yellow heart' },
  { e: '💚', k: 'green heart' },
  { e: '💙', k: 'blue heart' },
  { e: '💜', k: 'purple heart' },
  { e: '🖤', k: 'black heart' },
  { e: '🤍', k: 'white heart' },
  { e: '💔', k: 'broken heart sad' },
  { e: '💖', k: 'sparkle heart love' },
  { e: '🔥', k: 'fire hot lit flame' },
  { e: '⚡', k: 'lightning bolt fast' },
  { e: '✨', k: 'sparkle shine magic' },
  { e: '🎉', k: 'party celebrate' },
  { e: '🎊', k: 'confetti celebrate' },
  { e: '💯', k: 'hundred perfect 100' },
  { e: '💰', k: 'money bag rich' },
  { e: '💸', k: 'money fly wings' },
  { e: '🍀', k: 'clover luck lucky' },
  { e: '🚀', k: 'rocket fast launch' },
  { e: '💎', k: 'diamond gem value' },
  { e: '👑', k: 'crown king queen winner' },
  { e: '😈', k: 'devil evil smirk' },
  { e: '🎯', k: 'target dart aim bullseye' },
  { e: '🃏', k: 'joker card' },
  { e: '🎲', k: 'dice roll random' },
  { e: '🪙', k: 'coin flip money' },
  { e: '🐺', k: 'wolf' },
  { e: '🦊', k: 'fox' },
  { e: '🦁', k: 'lion' },
  { e: '🐉', k: 'dragon' },
  { e: '⚔️', k: 'swords battle fight' },
  { e: '🛡️', k: 'shield defense' },
  { e: '🏆', k: 'trophy win first' },
  { e: '🥇', k: 'gold medal first' },
  { e: '🥈', k: 'silver medal second' },
  { e: '🥉', k: 'bronze medal third' },
  { e: '🎁', k: 'gift present tip' },
  { e: '🌟', k: 'star glow' },
  { e: '⭐', k: 'star' },
  { e: '☀️', k: 'sun shine' },
  { e: '🌙', k: 'moon night' },
  { e: '🌈', k: 'rainbow' },
  { e: '🎮', k: 'game controller play' },
  { e: '🕹️', k: 'joystick arcade' },
  { e: '🍿', k: 'popcorn movie watching' },
  { e: '☕', k: 'coffee' },
  { e: '🍺', k: 'beer drink' },
  { e: '🍕', k: 'pizza food' },
  { e: '🌮', k: 'taco food' },
  { e: '🍔', k: 'burger food' },
  { e: '🍟', k: 'fries food' },
  { e: '🍩', k: 'donut food sweet' },
  { e: '🍪', k: 'cookie food' },
  { e: '🍰', k: 'cake birthday' },
  { e: '🎂', k: 'birthday cake' },
  { e: '🍎', k: 'apple fruit' },
  { e: '🍌', k: 'banana fruit' },
  { e: '🍓', k: 'strawberry fruit' },
  { e: '🍉', k: 'watermelon fruit' },
  { e: '🍇', k: 'grapes fruit' },
  { e: '🍒', k: 'cherry fruit' },
  // Faces & expressions
  { e: '😉', k: 'wink flirt' },
  { e: '😜', k: 'tongue silly' },
  { e: '😝', k: 'tongue squint silly' },
  { e: '🤪', k: 'crazy silly zany' },
  { e: '🤓', k: 'nerd glasses' },
  { e: '🥸', k: 'disguise mustache' },
  { e: '😇', k: 'angel halo innocent' },
  { e: '🤠', k: 'cowboy hat' },
  { e: '🥳', k: 'party hat celebrate' },
  { e: '😬', k: 'grimace awkward' },
  { e: '😅', k: 'sweat smile awkward' },
  { e: '😆', k: 'laugh squint' },
  { e: '🙃', k: 'upside down silly' },
  { e: '😋', k: 'yum tongue tasty' },
  { e: '🤗', k: 'hug love' },
  { e: '🤭', k: 'shy oops giggle' },
  { e: '🤫', k: 'shhh quiet secret' },
  { e: '😶', k: 'no mouth speechless' },
  { e: '😑', k: 'expressionless meh' },
  { e: '😐', k: 'neutral face' },
  { e: '🥱', k: 'yawn tired bored' },
  { e: '🤐', k: 'zipper mouth quiet' },
  { e: '🤧', k: 'sneeze sick' },
  { e: '🤒', k: 'sick fever thermometer' },
  { e: '🤮', k: 'puke vomit gross' },
  { e: '🤢', k: 'nauseated sick' },
  { e: '🥴', k: 'woozy drunk dizzy' },
  { e: '🤥', k: 'liar lying pinocchio' },
  { e: '😵', k: 'dizzy knocked out' },
  { e: '😵‍💫', k: 'spiral eyes confused' },
  { e: '😳', k: 'flushed shocked embarrassed' },
  { e: '🙂', k: 'slight smile' },
  { e: '☹️', k: 'frown sad' },
  { e: '😟', k: 'worried' },
  { e: '😞', k: 'disappointed sad' },
  { e: '😔', k: 'pensive sad reflective' },
  { e: '🤨', k: 'eyebrow raised suspicious' },
  // Hands & gestures
  { e: '🤞', k: 'fingers crossed hope luck' },
  { e: '🤟', k: 'love you rock' },
  { e: '🤘', k: 'rock horns metal' },
  { e: '🤙', k: 'call me shaka' },
  { e: '👈', k: 'point left' },
  { e: '👉', k: 'point right' },
  { e: '👆', k: 'point up' },
  { e: '👇', k: 'point down' },
  { e: '☝️', k: 'index finger up' },
  { e: '✌️', k: 'peace victory' },
  { e: '🤚', k: 'raised back hand' },
  { e: '🖐️', k: 'splayed hand five' },
  { e: '🖖', k: 'spock vulcan' },
  { e: '👏', k: 'clap applause' },
  { e: '🙌', k: 'praise hands raised' },
  { e: '👐', k: 'open hands hug' },
  { e: '🫶', k: 'heart hands love' },
  { e: '🤲', k: 'palms together' },
  // Animals
  { e: '🐶', k: 'dog puppy' },
  { e: '🐱', k: 'cat kitten' },
  { e: '🐭', k: 'mouse' },
  { e: '🐹', k: 'hamster' },
  { e: '🐰', k: 'rabbit bunny' },
  { e: '🐻', k: 'bear' },
  { e: '🐼', k: 'panda' },
  { e: '🐨', k: 'koala' },
  { e: '🐯', k: 'tiger' },
  { e: '🐮', k: 'cow' },
  { e: '🐷', k: 'pig' },
  { e: '🐸', k: 'frog' },
  { e: '🐵', k: 'monkey' },
  { e: '🙈', k: 'see no evil monkey' },
  { e: '🙉', k: 'hear no evil monkey' },
  { e: '🙊', k: 'speak no evil monkey' },
  { e: '🐔', k: 'chicken' },
  { e: '🐧', k: 'penguin' },
  { e: '🐦', k: 'bird' },
  { e: '🦅', k: 'eagle' },
  { e: '🦄', k: 'unicorn magic rare' },
  { e: '🐝', k: 'bee' },
  { e: '🦋', k: 'butterfly' },
  { e: '🐢', k: 'turtle slow' },
  { e: '🐙', k: 'octopus' },
  { e: '🐠', k: 'fish tropical' },
  { e: '🦈', k: 'shark' },
  { e: '🐬', k: 'dolphin' },
  { e: '🐳', k: 'whale' },
  // Sports / activities
  { e: '⚽', k: 'soccer football' },
  { e: '🏀', k: 'basketball' },
  { e: '🏈', k: 'football american' },
  { e: '⚾', k: 'baseball' },
  { e: '🎾', k: 'tennis' },
  { e: '🏐', k: 'volleyball' },
  { e: '🥊', k: 'boxing glove fight' },
  { e: '🎱', k: 'pool billiards 8ball' },
  { e: '🏓', k: 'pingpong tabletennis' },
  // Travel / objects
  { e: '🚗', k: 'car' },
  { e: '🏎️', k: 'race car fast' },
  { e: '🚙', k: 'suv jeep' },
  { e: '🚓', k: 'police car' },
  // 🚀 already in catalog above — removed duplicate
  { e: '✈️', k: 'plane airplane' },
  { e: '🛸', k: 'ufo alien' },
  { e: '⛵', k: 'sailboat' },
  { e: '🌍', k: 'earth world' },
  { e: '🌋', k: 'volcano' },
  // Music / tech
  { e: '🎵', k: 'music note' },
  { e: '🎶', k: 'music notes' },
  { e: '🎸', k: 'guitar' },
  { e: '🎤', k: 'mic microphone sing' },
  { e: '🎧', k: 'headphones' },
  { e: '📱', k: 'phone smartphone' },
  { e: '💻', k: 'laptop computer' },
  { e: '⌨️', k: 'keyboard' },
  { e: '🖱️', k: 'mouse computer' },
  { e: '🔋', k: 'battery' },
  { e: '💡', k: 'lightbulb idea' },
  { e: '📸', k: 'camera flash photo' },
  // Symbols + checkmarks
  { e: '✅', k: 'check tick yes ok green' },
  { e: '❌', k: 'cross no wrong red' },
  { e: '❎', k: 'cross negative' },
  { e: '✔️', k: 'check tick' },
  { e: '⭕', k: 'circle round' },
  { e: '🚫', k: 'no prohibited not allowed' },
  { e: '⚠️', k: 'warning caution' },
  { e: '❗', k: 'exclamation' },
  { e: '❓', k: 'question mark' },
  { e: '‼️', k: 'double exclamation' },
  { e: '⁉️', k: 'interrobang' },
  { e: '💢', k: 'anger symbol angry' },
  { e: '💥', k: 'explosion boom' },
  { e: '💫', k: 'dizzy star' },
  { e: '💦', k: 'sweat drops' },
  { e: '💨', k: 'dash wind fast' },
  { e: '🕳️', k: 'hole' },
  { e: '🎈', k: 'balloon' },
  { e: '🎀', k: 'ribbon' },
  // 🎂 already in catalog above — removed duplicate
  // Letter / number squares (user said "harfler de olur")
  { e: '🅰️', k: 'a letter blood type' },
  { e: '🅱️', k: 'b letter blood type' },
  { e: '🆎', k: 'ab letters' },
  { e: '🅾️', k: 'o letter blood type' },
  { e: '🆑', k: 'cl letters' },
  { e: '🆒', k: 'cool letters' },
  { e: '🆓', k: 'free letters' },
  { e: '🆕', k: 'new letters' },
  { e: '🆖', k: 'ng letters bad' },
  { e: '🆗', k: 'ok letters' },
  { e: '🆘', k: 'sos letters help' },
  { e: '🆙', k: 'up letters' },
  { e: '🆚', k: 'vs letters versus' },
  { e: '🔠', k: 'capital letters abcd' },
  { e: '🔡', k: 'lowercase letters abcd' },
  { e: '🔢', k: 'numbers 1234' },
  { e: '🔣', k: 'symbols input' },
  { e: '🔤', k: 'letters abc input' },
  // Number keycaps
  { e: '0️⃣', k: 'zero 0 number' },
  { e: '1️⃣', k: 'one 1 number' },
  { e: '2️⃣', k: 'two 2 number' },
  { e: '3️⃣', k: 'three 3 number' },
  { e: '4️⃣', k: 'four 4 number' },
  { e: '5️⃣', k: 'five 5 number' },
  { e: '6️⃣', k: 'six 6 number' },
  { e: '7️⃣', k: 'seven 7 number' },
  { e: '8️⃣', k: 'eight 8 number' },
  { e: '9️⃣', k: 'nine 9 number' },
  { e: '🔟', k: 'ten 10 number' },
  // Arrows
  { e: '⬆️', k: 'up arrow' },
  { e: '⬇️', k: 'down arrow' },
  { e: '⬅️', k: 'left arrow' },
  { e: '➡️', k: 'right arrow' },
  { e: '↗️', k: 'up right arrow' },
  { e: '↘️', k: 'down right arrow' },
  { e: '↙️', k: 'down left arrow' },
  { e: '↖️', k: 'up left arrow' },
  { e: '🔄', k: 'refresh recycle' },
  { e: '🔁', k: 'repeat loop' },
  { e: '🔂', k: 'repeat one' },
  // Currency / commerce
  { e: '💵', k: 'dollar money' },
  { e: '💴', k: 'yen money' },
  { e: '💶', k: 'euro money' },
  { e: '💷', k: 'pound money' },
  // 🪙 already in catalog above — removed duplicate
  { e: '💳', k: 'credit card' },
  // Weather
  { e: '⛅', k: 'cloud sun partly' },
  { e: '☁️', k: 'cloud' },
  { e: '🌧️', k: 'rain cloud' },
  { e: '⛈️', k: 'thunderstorm' },
  { e: '🌩️', k: 'lightning thunder' },
  { e: '❄️', k: 'snow snowflake' },
  { e: '☃️', k: 'snowman' },
  { e: '⛄', k: 'snowman cold' },
  // Misc fun
  { e: '🍑', k: 'peach booty' },
  { e: '🍆', k: 'eggplant' },
  { e: '🦴', k: 'bone' },
  { e: '🪵', k: 'wood log' },
  { e: '🌶️', k: 'pepper spicy hot' },
  { e: '🪐', k: 'planet saturn' },
  { e: '⚓', k: 'anchor' },
  { e: '🎬', k: 'movie clapperboard' },
  { e: '🎨', k: 'art palette' },
  { e: '📚', k: 'books reading' },
  { e: '🔔', k: 'bell notification' },
  { e: '🔕', k: 'bell silent mute' },
  { e: '🔒', k: 'lock secure' },
  { e: '🔓', k: 'unlock' },
  { e: '🔑', k: 'key' },
  { e: '⏰', k: 'alarm clock' },
  { e: '⌛', k: 'hourglass wait' },
  { e: '⏳', k: 'hourglass loading' },
  // Full A-Z alphabet as regional indicator squares (one per letter)
  { e: '🇦', k: 'a letter alphabet' },
  { e: '🇧', k: 'b letter alphabet' },
  { e: '🇨', k: 'c letter alphabet' },
  { e: '🇩', k: 'd letter alphabet' },
  { e: '🇪', k: 'e letter alphabet' },
  { e: '🇫', k: 'f letter alphabet' },
  { e: '🇬', k: 'g letter alphabet' },
  { e: '🇭', k: 'h letter alphabet' },
  { e: '🇮', k: 'i letter alphabet' },
  { e: '🇯', k: 'j letter alphabet' },
  { e: '🇰', k: 'k letter alphabet' },
  { e: '🇱', k: 'l letter alphabet' },
  { e: '🇲', k: 'm letter alphabet' },
  { e: '🇳', k: 'n letter alphabet' },
  { e: '🇴', k: 'o letter alphabet' },
  { e: '🇵', k: 'p letter alphabet' },
  { e: '🇶', k: 'q letter alphabet' },
  { e: '🇷', k: 'r letter alphabet' },
  { e: '🇸', k: 's letter alphabet' },
  { e: '🇹', k: 't letter alphabet' },
  { e: '🇺', k: 'u letter alphabet' },
  { e: '🇻', k: 'v letter alphabet' },
  { e: '🇼', k: 'w letter alphabet' },
  { e: '🇽', k: 'x letter alphabet' },
  { e: '🇾', k: 'y letter alphabet' },
  { e: '🇿', k: 'z letter alphabet' },
];

// Custom emojis manifest — fetched once on first mount, cached in module scope.
// Eagerly fetched on app boot (see App's mount effect) so chat messages can
// resolve :code: tokens to <img> on the very first render — without this,
// the first batch of messages flickered as plain ":code:" text for a beat.
let _customEmojiCache = null;
let _customEmojiMap   = null;
let _customEmojiPromise = null;
const _customEmojiSubs = new Set(); // notified once when manifest finishes loading
function subscribeCustomEmojis(fn) {
  _customEmojiSubs.add(fn);
  return () => _customEmojiSubs.delete(fn);
}
async function loadCustomEmojis() {
  if (_customEmojiCache) return _customEmojiCache;
  if (_customEmojiPromise) return _customEmojiPromise;
  _customEmojiPromise = (async () => {
    try {
      // `force-cache` lets the browser serve the manifest from disk cache
      // across reloads (304 / immutable). Tradeoff: when you add a new
      // emoji, bump CACHE_VERSION in sw.js to invalidate the SW copy too.
      const res = await fetch('/assets/emojis/manifest.json', { cache: 'force-cache' });
      if (!res.ok) { _customEmojiCache = []; _customEmojiMap = {}; return []; }
      const list = await res.json();
      // SECURITY: restrict filenames to a strict allowlist regex — no '..',
      // no slashes, no leading dots. Prevents path-traversal via the <img src>
      // if the manifest is ever attacker-controlled.
      //
      // ROOT CAUSE of the long-running "only 29 custom emojis show" bug:
      // safeCode used to be /^:[a-z0-9_]{2,32}:$/i — letters/digits/underscore
      // only, NO hyphens. 33 of our 61 emoji codes are kebab-case (`pepe-cry`,
      // `cat-smile-2`, …) so they all failed the filter and got dropped. The
      // picker silently rendered only the 28 underscore-only / single-word
      // ones. Hyphens are safe in :codes: — they're not path separators —
      // so we add `-` to the allowlist here.
      const safeFile = /^[a-z0-9][a-z0-9_-]{0,63}\.(webp|png|gif|jpg|jpeg|svg)$/i;
      const safeCode = /^:[a-z0-9_-]{2,32}:$/i;
      const safe = (Array.isArray(list) ? list : []).filter(e =>
        e && typeof e.code === 'string' && safeCode.test(e.code) &&
        typeof e.file === 'string' && safeFile.test(e.file)
      );
      _customEmojiCache = safe;
      _customEmojiMap = Object.fromEntries(safe.map(e => [e.code, e.file]));
    } catch (_) { _customEmojiCache = []; _customEmojiMap = {}; }
    // Wake any subscribers (chat, reaction picker) so they re-render now
    // that :codes: can resolve to <img>. Errors in one subscriber must NOT
    // poison the others — wrap each call.
    _customEmojiSubs.forEach(fn => { try { fn(_customEmojiCache); } catch (_) {} });
    return _customEmojiCache;
  })();
  return _customEmojiPromise;
}

// Tiny helper used by chat message reactions + the picker: render a single
// reaction token, transparently handling both standard glyphs and custom
// `:code:` references. Falls back to the raw token if the manifest isn't
// loaded yet (rare — we eagerly fetch on mount).
function ReactionEmoji({ emoji }) {
  if (typeof emoji === 'string' && emoji.length > 2 && emoji[0] === ':' && emoji[emoji.length - 1] === ':' && _customEmojiMap) {
    const file = _customEmojiMap[emoji];
    if (file) return <img className="chat-emoji-img" src={`/assets/emojis/${file}`} alt={emoji} loading="lazy"/>;
  }
  return <span>{emoji}</span>;
}
// Render text with :code: tokens converted to <img>. Cheap: split on token regex.
function renderChatText(text, onUserClick) {
  if (!text) return text;
  // Reply prefix detection — replies come in as `↪ @user: snippet\nbody`.
  // Render the quote portion as a visually-distinct block ABOVE the body
  // so it doesn't look like the replier wrote the snippet themselves
  // (the source of the "sanki ben yazdım" confusion).
  const replyMatch = /^↪ @([A-Za-z0-9_]{2,32}): (.{0,160}?)\n([\s\S]*)$/.exec(text);
  if (replyMatch) {
    const [, repUser, repSnippet, body] = replyMatch;
    return (
      <>
        <button
          type="button"
          className="chat-reply-quote"
          onClick={(e) => { e.stopPropagation(); if (onUserClick) onUserClick(repUser, { x: e.clientX, y: e.clientY }); }}
          title={`Reply to @${repUser}`}
        >
          <i className="fa-solid fa-reply"/>
          <span className="crq-name">@{repUser}</span>
          <span className="crq-snippet">{repSnippet}</span>
        </button>
        <div className="chat-reply-body">{renderChatTextInner(body, onUserClick)}</div>
      </>
    );
  }
  return renderChatTextInner(text, onUserClick);
}

function renderChatTextInner(text, onUserClick) {
  if (!text) return text;
  const out = [];
  let i = 0;
  const pushMentions = (str, baseKey) => {
    // Match `@` followed by 2+ alnum/underscore. Roblox usernames are 3+ chars
    // but allowing 2 is harmless and tolerates partial typos.
    const mentRe = /@([A-Za-z0-9_]{2,32})/g;
    let last = 0; let mm;
    while ((mm = mentRe.exec(str)) !== null) {
      if (mm.index > last) out.push(str.slice(last, mm.index));
      const uname = mm[1];
      if (onUserClick) {
        out.push(
          <button
            type="button"
            key={`m-${baseKey}-${mm.index}`}
            className="chat-mention"
            onClick={(e) => { e.stopPropagation(); onUserClick(uname, { x: e.clientX, y: e.clientY }); }}
          >@{uname}</button>
        );
      } else {
        out.push(mm[0]);
      }
      last = mentRe.lastIndex;
    }
    if (last < str.length) out.push(str.slice(last));
  };

  if (!_customEmojiMap) {
    pushMentions(text, 'plain');
    return out.length === 1 && typeof out[0] === 'string' ? out[0] : out;
  }
  // Allow hyphens in :code: tokens — kebab-case names (`pepe-cry`, `the-rock`)
  // must match the same character set as the picker's allowlist (see
  // loadCustomEmojis above) or chat messages won't resolve them to <img>.
  const re = /:([a-z0-9_-]{2,32}):/gi;
  let last = 0; let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) pushMentions(text.slice(last, m.index), `e${i}`);
    const file = _customEmojiMap[m[0]];
    if (file) {
      out.push(<img key={'e' + (i++)} className="chat-emoji-img" src={`/assets/emojis/${file}`} alt={m[0]} loading="lazy"/>);
    } else {
      out.push(m[0]); // unknown :code: leaves text as-is
    }
    last = re.lastIndex;
  }
  if (last < text.length) pushMentions(text.slice(last), `t${i}`);
  return out.length === 1 && typeof out[0] === 'string' ? out[0] : out;
}

function EmojiPicker({ onPick, onClose, anchorRect }) {
  const ref = React.useRef(null);
  const gridRef = React.useRef(null);
  const sectionRefs = React.useRef({});  // { [categoryId]: HTMLElement }
  const [customs, setCustoms] = React.useState(() => _customEmojiCache || []);
  const [tab, setTab] = React.useState('standard');
  const [search, setSearch] = React.useState('');
  const [activeCat, setActiveCat] = React.useState('recent');
  // Recently used emojis — persisted in localStorage so they survive
  // reloads. Most-recent first, max 24 entries. Each pick promotes the
  // emoji to the top of the list.
  const [recents, setRecents] = React.useState(() => {
    try {
      const raw = localStorage.getItem('bloxhot.emojiRecents');
      if (raw) return JSON.parse(raw).slice(0, 24);
    } catch (_) {}
    return [];
  });
  const remember = React.useCallback((emojiOrCode) => {
    setRecents(prev => {
      const next = [emojiOrCode, ...prev.filter(e => e !== emojiOrCode)].slice(0, 24);
      try { localStorage.setItem('bloxhot.emojiRecents', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);
  React.useEffect(() => {
    if (!_customEmojiCache) loadCustomEmojis().then(setCustoms);
    const unsub = subscribeCustomEmojis((list) => setCustoms(list || []));
    return unsub;
  }, []);
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.chat-emoji')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  // Position the picker like the reaction picker: portal'd to document.body
  // and anchored to the chat-emoji button. Opens ABOVE the button so it
  // doesn't sit on top of "Type a message" (the bug the user reported).
  // anchorRect is { left, top, right, bottom } from getBoundingClientRect().
  // Picker dimensions — match the reaction picker exactly (380×460) so
  // the two popups feel like siblings. 8-col grid at 40px buttons gives
  // 8 emojis per row, ~5-6 visible rows before scroll.
  const PICKER_W = 380;
  const PICKER_H = Math.min(460, Math.max(360, Math.floor(window.innerHeight * 0.6)));
  let style = {};
  if (anchorRect) {
    const isMobile = window.innerWidth <= 720;
    // Mobile: center horizontally over the chat input; desktop: align to
    // the right edge of the emoji button so the picker sits over the input
    // field's right half.
    const left = isMobile
      ? Math.max(8, Math.min(window.innerWidth - PICKER_W - 8, anchorRect.left + anchorRect.width / 2 - PICKER_W / 2))
      : Math.max(8, Math.min(window.innerWidth - PICKER_W - 8, anchorRect.right - PICKER_W));
    // Always open ABOVE the button — 10px gap, never overlapping the input.
    const top = Math.max(8, anchorRect.top - PICKER_H - 10);
    // `height` (not maxHeight) gives flex children a concrete target so
    // `flex: 1` actually distributes leftover space to the grid, which
    // then scrolls when emojis overflow. With maxHeight only, the
    // picker sized to content, content overflowed, and overflow:hidden
    // chopped it off — no scrollbar appeared because the grid never
    // discovered it should scroll.
    style = { position: 'fixed', left, top, width: PICKER_W, height: PICKER_H, zIndex: 9999 };
  }
  // Same full catalog as the reaction picker — search by glyph or keyword.
  // Defensive dedupe by emoji glyph in case catalog has duplicate entries
  // (e.g. someone added 🚀 twice with different keywords) — duplicates would
  // cause the same emoji to render twice on partial-keyword matches.
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q
      ? EMOJI_CATALOG
      : EMOJI_CATALOG.filter(it => it.e === q || it.k.toLowerCase().includes(q));
    const seen = new Set();
    const out = [];
    for (const it of list) {
      if (seen.has(it.e)) continue;
      seen.add(it.e);
      out.push(it);
    }
    return out;
  }, [search]);
  const filteredCustoms = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customs;
    return customs.filter(c =>
      (c.code || '').toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q)
    );
  }, [customs, search]);

  // Group emojis by category — used to filter the grid based on
  // activeCat. (Old design used scrollable sections; new design shows
  // ONE category at a time so the small picker stays readable.)
  const groupedByCategory = React.useMemo(() => {
    const groups = Object.fromEntries(EMOJI_CATEGORIES.map(c => [c.id, []]));
    groups.recent = recents
      .map(rec => {
        if (typeof rec === 'string' && rec.startsWith(':')) return { e: rec, k: rec };
        const hit = EMOJI_CATALOG.find(c => c.e === rec);
        return hit || { e: rec, k: rec };
      })
      .filter(Boolean);
    for (const it of filtered) {
      const cat = emojiCategoryFor(it.e);
      (groups[cat] || groups.symbols).push(it);
    }
    return groups;
  }, [filtered, recents]);

  // Compute which categories have anything in them, for greying out
  // empty tabs. Recent is empty until the user picks something.
  const catHasItems = React.useMemo(() => {
    const map = {};
    for (const cat of EMOJI_CATEGORIES) {
      map[cat.id] = (groupedByCategory[cat.id] || []).length > 0;
    }
    return map;
  }, [groupedByCategory]);

  // Default active category: first non-empty (Recent if there's any, else
  // People). Stays put across re-renders unless user clicks another.
  React.useEffect(() => {
    if (!catHasItems[activeCat]) {
      const first = EMOJI_CATEGORIES.find(c => catHasItems[c.id]);
      if (first) setActiveCat(first.id);
    }
  }, [catHasItems, activeCat]);

  // Visible emojis = current category's slice (or all on search).
  const visibleStandard = React.useMemo(() => {
    if (search.trim()) return filtered; // search overrides cat filter
    return groupedByCategory[activeCat] || [];
  }, [search, filtered, groupedByCategory, activeCat]);

  // Switch category — no scroll involved anymore, just swap visible set.
  const scrollToCategory = React.useCallback((catId) => {
    setActiveCat(catId);
    if (gridRef.current) gridRef.current.scrollTop = 0;
  }, []);

  // Wrap onPick to also remember the choice → grows the "Recent" tab.
  const handlePick = (val) => {
    remember(val);
    onPick(val);
  };

  // When portal'd via anchorRect we use fixed positioning + add
  // `is-anchored` so the CSS knows not to apply its absolute-bottom rule.
  const picker = (
    <div className={`emoji-picker ${anchorRect ? 'is-anchored' : ''}`} ref={ref} style={style}>
      <div className="ep-search">
        <i className="fa-solid fa-magnifying-glass"/>
        <input
          type="text"
          placeholder="Search emoji…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div className="ep-tabs">
        <button
          type="button"
          className={`ep-tab ${tab === 'standard' ? 'is-active' : ''}`}
          onClick={() => setTab('standard')}
        >Standard</button>
        {customs.length > 0 && (
          <button
            type="button"
            className={`ep-tab ${tab === 'custom' ? 'is-active' : ''}`}
            onClick={() => setTab('custom')}
          >Custom</button>
        )}
      </div>
      {tab === 'custom' ? (
        <div className="ep-grid ep-grid--custom" ref={gridRef}>
          {filteredCustoms.length === 0 ? (
            <div className="ep-empty">No custom emoji matches "{search}"</div>
          ) : filteredCustoms.map((c) => (
            // NO `loading="lazy"` here — the picker is a scrollable grid;
            // lazy-load only fires when an image scrolls into the viewport,
            // but the picker root has overflow:hidden so most images never
            // qualified. The total payload is ~300 KB across 60+ files — fine
            // to eager-fetch. Also NO `onError={hide}` — if a file fails to
            // load we want to SEE the broken button so the issue is visible
            // instead of silently shrinking the catalog to 29 emojis.
            <button key={c.code} type="button" title={c.title || c.code} onClick={() => handlePick(c.code)}>
              <img src={`/assets/emojis/${c.file}`} alt={c.code}/>
            </button>
          ))}
        </div>
      ) : (
        // Flat grid — one category at a time (chosen via the bottom nav).
        // No section headers, no sticky labels — just a clean wall of
        // emojis for the currently-active category.
        <div className="ep-grid" ref={gridRef}>
          {visibleStandard.length === 0 ? (
            <div className="ep-empty">
              {search
                ? `No emoji matches "${search}"`
                : activeCat === 'recent' ? 'Your recently used emojis appear here.' : 'Nothing here.'}
            </div>
          ) : visibleStandard.map((it, i) => {
            // Recent can contain custom :codes: — render them as <img>.
            const isCustomCode = typeof it.e === 'string' && it.e.startsWith(':') && it.e.endsWith(':');
            const file = isCustomCode ? _customEmojiMap?.[it.e] : null;
            return (
              <button
                key={`${activeCat}-${i}-${it.e}`}
                type="button"
                title={it.k ? it.k.split(' ').slice(0, 3).join(', ') : it.e}
                onClick={() => handlePick(it.e)}
              >
                {file
                  ? <img src={`/assets/emojis/${file}`} alt={it.e}/>
                  : it.e}
              </button>
            );
          })}
        </div>
      )}
      {/* Category nav — only on the Standard tab. Clicking a cat filters
          the grid to that cat. Animated underline slides between active
          buttons via a single <span class="ep-catnav-pill"/> positioned
          absolutely (smooth transform between cats — single moving bar). */}
      {tab === 'standard' && !search && (
        <div className="ep-catnav">
          {EMOJI_CATEGORIES.map((cat, idx) => {
            return (
              <button
                key={cat.id}
                type="button"
                className={`ep-catbtn ${activeCat === cat.id ? 'is-active' : ''}`}
                onClick={() => scrollToCategory(cat.id)}
                title={cat.label}
                disabled={!catHasItems[cat.id]}
                data-cat-idx={idx}
              >
                <i className={cat.icon}/>
              </button>
            );
          })}
          {/* Single sliding underline — translated via CSS variable based
              on activeCat's index. Much smoother than per-button ::after
              animations, and reads as a single pill moving between cats. */}
          <span
            className="ep-catnav-pill"
            style={{
              '--cat-idx': EMOJI_CATEGORIES.findIndex(c => c.id === activeCat),
              '--cat-count': EMOJI_CATEGORIES.length,
            }}
          />
        </div>
      )}
    </div>
  );
  // Anchored mode → portal to <body> so we escape chat-side overflow
  // clipping (same trick the reaction picker uses). Otherwise render
  // inline (legacy fallback).
  return anchorRect ? ReactDOM.createPortal(picker, document.body) : picker;
}

// Live giveaway strip above chat — players host item giveaways and others
// click to enter. Polls /api/giveaways every 10s. Renders a carousel if there
// are multiple. Click "Host" → opens inventory in giveaway-create mode.
function GiveawayStrip({ currentUser, t, onOpenInventory }) {
  const [giveaways, setGiveaways] = React.useState([]);
  const [idx, setIdx] = React.useState(0);
  const [now, setNow] = React.useState(() => Date.now());

  // Poll giveaways every 10s. Also force-refresh after each click so the
  // entry count + i_entered flag updates immediately.
  const refresh = React.useCallback(async () => {
    try {
      const res = await apiFetch('/api/giveaways');
      setGiveaways(res.giveaways || []);
    } catch (_) {}
  }, []);
  React.useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    // Listen for in-app create/enter events → refresh immediately so the
    // newly-hosted giveaway appears without a page reload.
    const onChange = () => refresh();
    window.addEventListener('blox:giveawayChanged', onChange);
    // Also refresh when the tab regains focus (covers mobile bg-tab cases)
    const onFocus = () => { if (!document.hidden) refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('blox:giveawayChanged', onChange);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);
  // Tick for countdown display (1s)
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const active = giveaways.filter(g => g.status === 'active');
  const current = active[idx % Math.max(1, active.length)];
  // Cap idx so it never points past the end
  React.useEffect(() => {
    if (idx >= active.length && active.length > 0) setIdx(0);
  }, [active.length, idx]);

  const enter = async (gid) => {
    // Not logged in → prompt login instead of firing an auth-failed toast
    if (!currentUser) {
      try { window.dispatchEvent(new CustomEvent('blox:showAuth', { detail: { mode: 'login' } })); } catch (_) {}
      try { window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'info', text: 'Log in to enter giveaways.' } })); } catch (_) {}
      return;
    }
    try {
      const res = await apiFetch('/api/giveaways/enter?id=' + gid, { method: 'POST' });
      if (res && res.ok) {
        refresh();
        try { window.dispatchEvent(new CustomEvent('blox:giveawayChanged')); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'win', text: 'Entered giveaway! 🎁' } })); } catch (_) {}
      }
    } catch (err) {
      // Map server error codes to clean user-facing messages
      const code = err?.code;
      let text;
      let type = 'lose';
      if (code === 'not_eligible') {
        text = 'You need to play at least 1 coinflip in the last 24 hours to enter.';
        type = 'info';
      } else if (code === 'own_giveaway') {
        text = "You can't enter your own giveaway.";
      } else if (code === 'closed' || code === 'not_found') {
        text = 'This giveaway has already ended.';
      } else if (err?.status === 401) {
        text = 'Log in to enter giveaways.';
        type = 'info';
        try { window.dispatchEvent(new CustomEvent('blox:showAuth', { detail: { mode: 'login' } })); } catch (_) {}
      } else {
        text = String(err?.message || 'Could not enter giveaway').replace(/\s*\(\/api[^)]*\)\s*/g, '').trim();
      }
      try { window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type, text } })); } catch (_) {}
    }
  };

  if (!active.length) {
    // No active giveaways → don't render anything. The "Host a giveaway"
    // entry point lives inside the Inventory modal now, not in the chat strip.
    return null;
  }

  const fmtCountdown = (ends) => {
    const ms = Math.max(0, new Date(ends.replace(' ', 'T') + 'Z').getTime() - now);
    if (ms <= 0) return 'Drawing…';
    const s = Math.floor(ms / 1000);
    if (s < 60) return s + 's';
    if (s < 3600) return Math.floor(s / 60) + 'm ' + (s % 60) + 's';
    return Math.floor(s / 3600) + 'h ' + Math.floor((s % 3600) / 60) + 'm';
  };

  const g = current;
  const isMine = currentUser && g.creator_id === currentUser.id;

  return (
    <div className="giveaway-strip">
      <div className="giveaway-card" key={g.id}>
        <div className="giveaway-head">
          <div className="giveaway-creator">
            {g.creator_avatar
              ? <img src={g.creator_avatar} alt="" className="giveaway-av"/>
              : <div className="giveaway-av giveaway-av-fallback">{(g.creator_name || '?').slice(0,2).toUpperCase()}</div>}
            <div>
              <div className="giveaway-label">GIVEAWAY by</div>
              <div className="giveaway-creator-name">{g.creator_name}</div>
            </div>
          </div>
          {/* Host CTA removed — hosting happens from the Inventory modal now. */}
        </div>
        <div className="giveaway-body">
          <div className="giveaway-item">
            <img
              src={`assets/images/${encodeURIComponent(g.item_name)}.webp`}
              alt={g.item_name}
              className="giveaway-item-img"
              onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
            />
            {/* Corner "+N" badge removed — the inline "+N more" tag next to
                the item name already conveys the bundle size, badge on the
                image was redundant. */}
          </div>
          <div className="giveaway-info">
            <div className="giveaway-item-name" title={g.item_name}>
              {g.item_name}{(g.item_count || 1) > 1 && <span className="gv-bundle-tag">  +{(g.item_count || 1) - 1} more</span>}
            </div>
            <div className="giveaway-value">
              <i className="fa-solid fa-gem gem-i"/><b>{Math.round(g.item_price).toLocaleString()}</b>
            </div>
            <div className="giveaway-meta">
              <span className="giveaway-meta-pill" title="Time remaining">
                <i className="fa-regular fa-clock"/> {fmtCountdown(g.ends_at)}
              </span>
              <span className="giveaway-meta-pill" title="Entries">
                <i className="fa-solid fa-users"/> {g.entry_count}
              </span>
            </div>
          </div>
        </div>
        <div className="giveaway-foot">
          {isMine ? (
            <span className="giveaway-mine">Your giveaway is live</span>
          ) : g.i_entered ? (
            <span className="giveaway-entered"><i className="fa-solid fa-check"/> Entered</span>
          ) : (
            <button type="button" className="giveaway-join-btn" onClick={() => enter(g.id)}>
              Join giveaway
            </button>
          )}
        </div>
      </div>
      {active.length > 1 && (
        <div className="giveaway-pager">
          <button type="button" className="giveaway-arrow" onClick={() => setIdx((idx - 1 + active.length) % active.length)} aria-label="prev">
            <i className="fa-solid fa-chevron-left"/>
          </button>
          <div className="giveaway-dots">
            {active.map((_, i) => (
              <span key={i} className={`giveaway-dot ${i === (idx % active.length) ? 'is-active' : ''}`} onClick={() => setIdx(i)}/>
            ))}
          </div>
          <button type="button" className="giveaway-arrow" onClick={() => setIdx((idx + 1) % active.length)} aria-label="next">
            <i className="fa-solid fa-chevron-right"/>
          </button>
        </div>
      )}
    </div>
  );
}

// Modal where a user picks one or more inventory items + duration to host
// a giveaway. Multi-select via toggle, sum-of-values shown in the CTA.
function GiveawayCreatorModal({ items, onClose, onCreated, pushToast }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const [pickedIds, setPickedIds] = React.useState(() => new Set());
  const [durationMin, setDurationMin] = React.useState(60);
  const [submitting, setSubmitting] = React.useState(false);
  const available = (items || []).filter(it => !it.lockedFlipId);

  const pickedItems = available.filter(it => pickedIds.has(it.id));
  const totalValue  = pickedItems.reduce((s, it) => s + (Number(it.price) || 0), 0);
  const canSubmit   = pickedItems.length > 0 && totalValue >= 1 && !submitting;
  const valueLow    = pickedItems.length > 0 && totalValue < 1;

  const toggle = (id) => setPickedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const dragSel = useDragMultiSelect(toggle, (id) => pickedIds.has(id));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/giveaways', {
        method: 'POST',
        body: {
          inventory_ids: pickedItems.map(it => Number(it.id)),
          duration_minutes: durationMin,
        },
      });
      if (res && res.ok) {
        try { window.dispatchEvent(new CustomEvent('blox:giveawayChanged')); } catch (_) {}
        onCreated && onCreated();
      } else {
        pushToast && pushToast({ type: 'lose', text: 'Could not create giveaway' });
      }
    } catch (err) {
      // Strip "(/api/...)" path from server errors before surfacing as toast
      const raw = err?.message || 'Could not create giveaway';
      const clean = String(raw).replace(/\s*\(\/api[^)]*\)\s*/g, '').trim();
      pushToast && pushToast({ type: 'lose', text: clean || 'Could not create giveaway' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`modal-overlay ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-head">
          <h3>🎁 Host a giveaway</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body">
          <div className="inv-label">Pick item(s) from your inventory · max 20</div>
          {available.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-illu">🎁</div>
              <h3>No items available</h3>
              <p>Deposit items to start a giveaway.</p>
            </div>
          ) : (
            <div className="inv-grid" style={{ maxHeight: '34vh' }} {...dragSel.gridProps}>
              {available.map(it => (
                <button
                  key={it.id}
                  type="button"
                  className={`inv-item ${pickedIds.has(it.id) ? 'selected' : ''}`}
                  onClick={() => { if (!dragSel.shouldSuppressClick(it.id)) toggle(it.id); }}
                  disabled={!pickedIds.has(it.id) && pickedIds.size >= 20}
                  {...dragSel.itemProps(it.id)}
                >
                  <FlipItem item={it} size={56} noTooltip/>
                  <div className="inv-item-name" title={it.name}>{it.name}</div>
                  <div className="inv-item-price"><i className="fa-solid fa-gem gem-i"/>{Math.round(it.price || 0).toLocaleString()}</div>
                </button>
              ))}
            </div>
          )}

          {pickedItems.length > 0 && (
            <div className="gv-pick-summary">
              <span><b>{pickedItems.length}</b> {pickedItems.length === 1 ? 'item' : 'items'} selected</span>
              <span className="gv-pick-total"><i className="fa-solid fa-gem gem-i"/>{Math.round(totalValue).toLocaleString()}</span>
            </div>
          )}

          <div className="inv-label" style={{ marginTop: 12 }}>Duration</div>
          <div className="match-range">
            {[15, 30, 60, 120, 360, 1440].map(m => (
              <button
                key={m}
                type="button"
                className={`mr-chip ${durationMin === m ? 'is-active' : ''}`}
                onClick={() => setDurationMin(m)}
              >
                {m < 60 ? m + 'm' : (m / 60) + 'h'}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 12, padding: 12, background: 'rgba(16,185,129,0.08)', borderRadius: 10, fontSize: 12, color: 'var(--g-700)', lineHeight: 1.5 }}>
            <b style={{ color: 'var(--orange)' }}>How it works:</b> Selected items are locked while the giveaway runs.
            Anyone who's played a coinflip in the last 24h can enter. When time's up, a random entrant wins the whole bundle.
            If no one enters, items return to you. Minimum total value: 1 gem.
          </div>

          <button
            className="modal-cta"
            disabled={!canSubmit}
            onClick={submit}
            style={{ marginTop: 14 }}
          >
            {submitting
              ? tt('hosting', 'Hosting…')
              : valueLow
                ? tt('totalValueTooLow', 'Total value too low (min 1)')
                : pickedItems.length === 0
                  ? tt('selectAtLeastOne', 'Select at least 1 item')
                  : pickedItems.length === 1
                    ? tt('hostGiveawayBtnOne', 'Host giveaway · 1 item · {amount}', { amount: Math.round(totalValue).toLocaleString() })
                    : tt('hostGiveawayBtn', 'Host giveaway · {n} items · {amount}', { n: pickedItems.length, amount: Math.round(totalValue).toLocaleString() })}
          </button>
        </div>
      </div>
    </div>
  );
}

function Chat({ messages, online, onSend, t, lang, open, onClose, tab, onTabChange, onUserClick, pinned, currentUser }) {
  const langInfo = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];
  const [draft, setDraft] = React.useState('');
  const [emojiOpen, setEmojiOpen] = React.useState(false);
  // Captured DOMRect of the chat-emoji button — used by the portal'd
  // EmojiPicker to position itself anchored to the button (the way the
  // reaction picker does it). Without an anchor the picker falls back
  // to its legacy absolute positioning inside the chat-input-row.
  const [emojiAnchorRect, setEmojiAnchorRect] = React.useState(null);
  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const stickToBottom = React.useRef(true);
  const [newCount, setNewCount] = React.useState(0); // "↓ N new" pill when scrolled up

  // ===== Reactions state =====
  // Map of message_id → array of { emoji, count, mine } objects.
  // Server returns them in first-reacted-first order so the order is stable.
  // Old monolithic `reactions` state replaced by split server+local model below.
  // The `reactions` variable now comes from a useMemo combining both stores.
  const [reactPickerFor, setReactPickerFor] = React.useState(null);
  const [menuFor, setMenuFor]               = React.useState(null); // context menu open for message
  // Reply target — when set, the chat input row shows a quoted preview above
  // the input and prepends ">> @username: text\n" when the user sends.
  const [replyTo, setReplyTo] = React.useState(null);
  const [menuPos, setMenuPos]               = React.useState({ x: 0, y: 0 });
  // Reaction-detail (who reacted) state REMOVED per user request.
  const [reportingMsg, setReportingMsg]     = React.useState(null); // {srvId, name, text, avatarUrl, ts}
  const [reportStep, setReportStep]         = React.useState('category'); // category | summary | sent
  const [reportReason, setReportReason]     = React.useState(null); // {code, label}
  const [reportOther, setReportOther]       = React.useState(''); // free-text when reason.code === 'other'
  const [emojiSearch, setEmojiSearch]       = React.useState('');
  const [emojiTab, setEmojiTab]             = React.useState('standard');
  // Cat-nav for the reaction picker (mirrors the chat picker's nav).
  // Persisted across opens so the user lands back where they left off.
  const [reactActiveCat, setReactActiveCat] = React.useState('people');
  // 4 most-used reactions (Discord-style quick row)
  const QUICK_REACTIONS = ['❤️','🔥','😂','👍'];

  // Custom emojis (from /assets/emojis/manifest.json). Loaded once at
  // App-mount; we also subscribe here so the picker re-renders if it
  // loads while the picker is already open.
  const [customs, setCustoms] = React.useState(() => _customEmojiCache || []);
  React.useEffect(() => {
    if (!_customEmojiCache) loadCustomEmojis().then(setCustoms);
    const unsub = subscribeCustomEmojis((list) => setCustoms(list || []));
    return unsub;
  }, []);

  // Filter the searchable emoji catalog by typed keyword, with defensive
  // dedupe by emoji glyph (covers any future stray duplicates in catalog).
  const filteredEmojis = React.useMemo(() => {
    const q = emojiSearch.trim().toLowerCase();
    const list = !q
      ? EMOJI_CATALOG
      : EMOJI_CATALOG.filter(it => it.e === q || it.k.toLowerCase().includes(q));
    const seen = new Set();
    const out = [];
    for (const it of list) {
      if (seen.has(it.e)) continue;
      seen.add(it.e);
      out.push(it);
    }
    return out;
  }, [emojiSearch]);

  // Same filter applied to the custom emoji set — search by `:code:` or title.
  const filteredCustoms = React.useMemo(() => {
    const q = emojiSearch.trim().toLowerCase();
    if (!q) return customs;
    return customs.filter(c =>
      (c.code || '').toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q)
    );
  }, [customs, emojiSearch]);

  // Group filtered emojis by category for the reaction picker's cat-nav.
  // (Recent is not included here — the quick-reactions row at the top of
  // the picker already serves that purpose better than another bucket.)
  const reactGroupedByCategory = React.useMemo(() => {
    const groups = Object.fromEntries(EMOJI_CATEGORIES.map(c => [c.id, []]));
    for (const it of filteredEmojis) {
      const cat = emojiCategoryFor(it.e);
      (groups[cat] || groups.symbols).push(it);
    }
    return groups;
  }, [filteredEmojis]);
  const reactCatHasItems = React.useMemo(() => {
    const map = {};
    for (const cat of EMOJI_CATEGORIES) {
      map[cat.id] = (reactGroupedByCategory[cat.id] || []).length > 0;
    }
    return map;
  }, [reactGroupedByCategory]);
  // Visible standard emojis when no search — filtered to active category.
  // While searching, show the full flat result (cat-nav doesn't make sense
  // for search results).
  const reactVisibleStandard = React.useMemo(() => {
    if (emojiSearch.trim()) return filteredEmojis;
    return reactGroupedByCategory[reactActiveCat] || [];
  }, [emojiSearch, filteredEmojis, reactGroupedByCategory, reactActiveCat]);
  // If active cat is empty, jump to the first non-empty one.
  React.useEffect(() => {
    if (!reactCatHasItems[reactActiveCat]) {
      const first = EMOJI_CATEGORIES.find(c => c.id !== 'recent' && reactCatHasItems[c.id]);
      if (first) setReactActiveCat(first.id);
    }
  }, [reactCatHasItems, reactActiveCat]);

  // openReactDetail removed — "who reacted" feature is gone.

  // Open the context menu at the click/tap position, anchored ABOVE the message
  const openContextMenu = (m, evt) => {
    if (evt) evt.preventDefault();
    if (!currentUser || !m.srvId) return;
    // Anchor near pointer but flip so menu appears ABOVE the click point
    const x = evt ? (evt.clientX || (evt.touches && evt.touches[0]?.clientX) || 0) : 0;
    const y = evt ? (evt.clientY || (evt.touches && evt.touches[0]?.clientY) || 0) : 0;
    setMenuPos({ x, y });
    setMenuFor(m.srvId);
    setReactPickerFor(null);
  };

  // Open the multi-step report flow for a message
  const openReport = (target) => {
    setReportingMsg(target);
    setReportStep('category');
    setReportReason(null);
    setReportOther('');
  };
  const closeReport = () => {
    setReportingMsg(null);
    setReportStep('category');
    setReportReason(null);
    setReportOther('');
  };
  // Submit the report — only called from the summary step
  const submitReport = async () => {
    if (!reportingMsg || !reportReason) return;
    // For "Something else" the free-text explanation is required
    if (reportReason.code === 'other' && reportOther.trim().length < 5) return;
    try {
      await apiFetch('/api/chat/report', {
        method: 'POST',
        body: {
          message_id: reportingMsg.srvId,
          reason: reportReason.code,
          // Server stores the free-text note when category is "other"
          ...(reportReason.code === 'other' ? { note: reportOther.trim().slice(0, 500) } : {}),
        },
      });
    } catch (_) {}
    setReportStep('sent');
    setTimeout(() => closeReport(), 1400);
  };

  // Outside-click is now handled by the portal-rendered .msg-ctx-backdrop
  // (a fullscreen invisible layer behind each popover). The old document
  // mousedown listener tried to use a stale selector and was prematurely
  // closing the menu BEFORE the inner click handler fired — which is why
  // "Add Reaction" and "Report" looked like they did nothing.

  // Detect @mentions of the current user
  const myHandle = (currentUser?.username || '').toLowerCase();
  const isMention = React.useCallback((text) => {
    if (!myHandle) return false;
    const re = new RegExp(`@${myHandle}\\b`, 'i');
    return re.test(text);
  }, [myHandle]);

  // Track whether user is near the bottom — only auto-scroll then
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottom.current = dist < 80;
    if (stickToBottom.current && newCount > 0) setNewCount(0);
  };

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    stickToBottom.current = true;
    setNewCount(0);
  };

  // Smooth-scroll on new messages when already at bottom; else increment pill.
  // Also bumps `unreadMentions` for the browser-title indicator when window
  // is unfocused — done in the same effect so we have access to `newOnes`
  // before prevMsgCount is mutated.
  const prevMsgCount = React.useRef(messages.length);
  const windowFocusedRef = React.useRef(true);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newOnes = messages.length - prevMsgCount.current;
    prevMsgCount.current = messages.length;
    if (newOnes > 0 && !windowFocusedRef.current) {
      const fresh = messages.slice(-newOnes);
      const m = fresh.filter(msg => msg.type !== 'system' && isMention(msg.text)).length;
      if (m > 0) {
        setUnreadMentions(c => {
          const next = c + m;
          document.title = `(${next}) blox.hot`;
          return next;
        });
      }
    }
    if (stickToBottom.current) {
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
    } else if (newOnes > 0) {
      setNewCount(n => n + newOnes);
    }
  }, [messages, isMention]);

  // ===== Reactions — split state model =====
  // Two separate stores. Render combines them. This kills all the merge bugs
  // that the previous designs suffered from (3rd emoji wiping the first two,
  // etc) because:
  //   • `reactionsServer` is RAW server data — replaced wholesale on every
  //     refetch. No conditional skip-or-replace logic.
  //   • `reactionsLocal` tracks MY personal pending adds + removes for each
  //     message. Survives every refetch. Cleared only when the POST succeeds
  //     AND the next refetch confirms the server has caught up (or 8s timeout).
  // The visible chips are a function of both via `useMemo` — guaranteed
  // consistent every render.

  const [reactionsServer, setReactionsServer] = React.useState({});
  // reactionsLocal[mid] = { adds: Set<emoji>, removes: Set<emoji> }
  const [reactionsLocal, setReactionsLocal] = React.useState({});
  // ts of each pending click, used to auto-expire stuck pending entries
  const pendingTsRef = React.useRef(new Map()); // "msgId:emoji" → click ms

  // Combined view used by the chip renderer
  const reactions = React.useMemo(() => {
    const out = {};
    const allMids = new Set([
      ...Object.keys(reactionsServer),
      ...Object.keys(reactionsLocal),
    ]);
    for (const mid of allMids) {
      const server = reactionsServer[mid] || [];
      const local  = reactionsLocal[mid]  || { adds: new Set(), removes: new Set() };
      const result = [];
      const seen = new Set();
      // Server reactions, with my pending removes filtered/decremented
      for (const r of server) {
        if (local.removes.has(r.emoji) && r.mine) {
          // I'm removing → count-- and mine=false (other users may still hold it)
          const c = Math.max(0, r.count - 1);
          if (c > 0) result.push({ emoji: r.emoji, count: c, mine: false });
        } else if (local.adds.has(r.emoji) && !r.mine) {
          // Server hasn't seen my add yet but has count from others → bump it
          result.push({ emoji: r.emoji, count: r.count + 1, mine: true });
        } else {
          result.push(r);
        }
        seen.add(r.emoji);
      }
      // Pending adds that the server doesn't have yet → optimistic chips
      for (const emoji of local.adds) {
        if (!seen.has(emoji)) {
          result.push({ emoji, count: 1, mine: true });
        }
      }
      if (result.length > 0) out[mid] = result;
    }
    return out;
  }, [reactionsServer, reactionsLocal]);

  const messagesRef = React.useRef(messages);
  React.useEffect(() => { messagesRef.current = messages; }, [messages]);

  const fetchReactionsNow = React.useCallback(async () => {
    const visible = messagesRef.current.filter(m => m.srvId).map(m => m.srvId).slice(-60);
    if (visible.length === 0) return;
    try {
      const res = await apiFetch(`/api/chat/react?message_ids=${visible.join(',')}`);
      const byMid = {};
      for (const r of (res.reactions || [])) {
        const mid = r.message_id;
        if (!byMid[mid]) byMid[mid] = [];
        byMid[mid].push({ emoji: r.emoji, count: r.count, mine: r.mine });
      }
      // REPLACE server state for every visible message. No skip-or-merge.
      // The pending overlay handles transitional state separately.
      setReactionsServer(prev => {
        const next = { ...prev };
        for (const mid of visible) {
          const arr = byMid[mid] || [];
          if (arr.length === 0) delete next[mid];
          else next[mid] = arr;
        }
        return next;
      });
      // Reconcile pending overlay: any pending add that now appears in server
      // with mine=true can be cleared (server caught up). Same for pending
      // remove that no longer appears in server.
      setReactionsLocal(prev => {
        const next = { ...prev };
        for (const mid of visible) {
          const local = next[mid];
          if (!local) continue;
          const serverEmojis = byMid[mid] || [];
          let dirty = false;
          const adds = new Set(local.adds);
          const removes = new Set(local.removes);
          for (const emoji of [...adds]) {
            const s = serverEmojis.find(r => r.emoji === emoji);
            if (s && s.mine) { adds.delete(emoji); dirty = true; }
          }
          for (const emoji of [...removes]) {
            const s = serverEmojis.find(r => r.emoji === emoji);
            if (!s || !s.mine) { removes.delete(emoji); dirty = true; }
          }
          // Auto-expire stuck pendings after 8s (in case of POST failure)
          for (const emoji of [...adds]) {
            const ts = pendingTsRef.current.get(mid + ':' + emoji);
            if (ts && Date.now() - ts > 8000) { adds.delete(emoji); dirty = true; }
          }
          for (const emoji of [...removes]) {
            const ts = pendingTsRef.current.get(mid + ':' + emoji);
            if (ts && Date.now() - ts > 8000) { removes.delete(emoji); dirty = true; }
          }
          if (dirty) {
            if (adds.size === 0 && removes.size === 0) delete next[mid];
            else next[mid] = { adds, removes };
          }
        }
        return next;
      });
    } catch (_) {}
  }, []);

  React.useEffect(() => {
    if (messages.length === 0) return;
    let cancelled = false;
    const tick = () => { if (!cancelled) fetchReactionsNow(); };
    tick(); // initial
    const interval = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [messages.length, fetchReactionsNow]);

  // SSE-pushed cross-device sync. When ANY user anywhere adds/removes a
  // reaction, the server pushes `reactions_changed` and the App-level SSE
  // handler dispatches a window event. We refetch immediately — total
  // latency from "click on PC" to "shows on phone" ≈ 500-800ms.
  React.useEffect(() => {
    const onChange = () => fetchReactionsNow();
    window.addEventListener('blox:reactionsChanged', onChange);
    return () => window.removeEventListener('blox:reactionsChanged', onChange);
  }, [fetchReactionsNow]);

  // Also refetch when the tab regains focus — covers SSE drops on mobile
  // (iOS/Android often suspend SSE on background tab) so coming back to
  // the chat shows the latest reactions immediately.
  React.useEffect(() => {
    const onFocus = () => { if (!document.hidden) fetchReactionsNow(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchReactionsNow]);

  // Click handler — toggles the LOCAL overlay (instant), then fires the POST.
  // Includes a client-side cap (10 unique emojis per message) so the user
  // doesn't fire a doomed POST and watch their optimistic chip rollback.
  const toggleReaction = (messageId, emoji) => {
    setReactPickerFor(null);

    // Figure out the action from the CURRENT visible state (server + overlay)
    const visibleList = reactions[messageId] || [];
    const visibleEmoji = visibleList.find(r => r.emoji === emoji);
    const wasMine = visibleEmoji ? visibleEmoji.mine : false;
    const emojiAlreadyOnMsg = !!visibleEmoji;

    // Client-side cap: max 10 distinct emoji types per message. Only blocks
    // when adding a NEW emoji that isn't already on the message.
    if (!wasMine && !emojiAlreadyOnMsg && visibleList.length >= 10) {
      pushToast({ type: 'lose', text: 'Max 10 different emojis per message.' });
      return;
    }

    pendingTsRef.current.set(messageId + ':' + emoji, Date.now());

    setReactionsLocal(prev => {
      const next = { ...prev };
      const cur = next[messageId] || { adds: new Set(), removes: new Set() };
      const adds    = new Set(cur.adds);
      const removes = new Set(cur.removes);
      if (wasMine) {
        // Currently showing as mine → user wants to remove
        adds.delete(emoji);        // cancel a pending add (if any)
        removes.add(emoji);        // mark as pending remove
      } else {
        // Currently NOT mine → user wants to add
        removes.delete(emoji);     // cancel a pending remove (if any)
        adds.add(emoji);           // mark as pending add
      }
      if (adds.size === 0 && removes.size === 0) delete next[messageId];
      else next[messageId] = { adds, removes };
      return next;
    });

    // Fire the POST. The server also enforces the cap (defense in depth) —
    // if it rejects with limit_reached we surface the toast.
    apiFetch('/api/chat/react', {
      method: 'POST',
      body: { message_id: messageId, emoji },
    }).catch((err) => {
      if (err && err.code === 'limit_reached') {
        pushToast({ type: 'lose', text: 'Max 10 different emojis per message.' });
      }
      pendingTsRef.current.set(messageId + ':' + emoji, Date.now());
    });
  };

  // ===== Browser title indicator — keeps `unreadMentions` in scope =====
  const [unreadMentions, setUnreadMentions] = React.useState(0);
  React.useEffect(() => {
    const onVis = () => {
      windowFocusedRef.current = !document.hidden;
      if (!document.hidden) {
        setUnreadMentions(0);
        document.title = 'blox.hot';
      }
    };
    onVis();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, []);

  // SCROLL-TO-BOTTOM ON LOAD / REOPEN — the user expects to land on the
  // newest messages, never mid-scroll. This was regressing intermittently
  // because the original fix only fired on `open` transition, and on PC
  // the chat sidebar is always open so that transition never happens.
  //
  // The fix now stacks THREE triggers, all idempotent:
  //   1. useLayoutEffect on mount → fires before browser paint, so the
  //      user never sees the wrong scroll position.
  //   2. effect on `open` flip (for mobile sheet reopen).
  //   3. effect on first batch of messages arriving (0 → N).
  // Each trigger schedules MULTIPLE delayed snaps (0 / 60 / 250 / 800ms)
  // to catch lazy-loaded images and async font/emoji renders that bump
  // scrollHeight after layout has settled.
  const snapToBottomNow = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickToBottom.current = true;
  }, []);
  const scheduleBottomSnaps = React.useCallback(() => {
    const snap = () => snapToBottomNow();
    requestAnimationFrame(snap);
    setTimeout(snap, 60);
    setTimeout(snap, 250);
    setTimeout(snap, 800);
  }, [snapToBottomNow]);

  // Trigger #1 — synchronous, runs on mount before paint.
  React.useLayoutEffect(() => {
    snapToBottomNow();
    scheduleBottomSnaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger #2 — chat panel reopens (mobile sheet).
  React.useEffect(() => {
    if (open) scheduleBottomSnaps();
  }, [open, scheduleBottomSnaps]);

  // Trigger #3 — first batch of messages lands after empty initial render.
  const firstMsgSnap = React.useRef(false);
  React.useEffect(() => {
    if (!firstMsgSnap.current && messages.length > 0) {
      firstMsgSnap.current = true;
      scheduleBottomSnaps();
    }
  }, [messages.length, scheduleBottomSnaps]);

  // (Tab state is owned by App now — see the Chat polling effect.)

  // 1.5-second client-side cooldown — gating the send button so the user
  // can't spam. Better UX than letting the server reject + showing a toast.
  const [cooldownUntil, setCooldownUntil] = React.useState(0);
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (cooldownUntil <= now) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [cooldownUntil, now]);
  const onCooldown = cooldownUntil > now;
  const remainingMs = Math.max(0, cooldownUntil - now);

  // Chat anti-spam rules. Limits:
  //  - MAX_LEN          → hard input cap (also enforced by maxLength)
  //  - MAX_EMOJIS       → no more than 10 emoji tokens (standard + custom) per msg
  //  - DUPE_COUNT_MAX   → allow 3 consecutive identical sends; block the 4th+
  //  - DUPE_WINDOW_MS   → "consecutive" only counts within 60s of the last send
  //  - RATE_BURST       → 5 messages in 10s → 8s cooldown (burst penalty)
  // Discord-style: the send button shows the live countdown, and the input
  // shows an inline error chip for rule violations (`Max 10 emojis`, etc.).
  const MAX_LEN = 200;
  const MAX_EMOJIS = 10;
  const DUPE_COUNT_MAX = 3;
  const DUPE_WINDOW_MS = 60_000;
  const RATE_BURST = 5;
  const RATE_WINDOW_MS = 10_000;
  const RATE_PENALTY_MS = 8_000;
  // Per-instance recent-send timestamps + last-text for dupe detection.
  const recentSendsRef = React.useRef([]);
  const lastSentTextRef = React.useRef('');
  const lastSentAtRef   = React.useRef(0);
  const dupeCountRef    = React.useRef(0);   // # of consecutive identical sends
  const [validationErr, setValidationErr] = React.useState('');
  // Live emoji count from draft — used to render the "8/10 emoji" hint
  // and disable the send button proactively (so the user sees feedback
  // BEFORE they click send).
  const emojiCount = React.useMemo(() => {
    if (!draft) return 0;
    // Standard emojis: Unicode emoji-style codepoints (rough heuristic).
    const std = (draft.match(/\p{Extended_Pictographic}/gu) || []).length;
    const custom = (draft.match(/:([a-z0-9_-]{2,32}):/gi) || []).length;
    return std + custom;
  }, [draft]);

  const submit = (e) => {
    if (e) e.preventDefault();
    if (onCooldown) return;
    const text = draft.trim();
    if (!text) return;
    if (text.length > MAX_LEN) {
      setValidationErr(`Message too long (${text.length}/${MAX_LEN}).`);
      return;
    }
    if (emojiCount > MAX_EMOJIS) {
      setValidationErr(`Max ${MAX_EMOJIS} emojis per message.`);
      return;
    }
    // Block AFTER N consecutive identical sends within DUPE_WINDOW_MS.
    // First (DUPE_COUNT_MAX) repeats are allowed — only the 4th+ identical
    // send in a row triggers the warning. This gives the user a couple of
    // legit re-sends (e.g. server didn't echo back fast enough) without
    // immediately gating.
    const isDupe = text === lastSentTextRef.current && (Date.now() - lastSentAtRef.current) < DUPE_WINDOW_MS;
    if (isDupe && dupeCountRef.current >= DUPE_COUNT_MAX) {
      setValidationErr('Same as your last message — wait a bit before repeating.');
      setCooldownUntil(Date.now() + 3000); // small penalty
      return;
    }
    // Burst-rate guard: RATE_BURST messages in RATE_WINDOW_MS → cooldown.
    const cutoff = Date.now() - RATE_WINDOW_MS;
    recentSendsRef.current = recentSendsRef.current.filter(t => t > cutoff);
    if (recentSendsRef.current.length >= RATE_BURST) {
      setValidationErr(`Slow down — you're sending too fast.`);
      setCooldownUntil(Date.now() + RATE_PENALTY_MS);
      return;
    }

    // When replying, prefix the message with a quoted snippet of the target
    // so other users see the context inline. Format: `↪ @user: snippet\n…`
    // STRIP any existing reply prefix from the target's text so we quote
    // only the actual body, not a chain of nested quotes (the source of
    // "what gay bro bro what what" accumulation bug).
    let finalText = text;
    if (replyTo) {
      const cleanOriginal = (replyTo.text || '').replace(/^↪ @[A-Za-z0-9_]{2,32}: .{0,160}?\n/, '');
      const snippet = cleanOriginal.slice(0, 80);
      finalText = `↪ @${replyTo.name}: ${snippet}\n${text}`;
    }
    onSend(finalText);
    // Track consecutive-dupe streak: same text within window → bump count,
    // anything else → reset to 1 (this send is the new "1st").
    if (isDupe) {
      dupeCountRef.current += 1;
    } else {
      dupeCountRef.current = 1;
    }
    lastSentTextRef.current = text;
    lastSentAtRef.current   = Date.now();
    recentSendsRef.current.push(Date.now());
    setDraft('');
    setValidationErr('');
    setEmojiOpen(false);
    setMentionOpen(false);
    setReplyTo(null);
    setCooldownUntil(Date.now() + 1500); // 1.5 second baseline
    // CRITICAL: force-scroll to bottom after sending, even if user had
    // scrolled up to find a message to reply to. Without this, the user
    // scrolls up → clicks Reply → types → sends → chat stays scrolled
    // up so they think their reply "didn't appear" or "went to the
    // bottom hidden". scheduleBottomSnaps runs a few RAFs to catch the
    // optimistic msg + first server echo so the scroll always lands.
    stickToBottom.current = true;
    scheduleBottomSnaps();
  };

  // Clear validation message as user types (gives them a fresh canvas).
  React.useEffect(() => {
    if (!validationErr) return;
    const t = setTimeout(() => setValidationErr(''), 3000);
    return () => clearTimeout(t);
  }, [validationErr, draft]);

  const insertEmoji = (emoji) => {
    setDraft(d => d + emoji);
    inputRef.current?.focus();
  };

  // ===== @mention auto-complete =====
  const [mentionOpen, setMentionOpen] = React.useState(false);
  const [mentionQuery, setMentionQuery] = React.useState('');
  const [mentionIdx, setMentionIdx] = React.useState(0);

  // Recent unique senders from the visible chat history. We include the
  // server avatarUrl (Roblox headshot) and username so the @mention dropdown
  // can render the real Roblox picture instead of the boring 2-letter fallback.
  const candidates = React.useMemo(() => {
    const seen = new Set(); const out = [];
    for (let i = messages.length - 1; i >= 0 && out.length < 20; i--) {
      const m = messages[i];
      if (m.type === 'user' && m.name && !seen.has(m.name)) {
        seen.add(m.name);
        out.push({
          name: m.name,
          color: m.color,
          av: m.av,
          avatarUrl: m.avatarUrl || '',
          username: m.username || m.name,
        });
      }
    }
    return out;
  }, [messages]);

  const mentionMatches = React.useMemo(() => {
    if (!mentionOpen) return [];
    const q = mentionQuery.toLowerCase();
    // Cap at 3 — keeps the popup compact; user narrows down by typing more
    // letters anyway. (6 was visually overwhelming on mobile.)
    return candidates.filter(c => c.name.toLowerCase().startsWith(q)).slice(0, 3);
  }, [candidates, mentionQuery, mentionOpen]);

  const onDraftChange = (val) => {
    setDraft(val);
    // Find an @token immediately before the cursor
    const caret = inputRef.current ? inputRef.current.selectionStart : val.length;
    const before = val.slice(0, caret);
    const m = before.match(/@([A-Za-z0-9_]*)$/);
    if (m) { setMentionOpen(true); setMentionQuery(m[1]); setMentionIdx(0); }
    else { setMentionOpen(false); }
  };

  const applyMention = (name) => {
    const caret = inputRef.current ? inputRef.current.selectionStart : draft.length;
    const before = draft.slice(0, caret).replace(/@[A-Za-z0-9_]*$/, `@${name} `);
    const after = draft.slice(caret);
    const next = before + after;
    setDraft(next);
    setMentionOpen(false);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const pos = before.length;
        inputRef.current.setSelectionRange(pos, pos);
        inputRef.current.focus();
      }
    });
  };

  const onKeyDown = (e) => {
    if (!mentionOpen || mentionMatches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, mentionMatches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      applyMention(mentionMatches[mentionIdx].name);
    } else if (e.key === 'Escape') {
      setMentionOpen(false);
    }
  };

  return (
    <aside className={`chat-side ${open ? 'is-open' : ''}`} aria-hidden={!open}>
      {/* Giveaway strip lives above the chat header — players can host item
          giveaways and other players join with a single click. Auto-scrolls
          if there are multiple active giveaways. */}
      <GiveawayStrip currentUser={currentUser} t={t} onOpenInventory={() => {
        // Dispatch a global event the App level catches to open inventory in
        // "create giveaway" mode.
        try { window.dispatchEvent(new CustomEvent('blox:openGiveawayCreator')); } catch (_) {}
      }}/>

      <div className="chat-head">
        <span className="ttl">
          {t.chat}
          <span className="online-pulse"><span className="dot"/>{online.toLocaleString()}</span>
        </span>
        <button className="modal-close chat-close" onClick={onClose} aria-label="close chat">
          <I name="close" size={16}/>
        </button>
      </div>
      <div className="chat-tabs">
        <button className={`chat-tab ${tab==='global'?'is-active':''}`} onClick={() => onTabChange('global')}>
          <I name="globe" size={11}/>{t.tabGlobal}
        </button>
        <button className={`chat-tab ${tab==='lang'?'is-active':''}`} onClick={() => onTabChange('lang')}>
          <img src={`assets/images/flag-${langInfo.flag}.webp`} className="lang-flag" alt={langInfo.code} loading="lazy"/>{langInfo.label}
        </button>
      </div>
      {pinned && (
        <div className="chat-pinned" title="Pinned by admin">
          <i className="fa-solid fa-thumbtack"/>
          <span>{pinned}</span>
          {/* Unpin button — admin-only. Hits the same chat-pin endpoint
              with an empty text payload (the server treats empty text as
              unpin → flips broadcast_active off). Fires a config-refresh
              event so the pinned strip vanishes instantly without
              waiting for the 60s site-config poll. */}
          {currentUser?.is_admin && (
            <button
              type="button"
              className="chat-pinned-unpin"
              title="Unpin message"
              onClick={async () => {
                try {
                  await apiFetch('/api/admin/chat-pin', { method: 'POST', body: { id: 0, text: '' } });
                  window.dispatchEvent(new CustomEvent('blox:config-refresh'));
                  window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'win', text: 'Unpinned' } }));
                } catch (_) {
                  window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'lose', text: 'Could not unpin' } }));
                }
              }}
            >
              <i className="fa-solid fa-xmark"/>
            </button>
          )}
        </div>
      )}
      <div className="chat-messages" ref={scrollRef} onScroll={onScroll}>
        {messages.map(m => (
          m.type === 'system' ? (
            <div className="chat-msg system" key={m.id}>
              {(() => {
                // Big win token from server: "__BIGWIN__|username|amount"
                if (m.text.startsWith('__BIGWIN__|')) {
                  const parts = m.text.split('|');
                  const who = parts[1] || 'someone';
                  const amt = Number(parts[2]) || 0;
                  return (
                    <div className="chat-bigwin">
                      <i className="fa-solid fa-trophy"/>
                      <span><b>@{who}</b> {tt('chatBigWonAmount', 'won {amount}', { amount: fmtVal(amt) })}</span>
                      <i className="fa-solid fa-gem gem-i"/>
                    </div>
                  );
                }
                // Detect tip note token: "...⁂note:the note text"
                const idx = m.text.indexOf('⁂note:');
                if (idx >= 0) {
                  const head = m.text.substring(0, idx).trim();
                  const note = m.text.substring(idx + 6).trim();
                  return (
                    <>
                      <div className="text">{head}</div>
                      <div className="tip-note-bubble">"{note}"</div>
                    </>
                  );
                }
                return <div className="text">{m.text}</div>;
              })()}
            </div>
          ) : (
            <div
              className={`chat-msg ${m.mine?'mine':''} ${isMention(m.text) ? 'is-mention' : ''}`}
              key={m.id}
              onContextMenu={(e) => openContextMenu(m, e)}
              onTouchStart={(e) => {
                if (!currentUser || !m.srvId) return;
                // Discord-style long-press → open reaction picker
                const target = m.srvId;
                const timer = setTimeout(() => {
                  if (navigator.vibrate) try { navigator.vibrate(30); } catch(_){}
                  setReactPickerFor(target);
                }, 450);
                const cancel = () => { clearTimeout(timer); document.removeEventListener('touchend', cancel); document.removeEventListener('touchmove', cancel); };
                document.addEventListener('touchend', cancel, { once: true });
                document.addEventListener('touchmove', cancel, { once: true });
              }}
            >
              <button
                type="button"
                className="av av-btn"
                style={!m.avatarUrl ? { background: COLOR_GRADIENTS[m.color] } : undefined}
                onClick={(e) => onUserClick && m.username && onUserClick(m.username, { x: e.clientX, y: e.clientY })}
                title={m.name}
              >
                {m.avatarUrl
                  ? <img src={m.avatarUrl} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : m.av}
              </button>
              <div className="body">
                <span
                  className={`name name--role-${m.is_admin ? 'admin' : (m.lvl >= 50 ? 'whale' : m.lvl >= 20 ? 'vet' : 'rookie')}`}
                  onClick={() => onUserClick && m.username && onUserClick(m.username)}
                  style={{cursor:'pointer'}}
                  title={`Level ${m.lvl}${m.is_admin ? ' · Admin' : (m.lvl >= 50 ? ' · Whale 🐳' : '')}`}
                >{m.name}</span>
                {/* Level rank emoji (🏅/🥈/🥉/⭐ etc.) — skipped for admins
                    because the MOD badge already signals their role and the
                    extra medal felt redundant per user request. */}
                {!m.is_admin && (
                  <span className="rank-emoji" title={`Level ${m.lvl}`}>{rankEmojiForLevel(m.lvl)}</span>
                )}
                <div className="text">{renderChatText(m.text, onUserClick)}</div>
                {/* Reaction counters under message — click to toggle your own.
                    "See who reacted" feature REMOVED per user request — chips
                    are pure counters now, no popup, no viewers button. */}
                {reactions[m.srvId] && reactions[m.srvId].length > 0 && (
                  <div className="msg-reactions">
                    {reactions[m.srvId].map(r => (
                      <button
                        key={r.emoji}
                        type="button"
                        className={`msg-react-chip ${r.mine ? 'is-mine' : ''}`}
                        onClick={() => currentUser && toggleReaction(m.srvId, r.emoji)}
                        title="Click to toggle your reaction"
                        disabled={!currentUser}
                      >
                        <ReactionEmoji emoji={r.emoji}/>
                        <span className="msg-react-count">{r.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* 3-dot button — same UX as right-click: opens the context menu */}
              {currentUser && m.srvId && (
                <button
                  type="button"
                  className="msg-menu-btn"
                  onClick={(e) => { e.stopPropagation(); openContextMenu(m, e); }}
                  aria-label="message menu"
                >
                  <i className="fa-solid fa-ellipsis-vertical"/>
                </button>
              )}
              {/* Full emoji picker is rendered via portal at top-level — see below the messages map. */}
            </div>
          )
        ))}
      </div>
      {/* Discord-style context menu — rendered via PORTAL to document.body so
          it escapes chat-side's overflow:hidden clipping and floats freely. */}
      {menuFor && (() => {
        const target = messages.find(mm => mm.srvId === menuFor);
        if (!target) return null;
        const MENU_W = 220;
        const MENU_H = 180;
        const isMobile = window.innerWidth <= 720;
        // Anchor the menu CLOSE to the click point on every device.
        // Horizontal: just left of the click on desktop, slightly off-center
        //   on mobile so the user's thumb doesn't cover the menu.
        // Vertical: prefer just BELOW the click (8px). If that would clip
        //   off-screen, flip ABOVE (8px above the click). No more huge gap.
        const left = isMobile
          ? Math.max(8, Math.min(window.innerWidth - MENU_W - 8, menuPos.x - MENU_W / 2))
          : Math.max(8, Math.min(window.innerWidth - MENU_W - 8, menuPos.x - 12));
        const spaceBelow = window.innerHeight - menuPos.y;
        const top = (spaceBelow >= MENU_H + 16)
          ? menuPos.y + 8                              // open below the click
          : Math.max(8, menuPos.y - MENU_H - 8);       // not enough room → open above
        const menu = (
          <>
            <div className="msg-ctx-backdrop" onClick={() => setMenuFor(null)} onContextMenu={(e) => { e.preventDefault(); setMenuFor(null); }}/>
            <div className="msg-ctx-menu" style={{ left, top }} onClick={(e) => e.stopPropagation()}>
              <div className="msg-ctx-quick">
                {QUICK_REACTIONS.map(e => (
                  <button
                    key={e}
                    type="button"
                    className="msg-ctx-quick-btn"
                    onClick={() => { toggleReaction(target.srvId, e); setMenuFor(null); }}
                    title={e}
                  >{e}</button>
                ))}
                <button
                  type="button"
                  className="msg-ctx-quick-btn add"
                  onClick={() => { setMenuFor(null); setReactPickerFor(target.srvId); setEmojiSearch(''); setEmojiTab('standard'); }}
                  title="More reactions"
                ><i className="fa-solid fa-plus"/></button>
              </div>
              <div className="msg-ctx-divider"/>
              <button
                type="button"
                className="msg-ctx-item"
                onClick={() => { setMenuFor(null); setReactPickerFor(target.srvId); setEmojiSearch(''); setEmojiTab('standard'); }}
              >
                <i className="fa-regular fa-face-smile"/>
                <span>Add Reaction</span>
                <i className="fa-solid fa-chevron-right ctx-arrow"/>
              </button>
              {/* Reply — quote the message in the input row */}
              <button
                type="button"
                className="msg-ctx-item"
                onClick={() => { setMenuFor(null); setReplyTo({ id: target.srvId, name: target.name, text: target.text }); inputRef.current?.focus(); }}
              >
                <i className="fa-solid fa-reply"/>
                <span>Reply</span>
              </button>
              {/* Pin — admins only, surfaces the message at the top for everyone.
                  Fires a `blox:config-refresh` event after success so the
                  pinning admin sees the pin INSTANTLY instead of waiting
                  for the 60s site-config poll. */}
              {currentUser?.is_admin && (
                <button
                  type="button"
                  className="msg-ctx-item"
                  onClick={async () => {
                    setMenuFor(null);
                    try {
                      await apiFetch('/api/admin/chat-pin', { method: 'POST', body: { id: target.srvId, text: target.text } });
                      window.dispatchEvent(new CustomEvent('blox:config-refresh'));
                      window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'win', text: '📌 Message pinned' } }));
                    } catch (e) {
                      window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'lose', text: 'Could not pin' } }));
                    }
                  }}
                >
                  <i className="fa-solid fa-thumbtack"/>
                  <span>Pin Message</span>
                </button>
              )}
              {/* MOD TOOLS — mods + admins only. Timeout the author or delete
                  the message. Mods can't act on staff (enforced server-side). */}
              {(currentUser?.is_mod || currentUser?.is_admin) && target.userId && target.userId !== currentUser?.id && (
                <>
                  <div className="msg-ctx-divider"/>
                  <div className="msg-ctx-label">MOD</div>
                  {[
                    { label: 'Timeout 10m', mins: 10 },
                    { label: 'Timeout 1h',  mins: 60 },
                    { label: 'Timeout 24h', mins: 1440 },
                  ].map(opt => (
                    <button
                      key={opt.mins}
                      type="button"
                      className="msg-ctx-item"
                      onClick={async () => {
                        setMenuFor(null);
                        try {
                          await apiFetch('/api/mod/action', { method: 'POST', body: { action: 'timeout', user_id: target.userId, minutes: opt.mins } });
                          window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'info', text: `⏳ @${target.name} timed out (${opt.label.replace('Timeout ','')})` } }));
                        } catch (e) {
                          window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'lose', text: e.message || 'Could not time out' } }));
                        }
                      }}
                    >
                      <i className="fa-solid fa-clock"/>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className="msg-ctx-item"
                    onClick={async () => {
                      setMenuFor(null);
                      try {
                        await apiFetch('/api/mod/action', { method: 'POST', body: { action: 'untimeout', user_id: target.userId } });
                        window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'info', text: `@${target.name} timeout lifted` } }));
                      } catch (e) {
                        window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'lose', text: e.message || 'Failed' } }));
                      }
                    }}
                  >
                    <i className="fa-solid fa-clock-rotate-left"/>
                    <span>Lift timeout</span>
                  </button>
                  <button
                    type="button"
                    className="msg-ctx-item danger"
                    onClick={async () => {
                      setMenuFor(null);
                      try {
                        await apiFetch('/api/mod/action', { method: 'POST', body: { action: 'delete_message', message_id: target.srvId } });
                        // App owns the chat list — ask it to drop this message.
                        window.dispatchEvent(new CustomEvent('blox:chat-remove', { detail: { srvId: target.srvId } }));
                      } catch (e) {
                        window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'lose', text: e.message || 'Could not delete' } }));
                      }
                    }}
                  >
                    <i className="fa-solid fa-trash"/>
                    <span>Delete message</span>
                  </button>
                </>
              )}
              <div className="msg-ctx-divider"/>
              <button
                type="button"
                className="msg-ctx-item danger"
                onClick={() => { setMenuFor(null); openReport(target); }}
              >
                <i className="fa-solid fa-flag"/>
                <span>Report Message</span>
              </button>
            </div>
          </>
        );
        return ReactDOM.createPortal(menu, document.body);
      })()}

      {/* Emoji picker — portal'd so it escapes chat overflow clipping.
          Anchored close to the click point on every device (same rules as
          the context menu so it appears right where the user is looking). */}
      {reactPickerFor && (() => {
        const target = messages.find(mm => mm.srvId === reactPickerFor);
        if (!target) return null;
        // Wider/taller so a chunk of the custom-emoji catalog is visible at
        // once instead of just one row — was 340×380 (≈14 emojis visible at
        // a time which made the user think the rest were missing).
        const PICKER_W = 380;
        const PICKER_H = 460;
        const isMobile = window.innerWidth <= 720;
        const left = isMobile
          ? Math.max(8, Math.min(window.innerWidth - PICKER_W - 8, menuPos.x - PICKER_W / 2))
          : Math.max(8, Math.min(window.innerWidth - PICKER_W - 8, menuPos.x - 12));
        const spaceBelow = window.innerHeight - menuPos.y;
        const top = (spaceBelow >= PICKER_H + 16)
          ? menuPos.y + 8
          : Math.max(8, menuPos.y - PICKER_H - 8);
        const picker = (
          <>
            <div className="msg-ctx-backdrop" onClick={() => setReactPickerFor(null)}/>
            <div className="msg-react-picker is-full" style={{ left, top }} onClick={(e) => e.stopPropagation()}>
              {/* Quick-reactions row — the 6 most common reactions are one
                  tap away at the top, no search needed. Mirrors Discord's
                  reaction picker default row. */}
              <div className="mrp-quick">
                {['👍','❤️','😂','😮','😢','🔥'].map(e => (
                  <button
                    key={e}
                    type="button"
                    className="mrp-quick-btn"
                    onClick={() => { toggleReaction(target.srvId, e); setReactPickerFor(null); }}
                  >{e}</button>
                ))}
              </div>
              <div className="mrp-search">
                <i className="fa-solid fa-magnifying-glass"/>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search emoji…"
                  value={emojiSearch}
                  onChange={(e) => setEmojiSearch(e.target.value)}
                />
                <button type="button" className="mrp-close" onClick={() => setReactPickerFor(null)} aria-label="close">
                  <i className="fa-solid fa-xmark"/>
                </button>
              </div>
              <div className="mrp-tabs">
                <button
                  type="button"
                  className={`mrp-tab ${emojiTab === 'standard' ? 'is-active' : ''}`}
                  onClick={() => setEmojiTab('standard')}
                >Standard</button>
                <button
                  type="button"
                  className={`mrp-tab ${emojiTab === 'custom' ? 'is-active' : ''}`}
                  onClick={() => setEmojiTab('custom')}
                >Custom</button>
              </div>
              <div className="mrp-grid">
                {emojiTab === 'standard' ? (
                  reactVisibleStandard.length === 0
                    ? <div className="mrp-empty">{emojiSearch ? `No emoji matches "${emojiSearch}"` : 'Nothing here.'}</div>
                    : reactVisibleStandard.map(it => (
                        <button
                          key={it.e}
                          type="button"
                          title={it.k.split(' ').slice(0, 3).join(', ')}
                          onClick={() => { toggleReaction(target.srvId, it.e); setReactPickerFor(null); }}
                        >{it.e}</button>
                      ))
                ) : (
                  customs.length === 0
                    ? <div className="mrp-empty">Custom emojis will appear here. Drop .webp files into <code>assets/emojis/</code>.</div>
                    : filteredCustoms.length === 0
                      ? <div className="mrp-empty">No custom emoji matches "{emojiSearch}"</div>
                      : filteredCustoms.map(c => (
                          <button
                            key={c.code}
                            type="button"
                            title={c.title || c.code}
                            className="mrp-custom-btn"
                            onClick={() => { toggleReaction(target.srvId, c.code); setReactPickerFor(null); }}
                          >
                            {/* Eager-load + no onError-hide — same reason as
                                the chat picker: silently dropping failed
                                images was hiding ~half the catalog. */}
                            <img src={`/assets/emojis/${c.file}`} alt={c.code}/>
                          </button>
                        ))
                )}
              </div>
              {/* Cat-nav row — only on Standard tab (custom set is small enough
                  to scroll without category jumps). Mirrors the chat picker
                  navbar with the same animated sliding pill. Skips the
                  "recent" cat because the quick-reactions row at the top
                  already serves that purpose. */}
              {emojiTab === 'standard' && !emojiSearch && (
                <div className="ep-catnav mrp-catnav">
                  {EMOJI_CATEGORIES.filter(c => c.id !== 'recent').map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      className={`ep-catbtn ${reactActiveCat === cat.id ? 'is-active' : ''}`}
                      onClick={() => setReactActiveCat(cat.id)}
                      title={cat.label}
                      disabled={!reactCatHasItems[cat.id]}
                    >
                      <i className={cat.icon}/>
                    </button>
                  ))}
                  <span
                    className="ep-catnav-pill"
                    style={{
                      '--cat-idx': EMOJI_CATEGORIES.filter(c => c.id !== 'recent').findIndex(c => c.id === reactActiveCat),
                      '--cat-count': EMOJI_CATEGORIES.filter(c => c.id !== 'recent').length,
                    }}
                  />
                </div>
              )}
            </div>
          </>
        );
        return ReactDOM.createPortal(picker, document.body);
      })()}

      {/* Report Message modal — PORTAL'd so it always centers on the viewport
          and never gets clipped by the chat-side overflow. Multi-step:
          step 1 (category) → step 2 (summary with Back+Submit) → step 3 (sent).
          Style matches the site (orange accents, no Discord red). */}
      {reportingMsg && ReactDOM.createPortal((
        <div className="modal-overlay report-overlay" onClick={closeReport} style={{zIndex: 9000}}>
          <div className="modal report-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: 480}}>
            {reportStep === 'category' && (
              <>
                <div className="modal-head">
                  <div>
                    <h3>Report message</h3>
                    <div className="report-sub">Please select the option that best describes the problem.</div>
                  </div>
                  <button className="modal-close" onClick={closeReport} aria-label="close"><I name="close" size={18}/></button>
                </div>
                <div className="modal-body">
                  <div className="report-section-label">Selected Message</div>
                  <div className="report-msg-preview">
                    {reportingMsg.avatarUrl
                      ? <img src={reportingMsg.avatarUrl} alt="" className="report-msg-av"/>
                      : <div className="report-msg-av report-msg-av-fallback">{reportingMsg.av || '??'}</div>}
                    <div className="report-msg-body">
                      <div className="report-msg-name">{reportingMsg.name}</div>
                      <div className="report-msg-text">{reportingMsg.text}</div>
                    </div>
                  </div>
                  <div className="report-options">
                    {[
                      // Discord-style minimal — generic categories, no
                      // platform-specific stuff ("fake giveaway" etc).
                      { code: 'spam',  label: 'Spam' },
                      { code: 'abuse', label: 'Harassment or bullying' },
                      { code: 'hate',  label: 'Hate speech' },
                      { code: 'other', label: 'Something else' },
                    ].map(opt => (
                      <button
                        key={opt.code}
                        type="button"
                        className="report-option"
                        onClick={() => { setReportReason(opt); setReportStep('summary'); }}
                      >
                        <span>{opt.label}</span>
                        <i className="fa-solid fa-chevron-right"/>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {reportStep === 'summary' && reportReason && (
              <>
                <div className="modal-head">
                  <div>
                    <h3>Report summary</h3>
                    <div className="report-sub">Review your report before submitting. By submitting you confirm it's truthful and made in good faith.</div>
                  </div>
                  <button className="modal-close" onClick={closeReport} aria-label="close"><I name="close" size={18}/></button>
                </div>
                <div className="modal-body">
                  <div className="report-section-label">Selected Message</div>
                  <div className="report-msg-preview">
                    {reportingMsg.avatarUrl
                      ? <img src={reportingMsg.avatarUrl} alt="" className="report-msg-av"/>
                      : <div className="report-msg-av report-msg-av-fallback">{reportingMsg.av || '??'}</div>}
                    <div className="report-msg-body">
                      <div className="report-msg-name">{reportingMsg.name}</div>
                      <div className="report-msg-text">{reportingMsg.text}</div>
                    </div>
                  </div>
                  <div className="report-section-label" style={{marginTop: 16}}>Report Category</div>
                  <div className="report-cat-row">
                    <span className="report-cat-dot"/>
                    {reportReason.label}
                  </div>
                  {/* "Something else" — quick-select suggestion chips + free-text */}
                  {reportReason.code === 'other' && (
                    <>
                      <div className="report-section-label" style={{marginTop: 16}}>Quick suggestions</div>
                      <div className="report-suggest-row">
                        {[
                          'Asking for personal info',
                          'Tries to trade outside the site',
                          'Repeated off-topic messages',
                          'Begging / asking for free items',
                          'Confusing or misleading offer',
                          'Inappropriate username',
                          'Suspicious link',
                        ].map(s => (
                          <button
                            key={s}
                            type="button"
                            className="report-suggest-chip"
                            onClick={() => setReportOther(s)}
                          >{s}</button>
                        ))}
                      </div>
                      <div className="report-section-label" style={{marginTop: 14}}>Please describe</div>
                      <textarea
                        className="report-other-input"
                        placeholder="Tell us what happened (min 5 chars)…"
                        value={reportOther}
                        onChange={(e) => setReportOther(e.target.value.slice(0, 500))}
                        rows={4}
                        autoFocus
                      />
                      <div className="report-other-count">{reportOther.length}/500</div>
                    </>
                  )}
                  <div className="report-actions">
                    <button type="button" className="report-btn-back" onClick={() => setReportStep('category')}>
                      Back
                    </button>
                    <button
                      type="button"
                      className="report-btn-submit"
                      onClick={submitReport}
                      disabled={reportReason.code === 'other' && reportOther.trim().length < 5}
                    >
                      Submit Report
                    </button>
                  </div>
                </div>
              </>
            )}
            {reportStep === 'sent' && (
              <div className="report-sent">
                <div className="report-sent-icon"><i className="fa-solid fa-check"/></div>
                <h3>Report submitted</h3>
                <p>Thanks — our admins will review it.</p>
              </div>
            )}
          </div>
        </div>
      ), document.body)}

      {/* Reaction detail modal REMOVED per user request — the "who reacted"
          popup is gone; chips are pure counters. */}
      {/* New messages pill (when scrolled up) */}
      {newCount > 0 && (
        <button className="chat-newmsg-pill" onClick={() => scrollToBottom(true)}>
          <i className="fa-solid fa-arrow-down"/> {newCount} new {newCount === 1 ? 'message' : 'messages'}
        </button>
      )}
      <form className="chat-input-row" onSubmit={submit}>
        {replyTo && (
          <div className="chat-reply-preview">
            <i className="fa-solid fa-reply"/>
            <span className="chat-reply-name">@{replyTo.name}</span>
            <span className="chat-reply-snippet">{(replyTo.text || '').slice(0, 60)}{(replyTo.text || '').length > 60 ? '…' : ''}</span>
            <button type="button" className="chat-reply-x" onClick={() => setReplyTo(null)} aria-label="cancel reply">
              <i className="fa-solid fa-xmark"/>
            </button>
          </div>
        )}
        {emojiOpen && <EmojiPicker onPick={insertEmoji} onClose={() => setEmojiOpen(false)} anchorRect={emojiAnchorRect}/>}
        {mentionOpen && mentionMatches.length > 0 && (
          <div className="mention-pop">
            {mentionMatches.map((m, i) => (
              <button
                key={m.name}
                type="button"
                className={`mention-item ${i === mentionIdx ? 'is-active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); applyMention(m.name); }}
                onMouseEnter={() => setMentionIdx(i)}
              >
                <span className="mention-av" style={!m.avatarUrl ? {background: COLOR_GRADIENTS[m.color]} : undefined}>
                  {m.avatarUrl
                    ? <img src={m.avatarUrl} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                    : m.av}
                </span>
                <span className="mention-name">@{m.name}</span>
              </button>
            ))}
          </div>
        )}
        {/* Validation chip — surfaced above the input when a send is
            blocked. Auto-fades after 3s. Discord-style red inline hint. */}
        {validationErr && (
          <div className="chat-validation-chip">
            <i className="fa-solid fa-circle-exclamation"/> {validationErr}
          </div>
        )}
        {/* Single field wrapping the input + emoji button — emoji icon sits
            INSIDE the field on the right. Send button is below as full-width pill. */}
        <div className="chat-input-field">
          <input
            ref={inputRef}
            className="chat-input"
            placeholder={t.sendPlaceholder || 'Type a message…'}
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={onKeyDown}
            maxLength={MAX_LEN}
          />
          <button
            type="button"
            className={`chat-emoji ${emojiOpen?'is-open':''}`}
            onClick={(e) => {
              // Capture the button's rect so the picker can position itself
              // ABOVE/BESIDE this exact spot (portal'd to body, same
              // approach as the reaction picker).
              const r = e.currentTarget.getBoundingClientRect();
              setEmojiAnchorRect({ left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
              setEmojiOpen(o => !o);
            }}
            aria-label="emoji"
          >
            <I name="smile" size={16}/>
          </button>
        </div>
        {/* Tiny char/emoji counter sits in its OWN row between the input
            and the send button — never overlaps typed text and doesn't
            steal space from the input. Only renders when approaching a
            limit. .over paints it red when actually over. */}
        {(draft.length > MAX_LEN * 0.75 || emojiCount > MAX_EMOJIS * 0.6) && (
          <div className={`chat-input-counter-row`}>
            <span className={`chat-input-counter ${draft.length > MAX_LEN || emojiCount > MAX_EMOJIS ? 'over' : ''}`}>
              <span className="cic-segment">{draft.length}/{MAX_LEN} chars</span>
              {emojiCount > 0 && (
                <span className="cic-segment">{emojiCount}/{MAX_EMOJIS} emoji</span>
              )}
            </span>
          </div>
        )}
        <button
          type="submit"
          className={`chat-send-btn ${onCooldown ? 'is-cooldown' : ''}`}
          disabled={!draft.trim() || onCooldown || emojiCount > MAX_EMOJIS}
        >
          {onCooldown ? `Wait ${(remainingMs / 1000).toFixed(1)}s` : 'Send'}
        </button>
      </form>
    </aside>
  );
}

// ============================================
// MATCH MODAL — pick items from inventory to match a flip's value
// ============================================
function MatchModal({ flip, userInventory, onClose, onConfirm }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlay = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);

  const [selected, setSelected] = React.useState(new Set());
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState('high');

  const target = flipValue(flip.items || []);
  const rangePct = typeof flip.matchRange === 'number' ? flip.matchRange : 0.05;
  const minVal = target * (1 - rangePct);
  const maxVal = target * (1 + rangePct);

  const visible = React.useMemo(() => {
    // Hide items locked in another flip — they can't be used here.
    let arr = userInventory.filter(it => !it.lockedFlipId);
    arr = arr.filter(it => !search || it.name.toLowerCase().includes(search.toLowerCase()));
    arr = [...arr].sort((a, b) => sort === 'high' ? b.price - a.price : a.price - b.price);
    return arr;
  }, [userInventory, search, sort]);

  const selectedItems = userInventory.filter(it => selected.has(it.id));
  const selectedTotal = flipValue(selectedItems);
  const inRange = selectedTotal >= minVal && selectedTotal <= maxVal;
  const tooLow = selectedTotal < minVal;
  const tooHigh = selectedTotal > maxVal;
  // Progress bar represents 0 → maxVal so MIN / TARGET / MAX markers align
  // with the actual range. Previous code hard-coded MIN at 90.5% (only
  // correct for a ±5% range), which made the marker visually lie when the
  // flip used a wider range like ±50%.
  const barMax = maxVal || 1;
  const fillPct   = Math.min(100, (selectedTotal / barMax) * 100);
  const minPct    = (minVal / barMax) * 100;
  const targetPct = (target  / barMax) * 100;

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const dragSel = useDragMultiSelect(toggle, (id) => selected.has(id));

  // Auto-pick — three-pass to cover minVal reliably:
  //   Pass 1: greedy high-value first while staying ≤ maxVal (clean match)
  //   Pass 2: still short → add ANY remaining item that fits within maxVal
  //   Pass 3: still short → smallest-first, accept overshoot (last resort)
  // CRITICAL: only operates on UNLOCKED items. Previously included locked
  // items in the sort, causing auto-pick to "select" items that the backend
  // would reject as unavailable — leaving the join request silently short.
  const MAX_AUTO_ITEMS = 50;
  const autoPick = () => {
    const available = userInventory.filter(it => !it.lockedFlipId);
    const sortedDesc = [...available].sort((a, b) => b.price - a.price);
    const sortedAsc  = [...available].sort((a, b) => a.price - b.price);
    const pickedIds = new Set();
    let acc = 0;
    // Pass 1: high-value first, stay ≤ maxVal (clean match)
    for (const it of sortedDesc) {
      if (pickedIds.size >= MAX_AUTO_ITEMS) break;
      if (acc >= minVal) break;
      if (acc + it.price > maxVal) continue;
      pickedIds.add(it.id);
      acc += it.price;
    }
    // Pass 2: still short → revisit smallest-first to fill gaps within range
    if (acc < minVal) {
      for (const it of sortedAsc) {
        if (pickedIds.size >= MAX_AUTO_ITEMS) break;
        if (pickedIds.has(it.id)) continue;
        if (acc + it.price > maxVal) continue;
        pickedIds.add(it.id);
        acc += it.price;
        if (acc >= minVal) break;
      }
    }
    // Pass 3: still short → grab anything remaining (overshoot allowed)
    if (acc < minVal) {
      for (const it of sortedAsc) {
        if (pickedIds.size >= MAX_AUTO_ITEMS) break;
        if (pickedIds.has(it.id)) continue;
        pickedIds.add(it.id);
        acc += it.price;
        if (acc >= minVal) break;
      }
    }
    setSelected(pickedIds);
  };

  const submit = () => animateThen(() => onConfirm(selectedItems));

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlay}>
      <div className="modal modal--wide" ref={sheetRef}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>{tt('matchPlayersFlip', "Match {name}'s flip", { name: flip.c.name })}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="inv-body">
          <div className="match-target">
            <div className="mt-side">
              <div className="mt-label">{tt('youNeedToMatch', 'You need to match')}</div>
              <div className="mt-value"><i className="fa-solid fa-gem gem-i"/>{fmtVal(target)}</div>
            </div>
            <div className="mt-side">
              <div className="mt-label">{tt('rangeLabel', 'Range')} (±{Math.round(rangePct * 100)}%)</div>
              <div className="mt-range">{fmtVal(minVal)} – {fmtVal(maxVal)}</div>
            </div>
            <div className="mt-side">
              <div className="mt-label">{tt('yourPick', 'Your pick')}</div>
              <div className={`mt-current ${inRange ? 'in-range' : tooLow ? 'low' : 'high'}`}>
                <i className="fa-solid fa-gem gem-i"/>{fmtVal(selectedTotal)}
              </div>
            </div>
          </div>

          {/* TRUE stake-weighted win chance — exactly value / pot, no
              artificial flooring. Each side's % is simply how much of the
              total pot they put in (what the user actually entered). The
              winner is NEVER shown as 100% unless the other side is empty. */}
          {selectedTotal > 0 && (() => {
            const pot = target + selectedTotal;
            const cPct = pot > 0 ? (target / pot) * 100 : 50;
            const yPct = 100 - cPct;
            const fmtPct = (n) => (Math.round(n * 10) / 10).toFixed(1);
            return (
              <div className="mt-odds">
                <div className="mt-odds-row">
                  <span>{flip.c.name} ({fmtVal(target)})</span>
                  <span><b>{fmtPct(cPct)}%</b> win chance</span>
                </div>
                <div className="mt-odds-row mt-odds-you">
                  <span>You ({fmtVal(selectedTotal)})</span>
                  <span><b>{fmtPct(yPct)}%</b> win chance</span>
                </div>
              </div>
            );
          })()}

          {/* Progress bar with min/max markers. Marker positions scale with
              the actual match range — previously hard-coded for ±5% so
              wider-range flips showed MIN in the wrong place and made the
              picker look broken. */}
          <div className="mp-bar">
            <div className="mp-track">
              <div className={`mp-fill ${inRange ? 'in-range' : tooHigh ? 'high' : ''}`} style={{ width: `${fillPct}%` }}/>
              {/* Only the MIN marker — the "target" marker was removed per
                  user. As long as your pick is at/above min (and ≤ max) the
                  match is valid; the extra target line was just clutter. */}
              <div className="mp-marker" style={{ left: `${minPct}%` }}>
                <span className="mp-marker-line"/>
                <span className="mp-marker-lbl">min</span>
              </div>
            </div>
          </div>

          <div className="inv-controls">
            <div className="inv-search">
              <I name="search" size={13} color="#838383"/>
              <input placeholder="Search items.." value={search} onChange={(e) => setSearch(e.target.value)}/>
            </div>
            <CustomSelect
              value={sort}
              onChange={setSort}
              ariaLabel="sort"
              options={[
                { value: 'high', label: 'High – Low' },
                { value: 'low',  label: 'Low – High' },
              ]}
            />
            <button className="inv-select-all" onClick={autoPick} style={{height: 42}}>
              <I name="bolt" size={12}/> Auto pick
            </button>
          </div>

          {userInventory.length === 0 ? (
            <div className="inv-empty" style={{padding: '36px 16px'}}>
              <h2 style={{fontSize: 18}}>{tt('noItemsToMatch', 'No items to match with')}</h2>
              <p>{tt('depositFirstJoin', 'Deposit items first via the wallet to join flips.')}</p>
            </div>
          ) : (
            <div className="inv-grid" style={{maxHeight: '32vh'}} {...dragSel.gridProps}>
              {visible.map(it => (
                <button
                  key={it.id}
                  type="button"
                  className={`inv-item ${selected.has(it.id) ? 'selected' : ''}`}
                  onClick={() => { if (!dragSel.shouldSuppressClick(it.id)) toggle(it.id); }}
                  {...dragSel.itemProps(it.id)}
                >
                  <FlipItem item={it} size={56} noTooltip/>
                  <div className="inv-item-name" title={it.name}>{it.name}</div>
                  <div className="inv-item-price"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                </button>
              ))}
            </div>
          )}

          <button className="modal-cta" disabled={!inRange} onClick={submit}>
            {tooLow
              ? tt('needMoreValue', 'Need {amount} more value', { amount: fmtVal(minVal - selectedTotal) })
              : tooHigh
              ? tt('removeValueExcess', 'Remove {amount} of value', { amount: fmtVal(selectedTotal - maxVal) })
              : selected.size === 0
              ? tt('pickItemsToMatch', 'Pick items to match')
              : selected.size === 1
              ? tt('matchFlipBtnOne', 'Match flip · 1 item · {amount}', { amount: fmtVal(selectedTotal) })
              : tt('matchFlipBtn', 'Match flip · {n} items · {amount}', { n: selected.size, amount: fmtVal(selectedTotal) })}
          </button>
          {/* t intentionally omitted — MatchModal doesn't receive it as a
              prop, and FairnessLink uses the global tt() helper anyway. */}
          <FairnessLink/>
        </div>
      </div>
    </div>
  );
}

// ============================================
// FLIP DETAIL MODAL — spectator view, optionally join from inside
// ============================================
function FlipDetailModal({ flip, onClose, onJoin, currentUser }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  // Hash-click → opens the fairness data modal (game id, server seed hash,
  // joiner/creator seeds). Hash itself is the trigger so no extra pill at
  // the bottom is needed.
  const [showFlipFairness, setShowFlipFairness] = React.useState(false);
  // For resolved/team flips: show all items in the pot (creator's + opponent's)
  const creatorItems = flip.items || [];
  const opponentItems = flip.oItems || [];
  const allItems = flip.state === 'open' ? creatorItems : [...creatorItems, ...opponentItems];
  const total = flipValue(allItems);
  const oppSide = flip.side === 'heads' ? 'tails' : 'heads';
  const joinable = flip.state === 'open' && !flip.mine && (flip.c?.id !== currentUser?.id);
  const resolved = flip.state === 'resolved';
  const creatorWon = resolved && flip.winnerId && (flip.winnerId === flip.c?.id);
  const opponentWon = resolved && flip.winnerId && flip.o && (flip.winnerId === flip.o.id);
  const youWon = resolved && currentUser && flip.winnerId === currentUser.id;
  const youParticipated = currentUser && (
    flip.c?.id === currentUser.id ||
    flip.o?.id === currentUser.id ||
    (flip.participants || []).some(p => Number(p.user_id) === Number(currentUser.id))
  );

  // Resolved-flip REPLAY — the user wants the coin animation back on
  // View click. Shortened from 4.7s → 3.4s so it doesn't feel like a
  // loading spinner; lands on the actual winning face.
  const winnerResultSide = resolved && flip.winnerId
    ? (flip.winnerId === flip.c?.id ? flip.side : (flip.side === 'heads' ? 'tails' : 'heads'))
    : null;
  const [replayLanded, setReplayLanded] = React.useState(false);
  React.useEffect(() => {
    // No animation for open / rolling / unknown-winner — flip straight
    // to landed so the coin slot renders something stable.
    if (!resolved || !winnerResultSide) { setReplayLanded(true); return; }
    // Restart from spinning on mount, then land. The class swap to
    // `is-landed` freezes the rotateY at the final value so the coin
    // doesn't "jump back" to neutral mid-frame.
    setReplayLanded(false);
    const t = setTimeout(() => setReplayLanded(true), 3400);
    return () => clearTimeout(t);
  }, [resolved, winnerResultSide]);

  return (
    <div className={`modal-overlay ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal modal--wide dice-match-modal-v2">
        {/* Branded head — same SVG logo treatment as FlipModal/DiceResolvedModal */}
        <div className="modal-head fm-brand-head">
          <div className="fm-brand fm-brand--full">
            <span className="brandmark brandmark--modal">
              <span className="brandmark-icon" aria-hidden="true"/>
              <span className="brandmark-text">BLOX<span className="brandmark-dot">.</span><span className="brandmark-hot">HOT</span></span>
            </span>
          </div>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="dm2-body">
          {/* Coinflip eyebrow — matches DiceResolvedModal's "DICE BATTLE · N ROUNDS" */}
          <div className="dm2-eyebrow">
            <i className="fa-solid fa-coins"/> {tt('coinflipBattle', 'Coinflip')} · {tt('coinflipOneShot', '1v1')}
          </div>

          {/* Players row — exact same dm2-players layout as the dice modal.
              Big centered avatars stacked over @username, crown badge on
              winner's avatar (top-left corner), "VS · FINAL" pill in the
              middle. The pick badge (picked H / picked T) sits under the
              username so the visual is identical to dice's score row. */}
          <div className="dm2-players">
            <div className={`dm2-player ${creatorWon ? 'is-winner' : (resolved && opponentWon ? 'is-loser' : '')}`}>
              <div className="dm2-avatar">
                {flip.c.avatarUrl
                  ? <img src={flip.c.avatarUrl} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : <div className="dm2-avatar-fallback">{flip.c.av}</div>}
                {creatorWon && <span className="dm2-crown"><i className="fa-solid fa-crown"/></span>}
              </div>
              <div className="dm2-name">@{flip.c.username || flip.c.name}</div>
              {/* Picked-side chip — replaces the old "picked heads" text.
                  Shows the coin face the player chose with a label, so
                  spectators instantly see who's on which side. */}
              <div className="dm2-pick-chip">
                <span className={`coin-mini ${flip.side === 'heads' ? 'h' : 't'}`}/>
                <span>{tt(flip.side === 'heads' ? 'sideHeads' : 'sideTails', flip.side).toUpperCase()}</span>
              </div>
            </div>

            {/* VS block — coin animation plays during BOTH rolling and
                resolved states so the modal never looks "blank" while
                waiting for the server to commit a result. Rolling = loop
                spin (no committed side yet). Resolved = replay landing
                on the actual winning face. Open = static VS text. */}
            <div className={`dm2-vs-block ${(resolved || flip.state === 'rolling') ? 'dm2-vs-block--coin' : ''}`}>
              {resolved && winnerResultSide ? (
                <BloxCoin
                  playing={true}
                  result={winnerResultSide}
                  landed={replayLanded}
                  size={140}
                />
              ) : flip.state === 'rolling' ? (
                <div className="blox-coin blox-coin--looping" style={{ width: 140, height: 140 }}>
                  <div className="blox-coin-face blox-coin-face--h"/>
                  <div className="blox-coin-face blox-coin-face--t"/>
                </div>
              ) : (
                <div className="dm2-vs-text">VS</div>
              )}
            </div>

            {flip.o ? (
              <div className={`dm2-player ${opponentWon ? 'is-winner' : (resolved && creatorWon ? 'is-loser' : '')}`}>
                <div className="dm2-avatar">
                  {flip.o.avatarUrl
                    ? <img src={flip.o.avatarUrl} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                    : <div className="dm2-avatar-fallback">{flip.o.av}</div>}
                  {opponentWon && <span className="dm2-crown"><i className="fa-solid fa-crown"/></span>}
                </div>
                <div className="dm2-name">@{flip.o.username || flip.o.name}</div>
                <div className="dm2-pick-chip">
                  <span className={`coin-mini ${oppSide === 'heads' ? 'h' : 't'}`}/>
                  <span>{tt(oppSide === 'heads' ? 'sideHeads' : 'sideTails', oppSide).toUpperCase()}</span>
                </div>
              </div>
            ) : (
              <div className="dm2-player">
                <div className="dm2-avatar dm2-avatar--empty">
                  <i className="fa-solid fa-bolt"/>
                </div>
                <div className="dm2-name" style={{color:'var(--g-500)'}}>{tt('waitingPlayer', 'Waiting…')}</div>
                <div className="dm2-pick-chip dm2-pick-chip--ghost">
                  <span className={`coin-mini ${oppSide === 'heads' ? 'h' : 't'}`}/>
                  <span>{tt(oppSide === 'heads' ? 'sideHeads' : 'sideTails', oppSide).toUpperCase()}</span>
                </div>
              </div>
            )}
          </div>

          {/* Match hash — clickable, opens the fairness data modal so the
              user can copy the serverSeedHash / game id / verify the
              outcome themselves. The text shown is the actual server seed
              hash (or flip id if hash not exposed yet) — long enough to
              be useful as a verification fingerprint, not just a padded
              short id. */}
          {(() => {
            const fairnessHash = flip.serverSeedHash || flip.srvId || flip.id || '';
            const display = String(fairnessHash).slice(0, 24) + (String(fairnessHash).length > 24 ? '…' : '');
            return (
              <button
                type="button"
                className="dm2-hash dm2-hash--link"
                onClick={() => setShowFlipFairness(true)}
                title={tt('viewProof', 'View proof / fairness data')}
              >
                <i className="fa-solid fa-hashtag"/>
                <code>{display}</code>
              </button>
            );
          })()}

          {/* Stakes row — two pills with the % share of the pot each
              player STAKED at entry time, NOT a binary 100/0 on resolve.
              Pot share is the right framing because that's what the user
              put in. Winning side keeps the green tint as the "result"
              indicator. */}
          <div className="dm2-stakes">
            {(() => {
              const creatorVal = flipValue(creatorItems);
              const opponentVal = flipValue(opponentItems);
              const totalPot = creatorVal + opponentVal;
              const creatorPct  = totalPot > 0 ? (creatorVal  / totalPot) * 100 : (creatorVal  > 0 ? 100 : 0);
              const opponentPct = totalPot > 0 ? (opponentVal / totalPot) * 100 : 0;
              const creatorTone = resolved
                ? (creatorWon ? 'win' : 'loss')
                : 'neutral';
              const opponentTone = resolved
                ? (opponentWon ? 'win' : 'loss')
                : 'neutral';
              return (
                <>
                  <div className={`dm2-stake-pill dm2-stake-pill--${creatorTone}`}>
                    <div className="dm2-stake-pct">{creatorPct.toFixed(2)}%</div>
                    <div className="dm2-stake-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(creatorVal)}</div>
                  </div>
                  <div className={`dm2-stake-pill dm2-stake-pill--${opponentTone}`}>
                    <div className="dm2-stake-pct">{opponentPct.toFixed(2)}%</div>
                    <div className="dm2-stake-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(opponentVal)}</div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Items — two side-by-side columns, BloxyBet style. Each column
              is tagged with the player's coin side (heads/tails). Resolved
              flips: winning column tints green, losing tints red. */}
          {(() => {
            const creatorSide  = flip.side;
            const opponentSide = creatorSide === 'heads' ? 'tails' : 'heads';
            const cTone = resolved ? (creatorWon  ? 'win' : 'loss') : 'neutral';
            const oTone = resolved ? (opponentWon ? 'win' : 'loss') : 'neutral';
            return (
              <div className="dm2-items-cols">
                <div className={`dm2-items-col dm2-items-col--${cTone}`}>
                  <div className="dm2-items-col-list">
                    {creatorItems.length === 0
                      ? <div className="dm2-items-empty">{tt('noItems', 'No items')}</div>
                      : creatorItems.map((it, i) => (
                          <div key={i} className="dm2-items-row-item">
                            <FlipItem item={it} size={42} noTooltip/>
                            <div className="dm2-items-row-name">{it.name}</div>
                            <div className="dm2-items-row-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                          </div>
                        ))}
                  </div>
                </div>
                <div className={`dm2-items-col dm2-items-col--${oTone}`}>
                  <div className="dm2-items-col-list">
                    {opponentItems.length === 0
                      ? <div className="dm2-items-empty">{tt('noItems', 'Waiting…')}</div>
                      : opponentItems.map((it, i) => (
                          <div key={i} className="dm2-items-row-item">
                            <FlipItem item={it} size={42} noTooltip/>
                            <div className="dm2-items-row-name">{it.name}</div>
                            <div className="dm2-items-row-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                          </div>
                        ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* "Flip is rolling — coin is in the air" pink banner removed —
              the actual coin animation now plays in the VS slot above,
              which says the same thing visually. Cleaner modal. */}

          {flip.state === 'cancelled' && (
            <div className="dm2-cancelled-note">
              <i className="fa-solid fa-ban"/>
              {tt('flipCancelled', 'Flip was cancelled — items returned')}
            </div>
          )}

          {resolved && youParticipated && total >= 50 && (
            <ShareWinButtons
              youName={currentUser?.username || 'you'}
              pot={total}
              stake={flipValue(flip.items || [])}
              items={allItems}
              result={flip.result || (youWon ? flip.side : (flip.side === 'heads' ? 'tails' : 'heads'))}
              won={youWon}
            />
          )}

          {joinable ? (
            <button className="modal-cta" onClick={() => animateThen(() => onJoin(flip))}>
              {tt('joinThisFlip', 'Join this flip · {amount}', { amount: fmtVal(total) })}
            </button>
          ) : flip.mine && !resolved ? (
            <div className="fd-mine">
              <I name="check" size={14} color="#10B981"/> {tt('flipMineWaiting', 'This is your flip — waiting for an opponent')}
            </div>
          ) : !resolved ? (
            <button className="modal-cta" disabled style={{cursor:'default'}}>
              {tt('flipWatching', 'Watching…')}
            </button>
          ) : null}

          {/* Provably-fair link removed — the # hash at the top of the
              modal IS now the trigger (click it for the data). One entry
              point instead of two saves vertical real-estate and matches
              the BloxyBet pattern the user pointed at. */}
        </div>
      </div>
      {showFlipFairness && (
        <FlipFairnessDataModal flip={flip} onClose={() => setShowFlipFairness(false)}/>
      )}
    </div>
  );
}

// ============================================
// FlipFairnessDataModal — small dialog that shows the seeds/hash/game-id
// for a flip so the user can re-compute the result themselves. Reached
// by clicking the # hash in the flip view. Mirrors the data modal that
// already exists inside FlipModal during play.
// ============================================
function FlipFairnessDataModal({ flip, onClose }) {
  const [copied, setCopied] = React.useState(null);
  const copy = (val, k) => {
    try { navigator.clipboard.writeText(String(val || '')); setCopied(k); setTimeout(() => setCopied(null), 1200); } catch (_) {}
  };
  const rows = [
    { k: 'gameId',        label: 'Game Id',           value: flip.serverSeedHash || flip.srvId || flip.id || '—' },
    { k: 'serverHash',    label: 'Server Seed Hash',  value: flip.serverSeedHash || '—' },
    { k: 'serverSeed',    label: 'Unhashed Server Seed', value: flip.serverSeed || 'Revealed after the flip resolves' },
    { k: 'creatorSeed',   label: 'Creator Seed',      value: flip.creatorSeed || '—' },
    { k: 'joinerSeed',    label: 'Joiner Seed',       value: flip.joinerSeed || '—' },
    { k: 'nonce',         label: 'Nonce',             value: flip.nonce || '—' },
    { k: 'result',        label: 'Result',            value: flip.result || (flip.state === 'resolved' ? flip.side : '—') },
  ];
  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{zIndex: 9450}} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{width: 560, maxWidth: '100%'}}>
        <div className="modal-head">
          <h3><i className="fa-solid fa-shield-halved" style={{marginRight:8,color:'var(--orange)'}}/> Coinflip Fairness</h3>
          <button className="modal-close" onClick={onClose} aria-label="close"><i className="fa-solid fa-xmark"/></button>
        </div>
        <div className="modal-body" style={{paddingTop:8, paddingBottom:18}}>
          <div className="fd-fairness-rows">
            {rows.map(r => (
              <div className="fd-fairness-row" key={r.k}>
                <div className="fd-fairness-lbl">{r.label}</div>
                <div className="fd-fairness-val">
                  <code title={r.value}>{r.value}</code>
                  {String(r.value || '').length > 0 && r.value !== '—' && r.value !== 'Revealed after the flip resolves' && (
                    <button className="fd-fairness-copy" onClick={() => copy(r.value, r.k)}>
                      <i className={`fa-solid ${copied === r.k ? 'fa-check' : 'fa-copy'}`}/>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// CONFIRM DIALOG (for big flips)
// ============================================
function ConfirmDialog({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel, askable, askLabel, icon, variant }) {
  const { closing, animateThen } = useExitAnimation();
  const [dontAsk, setDontAsk] = React.useState(false);
  const close = () => animateThen(onCancel);
  const confirm = () => animateThen(() => onConfirm(dontAsk));
  const overlayHandlers = useOverlayClose(close);
  return (
    <div className={`modal-overlay ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className={`modal confirm-modal ${variant === 'fail' ? 'is-fail' : ''}`}>
        <div className="confirm-body" style={{paddingTop: 24}}>
          <div className="confirm-icon"><I name={icon || 'warning'} size={26} color="white"/></div>
          <h3 className="confirm-title">{title}</h3>
          <p className="confirm-message">{message}</p>
          {askable && (
            <label className="dont-ask">
              <input type="checkbox" checked={dontAsk} onChange={(e) => setDontAsk(e.target.checked)}/>
              <span>{askLabel || "Don't ask again"}</span>
            </label>
          )}
          <div className="confirm-actions">
            <button className="confirm-btn cancel" onClick={close}>{cancelLabel || 'Cancel'}</button>
            <button className="confirm-btn confirm" onClick={confirm}>{confirmLabel || 'Confirm'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// FIRST-TIME TUTORIAL
// ============================================
const TUTORIAL_STEPS = [
  { emoji: '👋', title: 'Welcome to blox.hot', body: 'The cleanest coinflip for MM2 items. Quick walkthrough — under 30 seconds.' },
  { emoji: '📦', title: 'Deposit items via bot', body: 'Click the Inventory button in the header, then "Deposit Items" → pick an online bot and trade in-game. Items show up here in seconds.' },
  { emoji: '🪙', title: 'Create or join a flip', body: 'Pick heads or tails, select items from your inventory and create a flip. Or join someone else\'s flip with matching value items.' },
  { emoji: '🎉', title: 'Win it all — provably fair', body: 'SHA-256 commit/reveal guarantees no one can rig the result. Winner takes both sides, items go straight to inventory.' },
];

// Steps point at real elements via CSS selectors. The spotlight masks
// everything except the target, and the tooltip floats next to it.
const TUTORIAL_TOUR = [
  { selector: '.h-wallet-cta',  emoji: '👛', title: 'Deposit items',     body: 'Click your wallet to open inventory and deposit items via bot.' },
  { selector: '.create-btn',    emoji: '🎲', title: 'Create a flip',     body: 'Open the create flip dialog and pick your side + items.' },
  { selector: '.flip-card',     emoji: '⚔️', title: 'Join a flip',       body: 'Each card is a live coinflip. Click the + slot or "View" to spectate / join.' },
  { selector: '.subnav-pill',   emoji: '🧭', title: 'Pages',             body: 'Switch between Coinflip / Leaderboard / Rewards.' },
  { selector: '.h-icon-btn[aria-label="chat"], .mn-btn:nth-child(4)', emoji: '💬', title: 'Chat',              body: 'Talk with other players. Has Global + your-language tab.' },
  { selector: '.lang-btn',      emoji: '🌐', title: 'Language',          body: '13 languages supported. Auto-translates the whole UI.' },
  { selector: '.h-profile, .mn-btn:last-child', emoji: '👤', title: 'Profile',  body: 'Theme toggle, leaderboard, log out — all here.' },
];

function TutorialOverlay({ onClose }) {
  const [step, setStep] = React.useState(0);
  const [rect, setRect] = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({ top: 80, left: 20, side: 'bottom' });
  const current = TUTORIAL_TOUR[step];
  const isLast = step === TUTORIAL_TOUR.length - 1;

  // Find the target element + compute spotlight + tooltip position
  React.useLayoutEffect(() => {
    const update = () => {
      const el = current.selector ? document.querySelector(current.selector) : null;
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      // Scroll target into view if off-screen
      const vh = window.innerHeight;
      if (r.top < 60 || r.bottom > vh - 100) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(update, 250);
        return;
      }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      // Place tooltip below by default, above if near bottom
      const placeBelow = r.bottom + 200 < vh;
      const top = placeBelow ? r.bottom + 16 : r.top - 16;
      const left = Math.max(12, Math.min(window.innerWidth - 340, r.left + r.width / 2 - 160));
      setTooltipPos({ top, left, side: placeBelow ? 'bottom' : 'top' });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step]);

  const next = () => isLast ? onClose() : setStep(s => s + 1);
  const back = () => setStep(s => Math.max(0, s - 1));

  return (
    <div className="tut-overlay-host">
      {rect && (
        <div
          className="tut-spotlight"
          style={{
            top: rect.top - 8, left: rect.left - 8,
            width: rect.width + 16, height: rect.height + 16,
          }}
        />
      )}
      <div
        className={`tut-card tut-side-${tooltipPos.side}`}
        style={{
          top: tooltipPos.side === 'top' ? tooltipPos.top - 200 : tooltipPos.top,
          left: tooltipPos.left,
        }}
      >
        <div className="tut-step">{step + 1} / {TUTORIAL_TOUR.length}</div>
        <div className="tut-emoji">{current.emoji}</div>
        <h3 className="tut-title">{current.title}</h3>
        <p className="tut-body">{current.body}</p>
        <div className="tut-progress">
          {TUTORIAL_TOUR.map((_, i) => (
            <div key={i} className={`tut-dot ${i === step ? 'is-active' : i < step ? 'is-done' : ''}`}/>
          ))}
        </div>
        <div className="tut-actions">
          <button className="tut-back" onClick={back} disabled={step === 0}>← Back</button>
          <button className="tut-skip-btn" onClick={onClose}>Skip</button>
          <button className="tut-next" onClick={next}>{isLast ? "Let's go!" : 'Next →'}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LUCKY 7 STREAK BURST (7 wins in a row → fire animation)
// ============================================
function LuckyBurst({ onEnd }) {
  React.useEffect(() => {
    const t = setTimeout(onEnd, 2400);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="lucky-burst">
      <div className="ring">🔥</div>
      <div className="text">LUCKY 7 STREAK!</div>
    </div>
  );
}

// ============================================
// REWARDS PAGE (wager race with item prizes)
// Admin-controlled later — for now, hardcoded demo data
// ============================================
const REWARDS_RACE = {
  title: 'Chroma Evergreen Race',
  description: 'Top wagerers this month win exclusive Chroma items',
  endsAt: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1), // first of next month
  prizes: [
    { rank: 1,  item: 'Chroma Evergreen',       value: 522,    rarity: 'chroma' },
    { rank: 2,  item: "Chroma Vampire's Gun",   value: 342,    rarity: 'chroma' },
    { rank: 3,  item: 'Chroma Constellation',   value: 314,    rarity: 'chroma' },
    { rank: 4,  item: 'Chroma Alienbeam',       value: 285,    rarity: 'chroma' },
    { rank: 5,  item: 'Chroma Raygun',          value: 138,    rarity: 'chroma' },
    { rank: 6,  item: 'Chroma Sunrise',         value: 95,     rarity: 'chroma' },
    { rank: 7,  item: 'Chroma Snowcannon',      value: 76,     rarity: 'chroma' },
    { rank: 8,  item: 'Chroma Blizzard',        value: 71.2,   rarity: 'chroma' },
    { rank: 9,  item: 'Chroma Sunset',          value: 61.8,   rarity: 'chroma' },
    { rank: 10, item: 'Chroma Treat',           value: 57,     rarity: 'chroma' },
  ],
};

function useCountdown(target) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000) / 60000);
  const secs  = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs };
}

function RewardsPage({ t, onGo }) {
  const [top, setTop] = React.useState([]);
  const [loadingTop, setLoadingTop] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    setLoadingTop(true);
    apiFetch('/api/leaderboard?period=month&limit=10')
      .then(res => {
        if (cancelled) return;
        setTop((res.leaders || []).map(p => ({
          rank: p.rank,
          name: p.username,
          av: synthAvatar(p.username),
          color: deterministicColor(p.user_id),
          avatarUrl: p.avatar_url || '',
          totalValue: Number(p.total_value) || 0,
        })));
      })
      .catch(() => { if (!cancelled) setTop([]); })
      .finally(() => { if (!cancelled) setLoadingTop(false); });
    return () => { cancelled = true; };
  }, []);
  const { days, hours, mins, secs } = useCountdown(REWARDS_RACE.endsAt);
  // Top prize + total prize pool. The top prize ITEM image replaces the
  // generic trophy icon in the hero; total value is displayed under the
  // title so users see at a glance what's at stake.
  const topPrize  = REWARDS_RACE.prizes[0];
  const totalPool = REWARDS_RACE.prizes.reduce((s, p) => s + (Number(p.value) || 0), 0);

  const topThree = REWARDS_RACE.prizes.slice(0, 3);

  return (
    <div className="rewards-page">
      <div className="rewards-hero">
        {/* Mirror of the coinflip banner — same eyebrow + huge pool +
            subtitle. No countdown here per user request (it's in the
            subnav pill already). Top-3 podium row below. */}
        <div className="rh-headline">
          <div className="bc-eyebrow">{tt('rewardsPrizePool', 'Prize pool').toUpperCase()}</div>
          <h1 className="bc-title bc-title--pool">
            <i className="fa-solid fa-gem gem-i"/>{fmtVal(totalPool)}
          </h1>
          <p className="rh-headline-sub">{tt('rewardsBannerSub', 'Top 10 wagerers this month win exclusive Chroma items')}</p>
        </div>

        {/* Top-3 podium — full-width on the events page so all three items
            breathe. Responsive: 3 columns on desktop, 2 on tablet, 1 on
            phone. */}
        <div className="rh-podium">
          {topThree.map((p) => (
            <div key={p.rank} className={`rh-podium-tile rank-${p.rank}`}>
              <span className="rh-podium-rank">#{p.rank}</span>
              <div className="rh-podium-img">
                <img
                  src={itemImg(p.item)}
                  alt={p.item}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <div className="rh-podium-meta">
                <div className="rh-podium-name" title={p.item}>{p.item}</div>
                <div className="rh-podium-val">
                  <i className="fa-solid fa-gem gem-i"/>{fmtVal(p.value)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rewards-table">
        <div className="rw-head">
          <div>#</div>
          <div>Username</div>
          <div className="num">Wagered</div>
          <div className="prize-col">Prize</div>
        </div>
        {loadingTop && (
          /* Skeleton — 10 rows mirroring the .rw-row layout so the page
             doesn't jump when real data arrives. */
          Array.from({ length: 10 }).map((_, i) => (
            <div className="rw-row" key={`sk-${i}`}>
              <div className="rw-rank"><Skeleton w={20} h={14} radius={4}/></div>
              <div className="rw-user">
                <Skeleton w={36} h={36} radius={999}/>
                <Skeleton w={120} h={14} radius={4} style={{marginLeft:10}}/>
              </div>
              <div className="num rw-wagered"><Skeleton w={60} h={14} radius={4}/></div>
              <div className="rw-prize"><Skeleton w={44} h={44} radius={6}/><Skeleton w={50} h={14} radius={4}/></div>
            </div>
          ))
        )}
        {/* Always render ALL prize ranks, even slots without a player yet.
            Previously we only mapped `top` (the API result) — if 3 users had
            wagered, only 3 of the 10 prizes were visible. Now we iterate
            over REWARDS_RACE.prizes and pair each with the corresponding
            top[i] user (or render an empty "open slot" if none). */}
        {!loadingTop && REWARDS_RACE.prizes.map((prize, i) => {
          const p = top[i] || null;
          const rankCls = i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : '';
          return (
            <div className={`rw-row ${rankCls} ${!p ? 'is-open' : ''}`} key={prize.rank}>
              <div className={`rw-rank ${rankCls}`}>#{prize.rank}</div>
              <div className="rw-user">
                {p ? (
                  <>
                    <div className={`fc-avatar ${p.color} ${p.avatarUrl ? 'has-img' : ''}`} style={{width:36,height:36,fontSize:12,borderRadius:'999px',position:'relative',overflow:'hidden'}}>
                      {p.avatarUrl
                        ? <img src={p.avatarUrl} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                        : p.av}
                    </div>
                    <span className="rw-name">{p.name}</span>
                  </>
                ) : (
                  <>
                    <div className="rw-empty-av" aria-hidden="true">
                      <i className="fa-solid fa-question"/>
                    </div>
                    <span className="rw-empty-name">{tt('eventsOpenSlot', 'Open slot')}</span>
                  </>
                )}
              </div>
              <div className="num rw-wagered">
                {p
                  ? <><i className="fa-solid fa-gem gem-i"/>{fmtVal(p.totalValue)}</>
                  : <span className="rw-empty-wagered">—</span>}
              </div>
              <div className="rw-prize">
                <FlipItem item={{ name: prize.item, rarity: prize.rarity, price: prize.value }} size={44} noTooltip/>
                <span className="rw-prize-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(prize.value)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// LEADERBOARD
// ============================================
const LB_PERIODS = [
  { value: 'day',   label: 'Today' },
  { value: 'week',  label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'all',   label: 'All time' },
];

const generateLeaderboard = (count = 50, seed = 1) => {
  const baseTop = 2400;
  return Array.from({ length: count }, (_, i) => {
    const name = NAMES[(i * 3 + seed) % NAMES.length] + (i >= NAMES.length ? Math.floor(i / NAMES.length) + 1 : '');
    const decay = Math.pow(0.93, i);
    return {
      rank: i + 1,
      name,
      av: name.substring(0, 2).toUpperCase(),
      color: AVATAR_PALETTES[(i + seed) % AVATAR_PALETTES.length],
      totalValue: Math.max(8, Math.round((baseTop * decay + (Math.random() * 60 - 30)) * 100) / 100),
      flips:     Math.max(8, Math.floor(720 * decay + Math.random() * 60)),
      biggest:   Math.max(5, Math.round((420 * decay + Math.random() * 50) * 100) / 100),
    };
  });
};

const LB_DATA_CACHE = {};
const getLeaderboard = (period) => {
  if (!LB_DATA_CACHE[period]) {
    const seed = { day: 1, week: 7, month: 30, all: 365 }[period] || 1;
    LB_DATA_CACHE[period] = generateLeaderboard(50, seed);
  }
  return LB_DATA_CACHE[period];
};

function LeaderboardPage({ t }) {
  const [period, setPeriod] = React.useState('week');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch(`/api/leaderboard?period=${encodeURIComponent(period)}&limit=50`)
      .then(res => {
        if (cancelled) return;
        const mapped = (res.leaders || []).map(p => ({
          rank: p.rank,
          name: p.username || p.username,
          av:   synthAvatar(p.username || p.username),
          color: deterministicColor(p.user_id),
          avatarUrl: p.avatar_url || '',
          totalValue: Number(p.total_value) || 0,
          flips: Number(p.total_flips) || 0,
          biggest: Number(p.biggest_win) || 0,
        }));
        setRows(mapped);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="lb-page">
      <div className="lb-head">
        <div>
          <h1 className="lb-title"><I name="trophy" size={22} color="#FFD64A"/> {t.leaderboard || 'Leaderboard'}</h1>
          <p className="lb-sub">{t.lbSub || 'Top players ranked by total flip value'}</p>
        </div>
        <div className="lb-period">
          <CustomSelect
            value={period}
            onChange={setPeriod}
            ariaLabel="period"
            options={LB_PERIODS.map(p => ({ value: p.value, label: t['lbPeriod_'+p.value] || p.label }))}
          />
        </div>
      </div>

      {/* Top 3 podium cards */}
      <div className="lb-top3">
        {top3.map((p, i) => (
          <div key={p.rank} className={`lb-top-card lb-rank-${i+1}`}>
            <div className="lb-top-medal">
              {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
            </div>
            <div className="lb-top-row">
              <div className={`lb-avatar fc-avatar ${p.color} ${p.avatarUrl ? 'has-img' : ''}`}>
                {p.avatarUrl
                  ? <img src={p.avatarUrl} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : p.av}
              </div>
              <div className="lb-top-info">
                <div className="lb-top-name">{p.name}</div>
                <div className="lb-top-rank">Rank #{p.rank}</div>
              </div>
            </div>
            <div className="lb-top-stats">
              <div className="lb-ts-cell">
                <div className="lb-ts-lbl">{t?.lbFlips || 'Flips'}</div>
                <div className="lb-ts-val">{p.flips.toLocaleString()}</div>
              </div>
              <div className="lb-ts-cell">
                <div className="lb-ts-lbl">{t?.lbTotalValue || 'Total value'}</div>
                <div className="lb-ts-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(p.totalValue)}</div>
              </div>
              <div className="lb-ts-cell">
                <div className="lb-ts-lbl">{t?.lbBiggest || 'Biggest win'}</div>
                <div className="lb-ts-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(p.biggest)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && rows.length === 0 && (
        <div style={{textAlign:'center',padding:'40px 20px',color:'var(--g-500)',background:'var(--g-100)',borderRadius:8}}>
          No flips have been recorded yet for this period.
        </div>
      )}

      {/* Skeleton placeholder while leaderboard fetches */}
      {loading && rows.length === 0 && (
        <div className="lb-list">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="lb-row" key={`lbsk-${i}`}>
              <div className="lb-row-rank"><Skeleton w={28} h={16} radius={4}/></div>
              <div className="lb-row-player">
                <Skeleton w={38} h={38} radius={999}/>
                <Skeleton w={120} h={14} radius={4} style={{marginLeft:10}}/>
              </div>
              <Skeleton w={80} h={14} radius={4}/>
              <Skeleton w={50} h={14} radius={4}/>
            </div>
          ))}
        </div>
      )}

      {/* Rest of leaderboard as card rows */}
      <div className="lb-list">
        {rest.map(p => (
          <div key={p.rank} className="lb-row">
            <div className="lb-row-rank">#{p.rank}</div>
            <div className="lb-row-player">
              <div className={`fc-avatar ${p.color} ${p.avatarUrl ? 'has-img' : ''}`} style={{width:38,height:38,fontSize:12,borderRadius:'999px',position:'relative',overflow:'hidden'}}>
                {p.avatarUrl
                  ? <img src={p.avatarUrl} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : p.av}
              </div>
              <span className="lb-row-name">{p.name}</span>
            </div>
            <div className="lb-row-stat">
              <div className="lbs-lbl">{t?.lbFlips || 'Flips'}</div>
              <div className="lbs-val">{p.flips.toLocaleString()}</div>
            </div>
            <div className="lb-row-stat">
              <div className="lbs-lbl">{t?.lbTotalValue || 'Total value'}</div>
              <div className="lbs-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(p.totalValue)}</div>
            </div>
            <div className="lb-row-stat lb-hide-sm">
              <div className="lbs-lbl">{t?.lbBiggest || 'Biggest win'}</div>
              <div className="lbs-val highlight"><i className="fa-solid fa-gem gem-i"/>{fmtVal(p.biggest)} <span className="lbs-unit">Value</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// ADMIN PAGE — stats, events CRUD, user search/ban
// ============================================

// ErrorBoundary used around the admin tree so a render-time crash in a
// single panel (e.g. user-detail modal) doesn't blank the whole page. React
// 18 unmounts the entire root on unhandled render errors → white screen.
// With this boundary the user sees a useful message instead, plus a reload
// button. Keep it minimal — just the class needed for catching errors.
class AdminErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // Surface to console for the developer
    try { console.error('[Admin crash]', err, info); } catch (_) {}
  }
  render() {
    if (this.state.err) {
      return (
        <div className="page-state err" style={{padding:'40px 24px',textAlign:'center'}}>
          <div style={{fontSize:32,marginBottom:8}}>💥</div>
          <div style={{fontWeight:800,marginBottom:6}}>Admin panel crashed</div>
          <div style={{fontSize:12,color:'var(--g-600)',maxWidth:560,margin:'0 auto'}}>
            {String(this.state.err && this.state.err.message || this.state.err)}
          </div>
          <button
            className="settings-btn"
            style={{marginTop:16}}
            onClick={() => { this.setState({ err: null }); }}
          >Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// VALUE MANAGER — modal where value managers browse items, see the live
// value, and submit a change request. Admin must approve before it applies.
function ValueManagerModal({ onClose }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlay = useOverlayClose(close);
  const [items, setItems] = React.useState([]);
  const [requests, setRequests] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [picked, setPicked] = React.useState(null); // {name, price, rarity}
  const [newPrice, setNewPrice] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const load = () => apiFetch('/api/value/requests')
    .then(r => { setItems(r.items || []); setRequests(r.requests || []); })
    .catch(() => {});
  React.useEffect(() => { load(); }, []);

  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = q ? items.filter(it => it.name.toLowerCase().includes(q)) : items;
    return arr.slice(0, 40);
  }, [items, search]);

  const submit = async () => {
    if (!picked) return;
    const p = parseFloat(newPrice);
    if (!Number.isFinite(p) || p <= 0) { setMsg({ type: 'lose', text: 'Enter a valid price' }); return; }
    setBusy(true); setMsg(null);
    try {
      await apiFetch('/api/value/requests', { method: 'POST', body: { item_name: picked.name, new_price: p, note } });
      setMsg({ type: 'win', text: 'Request submitted — waiting for admin approval' });
      setPicked(null); setNewPrice(''); setNote('');
      load();
    } catch (e) {
      setMsg({ type: 'lose', text: e.message || 'Could not submit' });
    } finally { setBusy(false); }
  };

  return (
    <div className={`modal-overlay ${closing ? 'is-closing' : ''}`} {...overlay}>
      <div className="modal modal--wide vm-modal">
        <div className="modal-head">
          <h3><i className="fa-solid fa-gem" style={{color:'var(--orange)'}}/> Value Manager</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body vm-body">
          {picked ? (
            <div className="vm-form">
              <div className="vm-picked">
                <div className="vm-picked-name">{picked.name}</div>
                <div className="vm-picked-cur">Current: <b><i className="fa-solid fa-gem gem-i"/>{fmtVal(picked.price)}</b></div>
              </div>
              <label className="vm-label">New value</label>
              <input className="vm-input" type="number" min="0" step="0.01" value={newPrice} placeholder="0.00" onChange={(e) => setNewPrice(e.target.value)} autoFocus/>
              <label className="vm-label">Reason (optional)</label>
              <input className="vm-input" type="text" maxLength={280} value={note} placeholder="Why this change?" onChange={(e) => setNote(e.target.value)}/>
              {msg && <div className={`vm-msg ${msg.type}`}>{msg.text}</div>}
              <div className="vm-form-actions">
                <button className="settings-btn" onClick={() => { setPicked(null); setMsg(null); }}>Back</button>
                <button className="settings-btn primary" disabled={busy} onClick={submit}>Submit request</button>
              </div>
            </div>
          ) : (
            <>
              <div className="inv-search" style={{marginBottom:10}}>
                <I name="search" size={13} color="#838383"/>
                <input placeholder="Search items…" value={search} onChange={(e) => setSearch(e.target.value)}/>
              </div>
              {msg && <div className={`vm-msg ${msg.type}`}>{msg.text}</div>}
              <div className="vm-item-list">
                {visible.map(it => (
                  <button key={it.name} className="vm-item-row" onClick={() => { setPicked(it); setNewPrice(String(it.price)); setMsg(null); }}>
                    <span className="vm-item-name">{it.name}</span>
                    <span className="vm-item-price"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</span>
                  </button>
                ))}
              </div>
              {requests.length > 0 && (
                <div className="vm-mine">
                  <div className="vm-mine-label">Your recent requests</div>
                  {requests.slice(0, 8).map(r => (
                    <div key={r.id} className="vm-mine-row">
                      <span className="vm-mine-name">{r.item_name}</span>
                      <span className="vm-mine-change"><i className="fa-solid fa-gem gem-i"/>{fmtVal(Number(r.old_price ?? 0))} → {fmtVal(Number(r.new_price))}</span>
                      <span className={`vm-status ${r.status}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ADMIN: VALUE-CHANGE REQUEST QUEUE — value managers submit price changes,
// admins approve/reject here. Approval writes the new value into values.json.
function AdminValueRequests() {
  const [rows, setRows] = React.useState([]);
  const [pending, setPending] = React.useState(0);
  const [busy, setBusy] = React.useState(null);
  const load = () => apiFetch('/api/admin/value-requests?status=pending')
    .then(r => { setRows(r.requests || []); setPending(r.pending_count || 0); })
    .catch(() => {});
  React.useEffect(() => { load(); }, []);
  const review = async (id, action) => {
    setBusy(id);
    try { await apiFetch('/api/admin/value-requests', { method: 'POST', body: { id, action } }); load(); }
    catch (e) { window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'lose', text: e.message || 'Failed' } })); }
    finally { setBusy(null); }
  };
  return (
    <section className="admin-section">
      <h2 className="admin-h2"><i className="fa-solid fa-gem"/> Value change requests {pending > 0 && <span className="admin-badge">{pending}</span>}</h2>
      {rows.length === 0 ? (
        <div className="admin-empty">No pending value-change requests.</div>
      ) : (
        <div className="vcr-list">
          {rows.map(r => {
            const up = Number(r.new_price) >= Number(r.old_price ?? 0);
            return (
              <div key={r.id} className="vcr-row">
                <div className="vcr-item">
                  <div className="vcr-name">{r.item_name}</div>
                  <div className="vcr-by">by @{r.requester_username || '—'}</div>
                </div>
                <div className="vcr-prices">
                  <span className="vcr-old"><i className="fa-solid fa-gem gem-i"/>{fmtVal(Number(r.old_price ?? 0))}</span>
                  <i className={`fa-solid fa-arrow-right vcr-arrow ${up ? 'up' : 'down'}`}/>
                  <span className={`vcr-new ${up ? 'up' : 'down'}`}><i className="fa-solid fa-gem gem-i"/>{fmtVal(Number(r.new_price))}</span>
                </div>
                {r.note && <div className="vcr-note">“{r.note}”</div>}
                <div className="vcr-actions">
                  <button className="settings-btn danger" disabled={busy === r.id} onClick={() => review(r.id, 'reject')}>Reject</button>
                  <button className="settings-btn primary" disabled={busy === r.id} onClick={() => review(r.id, 'approve')}>Approve</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const ADMIN_SECTIONS = [
  { key: 'overview',   label: 'Overview',       icon: 'fa-chart-line',             render: () => <><AdminStats/><AdminAnalytics/></> },
  { key: 'users',      label: 'Users',          icon: 'fa-users',                  render: () => <><AdminUsers/><AdminAdjust/></> },
  { key: 'moderation', label: 'Moderation',     icon: 'fa-gavel',                  render: () => <><AdminChatMod/><AdminReports/></> },
  { key: 'values',     label: 'Value Requests', icon: 'fa-gem',                    render: () => <AdminValueRequests/> },
  { key: 'insights',   label: 'Insights',       icon: 'fa-magnifying-glass-chart', render: () => <><AdminSuspicious/><AdminItemHeatmap/></> },
  { key: 'settings',   label: 'Settings',       icon: 'fa-sliders',                render: () => <><AdminSettings/><AdminEvents/></> },
  { key: 'audit',      label: 'Audit Log',      icon: 'fa-scroll',                 render: () => <AdminAudit/> },
];

function AdminPage({ t, currentUser }) {
  if (!currentUser?.is_admin) {
    return <div className="page-state err">Admin only. Your account is not flagged as admin.</div>;
  }
  const [section, setSection] = React.useState('overview');
  // Pending value-request count → badge on the sidebar nav.
  const [pendingValues, setPendingValues] = React.useState(0);
  React.useEffect(() => {
    apiFetch('/api/admin/value-requests?status=pending')
      .then(r => setPendingValues(r.pending_count || 0))
      .catch(() => {});
  }, [section]);
  const active = ADMIN_SECTIONS.find(s => s.key === section) || ADMIN_SECTIONS[0];
  return (
    <AdminErrorBoundary>
      <div className="admin-shell">
        <aside className="admin-sidebar">
          <div className="admin-sidebar-brand"><i className="fa-solid fa-shield-halved"/> <span>Admin</span></div>
          <nav className="admin-nav">
            {ADMIN_SECTIONS.map(s => (
              <button
                key={s.key}
                className={`admin-nav-item ${section === s.key ? 'is-active' : ''}`}
                onClick={() => setSection(s.key)}
              >
                <i className={`fa-solid ${s.icon}`}/>
                <span className="admin-nav-label">{s.label}</span>
                {s.key === 'values' && pendingValues > 0 && <span className="admin-nav-badge">{pendingValues}</span>}
              </button>
            ))}
          </nav>
        </aside>
        <main className="admin-main" key={section}>
          {active.render()}
        </main>
      </div>
    </AdminErrorBoundary>
  );
}

// ============================================
// ConfirmModal / AlertModal — replaces window.confirm/alert across admin.
// Centered, capped width, escape + overlay-click to dismiss, portal-mounted
// so they render above the parent modal without z-index battles.
// ============================================
function ConfirmModal({ open, title, body, cta = 'Confirm', kind = 'danger', onConfirm, onCancel }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="cm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}>
      <div className="cm-card" role="dialog" aria-modal="true">
        <div className={`cm-icon ${kind}`}>
          <i className={`fa-solid ${kind === 'danger' ? 'fa-triangle-exclamation' : 'fa-circle-question'}`}/>
        </div>
        <div className="cm-title">{title}</div>
        {body && <div className="cm-body">{body}</div>}
        <div className="cm-actions">
          <button type="button" className="cm-btn cm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className={`cm-btn ${kind === 'danger' ? 'cm-btn-danger' : 'cm-btn-primary'}`} onClick={onConfirm} autoFocus>
            {cta}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AlertModal({ open, title, body, kind = 'info', onClose }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' || e.key === 'Enter') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const icon = kind === 'error' ? 'fa-circle-xmark' : kind === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info';
  return ReactDOM.createPortal(
    <div className="cm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="cm-card" role="alertdialog" aria-modal="true">
        <div className={`cm-icon ${kind}`}>
          <i className={`fa-solid ${icon}`}/>
        </div>
        <div className="cm-title">{title}</div>
        {body && <div className="cm-body">{body}</div>}
        <div className="cm-actions">
          <button type="button" className="cm-btn cm-btn-primary" onClick={onClose} autoFocus>OK</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// ADMIN: REPORTS QUEUE — search, date filter, status, resolve/dismiss
// ============================================
function AdminReports() {
  const [filters, setFilters] = React.useState({ status: 'open', search: '', from: '', to: '' });
  const [data, setData] = React.useState({ reports: [], total: 0 });
  const [stats, setStats] = React.useState(null);
  const [offset, setOffset] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [acting, setActing] = React.useState(null); // {id, note}
  const LIMIT = 25;

  const reload = React.useCallback(async () => {
    setBusy(true);
    try {
      const q = new URLSearchParams();
      q.set('status', filters.status);
      if (filters.search) q.set('search', filters.search);
      if (filters.from)   q.set('from', filters.from);
      if (filters.to)     q.set('to', filters.to);
      q.set('limit', LIMIT);
      q.set('offset', offset);
      const res = await apiFetch('/api/admin/reports?' + q.toString());
      setData({ reports: res.reports || [], total: res.total || 0 });
    } catch (_) {}
    setBusy(false);
  }, [filters, offset]);

  const reloadStats = React.useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/reports?stats=1');
      setStats(res);
    } catch (_) {}
  }, []);

  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => { reloadStats(); }, [reloadStats, data.total]);

  const onSearch = (e) => { e.preventDefault(); setOffset(0); reload(); };
  const setFilter = (k, v) => { setOffset(0); setFilters(f => ({ ...f, [k]: v })); };

  const resolve = async (id, status, note) => {
    try {
      await apiFetch('/api/admin/reports', { method: 'POST', body: { id, status, note: note || '' } });
      setActing(null);
      reload();
      reloadStats();
    } catch (_) {}
  };

  const reasonLabel = (code) => ({
    dislike: "I don't like it",
    spam: 'Spam',
    abuse: 'Abuse or harassment',
    harmful: 'Harmful content',
    doxxing: 'Doxxing / private info',
    other: 'Something else',
  })[code] || code;

  const openCounts = (stats?.by_status || []).reduce((m, r) => ({ ...m, [r.status]: Number(r.c) }), {});
  const totalPages = Math.max(1, Math.ceil(data.total / LIMIT));
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <section className="admin-section">
      <h2 className="admin-section-title">
        <i className="fa-solid fa-flag"/> Reports
        {openCounts.open > 0 && <span className="admin-pill-warn">{openCounts.open} open</span>}
      </h2>

      {/* Filter bar */}
      <form className="admin-filter-bar" onSubmit={onSearch}>
        <div className="admin-status-pills">
          {[
            { code: 'open',      label: 'Open',      count: openCounts.open || 0 },
            { code: 'resolved',  label: 'Resolved',  count: openCounts.resolved || 0 },
            { code: 'dismissed', label: 'Dismissed', count: openCounts.dismissed || 0 },
            { code: 'all',       label: 'All',       count: null },
          ].map(s => (
            <button
              key={s.code}
              type="button"
              className={`admin-pill ${filters.status === s.code ? 'is-active' : ''}`}
              onClick={() => setFilter('status', s.code)}
            >
              {s.label}{s.count !== null ? ` (${s.count})` : ''}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="admin-search"
          placeholder="Search message, reporter, reported user…"
          value={filters.search}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
        />
        <input type="date" className="admin-date" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} title="From"/>
        <input type="date" className="admin-date" value={filters.to}   onChange={(e) => setFilter('to', e.target.value)}   title="To"/>
        <button type="submit" className="admin-btn primary">Search</button>
      </form>

      {/* Table */}
      {busy ? (
        <div className="page-state">Loading…</div>
      ) : data.reports.length === 0 ? (
        <div className="page-state">No reports match these filters.</div>
      ) : (
        <table className="admin-table admin-reports-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Reporter</th>
              <th>Reported</th>
              <th>Reason</th>
              <th>Message</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.reports.map(r => (
              <tr key={r.id} className={`status-${r.status}`}>
                <td className="t-date">{new Date(r.created_at).toLocaleString()}</td>
                <td className="t-user">@{r.reporter_username || '—'}</td>
                <td className="t-user">
                  @{r.reported_username || '—'}
                  {Number(r.reported_user_total_reports) > 1 && (
                    <span className="reports-count-badge" title="Total open+closed reports on this user">
                      {r.reported_user_total_reports}×
                    </span>
                  )}
                </td>
                <td>{reasonLabel(r.reason)}</td>
                <td className="t-msg">{r.message_text_snapshot || <em>(empty)</em>}</td>
                <td><span className={`status-badge status-${r.status}`}>{r.status}</span></td>
                <td className="t-actions">
                  {r.status === 'open' ? (
                    <>
                      <button className="admin-btn small primary" onClick={() => setActing({ id: r.id, status: 'resolved', note: '', target: r.reported_username })}>Resolve</button>
                      <button className="admin-btn small ghost"   onClick={() => setActing({ id: r.id, status: 'dismissed', note: '', target: r.reported_username })}>Dismiss</button>
                    </>
                  ) : (
                    <span className="t-resolver">{r.resolver_username ? 'by @' + r.resolver_username : ''}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      {data.total > LIMIT && (
        <div className="admin-pager">
          <button className="admin-btn ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
            ← Prev
          </button>
          <span className="admin-pager-info">Page {currentPage} of {totalPages} · {data.total} total</span>
          <button className="admin-btn ghost" disabled={currentPage >= totalPages} onClick={() => setOffset(offset + LIMIT)}>
            Next →
          </button>
        </div>
      )}

      {/* Resolution modal */}
      {acting && (
        <div className="modal-overlay" onClick={() => setActing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: 440}}>
            <div className="modal-head">
              <h3>{acting.status === 'resolved' ? 'Resolve report' : 'Dismiss report'}</h3>
              <button className="modal-close" onClick={() => setActing(null)}><I name="close" size={18}/></button>
            </div>
            <div className="modal-body">
              <div style={{fontSize: 13, color: 'var(--g-600)', marginBottom: 10}}>
                Optional admin note (what action was taken, why dismissed, etc.)
              </div>
              <textarea
                className="admin-note-textarea"
                rows={3}
                placeholder="Note…"
                value={acting.note}
                onChange={(e) => setActing({ ...acting, note: e.target.value.slice(0, 500) })}
              />
              <div style={{display: 'flex', gap: 8, marginTop: 14}}>
                <button className="admin-btn ghost" onClick={() => setActing(null)} style={{flex: 1}}>Cancel</button>
                <button
                  className={`admin-btn ${acting.status === 'resolved' ? 'primary' : 'danger'}`}
                  onClick={() => resolve(acting.id, acting.status, acting.note)}
                  style={{flex: 1}}
                >
                  Confirm {acting.status}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function AdminAnalytics() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { apiFetch('/api/admin/analytics').then(setD).catch(() => setD(null)); }, []);
  if (!d) return null;

  // Build a simple SVG line chart for one daily series
  const Chart = ({ data, color = 'var(--orange)', label }) => {
    if (!data || data.length === 0) return <div className="chart-empty">No data</div>;
    const w = 320, h = 80, pad = 4;
    const max = Math.max(...data.map(x => x.value), 1);
    const stepX = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
    const pts = data.map((p, i) => {
      const x = pad + i * stepX;
      const y = h - pad - ((p.value / max) * (h - pad * 2));
      return [x, y];
    });
    const path = pts.map(([x, y], i) => (i === 0 ? 'M' : 'L') + x.toFixed(1) + ',' + y.toFixed(1)).join(' ');
    const area = path + ` L${pts[pts.length-1][0].toFixed(1)},${h - pad} L${pts[0][0].toFixed(1)},${h - pad} Z`;
    return (
      <div className="chart-card">
        <div className="chart-label">{label}</div>
        <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
          <path d={area} fill={color} opacity="0.15"/>
          <path d={path} stroke={color} strokeWidth="2" fill="none"/>
          {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2" fill={color}/>)}
        </svg>
        <div className="chart-foot">
          <span>{data[0]?.date}</span>
          <span>{data[data.length-1]?.date}</span>
        </div>
      </div>
    );
  };

  return (
    <section className="admin-section">
      <h3>Analytics</h3>
      <div className="admin-charts">
        <Chart data={d.daily.registrations} label="Registrations (30d)"/>
        <Chart data={d.daily.flips}         label="Flips resolved (30d)" color="#10B981"/>
        <Chart data={d.daily.volume}        label="Volume (30d)" color="#7048E8"/>
      </div>

      <div className="admin-retention">
        <div className="admin-stat"><div className="as-lbl">NEW USERS / 30D</div><div className="as-val">{d.retention.new_users_30d}</div></div>
        <div className="admin-stat"><div className="as-lbl">ACTIVE (7d)</div><div className="as-val">{d.retention.active_7d}</div></div>
        <div className="admin-stat"><div className="as-lbl">RETENTION</div><div className="as-val">{d.retention.retention_pct}%</div></div>
      </div>

      <div style={{marginTop: 14}}>
        <div className="quests-section-label">Top items (by appearances in resolved flips)</div>
        <table className="admin-table">
          <thead><tr><th>Item</th><th>Rarity</th><th>Appeared</th><th>Avg price</th></tr></thead>
          <tbody>
            {d.top_items.map((it, i) => (
              <tr key={i}>
                <td>{it.name}</td>
                <td><span style={{
                  padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                  background: 'var(--g-200)', color: 'var(--g-800)'
                }}>{it.rarity}</span></td>
                <td>{it.appeared}</td>
                <td>{fmtVal(it.avg_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdminSettings() {
  const [s, setS] = React.useState(null);
  const reload = () => apiFetch('/api/admin/settings').then(r => setS(r.settings || {})).catch(() => setS({}));
  React.useEffect(() => { reload(); }, []);
  const save = async (key, value) => {
    await apiFetch('/api/admin/settings', { method: 'POST', body: { key, value: String(value) } });
    reload();
  };
  if (!s) return <div className="page-state">Loading settings…</div>;
  return (
    <section className="admin-section">
      <h3>Site settings</h3>
      <div className="admin-settings-grid">
        <label className="admin-set">
          <span className="as-name">House edge %</span>
          <input type="number" step="0.01" min="0" max="0.20" defaultValue={s.house_edge_pct ?? '0.05'} onBlur={(e) => save('house_edge_pct', e.target.value)}/>
          <span className="as-hint">0.05 = 5%</span>
        </label>
        <label className="admin-set">
          <span className="as-name">XP multiplier</span>
          <input type="number" step="0.1" min="1" max="5" defaultValue={s.xp_multiplier ?? '1.0'} onBlur={(e) => save('xp_multiplier', e.target.value)}/>
          <span className="as-hint">2.0 = 2x weekend</span>
        </label>
        <label className="admin-set">
          <span className="as-name">Maintenance mode</span>
          <select defaultValue={s.maintenance_mode === '1' ? '1' : '0'} onChange={(e) => save('maintenance_mode', e.target.value)}>
            <option value="0">Off</option>
            <option value="1">ON (block all but admins)</option>
          </select>
        </label>
        <label className="admin-set wide">
          <span className="as-name">Maintenance message</span>
          <input type="text" defaultValue={s.maintenance_message ?? ''} onBlur={(e) => save('maintenance_message', e.target.value)} placeholder="We'll be right back."/>
        </label>
        <label className="admin-set">
          <span className="as-name">Broadcast banner</span>
          <select defaultValue={s.broadcast_active === '1' ? '1' : '0'} onChange={(e) => save('broadcast_active', e.target.value)}>
            <option value="0">Off</option>
            <option value="1">On</option>
          </select>
        </label>
        <label className="admin-set wide">
          <span className="as-name">Broadcast message</span>
          <input type="text" defaultValue={s.broadcast_message ?? ''} onBlur={(e) => save('broadcast_message', e.target.value)} placeholder="2x XP this weekend!"/>
        </label>
      </div>
    </section>
  );
}

function AdminAdjust() {
  const [userId, setUserId] = React.useState('');
  const [itemName, setItemName] = React.useState('');
  const [itemPrice, setItemPrice] = React.useState('1');
  const [itemRarity, setItemRarity] = React.useState('godly');
  const [msg, setMsg] = React.useState('');
  const grant = async () => {
    if (!userId.trim() || !itemName.trim()) return;
    try {
      await apiFetch('/api/admin/adjust', { method: 'POST', body: {
        user_id: Number(userId), action: 'grant',
        item_name: itemName, item_price: Number(itemPrice), item_rarity: itemRarity,
      }});
      setMsg('Item granted.'); setItemName('');
    } catch (e) { setMsg(e.message || 'Failed'); }
    setTimeout(() => setMsg(''), 2000);
  };
  return (
    <section className="admin-section">
      <h3>Manual item grant</h3>
      <div className="admin-event-form">
        <input className="settings-select" placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)}/>
        <input className="settings-select" placeholder="Item name (e.g. Harvester)" value={itemName} onChange={(e) => setItemName(e.target.value)}/>
        <input className="settings-select" placeholder="Price" type="number" step="0.01" value={itemPrice} onChange={(e) => setItemPrice(e.target.value)}/>
        <select className="settings-select" value={itemRarity} onChange={(e) => setItemRarity(e.target.value)}>
          <option value="godly">godly</option>
          <option value="ancient">ancient</option>
          <option value="chroma">chroma</option>
          <option value="unique">unique</option>
        </select>
        <button className="settings-btn" onClick={grant}>+ Grant</button>
      </div>
      {msg && <div className="ref-msg" style={{marginTop: 8}}>{msg}</div>}
    </section>
  );
}

function AdminAudit() {
  const [rows, setRows] = React.useState([]);
  React.useEffect(() => { apiFetch('/api/admin/audit?limit=50').then(r => setRows(r.audit || [])).catch(() => {}); }, []);
  return (
    <section className="admin-section">
      <h3>Audit log</h3>
      <table className="admin-table">
        <thead><tr><th>When</th><th>Admin</th><th>Action</th><th>Target</th><th>Payload</th><th>IP</th></tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={6} style={{color:'var(--g-500)'}}>No audit entries yet.</td></tr>
            : rows.map(r => (
              <tr key={r.id}>
                <td>{new Date(r.created_at).toLocaleString()}</td>
                <td>@{r.admin_username}</td>
                <td><code style={{fontSize:11}}>{r.action}</code></td>
                <td>{r.target_id ?? '—'}</td>
                <td><code style={{fontSize:11}}>{r.payload ? JSON.stringify(r.payload).slice(0,80) : ''}</code></td>
                <td><code style={{fontSize:11}}>{r.ip}</code></td>
              </tr>
            ))}
        </tbody>
      </table>
    </section>
  );
}

function AdminStats() {
  const [s, setS] = React.useState(null);
  const [onlineSeries, setOnlineSeries] = React.useState([]);
  React.useEffect(() => {
    const tick = () => apiFetch('/api/admin/stats').then(d => {
      setS(d);
      // Build a simple in-memory rolling time series of "online users" for
      // the dashboard chart. We poll every 30s and keep the last 30 points.
      setOnlineSeries(prev => [...prev, { t: Date.now(), v: d.online || 0 }].slice(-30));
    }).catch(() => setS(null));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);
  if (!s) return <div className="page-state">Loading stats…</div>;
  const cell = (lbl, val, sub) => (
    <div className="admin-stat">
      <div className="as-lbl">{lbl}</div>
      <div className="as-val">{val}</div>
      {sub && <div className="as-sub">{sub}</div>}
    </div>
  );
  // Revenue calc — house edge is configured server-side (default 2%) but
  // we estimate based on declared volume. ARPU = total volume / active users.
  const houseEdgePct = (s.house_edge_pct != null) ? Number(s.house_edge_pct) : 2;
  const revenueToday = (Number(s.volume_today) || 0) * (houseEdgePct / 100);
  const revenueTotal = (Number(s.volume_total) || 0) * (houseEdgePct / 100);
  const arpu = s.users > 0 ? (Number(s.volume_total) || 0) / s.users : 0;
  // Online chart — sparkline path
  const sparkPath = (() => {
    if (onlineSeries.length < 2) return '';
    const W = 200; const H = 40;
    const max = Math.max(...onlineSeries.map(p => p.v), 1);
    const min = Math.min(...onlineSeries.map(p => p.v), 0);
    const range = Math.max(max - min, 1);
    return onlineSeries.map((p, i) => {
      const x = (i / (onlineSeries.length - 1)) * W;
      const y = H - ((p.v - min) / range) * H;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  })();
  return (
    <section className="admin-section">
      <h3>Overview</h3>
      <div className="admin-stats-grid">
        {cell('USERS', s.users.toLocaleString())}
        {cell('BANNED', s.banned.toLocaleString())}
        {cell('FLIPS / 24h', s.flips_today.toLocaleString())}
        {cell('FLIPS TOTAL', s.flips_total.toLocaleString())}
        {cell('VOLUME / 24h', fmtVal(s.volume_today))}
        {cell('VOLUME TOTAL', fmtVal(s.volume_total))}
        {cell('OPEN FLIPS', s.open_flips.toLocaleString())}
        {cell('ONLINE', (s.online || 0).toLocaleString(), 'live')}
      </div>

      {/* Revenue dashboard — house edge × volume = expected revenue */}
      <h3 style={{marginTop:18}}>Revenue</h3>
      <div className="admin-stats-grid">
        {cell('HOUSE EDGE', houseEdgePct + '%')}
        {cell('REVENUE / 24h', fmtVal(revenueToday), 'estimated')}
        {cell('REVENUE TOTAL', fmtVal(revenueTotal), 'estimated')}
        {cell('ARPU', fmtVal(arpu), 'per user, lifetime')}
      </div>

      {/* Real-time online users sparkline — last 30 polls (~15 min window) */}
      {onlineSeries.length >= 2 && (
        <>
          <h3 style={{marginTop:18}}>Online users — last 15 min</h3>
          <div className="admin-online-chart">
            <svg viewBox="0 0 200 40" preserveAspectRatio="none" width="100%" height="60">
              <defs>
                <linearGradient id="onlineGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgba(16,185,129,0.4)"/>
                  <stop offset="100%" stopColor="rgba(16,185,129,0)"/>
                </linearGradient>
              </defs>
              <path d={`${sparkPath} L200,40 L0,40 Z`} fill="url(#onlineGrad)"/>
              <path d={sparkPath} fill="none" stroke="var(--orange)" strokeWidth="1.5"/>
            </svg>
            <div className="admin-online-meta">
              <span>Now: <b>{(onlineSeries[onlineSeries.length - 1]?.v || 0).toLocaleString()}</b></span>
              <span>Peak: <b>{Math.max(...onlineSeries.map(p => p.v)).toLocaleString()}</b></span>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// Auto-flag dashboard — surfaces users tripping our anti-cheat heuristics.
// Polls /api/admin/suspicious every 60s. Each row shows the username,
// severity score, and flag chips (burst_flip, wash_trade, etc.).
function AdminSuspicious() {
  const [items, setItems] = React.useState(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    const tick = () => apiFetch('/api/admin/suspicious')
      .then(r => { setItems(r.items || []); setErr(''); })
      .catch(e => setErr(e?.message || 'Endpoint missing'));
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);
  if (err) return (
    <section className="admin-section">
      <h3><i className="fa-solid fa-triangle-exclamation"/> Suspicious activity</h3>
      <div className="page-state err" style={{fontSize:12}}>
        Upload <code>api/admin/suspicious.php</code> to enable.
      </div>
    </section>
  );
  if (!items) return <section className="admin-section"><h3>Suspicious</h3><div className="page-state">Loading…</div></section>;
  const flagLabels = {
    burst_flip:     'BURST FLIPPING',
    wash_trade:     'WASH TRADING',
    win_rate_high:  'WIN RATE > 85%',
    rapid_signup:   'RAPID SIGNUP',
    ip_cluster:     'IP CLUSTER',
  };
  return (
    <section className="admin-section">
      <h3>
        <i className="fa-solid fa-triangle-exclamation"/> Suspicious activity
        <span className="adm-susp-count">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <div className="page-state" style={{fontSize:12,padding:'20px',textAlign:'center',color:'var(--g-500)'}}>
          <i className="fa-solid fa-check-circle" style={{color:'var(--emerald)',fontSize:18,marginRight:6}}/>
          No suspicious patterns detected.
        </div>
      ) : (
        <div className="adm-susp-list">
          {items.map(u => (
            <div key={u.user_id} className={`adm-susp-row sev-${Math.min(5, u.severity)}`}>
              <span className="adm-susp-sev" title={`Severity ${u.severity}`}>{u.severity}</span>
              <span className="adm-susp-user">@{u.username}</span>
              <div className="adm-susp-flags">
                {u.flags.map(f => (
                  <span key={f} className={`adm-susp-flag flag-${f}`}>{flagLabels[f] || f}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Item flip heatmap — popularity of items in completed flips. Backend
// endpoint: /api/admin/item-heatmap → [{ name, count, volume }, ...]
// Fails gracefully if endpoint isn't deployed yet.
function AdminItemHeatmap() {
  const [items, setItems] = React.useState(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    apiFetch('/api/admin/item-heatmap')
      .then(r => setItems(r.items || []))
      .catch(e => setErr(e?.message || 'Endpoint missing'));
  }, []);
  if (err) return (
    <section className="admin-section">
      <h3><i className="fa-solid fa-fire"/> Item heatmap</h3>
      <div className="page-state err" style={{fontSize:12}}>
        Upload <code>api/admin/item-heatmap.php</code> to enable.
      </div>
    </section>
  );
  if (!items) return <section className="admin-section"><h3>Item heatmap</h3><div className="page-state">Loading…</div></section>;
  const maxCount = Math.max(...items.map(it => Number(it.count) || 0), 1);
  return (
    <section className="admin-section">
      <h3><i className="fa-solid fa-fire"/> Item popularity heatmap</h3>
      <div className="adm-heatmap">
        {items.slice(0, 30).map(it => {
          const pct = (Number(it.count) || 0) / maxCount;
          return (
            <div key={it.name} className="adm-heat-row">
              <span className="adm-heat-name">{it.name}</span>
              <div className="adm-heat-bar">
                <div className="adm-heat-fill" style={{width: `${pct * 100}%`, background: `hsl(${30 + (1 - pct) * 30}, ${60 + pct * 40}%, ${50 - pct * 10}%)`}}/>
              </div>
              <span className="adm-heat-count">{Number(it.count).toLocaleString()}</span>
              <span className="adm-heat-vol"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.volume)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AdminEvents() {
  const [rows, setRows] = React.useState([]);
  const [draft, setDraft] = React.useState({ title: '', subtitle: '', cta_label: '', link: '', image_url: '', accent: 'orange', is_active: 1, sort_order: 0 });
  const reload = () => apiFetch('/api/admin/events').then(r => setRows(r.events || []));
  React.useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!draft.title.trim()) return;
    await apiFetch('/api/admin/events', { method: 'POST', body: draft });
    setDraft({ title: '', subtitle: '', cta_label: '', link: '', image_url: '', accent: 'orange', is_active: 1, sort_order: 0 });
    reload();
  };
  const toggle = async (ev) => {
    await apiFetch('/api/admin/events', { method: 'PUT', body: { ...ev, is_active: ev.is_active ? 0 : 1 } });
    reload();
  };
  const del = async (id) => {
    if (!confirm('Delete this event?')) return;
    await apiFetch('/api/admin/events', { method: 'DELETE', body: { id } });
    reload();
  };

  return (
    <section className="admin-section">
      <h3>Events & banners</h3>
      <div className="admin-event-form">
        <input className="settings-select" placeholder="Title" value={draft.title} onChange={(e) => setDraft({...draft, title: e.target.value})}/>
        <input className="settings-select" placeholder="Subtitle" value={draft.subtitle} onChange={(e) => setDraft({...draft, subtitle: e.target.value})}/>
        <input className="settings-select" placeholder="CTA label" value={draft.cta_label} onChange={(e) => setDraft({...draft, cta_label: e.target.value})}/>
        <input className="settings-select" placeholder="Link (e.g. /rewards)" value={draft.link} onChange={(e) => setDraft({...draft, link: e.target.value})}/>
        <input className="settings-select" placeholder="Image URL" value={draft.image_url} onChange={(e) => setDraft({...draft, image_url: e.target.value})}/>
        <button className="settings-btn" onClick={create}>+ Create</button>
      </div>
      <table className="admin-table">
        <thead><tr><th>ID</th><th>Title</th><th>Link</th><th>Active</th><th></th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td>{r.id}</td>
              <td>{r.title}</td>
              <td><code>{r.link}</code></td>
              <td>{r.is_active ? '✓' : '✗'}</td>
              <td>
                <button className="settings-btn" onClick={() => toggle(r)}>{r.is_active ? 'Disable' : 'Enable'}</button>
                <button className="settings-btn danger" onClick={() => del(r.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AdminUsers() {
  const [q, setQ] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [detailFor, setDetailFor] = React.useState(null);
  const [selected, setSelected] = React.useState(() => new Set());
  const [confirmState, setConfirmState] = React.useState({ open: false });
  const [alertState, setAlertState] = React.useState({ open: false });
  const load = async (query = '') => {
    const res = await apiFetch(`/api/admin/users?q=${encodeURIComponent(query)}`);
    setRows(res.users || []);
    setSelected(new Set()); // clear selection on reload
  };
  React.useEffect(() => { load(''); }, []);
  const act = async (userId, action) => {
    if (action === 'ban' && !confirm('Ban this user?')) return;
    if (action === 'admin' && !confirm('Make admin?')) return;
    await apiFetch('/api/admin/users', { method: 'POST', body: { user_id: userId, action } });
    load(q);
  };
  // Assign a staff role (user / mod / value_manager / admin).
  const setRole = async (userId, role) => {
    try {
      await apiFetch('/api/admin/users', { method: 'POST', body: { user_id: userId, action: 'set_role', role } });
      load(q);
    } catch (err) {
      setAlertState({ open: true, kind: 'warn', title: 'Could not set role', body: err.message || 'Failed' });
    }
  };

  // Bulk: ban / unban N users in parallel via the existing per-user endpoint.
  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllVisible = () => setSelected(new Set(rows.map(r => r.id)));
  const clearSel = () => setSelected(new Set());
  const bulkAct = (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setConfirmState({
      open: true,
      title: `${action === 'ban' ? 'Ban' : action === 'unban' ? 'Unban' : 'Promote'} ${ids.length} users?`,
      body: `This will ${action} ${ids.length} accounts. Continue?`,
      cta: action === 'ban' ? `Ban ${ids.length}` : `${action} ${ids.length}`,
      onConfirm: async () => {
        setConfirmState(s => ({ ...s, open: false }));
        const results = await Promise.allSettled(
          ids.map(id => apiFetch('/api/admin/users', { method: 'POST', body: { user_id: id, action } }))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        load(q);
        if (failed > 0) {
          setAlertState({ open: true, kind: 'warn', title: 'Partial result', body: `${ids.length - failed} ok, ${failed} failed.` });
        }
      },
    });
  };

  return (
    <section className="admin-section">
      <h3><i className="fa-solid fa-users"/> Users</h3>
      <div className="admin-search-row">
        <input className="settings-select" placeholder="Search by username" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(q)}/>
        <button className="settings-btn primary" onClick={() => load(q)}>Search</button>
      </div>

      {/* Bulk-action bar — appears when 1+ users are selected. */}
      {rows.length > 0 && (
        <div className="adm-msg-bulkbar" style={{marginBottom:12}}>
          <span className="adm-msg-bulkbar-count">
            {selected.size > 0 ? `${selected.size} selected` : `${rows.length} users`}
          </span>
          {selected.size > 0 ? (
            <>
              <button type="button" className="adm-bulk-btn danger" onClick={() => bulkAct('ban')}>
                <i className="fa-solid fa-ban"/> Ban selected
              </button>
              <button type="button" className="adm-bulk-btn" onClick={() => bulkAct('unban')}>
                <i className="fa-solid fa-rotate-left"/> Unban selected
              </button>
              <button type="button" className="adm-bulk-btn" onClick={clearSel}>Clear</button>
            </>
          ) : (
            <button type="button" className="adm-bulk-btn" onClick={selectAllVisible}>
              <i className="fa-regular fa-square-check"/> Select all
            </button>
          )}
        </div>
      )}

      <div className="admin-users-grid">
        {rows.map(r => {
          const isSel = selected.has(r.id);
          return (
            <div key={r.id} className={`admin-user-card ${r.is_banned ? 'is-banned' : ''} ${r.is_admin ? 'is-admin' : ''} ${isSel ? 'is-selected' : ''}`}>
              <input
                type="checkbox"
                className="adm-user-check"
                checked={isSel}
                onChange={() => toggleSel(r.id)}
                aria-label={`select ${r.username}`}
              />
              <div className="auc-head">
                <button type="button" className="auc-name" onClick={() => setDetailFor(r.id)} title="Open dossier">
                  @{r.username}
                </button>
                <div className="auc-tags">
                  {r.is_admin   && <span className="auc-tag admin">ADMIN</span>}
                  {!r.is_admin && r.role === 'mod' && <span className="auc-tag mod">MOD</span>}
                  {!r.is_admin && r.role === 'value_manager' && <span className="auc-tag vm">VALUE MGR</span>}
                  {r.is_banned  && <span className="auc-tag banned">BANNED</span>}
                </div>
              </div>
              <div className="auc-stats">
                <div><span className="lbl">Flips</span><span className="val">{r.total_flips}</span></div>
                <div><span className="lbl">Wins</span><span className="val">{r.total_wins}</span></div>
                <div><span className="lbl">Value</span><span className="val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(Number(r.total_value))}</span></div>
                <div><span className="lbl">ID</span><span className="val">#{r.id}</span></div>
              </div>
              <div className="auc-actions">
                <button className="settings-btn" onClick={() => setDetailFor(r.id)} aria-label={`open detail for ${r.username}`}><i className="fa-solid fa-eye"/> Detail</button>
                <button className={`settings-btn ${r.is_banned ? '' : 'danger'}`} onClick={() => act(r.id, r.is_banned ? 'unban' : 'ban')} aria-label={`${r.is_banned ? 'unban' : 'ban'} ${r.username}`}>
                  {r.is_banned ? 'Unban' : 'Ban'}
                </button>
                <button className="settings-btn" onClick={() => act(r.id, r.is_admin ? 'unadmin' : 'admin')} aria-label={`${r.is_admin ? 'demote' : 'promote'} ${r.username}`}>
                  {r.is_admin ? 'Demote' : 'Promote'}
                </button>
                {/* Staff role selector — assign mod / value manager / admin. */}
                <select
                  className="auc-role-select"
                  value={r.is_admin ? 'admin' : (r.role || 'user')}
                  onChange={(e) => setRole(r.id, e.target.value)}
                  aria-label={`role for ${r.username}`}
                >
                  <option value="user">User</option>
                  <option value="mod">Mod</option>
                  <option value="value_manager">Value Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          );
        })}
      </div>
      {detailFor && <AdminUserDetail id={detailFor} onClose={() => setDetailFor(null)}/>}
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        body={confirmState.body}
        cta={confirmState.cta}
        kind="danger"
        onConfirm={() => confirmState.onConfirm?.()}
        onCancel={() => setConfirmState(s => ({ ...s, open: false }))}
      />
      <AlertModal
        open={alertState.open}
        title={alertState.title}
        body={alertState.body}
        kind={alertState.kind}
        onClose={() => setAlertState(s => ({ ...s, open: false }))}
      />
    </section>
  );
}

// ============================================
// ADMIN: USER DETAIL — full dossier modal (clicking a user card)
// Shows flips, chat messages, sessions/IPs, totals won/lost, inventory.
// ============================================
function AdminUserDetail({ id, onClose }) {
  const [data, setData] = React.useState(null);
  const [tab, setTab] = React.useState('overview');
  const [msgSearch, setMsgSearch] = React.useState('');
  const [msgChannel, setMsgChannel] = React.useState('all');
  const [errMsg, setErrMsg] = React.useState('');
  // Messages the admin has marked for bulk delete. Set, not array, for O(1)
  // membership tests in the row checkbox.
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  // Optimistic-deleted ids — we hide them from the UI immediately and remove
  // them from data.messages after the server confirms.
  const [deletingIds, setDeletingIds] = React.useState(() => new Set());
  // Custom in-app confirm + alert dialogs (replaces window.confirm/alert
  // which on iOS PWA pops a system dialog that can ignore taps + render
  // off-axis on a notched device).
  const [confirmState, setConfirmState] = React.useState({ open: false });
  const [alertState, setAlertState]     = React.useState({ open: false });
  // Safe date formatters — backend rows can legitimately have NULL timestamps
  // (e.g. last_login_at if user never returned after first signup). new Date(null)
  // returns 1970-01-01 and toLocale*() on an invalid date returns "Invalid Date",
  // neither of which we want to render. Show an em-dash instead.
  const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };
  const fmtDateTime = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  const reload = React.useCallback(async (q = '') => {
    setErrMsg('');
    try {
      const url = q
        ? `/api/admin/user-detail?id=${id}&q=${encodeURIComponent(q)}`
        : `/api/admin/user-detail?id=${id}`;
      const res = await apiFetch(url);
      // Defensive: backend may be old version → fill missing keys with safe defaults
      setData({
        user:         res.user || null,
        flips:        Array.isArray(res.flips) ? res.flips : [],
        messages:     Array.isArray(res.messages) ? res.messages : [],
        msg_channels: Array.isArray(res.msg_channels) ? res.msg_channels : [],
        sessions:     Array.isArray(res.sessions) ? res.sessions : [],
        inventory:    res.inventory && typeof res.inventory === 'object' ? res.inventory : { count: 0, total_value: 0 },
        totals:       res.totals && typeof res.totals === 'object' ? res.totals : { won: 0, lost: 0 },
      });
    } catch (err) {
      setErrMsg(err && err.message ? err.message : 'Endpoint not found — upload api/admin/user-detail.php');
      setData({ error: true });
    }
  }, [id]);
  React.useEffect(() => { reload(''); }, [reload]);

  // Channel-colored chip helper
  const channelColor = (ch) => ({
    global: '#10B981', tr: '#E30A17', en: '#1E40AF', ru: '#0033A0',
    es: '#AA151B', it: '#008C45', fr: '#0055A4', de: '#000000',
    ph: '#0038A8', zh: '#DE2910', ja: '#BC002D', uk: '#005BBB',
    ar: '#007A3D', ur: '#01411C',
  }[ch] || '#6B7280');

  // Filtered messages for the chat tab (channel filter is client-side;
  // text search hits the backend via reload). Optimistically-deleted ids
  // are hidden so the row vanishes the moment admin clicks the trash.
  const filteredMsgs = data?.messages
    ? data.messages
        .filter(m => !deletingIds.has(m.id))
        .filter(m => msgChannel === 'all' ? true : m.channel === msgChannel)
    : [];

  // Net profit/loss for the overview card. Positive means user is up overall.
  const netPL = data?.totals
    ? (Number(data.totals.won) || 0) - (Number(data.totals.lost) || 0)
    : 0;

  // Single-message delete. Optimistic: hide row → call API → on success
  // remove from data, on fail restore. Server endpoint: DELETE /api/admin/chat
  // NOTE: pass body as an OBJECT — apiFetch does JSON.stringify internally.
  // Passing JSON.stringify(...) here would double-stringify (server sees a
  // JSON-encoded string instead of {id:N}) and every delete returned bad_id.
  const deleteMessage = async (msgId) => {
    setDeletingIds(prev => { const n = new Set(prev); n.add(msgId); return n; });
    try {
      await apiFetch('/api/admin/chat', { method: 'DELETE', body: { id: msgId } });
      setData(d => d && ({ ...d, messages: d.messages.filter(m => m.id !== msgId) }));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(msgId); return n; });
    } catch (err) {
      // Roll back the hide on error
      setDeletingIds(prev => { const n = new Set(prev); n.delete(msgId); return n; });
      setAlertState({ open: true, kind: 'error', title: 'Delete failed', body: err?.message || 'Server returned an error.' });
    } finally {
      setDeletingIds(prev => { const n = new Set(prev); n.delete(msgId); return n; });
    }
  };

  // Bulk delete — runs the singular endpoint in parallel. Custom confirm
  // modal (not browser confirm()) so the dialog has site styling and stays
  // tappable on mobile (browser confirm()s on iOS PWA can be janky).
  const deleteIds = async (ids) => {
    if (ids.length === 0) return;
    setConfirmState({
      open: true,
      title: 'Delete messages?',
      body: `This will permanently delete ${ids.length} message${ids.length === 1 ? '' : 's'}. This cannot be undone.`,
      cta: `Delete ${ids.length}`,
      onConfirm: async () => {
        setConfirmState(s => ({ ...s, open: false }));
        setDeletingIds(prev => { const n = new Set(prev); ids.forEach(i => n.add(i)); return n; });
        const results = await Promise.allSettled(
          ids.map(i => apiFetch('/api/admin/chat', { method: 'DELETE', body: { id: i } }))
        );
        const failedIds = ids.filter((id, idx) => results[idx].status === 'rejected');
        const succeededIds = ids.filter((id, idx) => results[idx].status === 'fulfilled');
        setData(d => d && ({ ...d, messages: d.messages.filter(m => !succeededIds.includes(m.id)) }));
        setSelectedIds(prev => { const n = new Set(prev); succeededIds.forEach(i => n.delete(i)); return n; });
        setDeletingIds(prev => { const n = new Set(prev); succeededIds.forEach(i => n.delete(i)); failedIds.forEach(i => n.delete(i)); return n; });
        if (failedIds.length > 0) {
          setAlertState({
            open: true,
            kind: failedIds.length === ids.length ? 'error' : 'warn',
            title: 'Partial result',
            body: `${succeededIds.length} deleted, ${failedIds.length} failed.`,
          });
        }
      },
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      filteredMsgs.forEach(m => n.add(m.id));
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal admin-detail-modal">
        {/* Premium gradient header — orange spotlight on the user's identity */}
        <div className="adm-detail-head">
          <div className="adm-detail-head-bg" aria-hidden="true"/>
          {data?.user?.avatar_url
            ? <img className="adm-detail-av" src={data.user.avatar_url} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
            : <div className="adm-detail-av adm-detail-av-fallback">{(data?.user?.username || '??').slice(0,2).toUpperCase()}</div>}
          <div className="adm-detail-id">
            <div className="adm-detail-name">
              @{data?.user?.username || '…'}
              {data?.user?.is_admin   && <span className="auc-tag admin">ADMIN</span>}
              {data?.user?.is_banned  && <span className="auc-tag banned">BANNED</span>}
            </div>
            <div className="adm-detail-sub">
              #{id} · {data?.user ? <>Lv {data.user.level || 1} · {data.user.total_flips || 0} flips · joined {fmtDate(data.user.created_at)}</> : 'loading…'}
            </div>
          </div>
          <button className="modal-close adm-detail-close" onClick={onClose} aria-label="close user detail"><i className="fa-solid fa-xmark"/></button>
        </div>
        {!data && <div className="page-state">Loading…</div>}
        {data?.error && (
          <div className="page-state err" style={{padding:'40px 24px',textAlign:'center'}}>
            <div style={{fontSize:32,marginBottom:8}}>⚠️</div>
            <div style={{fontWeight:800,marginBottom:6}}>Could not load user detail</div>
            <div style={{fontSize:12,color:'var(--g-600)'}}>{errMsg || 'Server returned an error.'}</div>
            <div style={{fontSize:11,color:'var(--g-500)',marginTop:8}}>
              Make sure <code>api/admin/user-detail.php</code> is uploaded to your server.
            </div>
          </div>
        )}
        {data?.user && (
          <div className="admin-detail-body">
            <div className="adm-tabs">
              {['overview','flips','chat','sessions'].map(k => (
                <button key={k} type="button" className={`adm-tab ${tab===k?'is-active':''}`} onClick={() => setTab(k)}>
                  {k === 'overview' && <><i className="fa-solid fa-chart-line"/> Overview</>}
                  {k === 'flips'    && <><i className="fa-solid fa-coins"/> Flips <span className="adm-tab-count">{data.flips.length}</span></>}
                  {k === 'chat'     && <><i className="fa-solid fa-comment-dots"/> Messages <span className="adm-tab-count">{data.messages.length}</span></>}
                  {k === 'sessions' && <><i className="fa-solid fa-network-wired"/> Sessions <span className="adm-tab-count">{data.sessions.length}</span></>}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="adm-overview">
                <div className="adm-card">
                  <div className="lbl">Total wagered</div>
                  <div className="val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.user.total_value)}</div>
                </div>
                <div className="adm-card good">
                  <div className="lbl">Total won (pot)</div>
                  <div className="val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.totals.won)}</div>
                </div>
                <div className="adm-card bad">
                  <div className="lbl">Total lost</div>
                  <div className="val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.totals.lost)}</div>
                </div>
                {/* Net profit / loss — green if up, red if down. Computed
                    client-side from totals.won - totals.lost so it stays in
                    sync with the two cards above even after a refresh. */}
                <div className={`adm-card ${netPL > 0 ? 'good' : netPL < 0 ? 'bad' : ''}`}>
                  <div className="lbl">Net P / L</div>
                  <div className="val">
                    {netPL >= 0 ? '+' : '−'}
                    <i className="fa-solid fa-gem gem-i"/>{fmtVal(Math.abs(netPL))}
                  </div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Win rate</div>
                  <div className="val">{data.user.total_flips > 0 ? Math.round((data.user.total_wins / data.user.total_flips) * 100) : 0}%</div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Biggest win</div>
                  <div className="val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.user.biggest_win)}</div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Inventory items</div>
                  <div className="val">{data.inventory.count} · <i className="fa-solid fa-gem gem-i"/>{fmtVal(data.inventory.total_value)}</div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Level / XP</div>
                  <div className="val">Lv {data.user.level} · {data.user.xp} XP</div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Referrals invited</div>
                  <div className="val">{data.user.referrals_count}</div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Roblox ID</div>
                  <div className="val">{data.user.roblox_id || '—'}</div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Joined</div>
                  <div className="val">{fmtDate(data.user.created_at)}</div>
                </div>
                <div className="adm-card">
                  <div className="lbl">Last seen</div>
                  <div className="val">{fmtDateTime(data.user.last_login_at)}</div>
                </div>
              </div>
            )}

            {tab === 'flips' && (
              <div className="adm-flip-list">
                {data.flips.length === 0 && <div className="page-state">No flips yet.</div>}
                {data.flips.map(f => (
                  <div key={f.id} className={`adm-flip-row ${f.won?'won':''} ${f.lost?'lost':''}`}>
                    <div className="adm-flip-id">#{f.id}</div>
                    <div className="adm-flip-side">vs @{f.opponent || '—'}</div>
                    <div className="adm-flip-stake">stake <b><i className="fa-solid fa-gem gem-i"/>{fmtVal(f.stake)}</b></div>
                    <div className="adm-flip-pot">pot <b><i className="fa-solid fa-gem gem-i"/>{fmtVal(f.pot)}</b></div>
                    <div className="adm-flip-state">
                      {f.won  && <span className="adm-state-pill won"><i className="fa-solid fa-check"/> WON</span>}
                      {f.lost && <span className="adm-state-pill lost"><i className="fa-solid fa-xmark"/> LOST</span>}
                      {!f.won && !f.lost && <span className="adm-state-pill">{f.state}</span>}
                    </div>
                    <div className="adm-flip-date">{fmtDateTime(f.created_at)}</div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'chat' && (
              <div className="adm-chat-wrap">
                {/* Per-channel activity summary */}
                {data.msg_channels && data.msg_channels.length > 0 && (
                  <div className="adm-msg-channels-bar">
                    <button
                      type="button"
                      className={`adm-msg-ch-pill ${msgChannel==='all'?'is-active':''}`}
                      onClick={() => setMsgChannel('all')}
                    >
                      <span>All</span>
                      <span className="adm-msg-ch-count">{data.messages.length}</span>
                    </button>
                    {data.msg_channels.map(c => (
                      <button
                        key={c.channel}
                        type="button"
                        className={`adm-msg-ch-pill ${msgChannel===c.channel?'is-active':''}`}
                        onClick={() => setMsgChannel(c.channel)}
                        style={{ '--ch-color': channelColor(c.channel) }}
                      >
                        <span className="adm-msg-ch-dot" style={{background: channelColor(c.channel)}}/>
                        <span>{c.channel}</span>
                        <span className="adm-msg-ch-count">{c.count}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Search inside this user's messages */}
                <div className="adm-msg-search">
                  <i className="fa-solid fa-magnifying-glass"/>
                  <input
                    type="text"
                    placeholder="Search messages by text…"
                    value={msgSearch}
                    onChange={(e) => setMsgSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && reload(msgSearch)}
                  />
                  <button className="settings-btn" onClick={() => reload(msgSearch)}>Search</button>
                  {msgSearch && (
                    <button className="settings-btn" onClick={() => { setMsgSearch(''); reload(''); }}>Clear</button>
                  )}
                </div>

                {/* Bulk-action bar — only shown when there's at least one
                    visible message. "Delete all visible" deletes everything
                    currently passing the channel + search filters; "Delete
                    selected" only the checked ones. Both confirm first. */}
                {filteredMsgs.length > 0 && (
                  <div className="adm-msg-bulkbar">
                    <span className="adm-msg-bulkbar-count">
                      {selectedIds.size > 0
                        ? `${selectedIds.size} selected`
                        : `${filteredMsgs.length} message${filteredMsgs.length === 1 ? '' : 's'}`}
                    </span>
                    {selectedIds.size > 0 ? (
                      <>
                        <button
                          type="button"
                          className="adm-bulk-btn danger"
                          onClick={() => deleteIds(Array.from(selectedIds))}
                        >
                          <i className="fa-solid fa-trash"/> Delete selected
                        </button>
                        <button type="button" className="adm-bulk-btn" onClick={clearSelection}>
                          Clear
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="adm-bulk-btn" onClick={selectAllVisible}>
                          <i className="fa-regular fa-square-check"/> Select all visible
                        </button>
                        <button
                          type="button"
                          className="adm-bulk-btn danger"
                          onClick={() => deleteIds(filteredMsgs.map(m => m.id))}
                        >
                          <i className="fa-solid fa-trash"/> Delete all visible
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Message list (Discord-like) */}
                <div className="adm-msg-list">
                  {filteredMsgs.length === 0 && <div className="page-state">No messages match.</div>}
                  {filteredMsgs.map(m => {
                    const text = m.text || '';
                    const flags = [];
                    if (/\b[a-z0-9-]+\.(com|net|io|gg|xyz|info|biz|store|shop|live|app|me|co|tv|club|online)\b/i.test(text)) flags.push('link');
                    if (/(t\.me\/|telegram\.me\/|wa\.me\/|discord\.gg\/)/i.test(text)) flags.push('invite');
                    if (/\b(free\s*(robux|items|skin)|airdrop)\b/i.test(text)) flags.push('scam');
                    const selected = selectedIds.has(m.id);
                    return (
                      <div key={m.id} className={`adm-msg ${flags.length ? 'is-flagged' : ''} ${selected ? 'is-selected' : ''}`}>
                        <input
                          type="checkbox"
                          className="adm-msg-check"
                          checked={selected}
                          onChange={() => toggleSelect(m.id)}
                          aria-label="select message"
                        />
                        <span
                          className="adm-msg-ch"
                          style={{ background: channelColor(m.channel), color: '#fff' }}
                        >{m.channel}</span>
                        <div className="adm-msg-body">
                          <div className="adm-msg-text">{text}</div>
                          {flags.length > 0 && (
                            <div className="adm-msg-flags">
                              {flags.map(f => <span key={f} className={`adm-flag flag-${f}`}>{f}</span>)}
                            </div>
                          )}
                        </div>
                        <span className="adm-msg-date" title={fmtDateTime(m.created_at)}>
                          {fmtDateTime(m.created_at)}
                        </span>
                        <button
                          type="button"
                          className="adm-msg-del"
                          title="Delete this message"
                          onClick={() => deleteMessage(m.id)}
                        >
                          <i className="fa-solid fa-trash"/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'sessions' && (
              <div className="adm-session-list">
                {data.sessions.length === 0 && <div className="page-state">No active sessions.</div>}
                {data.sessions.map((s, i) => (
                  <div key={i} className="adm-session">
                    <div className="adm-ip"><i className="fa-solid fa-globe"/> {s.ip || '—'}</div>
                    <div className="adm-ua">{(s.user_agent || '').slice(0, 90)}…</div>
                    <div className="adm-meta">
                      <span>created {fmtDateTime(s.created_at)}</span>
                      <span>last seen {fmtDateTime(s.last_seen_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* In-app confirm + alert (portal-mounted via the components) */}
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        body={confirmState.body}
        cta={confirmState.cta}
        kind="danger"
        onConfirm={() => confirmState.onConfirm?.()}
        onCancel={() => setConfirmState(s => ({ ...s, open: false }))}
      />
      <AlertModal
        open={alertState.open}
        title={alertState.title}
        body={alertState.body}
        kind={alertState.kind}
        onClose={() => setAlertState(s => ({ ...s, open: false }))}
      />
    </div>,
    document.body
  );
}

// ============================================
// ADMIN: CHAT MODERATION — all channels, flag spam/ads, delete
// ============================================
function AdminChatMod() {
  const [channel, setChannel] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [flaggedOnly, setFlaggedOnly] = React.useState(false);
  const [msgs, setMsgs] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [selected, setSelected] = React.useState(() => new Set());
  const [confirmState, setConfirmState] = React.useState({ open: false });
  const [alertState, setAlertState]     = React.useState({ open: false });

  const reload = React.useCallback(async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      if (channel !== 'all') qs.set('channel', channel);
      if (search) qs.set('search', search);
      if (flaggedOnly) qs.set('flagged', '1');
      qs.set('limit', '200');
      const res = await apiFetch('/api/admin/chat?' + qs.toString());
      setMsgs(res.messages || []);
      setSelected(new Set());
    } catch (_) {}
    setBusy(false);
  }, [channel, search, flaggedOnly]);
  React.useEffect(() => { reload(); }, [reload]);

  // Instant single-message delete — no confirm. Optimistic.
  const removeMsg = async (id) => {
    const prev = msgs;
    setMsgs(p => p.filter(m => m.id !== id));
    setSelected(s => { const n = new Set(s); n.delete(id); return n; });
    try {
      await apiFetch('/api/admin/chat', { method: 'DELETE', body: { id } });
    } catch (err) {
      setMsgs(prev);  // restore on failure
      setAlertState({ open: true, kind: 'error', title: 'Delete failed', body: err?.message || 'Server returned an error.' });
    }
  };

  // Bulk delete — custom confirm modal (no browser confirm()).
  const removeMany = (ids) => {
    if (ids.length === 0) return;
    setConfirmState({
      open: true,
      title: 'Delete messages?',
      body: `This will permanently delete ${ids.length} message${ids.length === 1 ? '' : 's'}. This cannot be undone.`,
      cta: `Delete ${ids.length}`,
      onConfirm: async () => {
        setConfirmState(s => ({ ...s, open: false }));
        setMsgs(p => p.filter(m => !ids.includes(m.id)));
        setSelected(new Set());
        const results = await Promise.allSettled(
          ids.map(i => apiFetch('/api/admin/chat', { method: 'DELETE', body: { id: i } }))
        );
        const failedCount = results.filter(r => r.status === 'rejected').length;
        if (failedCount > 0) {
          setAlertState({
            open: true,
            kind: failedCount === ids.length ? 'error' : 'warn',
            title: 'Partial result',
            body: `${ids.length - failedCount} deleted, ${failedCount} failed.`,
          });
        }
      },
    });
  };

  const toggle = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAll  = () => setSelected(new Set(msgs.map(m => m.id)));
  const clearSel   = () => setSelected(new Set());

  const CHANNELS = ['all','global','tr','en','ru','es','it','fr','de','ph','zh','ja','uk','ar','ur'];

  // Safe date formatter (same pattern as AdminUserDetail)
  const fmtDT = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d.getTime()) ? '—' : d.toLocaleString(); };

  return (
    <section className="admin-section">
      <h3><i className="fa-solid fa-comment-dots"/> Chat moderation</h3>
      <div className="admin-chat-filters">
        <select className="settings-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
          {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="settings-select" placeholder="Search text or username…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && reload()}/>
        <label className="admin-flag-toggle">
          <input type="checkbox" checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)}/>
          <span><i className="fa-solid fa-triangle-exclamation"/> Flagged only</span>
        </label>
        <button className="settings-btn primary" onClick={reload} disabled={busy}>{busy ? 'Loading…' : 'Reload'}</button>
      </div>

      {/* Bulk-action bar — mirrors the user-detail chat tab. Swaps between
          "Select all / Delete all visible" and "X selected → Delete selected". */}
      {msgs.length > 0 && (
        <div className="adm-msg-bulkbar">
          <span className="adm-msg-bulkbar-count">
            {selected.size > 0
              ? `${selected.size} selected`
              : `${msgs.length} message${msgs.length === 1 ? '' : 's'}`}
          </span>
          {selected.size > 0 ? (
            <>
              <button type="button" className="adm-bulk-btn danger" onClick={() => removeMany(Array.from(selected))}>
                <i className="fa-solid fa-trash"/> Delete selected
              </button>
              <button type="button" className="adm-bulk-btn" onClick={clearSel}>Clear</button>
            </>
          ) : (
            <>
              <button type="button" className="adm-bulk-btn" onClick={selectAll}>
                <i className="fa-regular fa-square-check"/> Select all visible
              </button>
              <button type="button" className="adm-bulk-btn danger" onClick={() => removeMany(msgs.map(m => m.id))}>
                <i className="fa-solid fa-trash"/> Delete all visible
              </button>
            </>
          )}
        </div>
      )}

      <div className="admin-chat-list">
        {msgs.length === 0 && <div className="page-state">No messages match.</div>}
        {msgs.map(m => (
          <div key={m.id} className={`adm-chat-row ${m.flags.length ? 'is-flagged' : ''} ${m.is_banned ? 'user-banned' : ''} ${selected.has(m.id) ? 'is-selected' : ''}`}>
            <input
              type="checkbox"
              className="adm-msg-check"
              checked={selected.has(m.id)}
              onChange={() => toggle(m.id)}
              aria-label="select message"
            />
            <div className="adm-chat-meta">
              <span className="adm-chat-channel">{m.channel}</span>
              {m.username
                ? <span className="adm-chat-user">@{m.username}</span>
                : <span className="adm-chat-user system">SYSTEM</span>}
              <span className="adm-chat-date">{fmtDT(m.created_at)}</span>
            </div>
            <div className="adm-chat-text">{m.text}</div>
            {m.flags.length > 0 && (
              <div className="adm-chat-flags">
                {m.flags.map(f => <span key={f} className={`adm-flag flag-${f}`}>{f.replace('_',' ')}</span>)}
              </div>
            )}
            <button className="adm-chat-del" onClick={() => removeMsg(m.id)} title="Delete instantly">
              <i className="fa-solid fa-trash"/>
            </button>
          </div>
        ))}
      </div>
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        body={confirmState.body}
        cta={confirmState.cta}
        kind="danger"
        onConfirm={() => confirmState.onConfirm?.()}
        onCancel={() => setConfirmState(s => ({ ...s, open: false }))}
      />
      <AlertModal
        open={alertState.open}
        title={alertState.title}
        body={alertState.body}
        kind={alertState.kind}
        onClose={() => setAlertState(s => ({ ...s, open: false }))}
      />
    </section>
  );
}

// ============================================
// ACHIEVEMENTS — catalog fetched once from /api/achievements
// ============================================
function AchievementsGrid({ unlocked, t }) {
  const [catalog, setCatalog] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    apiFetch('/api/achievements')
      .then(res => { if (!cancelled) setCatalog(res.achievements || []); })
      .catch(() => { if (!cancelled) setCatalog([]); });
    return () => { cancelled = true; };
  }, []);
  if (catalog === null) return <div className="page-state">{t?.loading || 'Loading…'}</div>;
  if (catalog.length === 0) return null;
  // Sort: unlocked first (most recent), then locked. Inside each group keep catalog order.
  const sorted = [...catalog].sort((a, b) => {
    const ua = unlocked.has(a.code) ? 1 : 0;
    const ub = unlocked.has(b.code) ? 1 : 0;
    return ub - ua;
  });
  return (
    <div className="ach-cards">
      {sorted.map(a => {
        const got = unlocked.has(a.code);
        return (
          <div
            key={a.code}
            className={`ach-card-new ${got ? `is-unlocked tier-${a.tier}` : 'is-locked'}`}
            title={a.desc}
          >
            <div className="ach-icon-wrap">
              <i className={`fa-solid ${a.icon}`}/>
            </div>
            <div className="ach-meta">
              <div className="ach-title">{a.title}</div>
              <div className="ach-desc">{a.desc}</div>
            </div>
            {got && <span className="ach-tick"><i className="fa-solid fa-check"/></span>}
            {!got && <span className="ach-lock"><i className="fa-solid fa-lock"/></span>}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// PROFILE PAGE — own or public via ?u=username
// ============================================
// Banner config — 8 presets, level-gated. Drop banner1.webp..banner8.webp
// into assets/images/ to override the gradient fallbacks.
const BANNER_PRESETS = [
  { n: 1, name: 'Sunrise',   minLevel: 1 },
  { n: 2, name: 'Ocean',     minLevel: 1 },
  { n: 3, name: 'Forest',    minLevel: 5 },
  { n: 4, name: 'Magenta',   minLevel: 15 },
  { n: 5, name: 'Inferno',   minLevel: 30 },
  { n: 6, name: 'Midnight',  minLevel: 50 },
  { n: 7, name: 'Phoenix',   minLevel: 75 },
  { n: 8, name: 'Diamond',   minLevel: 100 },
];

function BannerPicker({ currentBanner, userLevel, onPick, onClose, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const select = async (n) => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      await apiFetch('/api/profile-customize', { method: 'POST', body: { profile_banner: n } });
      onPick && onPick(n);
      animateThen(onClose);
    } catch (e) {
      setErr(e.message || 'Failed');
      setBusy(false);
    }
  };

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal banner-picker-modal" ref={sheetRef} style={{maxWidth: 640}}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>🎨 {t?.bannerPick || 'Pick a banner'}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body">
          <div className="bp-grid">
            {BANNER_PRESETS.map(b => {
              const locked = userLevel < b.minLevel;
              const active = b.n === currentBanner;
              return (
                <button
                  key={b.n}
                  className={`bp-cell prof-banner-${b.n} ${locked ? 'is-locked' : ''} ${active ? 'is-active' : ''}`}
                  disabled={locked || busy}
                  onClick={() => select(b.n)}
                  title={locked ? `Unlocks at Lv ${b.minLevel}` : b.name}
                >
                  <div className="bp-cell-overlay">
                    <div className="bp-cell-name">{b.name}</div>
                    {locked
                      ? <div className="bp-cell-lock"><i className="fa-solid fa-lock"/> Lv {b.minLevel}</div>
                      : active ? <div className="bp-cell-active"><i className="fa-solid fa-check"/> Equipped</div>
                      : <div className="bp-cell-pick">Pick</div>}
                  </div>
                </button>
              );
            })}
          </div>
          {err && <div className="bp-err">{err}</div>}
        </div>
      </div>
    </div>
  );
}

// Wishlist section on a profile. If editable=true, the owner can add/remove
// item names from a typeahead picker; otherwise it's a read-only chip list.
function WishlistSection({ initial, editable, t }) {
  const [items, setItems] = React.useState(initial || []);
  const [adding, setAdding] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [err, setErr] = React.useState('');

  // Pull all known item names from the global values map (server-injected).
  const allItemNames = React.useMemo(() => {
    try {
      const m = window.FLIPLINE_ITEMS || {};
      return Object.keys(m).sort();
    } catch (_) { return []; }
  }, []);

  const suggestions = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allItemNames
      .filter(n => n.toLowerCase().includes(q) && !items.includes(n))
      .slice(0, 8);
  }, [query, allItemNames, items]);

  const add = async (name) => {
    if (!name || items.includes(name)) return;
    setErr('');
    try {
      await apiFetch('/api/wishlist', { method: 'POST', body: { item_name: name, action: 'add' } });
      setItems(prev => [name, ...prev]);
      setQuery('');
    } catch (e) {
      setErr(e.message || 'Failed');
    }
  };
  const remove = async (name) => {
    setErr('');
    try {
      // POST with action: 'remove' — Hostinger / some shared hosts block DELETE.
      await apiFetch('/api/wishlist', { method: 'POST', body: { item_name: name, action: 'remove' } });
      setItems(prev => prev.filter(n => n !== name));
    } catch (e) {
      setErr(e.message || 'Failed');
    }
  };

  // Build a filtered list of items with their values/rarities for the picker
  const pickerItems = React.useMemo(() => {
    const map = window.FLIPLINE_ITEMS || {};
    const q = query.trim().toLowerCase();
    const out = [];
    for (const name of Object.keys(map).sort()) {
      if (items.includes(name)) continue;
      if (q && !name.toLowerCase().includes(q)) continue;
      const entry = map[name] || {};
      out.push({
        name,
        price: Number(entry.price ?? entry.value ?? entry.v ?? 0),
        rarity: entry.rarity || entry.r || '',
      });
      if (out.length >= 60) break;
    }
    return out;
  }, [query, items]);

  return (
    <div className="wishlist-section">
      <div className="showcase-head">
        <h3>🌟 Wishlist <span style={{fontSize:12, fontWeight:500, color:'var(--g-500)'}}>· {items.length}/6</span></h3>
        {editable && (
          <button className="wl-add-btn" onClick={() => setAdding(a => !a)}>
            {adding ? <><i className="fa-solid fa-xmark"/> {t?.close || 'Close'}</> : <><i className="fa-solid fa-plus"/> {t?.wlAdd || 'Add items'}</>}
          </button>
        )}
      </div>

      {editable && adding && (
        <div className="wl-picker">
          <div className="wl-picker-search">
            <i className="fa-solid fa-magnifying-glass"/>
            <input
              className="wl-input"
              autoFocus
              placeholder={t?.wlPick || 'Search items…'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              maxLength={120}
            />
            {query && (
              <button className="wl-clear" onClick={() => setQuery('')} aria-label="clear"><i className="fa-solid fa-xmark"/></button>
            )}
          </div>
          {pickerItems.length === 0 ? (
            <div className="wl-picker-empty">{t?.wlNoResults || 'No items match.'}</div>
          ) : (
            <div className="wl-picker-grid">
              {pickerItems.map(it => (
                <button
                  key={it.name}
                  className={`wl-pick-cell rarity-${it.rarity || 'common'}`}
                  onClick={() => add(it.name)}
                  title={`${it.name} · ${it.price}`}
                  disabled={items.length >= 12}
                >
                  <img
                    src={`assets/images/${it.name}.webp`}
                    alt={it.name}
                    className="wl-pick-img"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.opacity = 0; }}
                  />
                  <div className="wl-pick-name">{it.name}</div>
                  <div className="wl-pick-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="wishlist-empty">
          {editable
            ? (t?.wlEmptyMine || 'Add items you want — others get bonus XP when they tip them to you.')
            : (t?.wlEmpty || 'No wishlist yet.')}
        </div>
      ) : (
        <div className="wishlist-tiles">
          {items.map(name => (
            <div key={name} className="wl-tile">
              <img
                src={`assets/images/${name}.webp`}
                alt={name}
                className="wl-tile-img"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.opacity = 0; }}
              />
              <div className="wl-tile-name">{name}</div>
              {editable && (
                <button className="wl-tile-remove" onClick={() => remove(name)} aria-label="remove">
                  <i className="fa-solid fa-xmark"/>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {err && <div className="wl-err">{err}</div>}
    </div>
  );
}

// Replay-link copy button — uses React state for the "copied!" visual instead
// of mutating innerHTML on the DOM node. The old version was an XSS sink-in-
// waiting (if any user data ever flowed into the button content, the
// restore-innerHTML step would unsafely re-render it as HTML).
function ShareLinkButton({ flipId }) {
  const [copied, setCopied] = React.useState(false);
  const onClick = (e) => {
    e.stopPropagation();
    const url = window.location.origin + '/?flip=' + flipId;
    try { navigator.clipboard.writeText(url); } catch (_) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      className={`prr-share ${copied ? 'is-copied' : ''}`}
      title="Copy replay link"
      onClick={onClick}
    >
      <i className={copied ? 'fa-solid fa-check' : 'fa-solid fa-link'}/>
    </button>
  );
}

// CreateDiceModal — same inventory picker pattern as CreateModal (coinflip)
// + a rounds selector at the top. Picks items the user wants to wager;
// pot ends up being 2× total selected value once an opponent joins.
function CreateDiceModal({ onClose, onCreate, userInventory, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const [rounds, setRounds] = React.useState(3);
  const [selected, setSelected] = React.useState(new Set());
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState('high');

  const availableInventory = React.useMemo(() => (userInventory || []).filter(it => !it.lockedFlipId), [userInventory]);
  const visible = React.useMemo(() => {
    let arr = availableInventory.filter(it => !search || it.name.toLowerCase().includes(search.toLowerCase()));
    arr = [...arr].sort((a, b) => sort === 'high' ? b.price - a.price : a.price - b.price);
    return arr;
  }, [availableInventory, search, sort]);

  const selectedItems = availableInventory.filter(it => selected.has(it.id));
  const totalValue = selectedItems.reduce((s, it) => s + it.price, 0);
  const valid = selected.size > 0;
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => {
    if (selected.size === availableInventory.length) setSelected(new Set());
    else setSelected(new Set(availableInventory.map(it => it.id)));
  };
  const dragSel = useDragMultiSelect(toggle, (id) => selected.has(id));

  const submit = () => animateThen(() => onCreate({ rounds, item_ids: Array.from(selected) }));

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal modal--wide create-modal">
        <div className="modal-head">
          <h3>{tt('diceCreateTitle', 'Create dice match')}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="inv-body">
          <div className="inv-label">{tt('diceRounds', 'Rounds')}</div>
          <div className="ts-pick">
            {[1, 2, 3].map(n => (
              <button
                key={n}
                type="button"
                className={`ts-chip ${rounds === n ? 'is-active' : ''}`}
                onClick={() => setRounds(n)}
              >
                <i className="fa-solid fa-dice"/> {n} {n === 1 ? tt('diceRound', 'round') : tt('diceRoundsPl', 'rounds')}
              </button>
            ))}
          </div>

          <div className="inv-label" style={{marginTop: 4}}>{tt('diceSelectItems', 'Select items from your inventory')}</div>
          {availableInventory.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-illu">📦</div>
              <h3>{tt('createInvEmpty', 'Your inventory is empty')}</h3>
              <p>{tt('diceDepositFirst', 'Deposit items first, then you can wager them in dice matches.')}</p>
            </div>
          ) : (
            <>
              <div className="inv-controls">
                <div className="inv-search">
                  <I name="search" size={13} color="#838383"/>
                  <input placeholder="Search items.." value={search} onChange={(e) => setSearch(e.target.value)}/>
                </div>
                <CustomSelect
                  value={sort}
                  onChange={setSort}
                  ariaLabel="sort"
                  options={[
                    { value: 'high', label: 'High – Low' },
                    { value: 'low',  label: 'Low – High' },
                  ]}
                />
                <button className="inv-select-all" onClick={selectAll} style={{height: 42}}>
                  {selected.size === availableInventory.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="inv-grid" style={{maxHeight: '36vh'}} {...dragSel.gridProps}>
                {visible.map(it => (
                  <button
                    key={it.id}
                    type="button"
                    className={`inv-item ${selected.has(it.id) ? 'selected' : ''}`}
                    onClick={() => { if (!dragSel.shouldSuppressClick(it.id)) toggle(it.id); }}
                    {...dragSel.itemProps(it.id)}
                  >
                    <FlipItem item={it} size={56} noTooltip/>
                    <div className="inv-item-name" title={it.name}>{it.name}</div>
                    <div className="inv-item-price"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                  </button>
                ))}
              </div>
              <button className="modal-cta" disabled={!valid} onClick={submit}>
                {!valid
                  ? tt('selectAtLeastOne', 'Select at least 1 item')
                  : tt('diceOpenMatchValue', 'Open dice match · {n} items · {amount}', { n: selected.size, amount: fmtVal(totalValue) })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// JoinDiceModal — opponent picks items matching ±10% of creator's value.
function JoinDiceModal({ match, onClose, onJoin, userInventory, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  // Provably-fair hash shown BEFORE you commit — the server seed is already
  // hashed and committed for this match, so you can see the fingerprint up
  // front (the unhashed seed reveals only after the match resolves).
  const [showFair, setShowFair] = React.useState(false);
  const [selected, setSelected] = React.useState(new Set());
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState('high');
  const target = Number(match.creator_value) || 0;
  // ±5% — must match api/dice/join.php. Tight band because dice is flat 50/50,
  // so any value gap is free EV for the under-staker (see backend comment).
  const tolLow = target * 0.95;
  const tolHigh = target * 1.05;

  const availableInventory = React.useMemo(() => (userInventory || []).filter(it => !it.lockedFlipId), [userInventory]);
  const visible = React.useMemo(() => {
    let arr = availableInventory.filter(it => !search || it.name.toLowerCase().includes(search.toLowerCase()));
    arr = [...arr].sort((a, b) => sort === 'high' ? b.price - a.price : a.price - b.price);
    return arr;
  }, [availableInventory, search, sort]);

  const selectedItems = availableInventory.filter(it => selected.has(it.id));
  const totalValue = selectedItems.reduce((s, it) => s + it.price, 0);
  const inRange = totalValue >= tolLow && totalValue <= tolHigh;
  const tooLow  = totalValue < tolLow;
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const dragSel = useDragMultiSelect(toggle, (id) => selected.has(id));

  // Auto-pick — same 3-pass strategy as MatchModal (coinflip Join). Greedy
  // high-value first while staying ≤ tolHigh, then fill gaps, then accept
  // overshoot only as last resort.
  const MAX_AUTO_ITEMS = 50;
  const autoPick = () => {
    const sortedDesc = [...availableInventory].sort((a, b) => b.price - a.price);
    const sortedAsc  = [...availableInventory].sort((a, b) => a.price - b.price);
    const pickedIds = new Set();
    let acc = 0;
    for (const it of sortedDesc) {
      if (pickedIds.size >= MAX_AUTO_ITEMS) break;
      if (acc >= tolLow) break;
      if (acc + it.price > tolHigh) continue;
      pickedIds.add(it.id);
      acc += it.price;
    }
    if (acc < tolLow) {
      for (const it of sortedAsc) {
        if (pickedIds.size >= MAX_AUTO_ITEMS) break;
        if (pickedIds.has(it.id)) continue;
        if (acc + it.price > tolHigh) continue;
        pickedIds.add(it.id);
        acc += it.price;
        if (acc >= tolLow) break;
      }
    }
    if (acc < tolLow) {
      for (const it of sortedAsc) {
        if (pickedIds.size >= MAX_AUTO_ITEMS) break;
        if (pickedIds.has(it.id)) continue;
        pickedIds.add(it.id);
        acc += it.price;
        if (acc >= tolLow) break;
      }
    }
    setSelected(pickedIds);
  };

  // Pass the picked item OBJECTS (+ their total) alongside the ids so the
  // resolved modal can show YOUR items immediately — the /api/dice/join
  // response only returns rolls/winner/seed, not the items, so without this
  // the joiner's own column rendered "No items" until the next lobby poll.
  const submit = () => onJoin({
    id: match.id,
    item_ids: Array.from(selected),
    items: selectedItems.map(it => ({ inv_id: it.id, name: it.name, price: it.price, rarity: it.rarity, image: it.image })),
    value: totalValue,
    // Joiner's provably-fair seed — picked client-side without knowing the
    // committed server seed, so neither side can predict the dice.
    client_seed: randomHex(16),
  });

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal modal--wide create-modal">
        <div className="modal-head">
          <h3>{tt('diceJoinTitle', 'Join @{name}\'s dice match', { name: match.creator_username })}</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        {(match.server_seed_hash || match.id) && (() => {
          const fairnessHash = match.server_seed_hash || match.id || '';
          const display = String(fairnessHash).slice(0, 24) + (String(fairnessHash).length > 24 ? '…' : '');
          return (
            <button
              type="button"
              className="dm2-hash dm2-hash--link"
              style={{ margin: '6px auto 0' }}
              onClick={() => setShowFair(true)}
              title={tt('viewProof', 'View proof / fairness data')}
            >
              <i className="fa-solid fa-hashtag"/>
              <code>{display}</code>
            </button>
          );
        })()}
        <div className="inv-body">
          {/* Match target bar — identical structure to coinflip MatchModal so
              users get the same visual feedback (target value, ±10% range,
              live "your pick" total + progress bar with MIN / TARGET markers). */}
          <div className="match-target">
            <div className="mt-side">
              <div className="mt-label">{tt('youNeedToMatch', 'You need to match')}</div>
              <div className="mt-value"><i className="fa-solid fa-gem gem-i"/>{fmtVal(target)}</div>
            </div>
            <div className="mt-side">
              <div className="mt-label">{tt('rangeLabel', 'Range')} (±5%)</div>
              <div className="mt-range">{fmtVal(tolLow)} – {fmtVal(tolHigh)}</div>
            </div>
            <div className="mt-side">
              <div className="mt-label">{tt('yourPick', 'Your pick')}</div>
              <div className={`mt-current ${inRange ? 'in-range' : tooLow ? 'low' : 'high'}`}>
                <i className="fa-solid fa-gem gem-i"/>{fmtVal(totalValue)}
              </div>
            </div>
          </div>

          {/* Progress bar — exact same class chain as coinflip MatchModal
              (.mp-bar .mp-track .mp-fill .mp-marker) so the existing CSS
              handles styling. min/target ticks with labels below. */}
          {(() => {
            const tooHigh = totalValue > tolHigh;
            const barMax = tolHigh || 1;
            const fillPct   = Math.min(100, (totalValue / barMax) * 100);
            const minPct    = (tolLow / barMax) * 100;
            const targetPct = (target  / barMax) * 100;
            return (
              <div className="mp-bar">
                <div className="mp-track">
                  <div className={`mp-fill ${inRange ? 'in-range' : tooHigh ? 'high' : ''}`} style={{ width: `${fillPct}%` }}/>
                  <div className="mp-marker" style={{ left: `${minPct}%` }}>
                    <span className="mp-marker-line"/>
                    <span className="mp-marker-lbl">{tt('rangeMin', 'min')}</span>
                  </div>
                  <div className="mp-marker" style={{ left: `${targetPct}%` }}>
                    <span className="mp-marker-line mp-marker-target"/>
                    <span className="mp-marker-lbl">{tt('rangeTarget', 'target')}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="inv-label" style={{marginTop:10}}>
            {match.rounds} {match.rounds === 1 ? tt('diceRound', 'round') : tt('diceRoundsPl', 'rounds')} · {tt('diceBestOf', 'best of')}
          </div>
          {availableInventory.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-illu">📦</div>
              <h3>{tt('createInvEmpty', 'Your inventory is empty')}</h3>
            </div>
          ) : (
            <>
              <div className="inv-controls">
                <div className="inv-search">
                  <I name="search" size={13} color="#838383"/>
                  <input placeholder="Search items.." value={search} onChange={(e) => setSearch(e.target.value)}/>
                </div>
                <CustomSelect
                  value={sort}
                  onChange={setSort}
                  ariaLabel="sort"
                  options={[
                    { value: 'high', label: 'High – Low' },
                    { value: 'low',  label: 'Low – High' },
                  ]}
                />
                <button className="inv-select-all" onClick={autoPick} style={{height: 42}}>
                  <I name="bolt" size={12}/> {tt('autoPick', 'Auto pick')}
                </button>
              </div>
              <div className="inv-grid" style={{maxHeight: '32vh'}} {...dragSel.gridProps}>
                {visible.map(it => (
                  <button
                    key={it.id}
                    type="button"
                    className={`inv-item ${selected.has(it.id) ? 'selected' : ''}`}
                    onClick={() => { if (!dragSel.shouldSuppressClick(it.id)) toggle(it.id); }}
                    {...dragSel.itemProps(it.id)}
                  >
                    <FlipItem item={it} size={56} noTooltip/>
                    <div className="inv-item-name" title={it.name}>{it.name}</div>
                    <div className="inv-item-price"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                  </button>
                ))}
              </div>
              <button className="modal-cta" disabled={!inRange} onClick={submit}>
                {tooLow
                  ? tt('diceJoinTooLow', 'Add more — need {amount}+', { amount: fmtVal(tolLow) })
                  : !inRange
                  ? tt('diceJoinTooHigh', 'Too high — max {amount}', { amount: fmtVal(tolHigh) })
                  : tt('diceJoinReady', 'Join · {amount}', { amount: fmtVal(totalValue) })}
              </button>
            </>
          )}
        </div>
      </div>
      {showFair && (
        <DiceFairnessDataModal match={match} onClose={() => setShowFair(false)}/>
      )}
    </div>
  );
}

// DiceResolvedModal — dramatic round-by-round reveal. Each round:
//   1. SHAKE phase (~1.6s): both dice show random faces every 80ms
//   2. SETTLE: final values appear with a pop animation
//   3. PAUSE (~700ms): user reads the outcome before next round starts
// Total time per round = ~2.3s, so even a 3-round match takes ~7s — gives
// it the suspense of a real coinflip animation.
// A single TRUE 3D die — a CSS cube with 6 pip faces. While `rolling` it
// tumbles continuously; when it settles, the cube rotates so the resolved
// value's face turns to the front (smooth transition = the "landing").
// Standard die layout (opposite faces sum to 7): 1↔6, 2↔5, 3↔4.
function Die3D({ value, rolling, spin = 1 }) {
  const v = Math.max(1, Math.min(6, value || 1));
  // Rotation that brings face `v` to the front of the cube.
  const faceRot = {
    1: 'rotateX(0deg) rotateY(0deg)',
    2: 'rotateY(-90deg)',
    3: 'rotateX(-90deg)',
    4: 'rotateX(90deg)',
    5: 'rotateY(90deg)',
    6: 'rotateY(180deg)',
  };
  // When settled, add full turns before landing so it reads as a real roll.
  const settle = `rotateX(${360 * spin}deg) rotateY(${360 * spin}deg)`;
  const style = rolling ? undefined : { transform: `${settle} ${faceRot[v]}` };
  return (
    <div className={`die3d ${rolling ? 'is-rolling' : ''}`}>
      <div className="die3d-inner" style={style}>
        <div className="die3d-face die3d-front"><PipFace n={1}/></div>
        <div className="die3d-face die3d-back"><PipFace n={6}/></div>
        <div className="die3d-face die3d-right"><PipFace n={2}/></div>
        <div className="die3d-face die3d-left"><PipFace n={5}/></div>
        <div className="die3d-face die3d-top"><PipFace n={3}/></div>
        <div className="die3d-face die3d-bottom"><PipFace n={4}/></div>
      </div>
    </div>
  );
}

function DiceFace3D({ value, rolling }) {
  // Each player rolls TWO dice summing to `value`. Split for the settled
  // faces (e.g. 9 → 4 + 5). While rolling the faces are irrelevant — both
  // cubes just tumble — so we don't churn React state on a timer anymore.
  const a = rolling ? 1 : Math.max(1, Math.min(6, Math.floor(value / 2)));
  const b = rolling ? 1 : Math.max(1, Math.min(6, value - Math.floor(value / 2)));
  return (
    <div className={`dice-3d-pair ${rolling ? 'is-rolling' : ''}`}>
      <Die3D value={a} rolling={rolling} spin={2}/>
      <Die3D value={b} rolling={rolling} spin={3}/>
      {!rolling && <span className="dice-3d-sum">{value}</span>}
    </div>
  );
}

function PipFace({ n }) {
  // Standard die pip layout — 1: center, 2: TL+BR, 3: TL+center+BR,
  // 4: corners, 5: corners+center, 6: 2 cols of 3.
  const layouts = {
    1: ['c'],
    2: ['tl','br'],
    3: ['tl','c','br'],
    4: ['tl','tr','bl','br'],
    5: ['tl','tr','c','bl','br'],
    6: ['tl','tr','ml','mr','bl','br'],
  };
  return (
    <div className="pip-face">
      {(layouts[n] || []).map((pos, i) => <span key={i} className={`pip pip-${pos}`}/>)}
    </div>
  );
}

function DiceResolvedModal({ data, currentUserId, onClose, t }) {
  const { match, seed, skipAnim } = data;
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  // Hash-click → opens the dice fairness data modal (game id, server seed
  // hash, revealed seed, rolls). Same single-entry-point pattern as the
  // coinflip modals — the # hash IS the trigger.
  const [showDiceFairness, setShowDiceFairness] = React.useState(false);

  // totalRounds = every roll we animate (may include sudden-death rounds).
  // baseRounds  = the match's configured best-of-N (the label users picked).
  // When the base rounds tie, the backend appends sudden-death rolls, so
  // rolls.length can exceed baseRounds — that gap IS the auto-replay.
  const totalRounds = (match.rolls || []).length;
  const baseRounds  = Number(match.rounds) || totalRounds;
  const hadSuddenDeath = totalRounds > baseRounds;
  const youAreCreator = currentUserId === match.creator_id;

  // Animation state machine. skipAnim=true (spectating finished match)
  // jumps straight to 'done' so the user doesn't sit through the 11s
  // reveal again — they just want to see the final state.
  const [revealIdx, setRevealIdx] = React.useState(skipAnim ? totalRounds - 1 : -1);
  const [phase, setPhase] = React.useState(skipAnim ? 'done' : 'intro');
  React.useEffect(() => {
    if (skipAnim) return; // already in 'done' phase
    let timer;
    const seq = () => {
      timer = setTimeout(() => stepIntoRound(0), 600);
    };
    const stepIntoRound = (i) => {
      if (i >= totalRounds) {
        setPhase('finale');
        return;
      }
      setRevealIdx(i);
      setPhase('rolling');
      timer = setTimeout(() => {
        setPhase('reveal');
        timer = setTimeout(() => stepIntoRound(i + 1), 1100);
      }, 1800);
    };
    seq();
    return () => clearTimeout(timer);
  }, [totalRounds, skipAnim]);

  // Score only counts FULLY revealed rounds. DEFENSIVE: coerce both
  // sides of each roll tuple to Number so string-comparison bugs ("7"
  // > "10" === true in JS) can never miscount a round.
  const revealedRolls = (match.rolls || []).slice(0, phase === 'reveal' || phase === 'finale' || phase === 'done' ? revealIdx + 1 : Math.max(0, revealIdx));
  const creatorScore  = revealedRolls.filter(r => Number(r[0]) > Number(r[1])).length;
  const opponentScore = revealedRolls.filter(r => Number(r[0]) < Number(r[1])).length;
  const yourScore = youAreCreator ? creatorScore : opponentScore;
  const oppScore  = youAreCreator ? opponentScore : creatorScore;

  const isFinale = phase === 'finale' || phase === 'done';
  // `tied` is the AUTHORITATIVE source: server explicitly returns
  // winner_id=null when scores end equal. We also fall back to comparing
  // computed scores in case the server data is mid-flight (rare race).
  const tied    = isFinale && (match.winner_id == null || creatorScore === opponentScore);
  const youWon  = isFinale && !tied && match.winner_id === currentUserId;
  const youLost = isFinale && !youWon && !tied;

  // Player labels — use real usernames from the match data. Fall back to
  // currentUser (you) or 'Opponent' so we never show literal "@you" / "??".
  // When the creator's view fires from the lobby-polling refresh, opponent_*
  // is populated by the API list endpoint; when the joiner's view fires
  // straight after join, the lobby response already had it. But if there's
  // any data-shape mismatch we still want a readable fallback.
  const _meName  = window.__currentUserName  || null;
  const _meAvatar= window.__currentUserAvatar|| null;
  const youUsername = (youAreCreator ? match.creator_username : match.opponent_username) || _meName || 'you';
  const youAvatar   = (youAreCreator ? match.creator_avatar   : match.opponent_avatar)   || _meAvatar || '';
  // Opponent fallback: prefer the real username; if missing but we have an
  // opponent_id (resolved match where the JOIN didn't return for some
  // reason — rare) show "Player #N"; only fall back to "Opponent" when
  // there's literally no opponent (cancelled / open match).
  const oppId = youAreCreator ? match.opponent_id : match.creator_id;
  const oppUsername = (youAreCreator ? match.opponent_username : match.creator_username)
    || (oppId ? `Player #${oppId}` : 'Opponent');
  const oppAvatar   = (youAreCreator ? match.opponent_avatar   : match.creator_avatar)   || '';
  const yourItems = youAreCreator ? (match.creator_items || []) : (match.opponent_items || []);
  const theirItems = youAreCreator ? (match.opponent_items || []) : (match.creator_items || []);
  const allItems = [...yourItems, ...theirItems];
  const totalPotValue = allItems.reduce((s, it) => s + (Number(it.price) || 0), 0);
  const wonValue = youWon ? totalPotValue : 0;

  // Allow closing once finale lands
  React.useEffect(() => {
    if (phase === 'finale') {
      const t = setTimeout(() => setPhase('done'), 2000);
      return () => clearTimeout(t);
    }
  }, [phase]);

  return (
    <div className={`modal-overlay dice-match-overlay ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal modal--wide dice-match-modal-v2">
        {/* Branded BLOX.HOT head — same treatment as FlipModal */}
        <div className="modal-head fm-brand-head">
          <div className="fm-brand fm-brand--full">
            <span className="brandmark brandmark--modal">
              <span className="brandmark-icon" aria-hidden="true"/>
              <span className="brandmark-text">BLOX<span className="brandmark-dot">.</span><span className="brandmark-hot">HOT</span></span>
            </span>
          </div>
          {(isFinale) && (
            <button className="modal-close" onClick={close} aria-label="close"><i className="fa-solid fa-xmark"/></button>
          )}
        </div>

        {/* Body wash class — won/lost only when NOT a tie. Ties keep the
            neutral body bg so we don't wrongly tint the result red. */}
        <div className={`dm2-body ${isFinale && !tied ? (youWon ? 'dm2-body--won' : 'dm2-body--lost') : ''} ${isFinale && tied ? 'dm2-body--tied' : ''}`}>
          {/* Sub-title — shows the DRAW chip when it's a tie so the user
              isn't left wondering why no one got the crown. */}
          <div className="dm2-eyebrow">
            <i className="fa-solid fa-dice"/> {tt('diceBattle', 'Dice Battle')} · {baseRounds} {tt('diceRoundsPl', 'rounds')}
            {/* Sudden-death chip — rolls.length > the match's base rounds means
                the base rounds tied and we auto-played extra rounds until
                someone won. Surface it so the user understands the extra dice. */}
            {hadSuddenDeath && (
              <span className="dm2-sd-badge"><i className="fa-solid fa-bolt"/> {tt('diceSuddenDeath', 'SUDDEN DEATH')}</span>
            )}
            {isFinale && tied && (
              <span className="dm2-draw-badge"><i className="fa-solid fa-equals"/> DRAW · ITEMS RETURNED</span>
            )}
          </div>

          {/* Top players row — avatars, names, big score */}
          <div className="dm2-players">
            <div className={`dm2-player ${isFinale && youWon ? 'is-winner' : isFinale && youLost ? 'is-loser' : ''}`}>
              <div className="dm2-avatar">
                {youAvatar
                  ? <img src={youAvatar} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : <div className="dm2-avatar-fallback">{synthAvatar(youUsername)}</div>}
                {isFinale && youWon && <span className="dm2-crown"><i className="fa-solid fa-crown"/></span>}
              </div>
              <div className="dm2-name">@{youUsername || 'you'}</div>
              <div className="dm2-score">{yourScore}</div>
            </div>

            <div className="dm2-vs-block">
              <div className="dm2-vs-text">VS</div>
              {/* "Final" text removed — green/red pill tints + crown badge
                  already say it. Only show round/intro status mid-game. */}
              {!isFinale && (
                <div className="dm2-round-counter">
                  {phase === 'intro'
                    ? tt('diceGetReady', 'Get ready…')
                    : tt('diceRoundN', 'Round {n} of {total}', { n: Math.min(totalRounds, revealIdx + 1), total: totalRounds })}
                </div>
              )}
            </div>

            <div className={`dm2-player ${isFinale && youLost ? 'is-winner' : isFinale && youWon ? 'is-loser' : ''}`}>
              <div className="dm2-avatar">
                {oppAvatar
                  ? <img src={oppAvatar} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : <div className="dm2-avatar-fallback">{synthAvatar(oppUsername)}</div>}
                {isFinale && !youWon && !tied && <span className="dm2-crown"><i className="fa-solid fa-crown"/></span>}
              </div>
              <div className="dm2-name">@{oppUsername || 'opponent'}</div>
              <div className="dm2-score">{oppScore}</div>
            </div>
          </div>

          {/* Center stage — big dice arena */}
          <div className="dm2-arena">
            {revealIdx < 0 ? (
              <div className="dm2-arena-intro">
                <div className="dm2-arena-intro-pulse"><i className="fa-solid fa-dice"/></div>
                <div className="dm2-arena-intro-text">{tt('diceShuffling', 'Shuffling seed…')}</div>
              </div>
            ) : (() => {
              const r = match.rolls[Math.min(revealIdx, totalRounds - 1)];
              const yourVal = youAreCreator ? r[0] : r[1];
              const oppVal  = youAreCreator ? r[1] : r[0];
              const rolling = phase === 'rolling';
              const youWonRound = (phase === 'reveal' || isFinale) && yourVal > oppVal;
              const oppWonRound = (phase === 'reveal' || isFinale) && yourVal < oppVal;
              const tiedRound   = (phase === 'reveal' || isFinale) && yourVal === oppVal;
              return (
                <div className="dm2-arena-roll">
                  <div className={`dm2-die-side ${youWonRound ? 'won' : oppWonRound ? 'lost' : ''}`}>
                    <DiceFace3D value={yourVal} rolling={rolling}/>
                  </div>
                  <div className="dm2-die-vs">
                    {rolling
                      ? <i className="fa-solid fa-bolt dm2-vs-bolt"/>
                      : youWonRound ? <i className="fa-solid fa-caret-left dm2-vs-win"/>
                      : oppWonRound ? <i className="fa-solid fa-caret-right dm2-vs-lose"/>
                      : tiedRound   ? <i className="fa-solid fa-equals"/>
                      : '·'}
                  </div>
                  <div className={`dm2-die-side ${oppWonRound ? 'won' : youWonRound ? 'lost' : ''}`}>
                    <DiceFace3D value={oppVal} rolling={rolling}/>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Round history pills */}
          <div className="dm2-rounds">
            {Array.from({ length: totalRounds }).map((_, i) => {
              const done = i < revealIdx || (i === revealIdx && (phase === 'reveal' || isFinale));
              const current = i === revealIdx && phase === 'rolling';
              const r = match.rolls?.[i];
              const yourVal = r ? (youAreCreator ? r[0] : r[1]) : null;
              const oppVal  = r ? (youAreCreator ? r[1] : r[0]) : null;
              const youWonRound = done && yourVal > oppVal;
              const oppWonRound = done && yourVal < oppVal;
              return (
                <div key={i} className={`dm2-round ${done ? 'is-done' : ''} ${current ? 'is-current' : ''} ${youWonRound ? 'win' : oppWonRound ? 'lose' : ''}`}>
                  <span className="dm2-round-lbl">R{i + 1}</span>
                  <span className="dm2-round-val">{done ? yourVal : current ? '?' : '–'}</span>
                  <span className="dm2-round-sep">
                    {youWonRound ? <i className="fa-solid fa-caret-left"/> :
                     oppWonRound ? <i className="fa-solid fa-caret-right"/> : '·'}
                  </span>
                  <span className="dm2-round-val">{done ? oppVal : current ? '?' : '–'}</span>
                </div>
              );
            })}
          </div>

          {/* Match hash — ALWAYS visible (intro / rolling / finale), not just
              at the end. The server seed hash is committed BEFORE the dice
              are rolled, so showing it up-front is the whole point of provably
              fair: the player can see the commitment before the outcome.
              Click → fairness data (the unhashed seed reveals after resolve).
              Same single-entry-point pattern as the coinflip modal. */}
          {(() => {
            const fairnessHash = match.server_seed_hash || match.id || '';
            const display = String(fairnessHash).slice(0, 24) + (String(fairnessHash).length > 24 ? '…' : '');
            return (
              <button
                type="button"
                className="dm2-hash dm2-hash--link"
                onClick={() => setShowDiceFairness(true)}
                title={tt('viewProof', 'View proof / fairness data')}
              >
                <i className="fa-solid fa-hashtag"/>
                <code>{display}</code>
              </button>
            );
          })()}

          {/* Stake pills + 2-column items — same layout as the coinflip
              modals so the resolved view feels consistent across both games.
              Revealed at finale (the hash above shows throughout). */}
          {isFinale && (() => {
            const yourVal  = yourItems.reduce((s, it) => s + (Number(it.price) || 0), 0);
            const theirVal = theirItems.reduce((s, it) => s + (Number(it.price) || 0), 0);
            // Dice is a SYMMETRIC, genuinely-random game (see api/dice/join.php):
            // both players roll fair dice, higher sum wins, ties go to sudden
            // death. So each side's true win chance is exactly 50/50 — never
            // value-weighted. We show a flat 50% on each pill (the value below
            // is just what each player staked). This also makes the old
            // "100% / 0%" race impossible: the % no longer depends on item load.
            const yourPct  = 50;
            const theirPct = 50;
            const yourTone  = tied ? 'neutral' : (youWon  ? 'win' : 'loss');
            const theirTone = tied ? 'neutral' : (youWon  ? 'loss' : 'win');
            return (
              <>
                <div className="dm2-stakes">
                  <div className={`dm2-stake-pill dm2-stake-pill--${yourTone}`}>
                    <div className="dm2-stake-pct">{yourPct.toFixed(2)}%</div>
                    <div className="dm2-stake-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(yourVal)}</div>
                  </div>
                  <div className={`dm2-stake-pill dm2-stake-pill--${theirTone}`}>
                    <div className="dm2-stake-pct">{theirPct.toFixed(2)}%</div>
                    <div className="dm2-stake-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(theirVal)}</div>
                  </div>
                </div>

                <div className="dm2-items-cols">
                  <div className={`dm2-items-col dm2-items-col--${yourTone}`}>
                    <div className="dm2-items-col-list">
                      {yourItems.length === 0
                        ? <div className="dm2-items-empty">{tt('noItems', 'No items')}</div>
                        : yourItems.map((it, i) => (
                            <div key={i} className="dm2-items-row-item">
                              <FlipItem item={it} size={42} noTooltip/>
                              <div className="dm2-items-row-name">{it.name}</div>
                              <div className="dm2-items-row-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                            </div>
                          ))}
                    </div>
                  </div>
                  <div className={`dm2-items-col dm2-items-col--${theirTone}`}>
                    <div className="dm2-items-col-list">
                      {theirItems.length === 0
                        ? <div className="dm2-items-empty">{tt('noItems', 'No items')}</div>
                        : theirItems.map((it, i) => (
                            <div key={i} className="dm2-items-row-item">
                              <FlipItem item={it} size={42} noTooltip/>
                              <div className="dm2-items-row-name">{it.name}</div>
                              <div className="dm2-items-row-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                            </div>
                          ))}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Share — Canvas PNG for TikTok/IG. Both wins AND losses get a
              share button (people love sharing big lose-streaks too). Shown
              for any real pot — dice pots are often small (single-item ±10%
              matches), so the old ≥50 gate hid the button on almost every
              match. */}
          {(phase === 'done') && totalPotValue > 0 && (
            <ShareWinButtons
              youName={youUsername || 'you'}
              pot={totalPotValue}
              stake={yourItems.reduce((s, it) => s + Number(it.price || 0), 0)}
              items={allItems}
              result={`dice (${match.rounds} rds)`}
              won={youWon}
              onShare={() => null}
            />
          )}

          {/* Provably fair link removed — the # hash at the top of the
              dice modal already opens the fairness data. Single entry
              point, matches coinflip modal. */}
        </div>

        {/* Confetti removed per user request — keep the modal calm. */}
      </div>
      {showDiceFairness && (
        <DiceFairnessDataModal match={match} onClose={() => setShowDiceFairness(false)}/>
      )}
    </div>
  );
}

// ============================================
// DiceFairnessDataModal — seeds/hash/rolls for a resolved dice match so the
// player can re-compute the outcome themselves. Reached by clicking the #
// hash in the dice modal. Mirrors FlipFairnessDataModal. Dice has no
// per-player seeds/nonce — the result is server_seed → SHA → per-round
// rolls — so we surface the seed, its hash, and the raw rolls.
// ============================================
function DiceFairnessDataModal({ match, onClose }) {
  const [copied, setCopied] = React.useState(null);
  const [verifyState, setVerifyState] = React.useState(null); // null | 'checking' | 'ok' | 'fail'
  const copy = (val, k) => {
    try { navigator.clipboard.writeText(String(val || '')); setCopied(k); setTimeout(() => setCopied(null), 1200); } catch (_) {}
  };
  // FULL provable-fair verify, done entirely in YOUR browser:
  //   1) SHA-256(server_seed) must equal the hash committed BEFORE the match.
  //   2) Re-derive every roll from (server_seed, joiner_seed) and confirm they
  //      match the recorded rolls — mirrors api/dice/join.php exactly. Because
  //      the server committed its hash before you sent your joiner seed, a
  //      match here proves no one rigged the dice.
  const onVerify = async () => {
    if (!match.server_seed) return;
    setVerifyState('checking');
    try {
      const h = await sha256Hex(match.server_seed);
      if (h !== match.server_seed_hash) { setVerifyState('fail'); return; }
      const seed = match.server_seed;
      const js = match.joiner_seed; // legacy matches (no joiner seed) used seed:label
      const pair = async (label) => {
        const hh = await sha256Hex(js ? `${seed}:${js}:${label}` : `${seed}:${label}`);
        return [(parseInt(hh.slice(0, 2), 16) % 11) + 2, (parseInt(hh.slice(2, 4), 16) % 11) + 2];
      };
      const rounds = Number(match.rounds) || 0;
      const recomputed = [];
      let c = 0, o = 0;
      for (let i = 0; i < rounds; i++) { const [a, b] = await pair('r' + i); if (a > b) c++; else if (b > a) o++; recomputed.push([a, b]); }
      for (let sd = 0; c === o && sd < 50; sd++) { const [a, b] = await pair('sd' + sd); if (a > b) c++; else if (b > a) o++; recomputed.push([a, b]); }
      const actual = Array.isArray(match.rolls) ? match.rolls : [];
      const same = recomputed.length === actual.length &&
        recomputed.every((r, i) => Number(r[0]) === Number(actual[i][0]) && Number(r[1]) === Number(actual[i][1]));
      setVerifyState(same ? 'ok' : 'fail');
    } catch (_) { setVerifyState('fail'); }
  };
  const rollsStr = Array.isArray(match.rolls)
    ? match.rolls.map((r, i) => `R${i + 1}: ${r[0]}–${r[1]}`).join('   ')
    : '—';
  const resolved = match.state === 'resolved';
  const rows = [
    { k: 'gameId',     label: 'Game Id',              value: match.server_seed_hash || match.id || '—' },
    { k: 'serverHash', label: 'Server Seed Hash',     value: match.server_seed_hash || '—' },
    { k: 'joinerSeed', label: 'Joiner Seed',          value: match.joiner_seed || (resolved ? '—' : 'Set when an opponent joins') },
    { k: 'serverSeed', label: 'Unhashed Server Seed', value: match.server_seed || (resolved ? '—' : 'Revealed after the match resolves') },
    { k: 'rolls',      label: 'Rolls',                value: rollsStr },
    { k: 'rounds',     label: 'Rounds',               value: String(match.rounds || (Array.isArray(match.rolls) ? match.rolls.length : '—')) },
  ];
  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{zIndex: 9450}} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{width: 560, maxWidth: '100%'}}>
        <div className="modal-head">
          <h3><i className="fa-solid fa-shield-halved" style={{marginRight:8,color:'var(--orange)'}}/> Dice Fairness</h3>
          <button className="modal-close" onClick={onClose} aria-label="close"><i className="fa-solid fa-xmark"/></button>
        </div>
        <div className="modal-body" style={{paddingTop:8, paddingBottom:18}}>
          <div className="fd-fairness-rows">
            {rows.map(r => (
              <div className="fd-fairness-row" key={r.k}>
                <div className="fd-fairness-lbl">{r.label}</div>
                <div className="fd-fairness-val">
                  <code title={r.value}>{r.value}</code>
                  {String(r.value || '').length > 0 && r.value !== '—' && r.value !== 'Revealed after the match resolves' && r.value !== 'Set when an opponent joins' && (
                    <button className="fd-fairness-copy" onClick={() => copy(r.value, r.k)}>
                      <i className={`fa-solid ${copied === r.k ? 'fa-check' : 'fa-copy'}`}/>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {match.server_seed && (
            <button
              className={`fairness-verify ${verifyState || ''}`}
              onClick={onVerify}
              disabled={verifyState === 'checking'}
              style={{marginTop:12}}
            >
              {verifyState === 'checking' ? 'Verifying…'
               : verifyState === 'ok'   ? '✓ Verified — seed + rolls match'
               : verifyState === 'fail' ? '✗ Verification failed'
               : 'Verify fairness (SHA-256)'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Lightweight confetti rain — 24 falling pieces with random horizontal drift
// and rotation. CSS-only animation, no canvas. Only mounts when the user
// actually wins so we don't pay the cost otherwise.
function DiceConfetti() {
  const pieces = React.useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.4,
    duration: 1.6 + Math.random() * 1.4,
    color: ['#10B981', '#FFD64A', '#10B981', '#E94B7B', '#7C3AED'][i % 5],
    rotateEnd: 180 + Math.floor(Math.random() * 540),
  })), []);
  return (
    <div className="dm2-confetti" aria-hidden="true">
      {pieces.map(p => (
        <span
          key={p.id}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--rot-end': `${p.rotateEnd}deg`,
          }}
        />
      ))}
    </div>
  );
}

// ============================================
// DICE FILTER BAR — coinflip-parity layout: [Search] [round pills] [Create].
// On mobile the round pills collapse into a tiny sliders dropdown so the
// search bar gets the row real-estate (same pattern as coinflip FilterBar).
// ============================================
function DiceFilterBar({ t, diceSearch, setDiceSearch, roundFilter, setRoundFilter, onCreate }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!mobileOpen) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMobileOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [mobileOpen]);

  const activeLabel = roundFilter === 0
    ? tt('diceAllRounds', 'All')
    : `${roundFilter} ${roundFilter === 1 ? 'rd' : 'rds'}`;

  return (
    <div className="filter-bar is-dice has-search">
      {/* Search — first slot, grows to fill remaining width. */}
      <div className="fb-search">
        <I name="search" size={15} color="#838383" />
        <input
          placeholder={t.searchPlaceholder || 'Search players, items…'}
          value={diceSearch}
          onChange={(e) => setDiceSearch(e.target.value)}
          aria-label="Search dice matches"
        />
        {diceSearch ? (
          <button className="fb-search-clear" onClick={() => setDiceSearch('')} aria-label="clear search">
            <i className="fa-solid fa-xmark"/>
          </button>
        ) : null}
      </div>

      {/* Desktop pills (round filter + sort) */}
      <div className="fb-pills">
        <button
          type="button"
          className={`fp ${roundFilter === 0 ? 'is-active' : ''}`}
          onClick={() => setRoundFilter(0)}
        >{tt('diceAllRounds', 'All')}</button>
        {[1, 2, 3].map(n => (
          <button
            key={n}
            type="button"
            className={`fp ${roundFilter === n ? 'is-active' : ''}`}
            onClick={() => setRoundFilter(n)}
          >
            <i className="fa-solid fa-dice" style={{fontSize:11, marginRight:4}}/>
            {n} {n === 1 ? 'rd' : 'rds'}
          </button>
        ))}
        <div className="spacer"/>
        <button className="fp fp-sort" onClick={() => {}}>
          <I name="sort" size={13}/>{tt('newest', 'Newest')}
        </button>
      </div>

      {/* Mobile icon-only filter dropdown — sliders + chevron, opens a small
          menu with the round filter buttons. Same pattern coinflip uses. */}
      <div className="fb-mobile" ref={ref}>
        <button
          className={`fb-mb-btn fb-mb-btn--icon ${mobileOpen ? 'is-open' : ''}`}
          onClick={() => setMobileOpen(o => !o)}
          aria-label={`Round filter — ${activeLabel}`}
          title={activeLabel}
        >
          <I name="sliders" size={15}/>
          <i className={`fa-solid fa-chevron-down fb-mb-chev ${mobileOpen ? 'is-up' : ''}`}/>
        </button>
        {mobileOpen && (
          <div className="fb-mb-menu">
            <div className="fb-mb-group">
              <div className="fb-mb-label">{tt('diceRounds', 'Rounds')}</div>
              <div className="fb-mb-row">
                <button
                  className={`fp ${roundFilter === 0 ? 'is-active' : ''}`}
                  onClick={() => { setRoundFilter(0); setMobileOpen(false); }}
                >{tt('diceAllRounds', 'All')}</button>
                {[1, 2, 3].map(n => (
                  <button
                    key={n}
                    className={`fp ${roundFilter === n ? 'is-active' : ''}`}
                    onClick={() => { setRoundFilter(n); setMobileOpen(false); }}
                  >
                    <i className="fa-solid fa-dice" style={{fontSize:11, marginRight:4}}/>
                    {n} {n === 1 ? 'rd' : 'rds'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <button className="create-btn" onClick={onCreate}>
        <I name="plus" size={14} color="white"/>{tt('diceCreate', 'Create dice')}
      </button>
    </div>
  );
}

// ============================================
// DICE GAME — peer-vs-peer dice battles. Real backend at /api/dice
// (lobby), /api/dice/create (POST), /api/dice/join (POST), /api/dice/cancel.
// Identical UX language as Coinflip: lobby cards with items, Create
// modal with inventory picker, Join button → match resolves server-side
// and items transfer to the winner.
// ============================================
function DicePage({ t, currentUser, onNav, requireAuth, userInventory, refreshInventory, showHero }) {
  const [showCreate, setShowCreate] = React.useState(false);
  const [matches, setMatches] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [schemaMissing, setSchemaMissing] = React.useState(false);
  const [roundFilter, setRoundFilter] = React.useState(0); // 0 = all
  const [joiningFor, setJoiningFor] = React.useState(null); // match obj when user wants to join
  const [resolved, setResolved] = React.useState(null);     // resolved match for result modal
  // Right-click context menu — same UX as coinflip's FlipContextMenu.
  // Stores the match + click coordinates so we can position the menu.
  const [diceCtx, setDiceCtx] = React.useState(null);       // { match, pos:{x,y} } | null
  // Lobby search box (in FilterBar). Matches against creator username + any
  // staked item name — same logic as the coinflip lobby search.
  const [diceSearch, setDiceSearch] = React.useState('');

  // Lobby polling — refresh every 3s like coinflip
  const reload = React.useCallback(() => {
    apiFetch('/api/dice')
      .then(r => {
        setMatches(prev => {
          // Detect when one of MY (creator) matches resolved while waiting
          // → trigger inventory refresh so the creator sees won items
          // arrive (or lost items disappear) without a 45s heartbeat lag.
          const next = r.matches || [];
          if (currentUser) {
            const newlyResolved = next.find(n =>
              n.state === 'resolved' &&
              n.creator_id === currentUser.id &&
              prev.some(p => p.id === n.id && p.state !== 'resolved')
            );
            if (newlyResolved) {
              refreshInventory && refreshInventory();
              // Surface the result modal to the creator with the rolls
              setResolved({ match: newlyResolved, seed: newlyResolved.server_seed });
            }
          }
          return next;
        });
        setSchemaMissing(!!r.schema_missing);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentUser, refreshInventory]);
  React.useEffect(() => {
    reload();
    const id = setInterval(reload, 3000);
    return () => clearInterval(id);
  }, [reload]);

  // Bridge for the mobile nav's center "+" button: when the user is on the
  // Dice page and taps it, App-level code dispatches `blox:open-dice-create`
  // and we open the dice create modal here. Keeps the modal state local but
  // lets the global navbar trigger it.
  React.useEffect(() => {
    const open = () => { if (requireAuth && !requireAuth()) return; setShowCreate(true); };
    window.addEventListener('blox:open-dice-create', open);
    return () => window.removeEventListener('blox:open-dice-create', open);
  }, [requireAuth]);

  // Listen for blox:dice-joined fired from the App-level insta-join flow.
  // When a quick-join succeeds, immediately open the resolved modal with
  // the freshly-resolved match so the user gets the same satisfying
  // result reveal as the normal join path. We synthesise a "match"
  // object from the open lobby entry + the join response + the items
  // the user just staked (pickedItems/pickedValue from the event).
  React.useEffect(() => {
    const onJoined = (e) => {
      const { match, rolls, winner_id, seed, joiner_seed, pickedItems, pickedValue } = e.detail || {};
      if (!match) return;
      reload(); // refresh lobby so the entry flips to resolved
      // Synthesise the resolved match for the modal. Without
      // opponent_items/opponent_value the stake pill rendered 0%/100%
      // and the joiner column showed "No items" — that was the bug.
      const resolvedMatch = {
        ...match,
        state: 'resolved',
        rolls,
        winner_id,
        server_seed: seed,
        opponent_id: currentUser?.id || null,
        opponent_username: currentUser?.username || 'you',
        opponent_avatar: currentUser?.avatar_url || '',
        opponent_items: pickedItems || match.opponent_items || [],
        opponent_value: pickedValue != null ? pickedValue : (match.opponent_value || 0),
        joiner_seed: joiner_seed || null,
      };
      setResolved({ match: resolvedMatch, seed });
    };
    window.addEventListener('blox:dice-joined', onJoined);
    return () => window.removeEventListener('blox:dice-joined', onJoined);
  }, [reload, currentUser]);

  // Lobby visibility: open + rolling + last 10 resolved (matches coinflip).
  // Backend already returns recent resolved/cancelled matches; keep them
  // visible here so the lobby feels alive after wins instead of going
  // empty. Cap finished cards at 10 like the coinflip lobby does.
  const ordered = React.useMemo(() => {
    const open    = matches.filter(m => m.state === 'open');
    const rolling = matches.filter(m => m.state === 'rolling');
    const done    = matches
      .filter(m => m.state === 'resolved' || m.state === 'cancelled')
      .slice(0, 10);
    return [...open, ...rolling, ...done];
  }, [matches]);
  const visibleMatches = React.useMemo(() => {
    let list = roundFilter === 0 ? ordered : ordered.filter(m => m.rounds === roundFilter);
    const q = (diceSearch || '').trim().toLowerCase();
    if (q) {
      list = list.filter(m => {
        const name = (m.creator_username || m.creator_display || '').toLowerCase();
        if (name.includes(q)) return true;
        if ((m.creator_items || []).some(it => (it.name || '').toLowerCase().includes(q))) return true;
        if ((m.opponent_items || []).some(it => (it.name || '').toLowerCase().includes(q))) return true;
        return false;
      });
    }
    return list;
  }, [ordered, roundFilter, diceSearch]);
  // Derived stats — only OPEN matches count toward "active" totals
  // (resolved cards are shown for context but they're not in play anymore).
  const diceStats = React.useMemo(() => {
    const openOnly = matches.filter(m => m.state === 'open');
    return {
      totalValue: openOnly.reduce((s, m) => s + (Number(m.creator_value) || 0), 0),
      totalItems: openOnly.reduce((s, m) => s + ((m.creator_items || []).length), 0),
      activeGames: openOnly.length,
    };
  }, [matches]);

  const onCreateSubmit = async ({ rounds, item_ids }) => {
    if (!requireAuth || !requireAuth()) return;
    try {
      await apiFetch('/api/dice', { method: 'POST', body: { rounds, item_ids } });
      setShowCreate(false);
      reload();
      refreshInventory && refreshInventory();
    } catch (e) {
      alert('Create failed: ' + (e?.message || 'unknown'));
    }
  };
  const onJoinSubmit = async ({ id, item_ids, items, value, client_seed }) => {
    try {
      const res = await apiFetch('/api/dice/join', { method: 'POST', body: { id, item_ids, client_seed } });
      const match = matches.find(m => m.id === id);
      // Inject currentUser's identity AND staked items into the match so the
      // resolved modal shows your own username/avatar/items instead of falling
      // back to "@Opponent" / "No items". The /api/dice/join response only
      // returns rolls/winner/seed — opponent_items stays null on the cached
      // open match until the next /api/dice poll lands, which is why the
      // joiner's column was empty.
      setResolved({
        match: {
          ...match,
          rolls: res.rolls,
          winner_id: res.winner_id,
          state: 'resolved',
          opponent_id:       currentUser?.id,
          opponent_username: currentUser?.username,
          opponent_avatar:   currentUser?.avatar_url,
          opponent_level:    currentUser?.level,
          opponent_items:    items || match?.opponent_items || [],
          opponent_value:    value != null ? value : (match?.opponent_value || 0),
          joiner_seed:       res.joiner_seed || null,
        },
        seed: res.seed,
      });
      setJoiningFor(null);
      reload();
      refreshInventory && refreshInventory();
    } catch (e) {
      try {
        window.dispatchEvent(new CustomEvent('blox:toast', {
          detail: { type: 'error', text: e?.message || 'Join failed' }
        }));
      } catch (_) {}
    }
  };
  const onCancel = async (id) => {
    // Direct cancel — no confirm popup (matches coinflip UX). Errors
    // surface via the same toast queue the rest of the site uses.
    try {
      await apiFetch('/api/dice/cancel', { method: 'POST', body: { id } });
      reload();
      refreshInventory && refreshInventory();
      try {
        window.dispatchEvent(new CustomEvent('blox:toast', {
          detail: { type: 'info', text: tt('diceCancelled', 'Dice match cancelled') }
        }));
      } catch (_) {}
    } catch (e) {
      try {
        window.dispatchEvent(new CustomEvent('blox:toast', {
          detail: { type: 'error', text: e?.message || 'Cancel failed' }
        }));
      } catch (_) {}
    }
  };

  return (
    <div className="dice-page">
      {schemaMissing && (
        <div className="page-state err" style={{padding:'14px 18px',fontSize:13,marginBottom:14}}>
          ⚠️ Dice backend schema not migrated yet. Run{' '}
          <code>db/migrations/2026-05-26-dice.sql</code> on your database.
        </div>
      )}

      {/* Banner — exact same component the coinflip lobby renders, so the
          PRIZE POOL / podium / CTA appear identically on Dice too. */}
      {showHero !== false && <BannerCarousel onGo={(page) => onNav && onNav(page)}/>}

      {/* Tickers row — same component coinflip uses, fed with dice-derived
          stats. Keeps visual identity unified across both game types. */}
      <Tickers stats={diceStats} t={t} loading={loading && matches.length === 0}/>

      <section className="section" data-screen-label="Dice lobby">
        <div className="section-head">
          <h2>
            {tt('diceLobbyTitle', 'Active dice')}
            <span className="count">{visibleMatches.length}</span>
          </h2>
        </div>

        {/* FilterBar — uses EXACT same class names as the coinflip FilterBar
            (`.filter-bar` / `.fb-pills` / `.fp` / `.create-btn`) so the
            existing CSS picks them up. Round-filter pills replace the
            Heads/Tails ones; sort + create button stay identical.
            .filter-bar.is-dice keeps the pills visible on mobile too —
            coinflip swaps them out for a combined dropdown but dice only
            has 4 small pills which fit fine on phone widths. */}
        <DiceFilterBar
          t={t}
          diceSearch={diceSearch}
          setDiceSearch={setDiceSearch}
          roundFilter={roundFilter}
          setRoundFilter={setRoundFilter}
          onCreate={() => { if (requireAuth && !requireAuth()) return; setShowCreate(true); }}
        />

        <div className="flip-grid">
          {loading && matches.length === 0 ? (
            <><FlipCardSkeleton/><FlipCardSkeleton/><FlipCardSkeleton/></>
          ) : visibleMatches.length === 0 ? (
            <div className="empty-state" style={{gridColumn:'1/-1'}}>
              <div className="empty-state-illu">🎲</div>
              <h3>{tt('diceNoMatches', 'No open dice matches right now')}</h3>
              <p>{tt('diceBeFirst', 'Be the first to open one — others will jump in.')}</p>
              <button className="empty-cta" onClick={() => { if (requireAuth && !requireAuth()) return; setShowCreate(true); }}>
                <i className="fa-solid fa-plus"/> {tt('diceCreate', 'Create dice')}
              </button>
            </div>
          ) : visibleMatches.map(m => {
            const isMine = currentUser && (currentUser.id === m.creator_id);
            const items = m.creator_items || [];
            const oppItems = m.opponent_items || [];
            const cShown = items.slice(0, 2);
            const cExtra = items.length - 2;
            const oShown = oppItems.slice(0, 2);
            const oExtra = oppItems.length - 2;
            // Central items strip (list view) — combine both sides' items,
            // capped at 4 visible, +N overflow chip if more.
            const allItems = [...items, ...oppItems];
            const allShown = allItems.slice(0, 4);
            const allExtra = allItems.length - 4;
            const totalValue = Number(m.creator_value || 0) + Number(m.opponent_value || 0);
            const creatorColor = deterministicColor(m.creator_id);
            const oppColor = m.opponent_id ? deterministicColor(m.opponent_id) : 'plum';
            const resolved = m.state === 'resolved';
            const creatorWon = resolved && m.winner_id === m.creator_id;
            const opponentWon = resolved && m.winner_id === m.opponent_id;
            return (
              // Reuses EXACT same .flip-card class chain coinflip uses so the
              // list-row layout (CSS-driven) renders identically. fc-side-badge.d
              // is a dice variant (orange die icon) instead of H/T coin badge.
              <div
                key={m.id}
                className={`flip-card ${m.state} ${isMine ? 'mine' : ''} dice-row`}
                onContextMenu={(e) => {
                  // Right-click → open the dice context menu (View match,
                  // View creator profile, Report). Same UX as coinflip.
                  e.preventDefault();
                  setDiceCtx({ match: m, pos: { x: e.clientX, y: e.clientY } });
                }}
              >
                <span className="fc-countdown fc-countdown--placeholder" aria-hidden="true"/>
                <span className="fc-vs" aria-hidden="true">VS</span>

                <div className="fc-creator">
                  <div
                    className={`fc-avatar ${creatorColor} ${m.creator_avatar ? 'has-img' : ''} ${creatorWon ? 'is-winner' : ''} ${opponentWon ? 'is-loser' : ''} clickable`}
                    title={m.creator_username}
                    onClick={(e) => { e.stopPropagation(); /* future: open user popover */ }}
                  >
                    {m.creator_avatar
                      ? <img src={m.creator_avatar} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                      : synthAvatar(m.creator_username)}
                    <span className="fc-side-badge fc-side-badge--dice" title={`${m.rounds}-round dice`}>{m.rounds}</span>
                    {creatorWon && <span className="fc-winner-pill"><i className="fa-solid fa-crown"/></span>}
                  </div>
                  <div className="fc-side-items">
                    {cShown.map((it, i) => <FlipItem key={'c-'+i} item={it} size={42} noTooltip/>)}
                    {cExtra > 0 && <div className="fc-side-overflow">+{cExtra}</div>}
                  </div>
                </div>

                <div className="fc-opponent">
                  {m.opponent_id ? (
                    <div
                      className={`fc-avatar ${oppColor} ${m.opponent_avatar ? 'has-img' : ''} ${opponentWon ? 'is-winner' : ''} ${creatorWon ? 'is-loser' : ''} clickable`}
                      title={m.opponent_username}
                    >
                      {m.opponent_avatar
                        ? <img src={m.opponent_avatar} alt="" className="fc-avatar-img" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                        : synthAvatar(m.opponent_username)}
                      <span className="fc-side-badge fc-side-badge--dice">{m.rounds}</span>
                      {opponentWon && <span className="fc-winner-pill"><i className="fa-solid fa-crown"/></span>}
                    </div>
                  ) : (
                    /* Quick-join — dice variant. INSTA-JOIN: clicking
                        auto-picks items matching creator value and joins
                        immediately (gated by the 15-min "don't ask again"
                        confirm modal — same UX as coinflip's quick-join). */
                    <button
                      type="button"
                      className="fc-empty-join fc-empty-join--dice"
                      title={`Quick join · ${m.rounds}-round dice`}
                      onClick={() => {
                        if (requireAuth && !requireAuth()) return;
                        window.dispatchEvent(new CustomEvent('blox:dice-insta-join', { detail: m }));
                      }}
                    >
                      <span className="fc-empty-dice" aria-hidden="true">
                        <i className="fa-solid fa-dice"/>
                        <span className="fc-empty-dice-n">{m.rounds}</span>
                      </span>
                    </button>
                  )}
                  <div className="fc-side-items">
                    {oShown.map((it, i) => <FlipItem key={'o-'+i} item={it} size={42} noTooltip/>)}
                    {oExtra > 0 && <div className="fc-side-overflow">+{oExtra}</div>}
                  </div>
                </div>

                {/* Central items strip — only visible in LIST view (desktop).
                    Coinflip uses this for the same purpose; .fc-side-items
                    above is the mobile-card variant (hidden on desktop via
                    CSS rule `.fc-side-items { display: none }` at line 5908). */}
                <div className="fc-items-wrap">
                  <div className="fc-items">
                    {allShown.map((it, i) => <FlipItem key={'all-'+i} item={it} noTooltip/>)}
                  </div>
                  {allExtra > 0 && <div className="fc-overflow">+{allExtra}</div>}
                </div>

                <div className="fc-value">
                  <div className="total"><i className="fa-solid fa-gem gem-i"/>{fmtVal(totalValue || m.creator_value)}</div>
                </div>

                <div className="fc-actions">
                  {isMine && m.state === 'open' ? (
                    /* Same revert — click-to-cancel, no hold. */
                    <button className="fc-btn cancel" onClick={() => onCancel(m.id)}>
                      <i className="fa-solid fa-xmark"/> {tt('diceCancel', 'Cancel')}
                    </button>
                  ) : !m.opponent_id && m.state === 'open' ? (
                    <button className="fc-btn join" onClick={() => { if (requireAuth && !requireAuth()) return; setJoiningFor(m); }}>
                      {tt('diceJoin', 'Join')}
                    </button>
                  ) : (
                    <button className="fc-btn view" onClick={() => {
                      // Open the resolved modal in "done" phase (skip the
                      // rolling animation since the user is just spectating
                      // a finished match, not living through it).
                      setResolved({ match: m, seed: m.server_seed, skipAnim: true });
                    }}>
                      {tt('view', 'View')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {showCreate && <CreateDiceModal
        onClose={() => setShowCreate(false)}
        onCreate={onCreateSubmit}
        userInventory={userInventory || []}
        t={t}
      />}
      {joiningFor && <JoinDiceModal
        match={joiningFor}
        onClose={() => setJoiningFor(null)}
        onJoin={onJoinSubmit}
        userInventory={userInventory || []}
        t={t}
      />}
      {resolved && <DiceResolvedModal
        data={resolved}
        currentUserId={currentUser?.id}
        onClose={() => setResolved(null)}
        t={t}
      />}
      {/* Right-click context menu — re-uses FlipContextMenu's UI by feeding
          it a flip-shaped object adapted from the dice match. View opens
          the result modal (or join modal if state==='open'), View creator
          navigates to the profile page, Report fires a toast for now. */}
      {diceCtx && (
        <FlipContextMenu
          flip={{
            // Minimal shape FlipContextMenu reads: flip.c.username
            c: { username: diceCtx.match.creator_username, name: diceCtx.match.creator_username },
            id: diceCtx.match.id,
          }}
          pos={diceCtx.pos}
          t={t}
          onClose={() => setDiceCtx(null)}
          onWatch={() => {
            // OPEN match → JoinDiceModal. RESOLVED/active → DiceResolvedModal.
            if (diceCtx.match.state === 'open') {
              if (requireAuth && !requireAuth()) return;
              setJoiningFor(diceCtx.match);
            } else {
              setResolved({ match: diceCtx.match, seed: diceCtx.match.server_seed, skipAnim: true });
            }
          }}
          onViewProfile={(username) => {
            try { window.history.pushState({ page: 'profile' }, '', `/profile?u=${encodeURIComponent(username)}`); } catch (_) {}
            onNav && onNav('profile');
          }}
          onReport={() => {
            // Match the coinflip behaviour — instant toast, real report flow
            // for matches can ride on the same /api/chat/report admin queue
            // later. For now: acknowledgement only.
            window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'info', text: 'Reported — admins notified.' } }));
          }}
        />
      )}
    </div>
  );
}

function ProfilePage({ t, currentUser, onNav }) {
  // Re-read the URL ?u= param on every change so navigating from one
  // profile to another (or back to your own) actually re-fetches.
  // Previously this was captured once at mount — clicking your own
  // profile from someone else's page silently failed because the
  // component never knew the URL changed.
  const [targetUsername, setTargetUsername] = React.useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('u') || currentUser?.username || '';
  });
  React.useEffect(() => {
    const onUrlChange = () => {
      const p = new URLSearchParams(window.location.search);
      setTargetUsername(p.get('u') || currentUser?.username || '');
    };
    window.addEventListener('popstate', onUrlChange);
    // Also listen for our custom event fired by navToProfile() so the
    // pushState path (no native event) still triggers a refetch.
    window.addEventListener('bloxhot:navProfile', onUrlChange);
    onUrlChange(); // sync on currentUser change too
    return () => {
      window.removeEventListener('popstate', onUrlChange);
      window.removeEventListener('bloxhot:navProfile', onUrlChange);
    };
  }, [currentUser?.username]);
  const [data, setData]   = React.useState(null);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const isOwn = currentUser && targetUsername && targetUsername.toLowerCase() === (currentUser.username || '').toLowerCase();
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 10;
  // Recent-activity filter/search state. Reset back to page 1 whenever
  // the filter changes so the user isn't stranded on an empty page.
  const [recentSearch, setRecentSearch] = React.useState('');
  const [recentFilter, setRecentFilter] = React.useState('all'); // 'all' | 'won' | 'lost'
  React.useEffect(() => { setPage(1); }, [recentSearch, recentFilter]);
  // Hooks must be declared at the TOP — never after early returns (Rules of Hooks).
  // Banner state is sync'd from `data` via useEffect after fetch completes.
  const [bannerN, setBannerN] = React.useState(1);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  React.useEffect(() => {
    if (data?.profile_banner) {
      const n = Math.max(1, Math.min(8, Number(data.profile_banner)));
      setBannerN(n);
    } else if (data?.user?.profile_banner) {
      setBannerN(Math.max(1, Math.min(8, Number(data.user.profile_banner))));
    }
  }, [data]);

  React.useEffect(() => {
    if (!targetUsername) { setLoading(false); setError(t.profileLoginNeeded || 'Log in to view your profile.'); return; }
    let cancelled = false;
    setLoading(true); setError('');
    // Own profile pulls more history so the user can scroll through pages.
    const limit = isOwn ? 100 : 10;
    apiFetch(`/api/user?username=${encodeURIComponent(targetUsername)}&limit=${limit}`)
      .then(res => { if (!cancelled) { setData(res); setPage(1); } })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load profile'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [targetUsername, isOwn]);

  if (loading) return <div className="page-state">{t.loading || 'Loading…'}</div>;
  if (error)   return <div className="page-state err">{error}</div>;
  if (!data)   return <div className="page-state">No data</div>;

  const u = data.user;
  const recent = data.recent || [];
  const unlocked = new Set((data.achievements || []).map(a => a.code));
  const memberSince = new Date(u.created_at).toLocaleDateString();
  const initials = (u.username || u.username).replace(/[^a-zA-Z0-9]/g, '').substring(0,2).toUpperCase() || '??';

  const showcase = data.showcase || [];
  const wishlist = data.wishlist || [];

  return (
    <div className="profile-page">
      {pickerOpen && (
        <BannerPicker
          currentBanner={bannerN}
          userLevel={Number(currentUser?.level || 1)}
          t={t}
          onClose={() => setPickerOpen(false)}
          onPick={(n) => setBannerN(n)}
        />
      )}
      <div className={`prof-header prof-banner-${bannerN}`}>
        {isOwn && (
          <button className="prof-banner-edit" onClick={() => setPickerOpen(true)} title={t?.bannerEdit || 'Change banner'}>
            <i className="fa-solid fa-paintbrush"/>
          </button>
        )}
        <div className="prof-av-wrap">
          {u.avatar_url
            ? <img src={u.avatar_url} alt="" className="prof-avatar"/>
            : <div className="prof-avatar prof-avatar-fallback">{initials}</div>}
          <span className="prof-level-badge">Lv {u.level || 1}</span>
        </div>
        <div className="prof-meta">
          {/* Single-line title: only @handle (username === handle for Roblox
              auth, so the duplicate plain-name row above was redundant). */}
          <h1 className="prof-name">@{u.username}</h1>
          <div className="prof-since">{t.profileMemberSince || 'Member since'} {memberSince}</div>
          <div className="prof-xp">
            <div className="prof-xp-head">
              <span>Level {u.level || 1}</span>
              <span className="prof-xp-num">{u.xp_in_level || 0} / {u.xp_to_next || 0} XP</span>
            </div>
            <div className="prof-xp-bar"><div className="prof-xp-fill" style={{width: `${Math.min(100, u.xp_pct || 0)}%`}}/></div>
          </div>
        </div>
      </div>

      <div className="prof-stats">
        <div className="prof-stat"><div className="ps-lbl">{t.profileFlips || 'Total flips'}</div><div className="ps-val">{u.total_flips}</div></div>
        <div className="prof-stat"><div className="ps-lbl">{t.profileWins || 'Wins'}</div><div className="ps-val">{u.total_wins}</div></div>
        <div className="prof-stat"><div className="ps-lbl">{t.profileWinRate || 'Win rate'}</div><div className="ps-val">{u.win_rate}%</div></div>
        <div className="prof-stat"><div className="ps-lbl">{t.profileWagered || 'Wagered'}</div><div className="ps-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(u.total_value)}</div></div>
        <div className="prof-stat"><div className="ps-lbl">{t.profileBiggest || 'Biggest win'}</div><div className="ps-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(u.biggest_win)}</div></div>
      </div>

      {/* Personal stats graph — derives win/loss per day from the user's
          recent[] array (last 100 flips). ONLY shown on OWN profile —
          other users shouldn't see your daily win/loss breakdown.
          Bars are colored green/red and stacked. Shows the last 14 days. */}
      {isOwn && recent.length > 0 && (() => {
        const dayMs = 86400000;
        const today = new Date(); today.setHours(0,0,0,0);
        const buckets = Array.from({ length: 14 }, (_, i) => {
          const d = new Date(today.getTime() - (13 - i) * dayMs);
          return { date: d, wins: 0, losses: 0, profit: 0 };
        });
        for (const f of recent) {
          const tsRaw = f.resolved_at || f.created_at;
          if (!tsRaw) continue;
          const ts = new Date(tsRaw).getTime();
          const daysAgo = Math.floor((today.getTime() + dayMs - ts) / dayMs);
          if (daysAgo < 0 || daysAgo >= 14) continue;
          const idx = 13 - daysAgo;
          if (f.won) {
            buckets[idx].wins++;
            buckets[idx].profit += Number(f.pot || 0) - Number(f.stake || 0);
          } else if (f.lost) {
            buckets[idx].losses++;
            buckets[idx].profit -= Number(f.stake || 0);
          }
        }
        const maxRows = Math.max(...buckets.map(b => b.wins + b.losses), 1);
        const totalWins = buckets.reduce((s, b) => s + b.wins, 0);
        const totalLost = buckets.reduce((s, b) => s + b.losses, 0);
        const netProfit = buckets.reduce((s, b) => s + b.profit, 0);
        return (
          <section className="profile-chart-section">
            <div className="profile-chart-head">
              <h3>{tt('profileChartTitle', 'Last 14 days')}</h3>
              <div className="profile-chart-summary">
                <span className="pcs-stat pcs-wins">
                  <i className="fa-solid fa-trophy win-loss-icon"/> {totalWins}W
                </span>
                <span className="pcs-stat pcs-losses">
                  <i className="fa-solid fa-xmark win-loss-icon"/> {totalLost}L
                </span>
                <span className={`pcs-stat pcs-net ${netProfit >= 0 ? 'is-profit' : 'is-loss'}`}>
                  {netProfit >= 0 ? '+' : '−'}<i className="fa-solid fa-gem gem-i"/>{fmtVal(Math.abs(netProfit))}
                </span>
              </div>
            </div>
            <div className="profile-chart" role="img" aria-label="Win/loss bar chart for the last 14 days">
              {buckets.map((b, i) => {
                const total = b.wins + b.losses;
                const h = total === 0 ? 0 : (total / maxRows) * 100;
                const winPct = total === 0 ? 0 : (b.wins / total) * 100;
                return (
                  <div key={i} className="pc-col" title={`${b.date.toLocaleDateString()} · ${b.wins}W / ${b.losses}L`}>
                    <div className="pc-bar" style={{ height: `${h}%` }}>
                      <div className="pc-bar-win" style={{ height: `${winPct}%` }}/>
                      <div className="pc-bar-loss" style={{ height: `${100 - winPct}%` }}/>
                    </div>
                    <div className="pc-day">{b.date.getDate()}</div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {showcase.length > 0 && (
        <div className="showcase-section">
          <div className="showcase-head"><h3>✨ Featured items</h3></div>
          <div className="showcase-grid">
            {showcase.map(it => (
              <div key={it.id} className="showcase-cell">
                <FlipItem item={it} size={56}/>
                <div className="showcase-cell-name">{it.name}</div>
                <div className="showcase-cell-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {(wishlist.length > 0 || isOwn) && (
        <WishlistSection
          initial={wishlist}
          editable={isOwn}
          t={t}
        />
      )}

      <h2 className="prof-section-title">{t.profileAchievements || 'Achievements'}</h2>
      <AchievementsGrid unlocked={unlocked} t={t}/>

      <h2 className="prof-section-title">{t.profileRecent || 'Recent activity'}</h2>
      {recent.length === 0 ? (
        <div className="page-state">{t.profileNoRecent || 'No flips yet.'}</div>
      ) : (() => {
        // ===== Filter + search =====
        // `recent` is fixed (server response). Apply local filter + search,
        // then paginate. Search matches opponent username; filter picks
        // won/lost/all. Both reset to page 1 (see useEffect above).
        const q = recentSearch.trim().toLowerCase();
        const filteredAll = recent.filter(f => {
          if (recentFilter === 'won'  && !f.won)  return false;
          if (recentFilter === 'lost' &&  f.won)  return false;
          if (q) {
            const opp = (f.opponent?.username || '').toLowerCase();
            const id  = String(f.id).toLowerCase();
            if (!opp.includes(q) && !id.includes(q)) return false;
          }
          return true;
        });
        const totalPages = Math.max(1, Math.ceil(filteredAll.length / PAGE_SIZE));
        const safePage  = Math.min(page, totalPages);
        const pageRows  = filteredAll.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

        // Lightweight aggregate stats for the row above the list — gives
        // the user a quick read on their recent run without scrolling.
        const wonCount  = filteredAll.filter(f => f.won).length;
        const lostCount = filteredAll.length - wonCount;
        const winRate   = filteredAll.length > 0 ? Math.round((wonCount / filteredAll.length) * 100) : 0;
        const netVal    = filteredAll.reduce((s, f) => s + (f.won ? (f.total_pot - (f.stake || 0)) : -(f.stake || 0)), 0);

        return (
          <div className="prof-recent-wrap">
            {/* Toolbar — filter pills + search input + aggregate chips */}
            <div className="prr-toolbar">
              <div className="prr-filter-pills">
                {[
                  { id: 'all',  label: 'All',  count: recent.length },
                  { id: 'won',  label: 'Wins', count: recent.filter(f => f.won).length },
                  { id: 'lost', label: 'Losses', count: recent.filter(f => !f.won).length },
                ].map(p => (
                  <button
                    key={p.id}
                    type="button"
                    className={`prr-filter-pill ${recentFilter === p.id ? 'is-active' : ''}`}
                    onClick={() => setRecentFilter(p.id)}
                  >
                    {p.label} <span className="prr-filter-count">{p.count}</span>
                  </button>
                ))}
              </div>
              <div className="prr-search">
                <i className="fa-solid fa-magnifying-glass"/>
                <input
                  type="text"
                  placeholder="Search opponent or ID…"
                  value={recentSearch}
                  onChange={(e) => setRecentSearch(e.target.value)}
                />
                {recentSearch && (
                  <button type="button" className="prr-search-clear" onClick={() => setRecentSearch('')} aria-label="clear">
                    <i className="fa-solid fa-xmark"/>
                  </button>
                )}
              </div>
            </div>
            {/* Mini stats strip — reflects the CURRENT filtered slice */}
            <div className="prr-stats-strip">
              <div className="prr-stat"><span className="prr-stat-lbl">Showing</span><b>{filteredAll.length}</b></div>
              <div className="prr-stat"><span className="prr-stat-lbl">Win rate</span><b>{winRate}%</b></div>
              <div className="prr-stat"><span className="prr-stat-lbl">Wins / Losses</span><b><span style={{color:'var(--emerald)'}}>{wonCount}</span> / <span style={{color:'var(--red)'}}>{lostCount}</span></b></div>
              <div className="prr-stat"><span className="prr-stat-lbl">Net</span><b style={{color: netVal >= 0 ? 'var(--emerald)' : 'var(--red)'}}><i className="fa-solid fa-gem gem-i"/>{netVal >= 0 ? '+' : ''}{fmtVal(netVal)}</b></div>
            </div>

            {filteredAll.length === 0 ? (
              <div className="page-state" style={{padding:'24px'}}>No flips match these filters.</div>
            ) : (
              <div className="prof-recent prof-recent-v2">
                {pageRows.map(f => {
                  const dt = new Date(f.resolved_at);
                  const relTime = (() => {
                    const diffMs = Date.now() - dt.getTime();
                    const min = Math.floor(diffMs / 60000);
                    if (min < 1)    return 'just now';
                    if (min < 60)   return `${min}m ago`;
                    const hr = Math.floor(min / 60);
                    if (hr < 24)    return `${hr}h ago`;
                    const day = Math.floor(hr / 24);
                    if (day < 30)   return `${day}d ago`;
                    return dt.toLocaleDateString();
                  })();
                  const fullDate = dt.toLocaleString(undefined, {
                    year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  });
                  const myItems  = (f.your_items || []).length;
                  const oppItems = (f.opponent_items || []).length;
                  return (
                    <div
                      key={f.id}
                      className={`prr-card ${f.won ? 'is-won' : 'is-lost'}`}
                      onClick={() => {
                        apiFetch(`/api/flip-public?id=${f.id}`).then(res => {
                          const fp = res.flip;
                          if (!fp) return;
                          const creator = fp.creator ? {
                            id: fp.creator.id, name: fp.creator.username,
                            username: fp.creator.username, avatarUrl: fp.creator.avatar_url || '',
                            lvl: fp.creator.level || 1, av: synthAvatar(fp.creator.username),
                            color: deterministicColor(fp.creator.id),
                          } : null;
                          const opponent = fp.opponent ? {
                            id: fp.opponent.id, name: fp.opponent.username,
                            username: fp.opponent.username, avatarUrl: fp.opponent.avatar_url || '',
                            lvl: fp.opponent.level || 1, av: synthAvatar(fp.opponent.username),
                            color: deterministicColor(fp.opponent.id),
                          } : null;
                          window.dispatchEvent(new CustomEvent('blox:viewFlip', { detail: {
                            id: `rep-${fp.id}`, srvId: fp.id, state: fp.state, side: fp.side, result: fp.result,
                            c: creator, o: opponent,
                            items: fp.creator_items || [], oItems: fp.opponent_items || [],
                            winnerId: fp.winner_id,
                            serverSeed: fp.server_seed, serverSeedHash: fp.server_seed_hash,
                            creatorSeed: fp.creator_seed, joinerSeed: fp.joiner_seed,
                            nonce: fp.nonce, participants: [],
                          }}));
                        }).catch(() => {});
                      }}
                    >
                      <div className="prr-left">
                        <div className={`prr-badge ${f.won ? 'win' : 'lose'}`}>
                          <i className={`fa-solid ${f.won ? 'fa-trophy' : 'fa-circle-xmark'}`}/>
                          {f.won ? 'WON' : 'LOST'}
                        </div>
                        <div className="prr-vs">
                          {f.opponent ? <>vs <b>@{f.opponent.username}</b></> : <>vs <em>—</em></>}
                        </div>
                        <div className="prr-meta-row">
                          <span className="prr-meta-id" title={`Flip #${f.id}`}>#{f.id}</span>
                          <span className="prr-meta-dot">·</span>
                          <span className="prr-meta-items">{myItems}v{oppItems} items</span>
                        </div>
                      </div>
                      <div className="prr-items-mini">
                        {(f.your_items || []).slice(0, 4).map((it, i) => (
                          <img key={i} src={itemImg(it.name)} alt="" className="prr-item" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = 0.4; }}/>
                        ))}
                        {(f.your_items || []).length > 4 && (
                          <span className="prr-item-more">+{(f.your_items || []).length - 4}</span>
                        )}
                      </div>
                      <div className="prr-pot-col">
                        <div className="prr-pot-value">
                          <i className="fa-solid fa-gem gem-i"/><b>{fmtVal(f.total_pot)}</b>
                        </div>
                        <div className="prr-time" title={fullDate}>{relTime}</div>
                        <div className="prr-date">{dt.toLocaleDateString(undefined, { month:'short', day:'numeric' })}</div>
                      </div>
                      <ShareLinkButton flipId={f.id}/>
                    </div>
                  );
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className="prof-pager">
                <button className="pager-btn" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                  <i className="fa-solid fa-chevron-left"/> {t?.prev || 'Prev'}
                </button>
                <span className="pager-info">{t?.page || 'Page'} <b>{safePage}</b> / {totalPages}</span>
                <button className="pager-btn" disabled={safePage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                  {t?.next || 'Next'} <i className="fa-solid fa-chevron-right"/>
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ============================================
// HISTORY PAGE — own resolved flips
// ============================================
function HistoryPage({ t, currentUser, onNav }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!currentUser) { setLoading(false); setError(t.historyLoginNeeded || 'Log in to view your flip history.'); return; }
    let cancelled = false;
    apiFetch('/api/flips/history?limit=100')
      .then(res => { if (!cancelled) setRows(res.history || []); })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load history'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentUser]);

  if (loading) return <div className="page-state">{t.loading || 'Loading…'}</div>;
  if (error)   return <div className="page-state err">{error}</div>;

  const openVerify = (f) => {
    if (!f.server_seed) return;
    const q = new URLSearchParams({
      server_seed: f.server_seed,
      creator_seed: f.creator_seed,
      joiner_seed: f.joiner_seed || '',
      nonce: String(f.nonce),
      hash: f.server_seed_hash,
    });
    // Push URL with the query string first, then update nav state.
    try { window.history.pushState({ page: 'fairness' }, '', `/fairness?${q.toString()}`); } catch (_) {}
    onNav && onNav('fairness');
  };

  return (
    <div className="history-page">
      <h1 className="hist-title">{t.historyTitle || 'Your flip history'}</h1>
      <p className="hist-sub">{t.historySub || 'Every flip you participated in, with full provably-fair data.'}</p>

      {rows.length === 0 ? (
        <div className="page-state">{t.historyEmpty || 'No flips yet — go create one!'}</div>
      ) : (
        <div className="hist-list">
          {rows.map(f => (
            // Key must combine game + id — flip #5 and dice #5 are different
            // rows but share the numeric id (independent auto-increment).
            <div key={`${f.game || 'coinflip'}-${f.id}`} className={`hist-row ${f.state === 'cancelled' ? 'cancelled' : (f.is_tie ? '' : (f.you_won ? 'won' : 'lost'))}`}>
              <div className="hist-result-col">
                {f.state === 'cancelled'
                  ? <span className="hist-tag tag-neutral">{t.cancelled || 'Cancelled'}</span>
                  : f.is_tie
                    ? <span className="hist-tag tag-neutral">{t.tie || 'Tie'}</span>
                    : f.you_won
                      ? <span className="hist-tag tag-win">{t.profileWon || 'Won'}</span>
                      : <span className="hist-tag tag-lose">{t.profileLost || 'Lost'}</span>}
                <div className="hist-side">
                  {f.game === 'dice'
                    ? <><i className="fa-solid fa-dice" style={{marginRight:4}}/>DICE{f.rounds ? ` · BO${f.rounds}` : ''}</>
                    : (f.you_side || '').toUpperCase()}
                </div>
              </div>
              <div className="hist-items-col">
                <div className="hist-row-items">
                  {(f.your_items || []).slice(0, 4).map((it, i) => (
                    <img key={i} src={itemImg(it.name)} alt={it.name} title={it.name + ' · ' + fmtVal(it.price)} className="hist-item-img" loading="lazy" decoding="async"/>
                  ))}
                  {(f.your_items || []).length > 4 && <span className="hist-row-extra">+{f.your_items.length - 4}</span>}
                </div>
                <div className="hist-vs">vs {f.opponent ? '@' + f.opponent.username : '—'}</div>
              </div>
              <div className="hist-pot-col">
                <div className="hist-pot"><i className="fa-solid fa-gem gem-i"/>{fmtVal(f.total_pot)}</div>
                <div className="hist-date">{new Date(f.resolved_at || f.created_at).toLocaleString()}</div>
              </div>
              <div className="hist-actions-col">
                {f.state === 'resolved' && f.server_seed && (
                  <button className="hist-verify-btn" onClick={() => openVerify(f)}>
                    <i className="fa-solid fa-shield"/> {t.historyVerify || 'Verify'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// FAIRNESS PAGE — explainer + standalone verifier
// ============================================
function FairnessPage({ t }) {
  // Pre-fill from URL ?server_seed=...&creator_seed=...&joiner_seed=...&nonce=...&hash=...
  const initial = React.useMemo(() => {
    const search = window.location.search.replace(/^\?/, '');
    const q = new URLSearchParams(search);
    return {
      serverSeed:  q.get('server_seed')  || '',
      creatorSeed: q.get('creator_seed') || '',
      joinerSeed:  q.get('joiner_seed')  || '',
      nonce:       q.get('nonce')        || '',
      hash:        q.get('hash')         || '',
    };
  }, []);

  const [serverSeed, setServerSeed]   = React.useState(initial.serverSeed);
  const [creatorSeed, setCreatorSeed] = React.useState(initial.creatorSeed);
  const [joinerSeed, setJoinerSeed]   = React.useState(initial.joinerSeed);
  const [nonce, setNonce]             = React.useState(initial.nonce);
  const [hashExpected, setHashExpected] = React.useState(initial.hash);
  const [output, setOutput] = React.useState(null);

  const run = async () => {
    if (!serverSeed || !creatorSeed || !nonce) {
      setOutput({ error: t.fairFillAll || 'Server seed, creator seed and nonce are required.' });
      return;
    }
    try {
      const computedHash = await sha256Hex(serverSeed);
      const proof = await hmacSha256Hex(serverSeed, `${creatorSeed}:${joinerSeed}:${nonce}`);
      const n = parseInt(proof.slice(0, 8), 16);
      const side = n % 2 === 0 ? 'heads' : 'tails';
      const hashOk = !hashExpected || computedHash === hashExpected;
      setOutput({ computedHash, proof, side, hashOk });
    } catch (err) {
      setOutput({ error: err.message || 'Compute failed' });
    }
  };

  React.useEffect(() => {
    if (initial.serverSeed && initial.creatorSeed && initial.nonce) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fairness-page">
      <h1 className="fair-title"><i className="fa-solid fa-shield"/> {t.fairTitle || 'Provably fair'}</h1>
      <p className="fair-sub">
        {t.fairSub || 'Every flip outcome is determined by an HMAC-SHA256 of public seeds + a server seed committed before the flip. You can re-compute the result here.'}
      </p>

      <div className="fair-how">
        <h3>{t.fairHow || 'How it works'}</h3>
        <ol>
          <li>{t.fairHow1 || 'When a flip is created, the server generates a random 32-byte server seed and shows you its SHA-256 hash (the commitment).'}</li>
          <li>{t.fairHow2 || 'Creator and joiner each contribute their own seed. These are public.'}</li>
          <li>{t.fairHow3 || 'After the flip resolves, the server reveals the server seed. You can SHA-256 it and confirm it matches the original hash — proving it was not changed.'}</li>
          <li>{t.fairHow4 || 'The result is HMAC-SHA256(serverSeed, creatorSeed:joinerSeed:nonce). The first 8 hex chars are parsed as a 32-bit integer; even = heads, odd = tails.'}</li>
        </ol>
      </div>

      <div className="fair-verifier">
        <h3>{t.fairVerifier || 'Verifier'}</h3>
        <label className="fair-label">{t.fairServerSeed || 'Server seed (revealed after flip)'}</label>
        <input className="fair-input" value={serverSeed} onChange={(e) => setServerSeed(e.target.value)} placeholder="hex…"/>
        <label className="fair-label">{t.fairServerHash || 'Server seed hash (committed before flip, optional)'}</label>
        <input className="fair-input" value={hashExpected} onChange={(e) => setHashExpected(e.target.value)} placeholder="sha256 hex (optional)"/>
        <div className="fair-row">
          <div style={{flex: 1}}>
            <label className="fair-label">{t.fairCreatorSeed || 'Creator seed'}</label>
            <input className="fair-input" value={creatorSeed} onChange={(e) => setCreatorSeed(e.target.value)}/>
          </div>
          <div style={{flex: 1}}>
            <label className="fair-label">{t.fairJoinerSeed || 'Joiner seed'}</label>
            <input className="fair-input" value={joinerSeed} onChange={(e) => setJoinerSeed(e.target.value)}/>
          </div>
          <div style={{flex: '0 0 130px'}}>
            <label className="fair-label">{t.fairNonce || 'Nonce'}</label>
            <input className="fair-input" value={nonce} onChange={(e) => setNonce(e.target.value)}/>
          </div>
        </div>
        <button className="fair-run-btn" onClick={run}>
          <i className="fa-solid fa-play"/> {t.fairRun || 'Compute result'}
        </button>

        {output && (
          <div className="fair-output">
            {output.error ? <div className="fair-err">{output.error}</div> : (
              <>
                <div className="fair-out-row">
                  <span className="fair-out-lbl">{t.fairHashCheck || 'Hash check'}</span>
                  <span className={`fair-out-val ${output.hashOk ? 'ok' : 'bad'}`}>
                    {hashExpected ? (output.hashOk ? '✓ ' + (t.fairMatches || 'matches') : '✗ ' + (t.fairMismatch || 'mismatch')) : (t.fairNoHash || 'no expected hash provided')}
                  </span>
                </div>
                <div className="fair-out-row">
                  <span className="fair-out-lbl">{t.fairComputedHash || 'SHA-256(serverSeed)'}</span>
                  <code className="fair-mono">{output.computedHash}</code>
                </div>
                <div className="fair-out-row">
                  <span className="fair-out-lbl">{t.fairProof || 'HMAC proof'}</span>
                  <code className="fair-mono">{output.proof}</code>
                </div>
                <div className="fair-out-row big">
                  <span className="fair-out-lbl">{t.fairResult || 'Result'}</span>
                  <span className="fair-result-pill">{output.side.toUpperCase()}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// SETTINGS PAGE
// ============================================
function ReferralBlock({ t, currentUser }) {
  const [data, setData] = React.useState(null);
  const [inputCode, setInputCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    apiFetch('/api/referral').then(setData).catch(() => setData(null));
  }, []);

  // Share link uses the username instead of the random code — easier to
  // share verbally and feels less spammy. Backend resolves /r/{username}
  // to the user's referral_code at signup time.
  const username = currentUser?.username || '';
  const shareUrl = username ? `${window.location.origin}/r/${username}` : '';
  const copyCode = async () => {
    if (!data?.code) return;
    try { await navigator.clipboard.writeText(data.code); setMsg(t?.refCopied || 'Code copied'); setTimeout(() => setMsg(''), 1500); } catch (_) {}
  };
  const copyLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); setMsg(t?.refLinkCopied || 'Invite link copied'); setTimeout(() => setMsg(''), 1500); } catch (_) {}
  };
  const shareNative = async () => {
    if (!shareUrl || !navigator.share) return copyLink();
    try {
      await navigator.share({ title: 'BLOX.HOT', text: 'Join me on BLOX.HOT — instant Roblox MM2 coinflips', url: shareUrl });
    } catch (_) {}
  };
  const useCode = async () => {
    if (busy || !inputCode.trim()) return;
    setBusy(true); setMsg('');
    try {
      await apiFetch('/api/referral', { method: 'POST', body: { code: inputCode.trim().toUpperCase() } });
      setMsg(t?.refUsed || 'Referral applied! +100 XP for both of you.');
      setInputCode('');
    } catch (err) { setMsg(err.message || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <section className="settings-section">
      <h3>{t?.referralTitle || 'Referrals'}</h3>
      <div className="settings-row">
        <div>
          <div className="sr-name">{t?.referralYourCode || 'Your code'}</div>
          <div className="sr-desc">{t?.referralShare || 'Share this code with friends. You both get bonus XP.'}</div>
        </div>
        <div className="ref-code-row">
          <code className="ref-code">{data.code}</code>
          <button className="settings-btn" onClick={copyCode}><i className="fa-solid fa-copy"/> {t?.loginCopy || 'Copy'}</button>
        </div>
      </div>
      <div className="settings-row">
        <div>
          <div className="sr-name">{t?.refShareLink || 'Share link'}</div>
          <div className="sr-desc">{t?.refShareLinkDesc || 'Send this to friends — auto-applies when they sign up.'}</div>
        </div>
        <div className="ref-code-row">
          <code className="ref-code" style={{fontSize:11,padding:'6px 10px',maxWidth:240,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{shareUrl}</code>
          {navigator.share
            ? <button className="settings-btn" onClick={shareNative}><i className="fa-solid fa-share-nodes"/> {t?.refShare || 'Share'}</button>
            : <button className="settings-btn" onClick={copyLink}><i className="fa-solid fa-link"/> {t?.refCopyLink || 'Copy link'}</button>}
        </div>
      </div>
      <div className="settings-row">
        <div>
          <div className="sr-name">{t?.referralUseCode || "Use a friend's code"}</div>
          <div className="sr-desc">{t?.referralUseDesc || 'One-time. Both of you get +100 XP.'}</div>
        </div>
        <div className="ref-use-row">
          <input className="settings-select ref-input" placeholder="ABCDEF" value={inputCode} maxLength={6}
            onChange={(e) => setInputCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}/>
          <button className="settings-btn" onClick={useCode} disabled={busy || inputCode.length !== 6}>{t?.referralApply || 'Apply'}</button>
        </div>
      </div>
      {msg && <div className="ref-msg">{msg}</div>}
      <div className="settings-row">
        <div>
          <div className="sr-name">{t?.referralInvited || 'Friends joined'}</div>
          <div className="sr-desc">{data.count} {(t?.referralPeople || 'people')}</div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// AFFILIATES PAGE — referral dashboard. Shows the user's code, their
// /r/<username> share link, lifetime earnings, total wagered by referred
// users, and a per-user breakdown sorted by wagered descending.
// Reads from /api/affiliates (computed on the fly from flips + dice_matches).
// ============================================
function AffiliatesPage({ t, currentUser }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [copied, setCopied] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    apiFetch('/api/affiliates')
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const copy = (val, key) => {
    try {
      navigator.clipboard.writeText(val);
      setCopied(key);
      setTimeout(() => setCopied(null), 1400);
    } catch (_) {}
  };

  if (loading) {
    return (
      <div className="affiliates-page">
        <div className="aff-skel"><Skeleton w={220} h={32}/><Skeleton w={420} h={16} radius={4} style={{marginTop:8}}/></div>
      </div>
    );
  }
  if (!data || !currentUser) {
    return (
      <div className="affiliates-page">
        <h1 className="aff-title"><i className="fa-solid fa-users"/> {t?.affTitle || 'Affiliates'}</h1>
        <p className="aff-sub">{t?.affLoginNeeded || 'Sign in to view your affiliate dashboard.'}</p>
      </div>
    );
  }

  const fullUrl = (typeof window !== 'undefined' ? window.location.origin : '') + (data.share_url || '/r/' + data.username);
  const pct = Math.round((data.commission_rate || 0) * 1000) / 10; // 0.05 → 5.0

  return (
    <div className="affiliates-page">
      <div className="aff-hero">
        <div className="aff-hero-left">
          <h1 className="aff-title">
            <i className="fa-solid fa-users"/>
            {t?.affTitle || 'Affiliates'}
          </h1>
          <p className="aff-sub">
            {t?.affSub || `Earn ${pct}% of every wager from users who sign up with your link. No cap, no expiry.`}
          </p>
        </div>
        <div className="aff-hero-right">
          <div className="aff-stat">
            <div className="aff-stat-lbl">{t?.affEarnings || 'Total earnings'}</div>
            <div className="aff-stat-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.total_earnings)}</div>
          </div>
          <div className="aff-stat">
            <div className="aff-stat-lbl">{t?.affWagered || 'Users wagered'}</div>
            <div className="aff-stat-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.total_wagered)}</div>
          </div>
          <div className="aff-stat">
            <div className="aff-stat-lbl">{t?.affRefs || 'Referred'}</div>
            <div className="aff-stat-val">{data.count}</div>
          </div>
        </div>
      </div>

      {/* ===== Claim card ===== Monthly 15th payout, admin-fulfilled.
          Mirrors rakeback's claim flow but for affiliate commissions. */}
      <div className="aff-claim-card">
        <div className="aff-claim-head">
          <div>
            <div className="aff-claim-lbl">{t?.affClaimable || 'Claimable'}</div>
            <div className="aff-claim-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.claimable || 0)}</div>
            <div className="aff-claim-sub">
              {(data.claimed || 0) > 0
                ? `Already paid out: ${fmtVal(data.claimed)} gems`
                : 'Earnings accrue every time a referred user wagers.'}
            </div>
          </div>
          <div className="aff-claim-action">
            {data.pending_claim ? (
              <div className="aff-claim-pending">
                <i className="fa-solid fa-clock"/>
                <div>
                  <b>Claim pending</b>
                  <span>{fmtVal(data.pending_claim.amount)} gems · awaiting admin</span>
                </div>
              </div>
            ) : data.claim_allowed ? (
              <button
                type="button"
                className="aff-claim-btn"
                onClick={async () => {
                  try {
                    const res = await apiFetch('/api/affiliates', { method: 'POST' });
                    window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'win', text: `Claim filed · ${fmtVal(res.amount)} gems · admin will pay out` } }));
                    // Refresh dashboard
                    apiFetch('/api/affiliates').then(setData).catch(() => {});
                  } catch (err) {
                    window.dispatchEvent(new CustomEvent('blox:toast', { detail: { type: 'lose', text: err.message || 'Could not claim' } }));
                  }
                }}
              >
                <i className="fa-solid fa-hand-holding-dollar"/> Claim {fmtVal(data.claimable)}
              </button>
            ) : (
              <div className="aff-claim-locked">
                <i className="fa-solid fa-calendar"/>
                <div>
                  <b>Next payout day</b>
                  <span>{data.next_payout} · claims open on the 15th</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="aff-links">
        {/* Single Share link row — "Your code" duplicate removed (it pointed
            to the same URL as the share link, no real value). */}
        <div className="aff-link-card aff-link-card--wide" style={{ gridColumn: '1 / -1' }}>
          <div className="aff-link-lbl">{t?.affLink || 'Share link'}</div>
          <div className="aff-link-row">
            <code className="aff-link-val">{fullUrl}</code>
            <button className="aff-copy-btn" onClick={() => copy(fullUrl, 'link')}>
              <i className={`fa-solid ${copied==='link'?'fa-check':'fa-copy'}`}/> {copied==='link' ? (t?.copied || 'Copied') : (t?.copy || 'Copy')}
            </button>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button
                className="aff-copy-btn aff-share-btn"
                onClick={() => {
                  try {
                    navigator.share({
                      title: 'BLOX.HOT',
                      text: 'Join me on BLOX.HOT — instant Roblox MM2 coinflips & dice',
                      url: fullUrl,
                    });
                  } catch (_) {}
                }}
              >
                <i className="fa-solid fa-share-nodes"/> {t?.share || 'Share'}
              </button>
            )}
          </div>
        </div>
      </div>

      <section className="aff-section">
        <div className="aff-section-head">
          <h3>{t?.affRefList || 'Referred users'}</h3>
          <span className="aff-section-meta">{pct}% {t?.affCommissionRate || 'commission'}</span>
        </div>

        {data.referred.length === 0 ? (
          <div className="aff-empty">
            <div className="aff-empty-illu">🤝</div>
            <h4>{t?.affEmptyTitle || 'No referrals yet'}</h4>
            <p>{t?.affEmptyBody || 'Share your link in your Roblox group, Discord or wherever your friends hang out — when they sign up with it you start earning a cut of everything they wager.'}</p>
          </div>
        ) : (
          <div className="aff-table">
            <div className="aff-row aff-row--head">
              <div className="aff-col-user">{t?.username || 'User'}</div>
              <div className="aff-col-joined">{t?.affJoined || 'Joined'}</div>
              <div className="aff-col-wagered">{t?.affWageredCol || 'Wagered'}</div>
              <div className="aff-col-earn">{t?.affEarnCol || 'Your earnings'}</div>
            </div>
            {data.referred.map(r => (
              <div className="aff-row" key={r.user_id}>
                <div className="aff-col-user">
                  {r.avatar_url
                    ? <img className="aff-av" src={r.avatar_url} alt=""/>
                    : <div className="aff-av aff-av--fallback">{(r.username || '?').slice(0,2).toUpperCase()}</div>}
                  <div className="aff-uname">
                    <div className="aff-uname-handle">@{r.username}</div>
                    <div className="aff-uname-display">{r.display_name}</div>
                  </div>
                </div>
                <div className="aff-col-joined">{new Date(r.joined_at).toLocaleDateString()}</div>
                <div className="aff-col-wagered"><i className="fa-solid fa-gem gem-i"/>{fmtVal(r.total_wagered)}</div>
                <div className="aff-col-earn aff-col-earn--positive"><i className="fa-solid fa-gem gem-i"/>{fmtVal(r.your_earnings)}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================
// RAKEBACK PAGE — accumulated 1% rakeback on every loss.
// Payout is MONTHLY: claim window opens only on the 15th of each month.
// Outside that window the page shows a countdown to the next 15th and
// the claim button is disabled. SQL migration:
//   db/migrations/2026-05-27-rakeback.sql
// API: /api/rakeback (GET stats + payout status, POST claim).
// ============================================
function RakebackPage({ t, currentUser, refreshMe }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [claiming, setClaiming] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    apiFetch('/api/rakeback')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const claim = async () => {
    if (!data || data.balance <= 0 || claiming) return;
    setClaiming(true);
    try {
      const r = await apiFetch('/api/rakeback', { method: 'POST' });
      if (r && r.ok) {
        window.dispatchEvent(new CustomEvent('blox:toast', {
          detail: { type: 'win', text: `Claimed ${fmtVal(r.claimed)} rakeback — bonus XP added!` }
        }));
        refreshMe && refreshMe();
        load();
      }
    } catch (e) {
      window.dispatchEvent(new CustomEvent('blox:toast', {
        detail: { type: 'error', text: e?.message || 'Claim failed' }
      }));
    } finally {
      setClaiming(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="rakeback-page">
        <Skeleton w={280} h={32}/>
      </div>
    );
  }

  const pct = Math.round((data.rate || 0.01) * 1000) / 10;
  // Monthly payout — server says whether today is the 15th (claim window
  // open) or computes the next 15th. Format a short "Xd Yh" countdown.
  const claimAllowed = !!data.claim_allowed;
  const formatCountdown = (unix) => {
    if (!unix) return null;
    const diff = Math.max(0, unix * 1000 - Date.now());
    const d = Math.floor(diff / 86_400_000);
    const h = Math.floor((diff % 86_400_000) / 3_600_000);
    if (d > 0) return `${d}d ${h}h`;
    const m = Math.floor((diff % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  };
  const countdown = formatCountdown(data.next_payout_unix);
  const claimDisabled = data.balance <= 0 || !claimAllowed || claiming;

  return (
    <div className="rakeback-page">
      <div className="rb-hero">
        <div>
          <h1 className="rb-title"><i className="fa-solid fa-rotate-left"/> {t?.rbTitle || 'Monthly Rakeback'}</h1>
          <p className="rb-sub">
            {t?.rbSub || `Lose a flip or dice? We refund you ${pct}% of every lost item value. Pool builds throughout the month and pays out on the 15th of every month.`}
          </p>
          {!claimAllowed && countdown && (
            <div className="rb-countdown">
              <i className="fa-solid fa-calendar-day"/> {t?.rbNextPayout || 'Next payout in'} <b>{countdown}</b>
            </div>
          )}
          {claimAllowed && (
            <div className="rb-countdown rb-countdown--open">
              <i className="fa-solid fa-circle-check"/> {t?.rbWindowOpen || 'Payout window is OPEN today!'}
            </div>
          )}
        </div>
        <div className="rb-claim">
          <div className="rb-claim-lbl">{t?.rbAvailable || 'Available to claim'}</div>
          <div className="rb-claim-val">
            <i className="fa-solid fa-gem gem-i"/>{fmtVal(data.balance)}
          </div>
          <button
            className={`rb-claim-btn ${claimDisabled ? 'is-disabled' : ''}`}
            disabled={claimDisabled}
            onClick={claim}
            title={!claimAllowed ? (t?.rbLockedTip || 'Rakeback pays out on the 15th of every month') : ''}
          >
            {claiming
              ? <><i className="fa-solid fa-spinner fa-spin"/> {t?.rbClaiming || 'Claiming…'}</>
              : !claimAllowed
                ? <><i className="fa-solid fa-lock"/> {t?.rbLocked || 'Locked until 15th'}</>
                : <><i className="fa-solid fa-hand-holding-dollar"/> {t?.rbClaim || 'Claim'}</>}
          </button>
        </div>
      </div>

      <div className="rb-stats">
        <div className="rb-stat">
          <div className="rb-stat-lbl">{t?.rbLifetime || 'Lifetime earned'}</div>
          <div className="rb-stat-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.lifetime_earned)}</div>
        </div>
        <div className="rb-stat">
          <div className="rb-stat-lbl">{t?.rbClaimed || 'Claimed so far'}</div>
          <div className="rb-stat-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(data.lifetime_claimed)}</div>
        </div>
        <div className="rb-stat">
          <div className="rb-stat-lbl">{t?.rbRate || 'Rate'}</div>
          <div className="rb-stat-val">{pct}%</div>
        </div>
      </div>

      <section className="rb-section">
        <div className="rb-section-head">
          <h3>{t?.rbHistory || 'Recent accruals'}</h3>
        </div>
        {(!data.recent || data.recent.length === 0) ? (
          <div className="rb-empty">
            <div className="rb-empty-illu">💎</div>
            <h4>{t?.rbEmptyTitle || 'Nothing here yet'}</h4>
            <p>{t?.rbEmptyBody || 'Once you start playing, every loss feeds rakeback into your balance. Then click Claim to convert it into bonus XP.'}</p>
          </div>
        ) : (
          <div className="rb-list">
            {data.recent.map(r => (
              <div className="rb-row" key={r.id}>
                <div className="rb-row-src">
                  <i className={`fa-solid ${r.source === 'dice' ? 'fa-dice' : 'fa-coins'} rb-row-icon`}/>
                  <div>
                    <div className="rb-row-title">{r.source === 'dice' ? 'Dice loss' : 'Coinflip loss'} · #{r.source_id}</div>
                    <div className="rb-row-time">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="rb-row-lost">−<i className="fa-solid fa-gem gem-i"/>{fmtVal(r.lost_value)}</div>
                <div className="rb-row-rake">+<i className="fa-solid fa-gem gem-i"/>{fmtVal(r.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SettingsPage({ t, lang, onLang, theme, onThemeToggle, onLogout, currentUser, accent, onAccentChange }) {
  const [savedToast, setSavedToast] = React.useState('');
  const flash = (msg) => { setSavedToast(msg); setTimeout(() => setSavedToast(''), 1500); };

  // Push notifications opt-in (preference only — actual SW push wired later)
  const [pushOn, setPushOn] = React.useState(() => {
    try { return localStorage.getItem('bloxhot.pushOn') === '1'; } catch (_) { return false; }
  });
  const togglePush = async () => {
    const next = !pushOn;
    if (next && 'Notification' in window && Notification.permission !== 'granted') {
      try { await Notification.requestPermission(); } catch (_) {}
    }
    setPushOn(next);
    try { localStorage.setItem('bloxhot.pushOn', next ? '1' : '0'); } catch (_) {}
    flash(next ? (t.settingsPushOn || 'Notifications enabled') : (t.settingsPushOff || 'Notifications disabled'));
  };

  // resetDontAsk helper removed — its UI row was inside the deleted
  // "Prompts & tutorial" section. The `bloxhot.instaAck` and
  // `bloxhot.skipBigConfirm` localStorage keys can still be cleared
  // manually via DevTools if a user wants the confirm dialogs back.

  // Font size — applied to <html data-font="..."> so every rem cascades.
  // Persisted in localStorage so it survives reloads.
  const [fontSize, setFontSize] = React.useState(() => {
    try { return localStorage.getItem('bloxhot.fontSize') || 'normal'; } catch (_) { return 'normal'; }
  });
  React.useEffect(() => {
    try {
      document.documentElement.setAttribute('data-font', fontSize);
      localStorage.setItem('bloxhot.fontSize', fontSize);
    } catch (_) {}
  }, [fontSize]);
  // retriggerTutorial removed — the tutorial feature was removed
  // entirely per user request (it was being skipped by most users
  // and added clutter to the Settings page).

  return (
    <div className="settings-page">
      <h1 className="settings-title">{t.settings || 'Settings'}</h1>

      <section className="settings-section">
        <h3>{t.settingsAppearance || 'Appearance'}</h3>
        <div className="settings-row">
          <div>
            <div className="sr-name">{t.settingsTheme || 'Theme'}</div>
            <div className="sr-desc">{t.settingsThemeDesc || 'Light or dark interface.'}</div>
          </div>
          <button className="settings-btn" onClick={onThemeToggle}>
            <i className={`fa-solid fa-${theme === 'dark' ? 'sun' : 'moon'}`}/>
            {theme === 'dark' ? (t.pmLightTheme || 'Light theme') : (t.pmDarkTheme || 'Dark theme')}
          </button>
        </div>
        {/* Accent color picker — switches the brand `--orange` CSS variable
            at runtime so every button, hover halo and ring updates live.
            Stored in localStorage (`bloxhot.accent`); restored on next load. */}
        {onAccentChange && (
          <div className="settings-row">
            <div>
              <div className="sr-name">{t.settingsAccent || 'Accent color'}</div>
              <div className="sr-desc">{t.settingsAccentDesc || 'Brand accent used across buttons, rings and highlights.'}</div>
            </div>
            <div className="settings-swatch-group" role="radiogroup" aria-label="Accent color">
              {SWATCHES.map(s => (
                <button
                  key={s.hex}
                  type="button"
                  className={`settings-swatch ${accent === s.hex ? 'is-active' : ''}`}
                  style={{ background: s.hex }}
                  onClick={() => { onAccentChange(s.hex); flash((t.settingsAccentSaved || 'Accent saved') + ' · ' + s.name); }}
                  aria-label={s.name}
                  aria-checked={accent === s.hex}
                  role="radio"
                  title={s.name}
                >
                  {accent === s.hex && <i className="fa-solid fa-check"/>}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="settings-row">
          <div>
            <div className="sr-name">{t.settingsLanguage || 'Language'}</div>
            <div className="sr-desc">{t.settingsLanguageDesc || 'Interface language.'}</div>
          </div>
          <CustomSelect
            value={lang}
            onChange={onLang}
            ariaLabel="language"
            options={LANGUAGES.map(l => ({
              value: l.code,
              label: l.label,
              icon: <img src={`assets/images/flag-${l.flag}.webp`} className="lang-flag" alt={l.code} loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }}/>,
            }))}
          />
        </div>
        <div className="settings-row">
          <div>
            <div className="sr-name">{t.settingsPush || 'Push notifications'}</div>
            <div className="sr-desc">{t.settingsPushDesc || 'Get notified when your flip is joined or resolved.'}</div>
          </div>
          <button className="settings-btn" onClick={togglePush}>
            <i className={`fa-solid fa-${pushOn ? 'bell' : 'bell-slash'}`}/>
            {pushOn ? (t.settingsUnmuted || 'On') : (t.settingsMuted || 'Off')}
          </button>
        </div>
        {/* Font size — affects rem-based sizing across the whole site. */}
        <div className="settings-row">
          <div>
            <div className="sr-name">{t.settingsFontSize || 'Text size'}</div>
            <div className="sr-desc">{t.settingsFontSizeDesc || 'Adjust the overall interface font scale.'}</div>
          </div>
          <div className="settings-pill-group">
            {['small','normal','large'].map(v => (
              <button
                key={v}
                type="button"
                className={`settings-pill ${fontSize === v ? 'is-active' : ''}`}
                onClick={() => setFontSize(v)}
                aria-pressed={fontSize === v}
              >
                {v === 'small'  ? (t.settingsFontSmall  || 'Small')
                 : v === 'large' ? (t.settingsFontLarge  || 'Large')
                 : (t.settingsFontNormal || 'Normal')}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* "Prompts & tutorial" section removed per user request — the
          tutorial overlay was unused (most users skipped it) and the
          "don't ask again" reset was hidden behind it. If we ever need
          to reset the don't-ask flags, surface a button somewhere more
          obvious (or just have users clear localStorage manually). */}

      {/* Referrals section removed from Settings — superseded by the
          dedicated Affiliates page (profile menu → Affiliates / drawer). */}

      {currentUser && (
        <section className="settings-section">
          <h3>{t.settingsAccount || 'Account'}</h3>
          <div className="settings-row">
            <div>
              <div className="sr-name">@{currentUser.username}</div>
              <div className="sr-desc">{t.settingsAccountDesc || 'Signed in via Roblox.'}</div>
            </div>
            <button className="settings-btn danger" onClick={onLogout}>
              <i className="fa-solid fa-right-from-bracket"/> {t.pmLogout || 'Log out'}
            </button>
          </div>
        </section>
      )}

      {savedToast && <div className="settings-saved">{savedToast}</div>}
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="shell">
        <div className="footer-cols footer-cols--slim">
          <div className="footer-brand">
            <a className="brandmark brandmark--footer" href="/" aria-label="BLOX.HOT home">
              <span className="brandmark-icon" aria-hidden="true"/>
              <span className="brandmark-text">BLOX<span className="brandmark-dot">.</span><span className="brandmark-hot">HOT</span></span>
            </a>
            <p>The cleanest coinflip experience for items. Provably fair, instant matches.</p>
            <div className="footer-socials">
              <a className="f-soc" href="#" aria-label="X"><I name="twitter" size={15}/></a>
              <a className="f-soc" href="#" aria-label="Discord"><I name="discord" size={15}/></a>
              <a className="f-soc" href="#" aria-label="Telegram"><I name="telegram" size={15}/></a>
            </div>
          </div>
          <div>
            <h4>Platform</h4>
            <ul>
              <li><a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('blox:nav', { detail: { page: 'coinflip' } })); }}>Coinflip</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('blox:nav', { detail: { page: 'dice' } })); }}>Dice</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('blox:open-fairness')); }}>Provably fair</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('blox:nav', { detail: { page: 'affiliates' } })); }}>Affiliates</a></li>
            </ul>
          </div>
          <div>
            <h4>Support</h4>
            <ul>
              <li><a href="#">Help center</a></li>
              <li><a href="#">Contact</a></li>
              <li><a href="#">Terms</a></li>
              <li><a href="#">Privacy</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-legal">
          <span>© 2026 blox.hot. All rights reserved.</span>
          <span style={{color:'var(--g-500)'}}>v1.0</span>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// Global focus trap — applied to ALL open modals (.modal-overlay).
// When a modal opens, focus moves to its first interactive element. Tab
// cycles inside the modal. Shift+Tab from the first cycles to the last.
// Escape was already handled per-modal — this adds the keyboard cycle.
// Restores focus to the previously-focused element when the modal closes.
// ============================================
// ============================================
// FairnessPopup — replaces the inline "Provably fair · seed reveals after
// the flip" footer with a tiny clickable link. Opens a centered modal
// explaining how the commit-reveal scheme works. Saves vertical space
// inside CreateModal / JoinDiceModal / etc.
// ============================================
function FairnessLink({ t, compact = false }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button type="button" className="fairness-link" onClick={() => setOpen(true)}>
        <I name="shield" size={12} color="#10B981"/>
        <span className="ok">{tt('provablyFair', 'Provably fair')}</span>
        {!compact && <i className="fa-solid fa-circle-info fairness-link-icon"/>}
      </button>
      {open && ReactDOM.createPortal(
        <div className="cm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="cm-card" role="dialog" aria-modal="true" style={{maxWidth: 460, textAlign: 'left'}}>
            <div className="cm-icon info" style={{margin: '0 auto 14px'}}>
              <i className="fa-solid fa-shield"/>
            </div>
            <div className="cm-title" style={{textAlign: 'center'}}>{tt('provablyFair', 'Provably fair')}</div>
            <div className="fairness-pop-body">
              <p>{tt('fairExplain1', 'Every flip uses a commit-reveal scheme so the outcome is impossible to manipulate by either side.')}</p>
              <ol className="fairness-pop-steps">
                <li>
                  <b>{tt('fairStep1Title', 'Commit')}</b>
                  <span>{tt('fairStep1Body', 'The server generates a random seed, hashes it with SHA-256, and shows you the hash before any items are locked.')}</span>
                </li>
                <li>
                  <b>{tt('fairStep2Title', 'Reveal')}</b>
                  <span>{tt('fairStep2Body', 'After the flip / dice rolls, the server publishes the original seed. Anyone can re-hash it and check it matches the commit.')}</span>
                </li>
                <li>
                  <b>{tt('fairStep3Title', 'Verify')}</b>
                  <span>{tt('fairStep3Body', 'The outcome is deterministically derived from the seed — same input, same result. You can re-run the calculation yourself.')}</span>
                </li>
              </ol>
              <div className="fairness-pop-foot">
                <i className="fa-solid fa-lock"/>
                <span>{tt('fairFoot', 'The seed is committed BEFORE the flip starts — nobody (including us) can change the result after the fact.')}</span>
              </div>
            </div>
            <div className="cm-actions">
              <button type="button" className="cm-btn cm-btn-primary" onClick={() => setOpen(false)} autoFocus>OK</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function GlobalModalFocusTrap() {
  React.useEffect(() => {
    let prevFocus = null;
    let activeModal = null;
    const focusableSel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const watchModals = () => {
      const overlays = document.querySelectorAll('.modal-overlay, .cm-overlay');
      const newest = overlays[overlays.length - 1] || null;
      if (newest === activeModal) return;
      if (!activeModal && newest) {
        prevFocus = document.activeElement;
        activeModal = newest;
        requestAnimationFrame(() => {
          const first = newest.querySelector(focusableSel);
          if (first && first.focus) first.focus();
        });
      } else if (activeModal && !newest) {
        activeModal = null;
        if (prevFocus && prevFocus.focus) {
          try { prevFocus.focus(); } catch (_) {}
        }
      } else {
        activeModal = newest;
      }
    };

    const obs = new MutationObserver(watchModals);
    obs.observe(document.body, { childList: true, subtree: true });
    watchModals();

    const onKey = (e) => {
      if (e.key !== 'Tab' || !activeModal) return;
      const focusables = activeModal.querySelectorAll(focusableSel);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      obs.disconnect();
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  return null;
}

// ============================================
// MODALS — with exit animation
// ============================================
function useExitAnimation(duration = 200) {
  const [closing, setClosing] = React.useState(false);
  const animateThen = React.useCallback((fn) => {
    setClosing(true);
    setTimeout(fn, duration);
  }, [duration]);
  return { closing, animateThen };
}

// Press Esc → call onClose. Use inside any modal-style component.
function useEscape(onClose) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}

// Closes the modal only if BOTH mousedown AND mouseup happen on the overlay
// (not when the user drags out from inside the modal to release outside)
function useOverlayClose(closeFn) {
  const downOnOverlay = React.useRef(false);
  return {
    onMouseDown: (e) => {
      downOnOverlay.current = (e.target === e.currentTarget);
    },
    onMouseUp: (e) => {
      const both = downOnOverlay.current && e.target === e.currentTarget;
      downOnOverlay.current = false;
      if (both) closeFn();
    },
  };
}

// Hook: swipe-down-to-dismiss for bottom-sheet style modals.
// Swipe-to-dismiss DISABLED per user request — mobile modals are now full-screen
// GUIs that can only be closed via the X button or the overlay-click handler.
// Hook signature kept so existing call sites compile without changes; handlers
// are empty no-ops and sheetRef is still returned for backwards compatibility.
function useSwipeToDismiss(_closeFn, _threshold = 120) {
  const sheetRef = React.useRef(null);
  return {
    sheetRef,
    sheetHandlers: {}, // no touch listeners — no drag interaction
  };
}

function CreateModal({ onClose, onCreate, userInventory, onOpenWallet, t }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const [side, setSide] = React.useState('heads');
  const [selected, setSelected] = React.useState(new Set());
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState('high');
  // Match-range UI removed — backend still accepts the param. ±10% keeps
  // matches reasonably fair (joiner can't undercut/overshoot the pot more
  // than 10% either way). 50% was way too loose.
  const matchRange = 0.10;
  const [gameMode, setGameMode] = React.useState('1v1'); // '1v1' | '2v2'
  const teamSize  = gameMode === '2v2' ? 2 : 1;
  const teamSizeB = gameMode === '2v2' ? 2 : 1;

  const availableInventory = React.useMemo(() => userInventory.filter(it => !it.lockedFlipId), [userInventory]);
  const visible = React.useMemo(() => {
    let arr = availableInventory.filter(it => !search || it.name.toLowerCase().includes(search.toLowerCase()));
    arr = [...arr].sort((a, b) => sort === 'high' ? b.price - a.price : a.price - b.price);
    return arr;
  }, [availableInventory, search, sort]);

  const selectedItems = availableInventory.filter(it => selected.has(it.id));
  const totalValue = selectedItems.reduce((s, it) => s + it.price, 0);
  const fee = totalValue * 0.02;
  const prize = totalValue * 2 - fee;
  const valid = selected.size > 0;

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selected.size === availableInventory.length) setSelected(new Set());
    else setSelected(new Set(availableInventory.map(it => it.id)));
  };
  // Drag-to-select — desktop: click+drag, mobile: long-press+drag.
  const dragSel = useDragMultiSelect(toggle, (id) => selected.has(id));

  const submit = () => animateThen(() => onCreate({ side, items: selectedItems, matchRange, teamSize, teamSizeB }));
  const openWallet = () => animateThen(() => onOpenWallet());
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal modal--wide create-modal" ref={sheetRef}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>Create a flip</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="inv-body">
          <div className="inv-label">{t?.createTeamSize || 'Game mode'}</div>
          <div className="ts-pick">
            <button type="button" className={`ts-chip ${gameMode === '1v1' ? 'is-active' : ''}`} onClick={() => setGameMode('1v1')}>
              <i className="fa-solid fa-user"/> 1v1
            </button>
            <button type="button" className={`ts-chip ${gameMode === '2v2' ? 'is-active' : ''}`} onClick={() => setGameMode('2v2')}>
              <i className="fa-solid fa-user-group"/> 2v2
            </button>
          </div>

          <div className="inv-label" style={{marginTop: 4}}>Pick your side</div>
          <div className="side-pick">
            <button className={`side-card ${side==='heads'?'selected':''}`} onClick={() => setSide('heads')}>
              <div className="side-coin h"/>
              <div className="lbl">Heads</div>
              <div className="sub">50.0% odds</div>
            </button>
            <button className={`side-card ${side==='tails'?'selected':''}`} onClick={() => setSide('tails')}>
              <div className="side-coin t"/>
              <div className="lbl">Tails</div>
              <div className="sub">50.0% odds</div>
            </button>
          </div>

          {/* Match-range chips removed per user request — backend uses a lenient
              default (50%) so joiners with reasonably-priced inventories can
              match without the creator picking a tolerance level. */}

          <div className="inv-label" style={{marginTop: 4}}>Select items from your inventory</div>

          {availableInventory.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-illu">📦</div>
              <h3>Your inventory is empty</h3>
              <p>Deposit your first item to start creating flips.</p>
              <button className="empty-cta" onClick={openWallet}>
                <i className="fa-solid fa-arrow-down"/> Open wallet to deposit
              </button>
            </div>
          ) : (
            <>
              <div className="inv-controls">
                <div className="inv-search">
                  <I name="search" size={13} color="#838383"/>
                  <input placeholder="Search items.." value={search} onChange={(e) => setSearch(e.target.value)}/>
                </div>
                <CustomSelect
                  value={sort}
                  onChange={setSort}
                  ariaLabel="sort"
                  options={[
                    { value: 'high', label: 'High – Low' },
                    { value: 'low',  label: 'Low – High' },
                  ]}
                />
                <button className="inv-select-all" onClick={selectAll} style={{height: 42}}>
                  {selected.size === userInventory.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              <div className="inv-grid" style={{maxHeight: '36vh'}} {...dragSel.gridProps}>
                {visible.map(it => (
                  <button
                    key={it.id}
                    type="button"
                    className={`inv-item ${selected.has(it.id) ? 'selected' : ''}`}
                    onClick={() => { if (!dragSel.shouldSuppressClick(it.id)) toggle(it.id); }}
                    {...dragSel.itemProps(it.id)}
                  >
                    <FlipItem item={it} size={56} noTooltip/>
                    <div className="inv-item-name" title={it.name}>{it.name}</div>
                    <div className="inv-item-price"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                  </button>
                ))}
                {visible.length === 0 && (
                  <div style={{gridColumn:'1/-1',textAlign:'center',padding:'24px',color:'var(--g-500)',fontSize:13}}>
                    No items match "{search}"
                  </div>
                )}
              </div>

              <button className="modal-cta" disabled={!valid} onClick={submit}>
                {!valid
                  ? tt('selectAtLeastOne', 'Select at least 1 item')
                  : selected.size === 1
                  ? tt('createFlipBtnOne', 'Create flip · 1 item · {amount}', { amount: fmtVal(totalValue) })
                  : tt('createFlipBtnFull', 'Create flip · {n} items · {amount}', { n: selected.size, amount: fmtVal(totalValue) })}
              </button>
              {/* Provably fair pill removed per user request — it's already
                  reachable from the header icon + drawer + footer. No need
                  to repeat it on the create flow. */}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Win effects inside the FlipModal — tiered by win value:
//   • Normal win  (< 100 gem pot): simple confetti rain
//   • MEGA WIN    (≥ 100 gem pot): the won items physically float up from
//     the coin like they're being collected, with a slowly-rotating gold
//     mandala in the background and gentle ambient sparkles
function FlipModalWinFx({ tier, amount, items }) {
  if (tier === 'mega') return <FlipModalMegaWin amount={amount} items={items}/>;
  return <FlipModalConfetti/>;
}

// Subtle win — multi-color confetti rain only. No screen shake or flash.
function FlipModalConfetti() {
  const confetti = React.useMemo(() => Array.from({ length: 50 }, (_, i) => ({
    i,
    left: Math.random() * 100,
    delay: Math.random() * 1.2,
    duration: 2.4 + Math.random() * 1.6,
    color: ['#10B981','#FFD64A','#FF3D9A','#7C3AED','#34D9A0','#5C9CFF','#EF4444','#22D3EE'][i % 8],
    size: 5 + Math.random() * 5,
    rot: Math.random() * 720,
  })), []);
  return (
    <div className="fm-winfx" aria-hidden="true">
      <div className="fm-winfx-confetti">
        {confetti.map(c => (
          <span
            key={c.i}
            className="bw-confetti-piece"
            style={{
              left: `${c.left}%`,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              background: c.color,
              width: c.size, height: c.size,
              '--rot': `${c.rot}deg`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// MEGA WIN cinematic — fires for 100+ gem wins.
// Visual design:
//   • Slow-rotating gold mandala/burst pattern in the background (BEHIND
//     content, ambient — sets the celebration tone without overwhelming)
//   • The won items physically float UP from the pot, drifting outward
//     with rotation and a fade-out trail — looks like the player is
//     "collecting" them from the table
//   • Soft ambient confetti rain on top
//   • Brief background gold pulse
function FlipModalMegaWin({ amount, items }) {
  const wonItems = Array.isArray(items) ? items.slice(0, 14) : [];
  // Each won item gets its own float trajectory — staggered timing,
  // randomized angle so they fan out across the modal
  const itemFloats = React.useMemo(() => wonItems.map((it, i) => {
    const angle = (-Math.PI / 2) + (i - (wonItems.length - 1) / 2) * 0.32;
    const dist = 220 + Math.random() * 80;
    return {
      item: it,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist,
      delay: 0.15 + i * 0.08,
      rot: (Math.random() * 30 - 15),
    };
  }), [wonItems.length]);

  // Ambient confetti — fewer, softer
  const confetti = React.useMemo(() => Array.from({ length: 32 }, (_, i) => ({
    i,
    left: Math.random() * 100,
    delay: Math.random() * 2.0,
    duration: 3.0 + Math.random() * 1.8,
    color: ['#10B981','#FFD64A','#FF3D9A','#7C3AED','#34D9A0','#5C9CFF','#EF4444','#22D3EE'][i % 8],
    size: 5 + Math.random() * 5,
    rot: Math.random() * 720,
  })), []);

  return (
    <div className="fm-mega" aria-hidden="true">
      {/* Mandala removed per user request — too much movement in background */}
      {/* Subtle pulsing radial behind everything */}
      <div className="fm-mega-bg-pulse"/>
      {/* 3. Won items floating up from the pot center */}
      <div className="fm-mega-items">
        {itemFloats.map((f, i) => (
          <div
            key={i}
            className="fm-mega-item"
            style={{
              '--dx': `${f.dx}px`,
              '--dy': `${f.dy}px`,
              '--rot': `${f.rot}deg`,
              animationDelay: `${f.delay}s`,
            }}
          >
            <img
              src={`assets/images/${encodeURIComponent(f.item.name || '')}.webp`}
              alt=""
              draggable={false}
              onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
            />
            <span className="fm-mega-item-glow"/>
          </div>
        ))}
      </div>
      {/* 4. Falling confetti rain */}
      <div className="fm-winfx-confetti">
        {confetti.map(c => (
          <span
            key={c.i}
            className="bw-confetti-piece"
            style={{
              left: `${c.left}%`,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              background: c.color,
              width: c.size, height: c.size,
              '--rot': `${c.rot}deg`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================
// BloxCoin — 3D coin flip using OUR actual heads.webp / tails.webp art
// (not a generic Lottie). Two CSS faces stacked back-to-back via
// backface-visibility, animated with a single keyframe that includes
// both the spin AND the landing bounce, so rotateY never resets to 0°
// mid-flight (fixes the old "shows tails then snaps to heads" bug).
// When `landed` flips true the spin animation pauses on its forwards
// final state — coin stops on the correct face and DOES NOT keep
// spinning (the lottie loop kept running before, that's gone now).
// ============================================
function BloxCoin({ playing, result, landed, size = 140 }) {
  // Two animation classes; one for each landing face.
  const sideClass = result === 'heads' ? 'spin-h' : (result === 'tails' ? 'spin-t' : '');
  // Render. We KEEP the same DOM element across phases so the running
  // animation isn't reset; classes just toggle.
  return (
    <div
      className={`blox-coin ${playing ? sideClass : ''} ${landed ? 'is-landed' : ''}`}
      style={{ width: size, height: size }}
      aria-label={result ? `Result: ${result}` : 'Coin flipping'}
    >
      <div className="blox-coin-face blox-coin-face--h" aria-hidden="true"/>
      <div className="blox-coin-face blox-coin-face--t" aria-hidden="true"/>
    </div>
  );
}

function FlipModal({ flip, yourSide, onClose, onResult, currentUser }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);

  // ============================================================
  // UNIFIED 3-SECOND FLIP — anchored to the SERVER's joined_at so the result
  // is revealed at the exact same instant in this modal AND on every
  // spectator's lobby card. Timeline (ms after joined_at):
  //   0 – 3000 → coin spins; "3 / 2 / 1" counts down over the coin (1s each)
  //   ~2750    → coin locks onto the winning face
  //   3000     → result UI revealed — everywhere, simultaneously
  // Because every surface keys off (serverNow() - joined_at), a player who
  // opens late, a laggy phone, and three spectators all land together.
  // ============================================================
  const FLIP_REVEAL_MS = 3000;   // result reveal — MUST match the card + hold
  const FLIP_LAND_MS   = 2750;   // coin snaps to the result face just before
  const _joinedMs = parseServerTs(flip.joinedAt);
  const _elapsed0 = _joinedMs ? Math.max(0, serverNow() - _joinedMs) : 0;
  const _resolvedResult = flip.result || (Math.random() < 0.5 ? 'heads' : 'tails');
  // Only SKIP the animation (open straight to result) when the shared server
  // clock is actually SYNCED. On phones the device clock often runs minutes
  // ahead; before the offset syncs, serverNow() == Date.now() would inflate
  // `_elapsed0` and wrongly skip the animation on a FRESH flip — that was the
  // "mobile instantly resolves / animation cut off, feels laggy" bug. When
  // unsynced we just play the full 3s animation (safe worst case).
  const _done0 = !!_joinedMs && serverClockSynced && _elapsed0 >= FLIP_REVEAL_MS;

  // phase: 'spinning' → 'result'. The spinning coin IS the countdown for the
  // player; the numeric 3-2-1 is shown on the spectator lobby cards.
  const [phase, setPhase]   = React.useState(_done0 ? 'result' : 'spinning');
  const [result, setResult] = React.useState(_resolvedResult);
  const [landed, setLanded] = React.useState(_done0 || _elapsed0 >= FLIP_LAND_MS);
  const [verifyState, setVerifyState] = React.useState(null); // null | 'checking' | 'ok' | 'fail'
  const [showFairness, setShowFairness] = React.useState(false);

  // Schedule the synchronized transitions with ABSOLUTE timers based on how
  // much of the 3 seconds has already elapsed on the shared server clock —
  // so the coin lands + result reveals at the same instant as every spectator.
  React.useEffect(() => {
    const e0 = _joinedMs ? Math.max(0, serverNow() - _joinedMs) : 0;
    const timers = [];
    const at = (target, fn) => {
      const d = target - e0;
      if (d <= 0) fn(); else timers.push(setTimeout(fn, d));
    };
    at(FLIP_LAND_MS,   () => setLanded(true));
    at(FLIP_REVEAL_MS, () => setPhase('result'));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVerify = async () => {
    setVerifyState('checking');
    try {
      const hash = await sha256Hex(flip.serverSeed);
      const proof = await hmacSha256Hex(flip.serverSeed, `${flip.creatorSeed}:${flip.joinerSeed}:${flip.nonce}`);
      const n = parseInt(proof.slice(0, 8), 16);
      const expected = n % 2 === 0 ? 'heads' : 'tails';
      const ok = hash === flip.serverSeedHash && expected === flip.result;
      setVerifyState(ok ? 'ok' : 'fail');
    } catch (e) { setVerifyState('fail'); }
  };

  // Apply state changes when result lands, but DO NOT auto-close the modal.
  // User closes manually via the X button — gives them time to read seeds, verify, etc.
  const [resultApplied, setResultApplied] = React.useState(false);
  React.useEffect(() => {
    if (phase === 'result' && result && !resultApplied) {
      const won = result === yourSide;
      // Slight delay so the user sees the spin land before stats update
      const t = setTimeout(() => {
        onResult(won, result);
        setResultApplied(true);
      }, 600);
      return () => clearTimeout(t);
    }
  }, [phase, result, resultApplied]);

  // Resolve "you" vs "opponent" based on who's actually logged in. If the
  // current user IS the creator, the opponent is flip.o (and vice versa).
  // Without this fix the modal always shows flip.c as the opponent, which is
  // wrong when the current user is the creator — they'd see themselves twice.
  const myId = currentUser?.id;
  const iAmCreator = myId != null && flip.c && flip.c.id === myId;
  const opponent = iAmCreator ? (flip.o || flip.c) : flip.c;
  const you = iAmCreator ? flip.c : (flip.o || null);
  const yourName = you?.name || currentUser?.username || 'You';
  const yourAvatarUrl = you?.avatarUrl || currentUser?.avatar_url || '';
  const yourAv = you?.av || synthAvatar(yourName);
  const yourColor = you?.color || (currentUser ? deterministicColor(currentUser.id) : 'purple');
  const items = flip.items || [];
  const stake = flipValue(items);
  const pot = stake * 2 * 0.98;
  const youWon = result && result === yourSide;
  const overlayHandlers = useOverlayClose(close);

  // Show win effects when the result lands and the user won. Tier picked
  // by pot value: <100 → simple confetti, ≥100 → MEGA WIN cinematic.
  const showWinCelebration = phase === 'result' && youWon;
  const winTier = pot >= 100 ? 'mega' : 'normal';
  const winAmount = Math.round(pot - stake); // net winnings (already accounts for fee)

  // Stake-weighted odds — read from server-side values when available.
  const yourValue = Number(flip.creator_value ?? stake) || stake;
  const opponentValue = Number(flip.opponent_value ?? stake) || stake;
  const pot2 = yourValue + opponentValue;
  let yourPct, oppPct;
  if (yourValue === opponentValue) { yourPct = 50; oppPct = 50; }
  else if (yourValue > opponentValue) {
    yourPct = Math.max(51, Math.ceil((yourValue / pot2) * 100));
    oppPct = 100 - yourPct;
  } else {
    oppPct = Math.max(51, Math.ceil((opponentValue / pot2) * 100));
    yourPct = 100 - oppPct;
  }
  const gameId = flip.srvId ? String(flip.srvId) : '—';
  const shortHash = flip.serverSeedHash ? flip.serverSeedHash.slice(0, 16) + '…' : '—';

  return (
    <div className={`modal-overlay flip-modal-overlay ${closing ? 'is-closing' : ''}`} {...(phase==='result' ? overlayHandlers : {})}>
      <div className={`modal flip-modal-wide ${showWinCelebration ? 'is-winner-celebrating' : ''} ${showWinCelebration && winTier === 'mega' ? 'is-mega-winner' : ''}`}>
        {/* Brand header — centered logo + close X (close visible only after result) */}
        <div className="modal-head fm-brand-head">
          <div className="fm-brand fm-brand--full">
            <span className="brandmark brandmark--modal">
              <span className="brandmark-icon" aria-hidden="true"/>
              <span className="brandmark-text">BLOX<span className="brandmark-dot">.</span><span className="brandmark-hot">HOT</span></span>
            </span>
          </div>
          {phase==='result' && <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>}
        </div>

        {/* Confetti / win FX disabled per user request — clean modal only. */}

        <div className="flip-stage">
          {/* Players row — dm2 layout (vertical: avatar / name / chip) so
              FlipModal matches FlipDetailModal exactly. The eyebrow above
              ("COINFLIP · 1V1") is rendered with the same dm2-eyebrow class
              so it inherits the view-modal styling. */}
          <div className="dm2-eyebrow">
            <i className="fa-solid fa-coins"/> {tt('coinflipBattle', 'Coinflip')} · {tt('coinflipOneShot', '1v1')}
          </div>
          <div className="dm2-players">
            <div className={`dm2-player ${phase==='result' && youWon ? 'is-winner' : (phase==='result' && !youWon ? 'is-loser' : '')}`}>
              <div className="dm2-avatar">
                {yourAvatarUrl
                  ? <img src={yourAvatarUrl} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                  : <div className="dm2-avatar-fallback">{yourAv}</div>}
                {phase==='result' && youWon && <span className="dm2-crown"><i className="fa-solid fa-crown"/></span>}
              </div>
              <div className="dm2-name">@{yourName}</div>
              <div className="dm2-pick-chip">
                <span className={`coin-mini ${yourSide === 'heads' ? 'h' : 't'}`}/>
                <span>{tt(yourSide === 'heads' ? 'sideHeads' : 'sideTails', yourSide).toUpperCase()}</span>
              </div>
            </div>

            {/* VS block — countdown then site-native coin (heads.webp /
                tails.webp). Single DOM element across spinning/result so
                rotateY isn't reset mid-animation; coin stops on the
                correct face when `landed` becomes true. */}
            <div className="dm2-vs-block dm2-vs-block--coin">
              {/* Just the spinning coin — the spin IS the countdown for the
                  player. The numeric 3-2-1 lives on the spectator lobby
                  cards (FlipCountdown ring). Overlaying a number ON the
                  3D-rotating coin made it bleed through the coin face, so
                  we keep the modal clean. */}
              <BloxCoin
                playing={phase === 'spinning' || phase === 'result'}
                result={result}
                landed={landed}
                size={160}/>
            </div>

            {(() => {
              const oppSide = iAmCreator ? (flip.side === 'heads' ? 'tails' : 'heads') : flip.side;
              return (
                <div className={`dm2-player ${phase==='result' && !youWon ? 'is-winner' : (phase==='result' && youWon ? 'is-loser' : '')}`}>
                  <div className="dm2-avatar">
                    {opponent?.avatarUrl
                      ? <img src={opponent.avatarUrl} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
                      : <div className="dm2-avatar-fallback">{opponent?.av || '?'}</div>}
                    {phase==='result' && !youWon && <span className="dm2-crown"><i className="fa-solid fa-crown"/></span>}
                  </div>
                  <div className="dm2-name">@{opponent?.name || 'Opponent'}</div>
                  <div className="dm2-pick-chip">
                    <span className={`coin-mini ${oppSide === 'heads' ? 'h' : 't'}`}/>
                    <span>{tt(oppSide === 'heads' ? 'sideHeads' : 'sideTails', oppSide).toUpperCase()}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Hash — clickable, centered, same component the view modal uses */}
          {(() => {
            const fairnessHash = flip.serverSeedHash || flip.srvId || flip.id || '';
            const display = String(fairnessHash).slice(0, 24) + (String(fairnessHash).length > 24 ? '…' : '');
            return (
              <button
                type="button"
                className="dm2-hash dm2-hash--link"
                onClick={() => setShowFairness(true)}
                title="View proof / fairness data"
              >
                <i className="fa-solid fa-hashtag"/>
                <code>{display}</code>
              </button>
            );
          })()}

          {/* coin-arena removed — the coin animation now lives INSIDE the
              dm2-vs-block (between the two players) per user request. The
              previous large center coin felt disconnected from the rest of
              the layout; inlining it keeps the action in one tight spot. */}

          <div className="flip-result">
            {phase==='spinning'  && <div style={{color:'var(--g-500)',fontSize:12}}>{tt('seedCommitted', 'Seed hash committed · result reveals after flip')}</div>}
            {phase==='result' && (() => {
              const creatorItems  = flip.items  || [];
              const opponentItems = flip.oItems || [];
              const allItems = [...creatorItems, ...opponentItems];
              const creatorSide  = flip.side;
              const opponentSide = creatorSide === 'heads' ? 'tails' : 'heads';
              // Who picked which side — used to badge each column with the
              // coin face that player chose ("heads/tails picked" indicator
              // the user explicitly asked for).
              const youAreCreator = iAmCreator;
              const leftIsYou  = true;  // your column always renders on the LEFT
              const leftItems  = youAreCreator ? creatorItems  : opponentItems;
              const rightItems = youAreCreator ? opponentItems : creatorItems;
              const leftSide   = youAreCreator ? creatorSide   : opponentSide;
              const rightSide  = youAreCreator ? opponentSide  : creatorSide;
              const leftWon    = youWon;
              const rightWon   = !youWon;
              const leftVal    = flipValue(leftItems);
              const rightVal   = flipValue(rightItems);
              const totalPot   = leftVal + rightVal;
              // Entry-share %, NOT a binary 100/0 — the user staked some
              // value going IN, that's what the percentage should reflect.
              // Win/loss is communicated by the pill tint (green/red).
              const leftPct  = totalPot > 0 ? (leftVal  / totalPot) * 100 : (leftVal  > 0 ? 100 : 0);
              const rightPct = totalPot > 0 ? (rightVal / totalPot) * 100 : 0;
              return (
                <>
                  {/* Stake pills — left = you, right = opponent. Winning
                      side tints green, losing side red. Loss-side opacity
                      dims slightly so the eye lands on the winner first. */}
                  <div className="dm2-stakes">
                    <div className={`dm2-stake-pill dm2-stake-pill--${leftWon ? 'win' : 'loss'}`}>
                      <div className="dm2-stake-pct">{leftPct.toFixed(2)}%</div>
                      <div className="dm2-stake-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(leftVal)}</div>
                    </div>
                    <div className={`dm2-stake-pill dm2-stake-pill--${rightWon ? 'win' : 'loss'}`}>
                      <div className="dm2-stake-pct">{rightPct.toFixed(2)}%</div>
                      <div className="dm2-stake-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(rightVal)}</div>
                    </div>
                  </div>

                  {/* Items — two side-by-side columns. Each column has a
                      tiny coin badge (heads/tails — whichever side that
                      player picked) and a scrollable list of items below.
                      No "Items moved to" label — the column tint + coin
                      badge tells the story without a separate banner. */}
                  <div className="dm2-items-cols">
                    <div className={`dm2-items-col dm2-items-col--${leftWon ? 'win' : 'loss'}`}>
                      <div className="dm2-items-col-list">
                        {leftItems.length === 0
                          ? <div className="dm2-items-empty">{tt('noItems', 'No items')}</div>
                          : leftItems.map((it, i) => (
                              <div key={i} className="dm2-items-row-item">
                                <FlipItem item={it} size={42} noTooltip/>
                                <div className="dm2-items-row-name">{it.name}</div>
                                <div className="dm2-items-row-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                              </div>
                            ))}
                      </div>
                    </div>
                    <div className={`dm2-items-col dm2-items-col--${rightWon ? 'win' : 'loss'}`}>
                      <div className="dm2-items-col-list">
                        {rightItems.length === 0
                          ? <div className="dm2-items-empty">{tt('noItems', 'No items')}</div>
                          : rightItems.map((it, i) => (
                              <div key={i} className="dm2-items-row-item">
                                <FlipItem item={it} size={42} noTooltip/>
                                <div className="dm2-items-row-name">{it.name}</div>
                                <div className="dm2-items-row-val"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                              </div>
                            ))}
                      </div>
                    </div>
                  </div>

                  {/* Share — Canvas PNG to TikTok/IG. Available on BOTH win
                      and loss (`won` flag flips the headline + colors). Big
                      pots only (≥50 gems) so we don't pollute social feeds
                      with tiny outcomes. */}
                  {pot >= 50 && (
                    <ShareWinButtons
                      youName={yourName}
                      pot={pot}
                      stake={stake}
                      items={allItems}
                      result={result}
                      won={youWon}
                      onShare={() => null}
                    />
                  )}
                </>
              );
            })()}
          </div>

          {/* Provably-fair pill removed — the # hash at the top of the
              modal is the trigger now (clickable, opens the fairness data
              modal). Single entry point, matches FlipDetailModal. */}
        </div>
      </div>

      {/* Coinflip Fairness modal */}
      {showFairness && (
        <div className="fairness-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowFairness(false); }}>
          <div className="fairness-modal">
            <div className="fairness-head">
              <h3>Coinflip Fairness</h3>
              <button className="modal-close" onClick={() => setShowFairness(false)}><I name="close" size={16}/></button>
            </div>
            <div className="fairness-body">
              <FairnessRow label="Game Id" value={gameId}/>
              <FairnessRow label="Server Seed Hash" value={flip.serverSeedHash || '—'}/>
              <FairnessRow label="Creator Seed" value={flip.creatorSeed || '—'}/>
              <FairnessRow label="Joiner Seed" value={flip.joinerSeed || '—'}/>
              <FairnessRow label="Nonce" value={String(flip.nonce ?? '—')}/>
              <FairnessRow label="Unhashed Server Seed" value={flip.serverSeed || 'Only shown after the game ends'}/>
              {flip.serverSeed && (
                <button className={`fairness-verify ${verifyState||''}`} onClick={onVerify} disabled={verifyState==='checking'}>
                  {verifyState === 'checking' ? 'Verifying…'
                   : verifyState === 'ok' ? '✓ Verified — seed matches'
                   : verifyState === 'fail' ? '✗ Verification failed'
                   : 'Verify with SHA-256'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FairnessRow({ label, value }) {
  const copy = async () => { try { await navigator.clipboard.writeText(value); } catch (_) {} };
  return (
    <div className="fairness-row">
      <div className="fairness-label">{label}</div>
      <div className="fairness-value-wrap">
        <code className="fairness-value" title={value}>{value}</code>
        <button className="fairness-copy" onClick={copy} aria-label="copy"><i className="fa-regular fa-copy"/></button>
      </div>
    </div>
  );
}

// ============================================
// INVENTORY + BOT DEPOSIT
// ============================================
function CustomSelect({ value, onChange, options, ariaLabel }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const current = options.find(o => o.value === value) || options[0];

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="cs" ref={ref}>
      <button type="button" className={`cs-btn ${open ? 'is-open' : ''}`} onClick={() => setOpen(o => !o)} aria-label={ariaLabel}>
        {current?.icon && <span style={{display:'inline-flex',marginRight:6}}>{current.icon}</span>}
        <span className="cs-label">{current?.label}</span>
        <span className="cs-chev"><I name="chev" size={11} /></span>
      </button>
      {open && (
        <div className="cs-menu">
          {options.map(o => (
            <button key={o.value} type="button" className={`cs-item ${o.value === value ? 'is-active' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              {o.icon && <span>{o.icon}</span>}
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InventoryModal({ onClose, items, onOpenDeposit, onWithdraw }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState('high'); // 'high' | 'low'
  const [selected, setSelected] = React.useState(new Set());

  // HIDE locked items entirely (flip-locked OR giveaway-locked). User asked
  // for them to "disappear" from inventory + create flip — no more "IN FLIP"
  // badge cluttering the grid. The locked items still exist server-side and
  // will reappear when their flip/giveaway resolves.
  const availableItems = React.useMemo(
    () => items.filter(it => !it.lockedFlipId),
    [items]
  );

  const visible = React.useMemo(() => {
    let arr = availableItems.filter(it =>
      !search || it.name.toLowerCase().includes(search.toLowerCase())
    );
    arr = [...arr].sort((a, b) => sort === 'high' ? b.price - a.price : a.price - b.price);
    return arr;
  }, [availableItems, search, sort]);

  // Worth + counts now reflect AVAILABLE items only (since locked ones
  // are hidden from the user's view, the sidebar stats should match)
  const totalWorth = availableItems.reduce((s, it) => s + it.price, 0);
  const selectedWorth = availableItems.filter(it => selected.has(it.id)).reduce((s, it) => s + it.price, 0);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  // Drag-to-select (desktop: click+drag, mobile: long-press then drag).
  // Was missing here — only the create-flip / giveaway pickers had it.
  const dragSel = useDragMultiSelect(toggle, (id) => selected.has(id));
  const selectAll = () => {
    if (selected.size === availableItems.length) setSelected(new Set());
    else setSelected(new Set(availableItems.map(it => it.id)));
  };
  const withdrawSelected = () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    onWithdraw(ids);
    setSelected(new Set());
  };
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);

  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal modal--wide inv-modal" ref={sheetRef}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>Inventory</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="inv-body">
          <div className="inv-label">Select item</div>
          <div className="inv-controls">
            <div className="inv-search">
              <I name="search" size={13} color="#838383"/>
              <input placeholder="Search for an item.." value={search} onChange={(e) => setSearch(e.target.value)}/>
            </div>
            <CustomSelect
              value={sort}
              onChange={setSort}
              ariaLabel="sort"
              options={[
                { value: 'high', label: 'High – Low' },
                { value: 'low',  label: 'Low – High' },
              ]}
            />
            <CustomSelect
              value="mm2"
              onChange={() => {}}
              ariaLabel="game"
              options={[
                { value: 'mm2', label: 'Murder Mystery 2' },
              ]}
            />
          </div>
          <div className="inv-summary">
            <div className="inv-stat">
              <i className="fa-solid fa-gem gem-i"/>
              <div><div className="lbl">WORTH</div><div className="val">{fmtVal(totalWorth)}</div></div>
            </div>
            <div className="inv-stat">
              <span className="roblox-box"/>
              <div><div className="lbl">ITEMS</div><div className="val">{items.length}</div></div>
            </div>
            <button className="inv-add" onClick={onOpenDeposit} aria-label="deposit">
              <I name="plus" size={14} color="white"/>
              <span className="inv-add-label">Deposit</span>
            </button>
          </div>

          {availableItems.length === 0 ? (
            <div className="inv-empty">
              <h2>No Items!</h2>
              <p>Your inventory seems to be empty...</p>
              <button className="inv-deposit-btn" onClick={onOpenDeposit}>
                <I name="download" size={13} color="white"/> Deposit Items
              </button>
            </div>
          ) : (
            <div className="inv-grid" {...dragSel.gridProps}>
              {visible.map(it => (
                <button
                  key={it.id}
                  className={`inv-item ${selected.has(it.id) ? 'selected' : ''}`}
                  onClick={() => { if (!dragSel.shouldSuppressClick(it.id)) toggle(it.id); }}
                  title={it.name}
                  {...dragSel.itemProps(it.id)}
                >
                  <FlipItem item={it} size={64} noTooltip/>
                  <div className="inv-item-name" title={it.name}>{it.name}</div>
                  <div className="inv-item-price"><i className="fa-solid fa-gem gem-i"/>{fmtVal(it.price)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        {availableItems.length > 0 && (
          <div className="inv-footer">
            <button className="inv-select-all" onClick={selectAll}>
              {selected.size === availableItems.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              className="inv-giveaway-btn"
              onClick={() => {
                close();
                // Slight delay so the modal close animation can finish before
                // the giveaway creator pops up. Smooths the transition.
                setTimeout(() => {
                  try { window.dispatchEvent(new CustomEvent('blox:openGiveawayCreator')); } catch (_) {}
                }, 180);
              }}
              title="Host a giveaway"
            >
              <i className="fa-solid fa-gift"/> Giveaway
            </button>
            <button className="inv-withdraw" onClick={withdrawSelected} disabled={selected.size === 0}>
              Withdraw <i className="fa-solid fa-gem gem-i"/> {fmtVal(selectedWorth)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const BOT_LIST = [
  { id: 'b1', name: 'BloxBot01', status: 'online',  itemCount: 142, color: '#10B981' },
  { id: 'b2', name: 'BloxBot02', status: 'online',  itemCount: 89,  color: '#2E7DFF' },
  { id: 'b3', name: 'BloxBot03', status: 'online',  itemCount: 213, color: '#8B5CF6' },
  { id: 'b4', name: 'BloxBot04', status: 'busy',    itemCount: 67,  color: '#10B981' },
  { id: 'b5', name: 'BloxBot05', status: 'offline', itemCount: 0,   color: '#646464' },
];

function BotDepositModal({ onClose, onSelectBot }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(onClose);
  const overlayHandlers = useOverlayClose(close);
  const { sheetRef, sheetHandlers } = useSwipeToDismiss(close);
  return (
    <div className={`modal-overlay modal-sheet ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="modal" ref={sheetRef}>
        <div className="modal-head" {...sheetHandlers}>
          <h3>Deposit via bot</h3>
          <button className="modal-close" onClick={close} aria-label="close"><I name="close" size={18}/></button>
        </div>
        <div className="modal-body">
          <p style={{color:'var(--g-600)',fontSize:13,margin:'0 0 14px'}}>
            Pick an online bot, send them your items in-game. Items appear in your inventory within seconds.
          </p>
          <div className="bot-list">
            {BOT_LIST.map(b => (
              <button
                key={b.id}
                className={`bot-item bot-item--${b.status}`}
                disabled={b.status !== 'online'}
                onClick={() => animateThen(() => onSelectBot(b))}
              >
                <div className="bot-avatar" style={{background: b.color}}>
                  <I name="robot" size={18} color="white"/>
                </div>
                <div className="bot-meta">
                  <div className="bot-name">{b.name}</div>
                  <div className="bot-sub">
                    <span className={`bot-status bot-status--${b.status}`}>● {b.status}</span>
                    <span>{b.itemCount} items</span>
                  </div>
                </div>
                {b.status === 'online' && <I name="arrow" size={14}/>}
              </button>
            ))}
          </div>
          <div className="fairness"><I name="shield" size={12} color="#10B981"/><span className="ok">Trade-verified bots</span><span style={{color:'var(--g-500)'}}>· items in &lt;30s</span></div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LOGIN — Roblox username + profile-description code
// Two visual modes (Login / Registration) — both call the same upsert flow.
// Rendered as a modal triggered from the header.
// ============================================
function Login({ onAuth, t, onClose }) {
  const { closing, animateThen } = useExitAnimation();
  const close = () => animateThen(() => onClose && onClose());
  const overlayHandlers = useOverlayClose(close);
  const [stage, setStage] = React.useState('username'); // 'username' | 'verify'
  const [username, setUsername] = React.useState('');
  const [code, setCode] = React.useState('');
  const [roblox, setRoblox] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const tl = (key, fallback) => (t && t[key]) || fallback;

  const requestChallenge = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const u = username.trim().replace(/[^A-Za-z0-9_]/g, '');
    if (!/^[A-Za-z0-9_]{3,20}$/.test(u)) {
      setError(tl('loginBadName', 'Username must be 3–20 chars (letters, digits, underscore).'));
      return;
    }
    setBusy(true); setError('');
    try {
      const res = await apiFetch('/api/auth/challenge', { method: 'POST', body: { username: u } });
      setCode(res.code);
      setRoblox(res.roblox || null);
      setStage('verify');
    } catch (err) {
      setError(err.message || tl('loginFail', 'Failed to start verification.'));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await apiFetch('/api/auth/verify', { method: 'POST', body: { code } });
      // Persist the token so subsequent requests can authenticate even if the
      // browser doesn't keep the cookie (mobile Safari ITP, in-app browsers).
      if (res.token) authToken.set(res.token);
      onAuth(res.user || res);
    } catch (err) {
      setError(err.message || tl('loginVerifyFail', 'Code not found in your bio yet.'));
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); } catch (_) {}
  };

  return (
    <div className={`login-screen ${closing ? 'is-closing' : ''}`} {...overlayHandlers}>
      <div className="login-screen-inner">
        <button className="login-screen-close" onClick={close} aria-label="close">
          <I name="close" size={18}/>
        </button>

        {/* Animated logo block: outer rotating ring + inner breathing halo
            + floating logo image. All decoration, pointer-events:none. */}
        <div className="login-logo-wrap" aria-hidden="true">
          <span className="login-logo-halo"/>
          <span className="login-logo-halo login-logo-halo--2"/>
          <img
            src={LOGO_SVG}
            alt=""
            className="login-logo-icon"
            draggable={false}
            onError={(e) => { e.currentTarget.style.display='none'; }}
          />
        </div>

        <h1 className="login-screen-title">
          <span className="brandmark-text" style={{fontWeight: 900, fontSize: 'inherit'}}>
            BLOX<span className="brandmark-dot">.</span><span className="brandmark-hot">HOT</span>
          </span>
        </h1>
        <p className="login-screen-sub">
          {stage === 'username'
            ? tl('loginSubLogin', 'Enter your Roblox username to continue.')
            : tl('loginSubVerify', 'Paste the code into your Roblox bio, then verify.')}
        </p>

        {stage === 'username' && (
          <form className="login-screen-form" onSubmit={requestChallenge}>
            <label className="login-label">{tl('loginRobloxName', 'Roblox username')}</label>
            <input
              className="login-input"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/[^A-Za-z0-9_]/g, '').slice(0, 20))}
              placeholder="YourRobloxName"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={20}
            />
            {error && <div className="login-error">{error}</div>}
            <button className="login-btn primary" type="submit" disabled={busy}>
              {busy ? tl('loginLoading', 'Loading…') : tl('loginContinue', 'Continue')}
            </button>
            <div className="login-note">
              {tl('loginNote', 'No password, no OAuth — we verify with a one-time code in your Roblox profile bio.')}
            </div>
          </form>
        )}

        {stage === 'verify' && (
          <div className="login-verify">
            {roblox && (
              <div className="login-rblx-row">
                {roblox.avatar && <img className="login-rblx-av" src={roblox.avatar} alt=""/>}
                <div>
                  <div className="login-rblx-name">{roblox.username}</div>
                  <div className="login-rblx-sub">@{roblox.username}</div>
                </div>
              </div>
            )}
            <ol className="login-steps">
              <li>{tl('loginStep1', 'Open Roblox → Settings → Account Info → Description.')}</li>
              <li>
                {tl('loginStep2', 'Paste this code anywhere in your description:')}
                <div className="login-code-row">
                  <code className="login-code">{code}</code>
                  <button type="button" className="login-copy" onClick={copyCode}>{tl('loginCopy', 'Copy')}</button>
                </div>
              </li>
              <li>{tl('loginStep3', 'Save your profile, then tap Verify below.')}</li>
            </ol>
            {error && <div className="login-error">{error}</div>}
            <div className="login-actions">
              <button className="login-btn primary" onClick={verifyCode} disabled={busy}>
                {busy ? tl('loginChecking', 'Checking…') : tl('loginVerify', 'Verify')}
              </button>
              <button className="login-btn ghost" onClick={() => { setStage('username'); setError(''); }} disabled={busy}>
                {tl('back', 'Back')}
              </button>
            </div>
            <div className="login-note">
              {tl('loginAfter', 'After verifying you can remove the code from your description.')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// APP
// ============================================
// ShareWinButtons — generates a TikTok-shaped 1080×1920 PNG of the user's
// win using a hidden canvas. Two CTAs:
//   • Share (mobile) — opens native share sheet with the image attached.
//   • Download — saves the PNG locally.
// Item thumbnails are fetched + drawn into the canvas so the share image
// is self-contained (no missing assets when reposted).
function ShareWinButtons({ youName, pot, stake, items, result, won = true }) {
  const [busy, setBusy] = React.useState(false);
  // `won` flag controls the headline + colors. When false we render a
  // "look how much I lost" share card — same canvas pipeline but the
  // amount banner is red and the headline says LOST instead of WON.
  const winAmount = won ? Math.round(pot - stake) : Math.round(stake);

  const buildImage = async () => {
    const W = 1080, H = 1920;
    const cnv = document.createElement('canvas');
    cnv.width = W; cnv.height = H;
    const ctx = cnv.getContext('2d');

    // Background — dark with orange radial glow at top
    const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
    bgGrad.addColorStop(0, '#1A1E26');
    bgGrad.addColorStop(1, '#0E1014');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    const orangeGlow = ctx.createRadialGradient(W/2, 280, 0, W/2, 280, 500);
    orangeGlow.addColorStop(0, 'rgba(16,185,129,0.55)');
    orangeGlow.addColorStop(1, 'rgba(16,185,129,0)');
    ctx.fillStyle = orangeGlow;
    ctx.fillRect(0, 0, W, H);

    // Top BLOX.HOT brand wordmark — logo image (SVG) + properly centered text
    // Logo is loaded from disk (LOGO_SVG) and drawn first; then BLOX + .HOT
    // are positioned using measureText so the orange ".HOT" lines up flush
    // against "BLOX" instead of the previous hardcoded ±90px offsets.
    const headerY = 160;
    const logoH = 80;
    let logoImg = null;
    try {
      logoImg = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = LOGO_SVG;
      }).catch(() => null);
    } catch (_) {}

    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = '900 72px Inter, sans-serif';

    const wBlox = ctx.measureText('BLOX').width;
    const wDot  = ctx.measureText('.').width;
    const wHot  = ctx.measureText('HOT').width;
    const logoW = logoImg ? logoH : 0;
    const logoGap = logoImg ? 18 : 0;
    const totalWidth = logoW + logoGap + wBlox + wDot + wHot;
    let cursor = (W - totalWidth) / 2;

    if (logoImg) {
      // Center-align the logo vertically with the text
      const ratio = (logoImg.naturalWidth || logoImg.width) / (logoImg.naturalHeight || logoImg.height || 1);
      const drawW = logoH * ratio;
      ctx.drawImage(logoImg, cursor, headerY - logoH/2, drawW, logoH);
      cursor += drawW + logoGap;
    }

    // BLOX (light)
    ctx.fillStyle = '#F0F2F6';
    ctx.fillText('BLOX', cursor, headerY);
    cursor += wBlox;
    // . (orange)
    ctx.fillStyle = '#10B981';
    ctx.fillText('.', cursor, headerY);
    cursor += wDot;
    // HOT (orange)
    ctx.fillText('HOT', cursor, headerY);

    // Reset alignment for the rest of the canvas
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // Big headline — WIN/LOST depending on outcome
    ctx.fillStyle = won ? '#FFD68A' : '#FFA0A0';
    ctx.font = '900 96px Inter, sans-serif';
    ctx.fillText(won ? 'YOU WON' : 'I LOST IT', W/2, 380);

    // Amount — orange for win, red for loss
    ctx.fillStyle = won ? '#10B981' : '#E94B4B';
    ctx.font = '900 196px Inter, sans-serif';
    ctx.fillText((won ? '+' : '-') + fmtVal(winAmount), W/2, 600);

    // "by flipping {side}"
    ctx.fillStyle = '#A4ABBA';
    ctx.font = '500 48px Inter, sans-serif';
    ctx.fillText('flipped ' + (result || '').toUpperCase(), W/2, 700);

    // Items grid — top 8 by value, with a 9th "+N more" overflow tile if
    // there are more than 8 items. User requested: don't show 9 random items
    // — show the biggest-value items first so the share image highlights
    // the trophy haul, and an overflow chip indicates the rest.
    const sortedByValue = [...items].sort((a, b) => (b.price || 0) - (a.price || 0));
    const TOP_N = 8;
    const top = sortedByValue.slice(0, TOP_N);
    const overflow = Math.max(0, sortedByValue.length - TOP_N);
    const tiles = overflow > 0 ? [...top, { _overflow: overflow }] : top;
    const cell = 200, gap = 24;
    const cols = Math.min(3, tiles.length);
    const rows = Math.ceil(tiles.length / 3);
    const gridW = cols * cell + (cols - 1) * gap;
    const startX = (W - gridW) / 2;
    const startY = 820;

    // Draw item cards (placeholder dark squares with rarity tint)
    const rarityTint = { ancient: '#FF3D9A', chroma: '#7C3AED', godly: '#10B981', unique: '#34D399' };
    for (let i = 0; i < tiles.length; i++) {
      const it = tiles[i];
      const cx = startX + (i % 3) * (cell + gap);
      const cy = startY + Math.floor(i / 3) * (cell + gap);
      const tint = it._overflow ? '#10B981' : (rarityTint[it.rarity] || '#3D4555');
      ctx.fillStyle = '#1A1E26';
      ctx.beginPath();
      const r = 24;
      ctx.moveTo(cx + r, cy);
      ctx.arcTo(cx + cell, cy, cx + cell, cy + cell, r);
      ctx.arcTo(cx + cell, cy + cell, cx, cy + cell, r);
      ctx.arcTo(cx, cy + cell, cx, cy, r);
      ctx.arcTo(cx, cy, cx + cell, cy, r);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = tint;
      ctx.lineWidth = 4;
      ctx.stroke();

      if (it._overflow) {
        // Overflow tile — "+N" centered in the box, orange text on dark bg.
        ctx.fillStyle = '#10B981';
        ctx.font = '900 76px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('+' + it._overflow, cx + cell/2, cy + cell/2 - 6);
        ctx.fillStyle = '#A4ABBA';
        ctx.font = '600 28px Inter, sans-serif';
        ctx.fillText('more', cx + cell/2, cy + cell/2 + 50);
        ctx.textBaseline = 'alphabetic';
        continue;
      }
      // Try to draw the item image
      try {
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.crossOrigin = 'anonymous';
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = itemImg(it.name);
        }).catch(() => null);
        if (img) {
          ctx.drawImage(img, cx + 16, cy + 16, cell - 32, cell - 32);
        }
      } catch (_) {}
    }

    // Bottom watermark — "blox.hot" + invite cta
    ctx.fillStyle = 'rgba(16,185,129,0.85)';
    ctx.font = '700 56px Inter, sans-serif';
    ctx.fillText('blox.hot', W/2, H - 200);
    ctx.fillStyle = '#A4ABBA';
    ctx.font = '500 38px Inter, sans-serif';
    ctx.fillText('Coinflip · Win Roblox MM2 items', W/2, H - 140);
    ctx.fillStyle = '#6A7080';
    ctx.font = '400 30px Inter, sans-serif';
    ctx.fillText('Provably fair · SHA-256', W/2, H - 80);

    return new Promise((resolve) => cnv.toBlob(resolve, 'image/png', 0.95));
  };

  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await buildImage();
      if (!blob) return;
      const file = new File([blob], `bloxhot-win-${Date.now()}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'BLOX.HOT win',
          text: `I just won +${fmtVal(winAmount)} on blox.hot!`,
        });
      } else {
        // Desktop fallback — download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (_) {}
    finally { setBusy(false); }
  };
  const download = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await buildImage();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `bloxhot-win-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) {}
    finally { setBusy(false); }
  };
  // Copy the generated PNG to the OS clipboard. Works on Chrome/Edge/Safari
  // 16.4+; older browsers fall through to download as a friendly fallback.
  const [copied, setCopied] = React.useState(false);
  const copyToClipboard = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await buildImage();
      if (!blob) return;
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } else {
        // Older browser — just download instead so the user gets something
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `bloxhot-win-${Date.now()}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (_) {}
    finally { setBusy(false); }
  };

  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;

  return (
    <div className="win-share-row">
      <button className="win-share-btn primary" onClick={share} disabled={busy}>
        <i className="fa-solid fa-share-nodes"/>
        <span>{busy ? tt('generating', 'Generating…') : (canNativeShare ? tt('shareWin', 'Share win') : tt('downloadWin', 'Download win card'))}</span>
      </button>
      <button className="win-share-btn" onClick={copyToClipboard} disabled={busy} title={copied ? 'Copied!' : 'Copy image to clipboard'}>
        <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}/>
      </button>
      <button className="win-share-btn" onClick={download} disabled={busy} title="Download">
        <i className="fa-solid fa-download"/>
      </button>
    </div>
  );
}

// PWA install prompt — a friendly bottom-left card that appears when the
// browser fires beforeinstallprompt. User can install or dismiss; we
// remember the dismissal for 7 days so we don't nag.
function PwaInstallPrompt() {
  const [deferred, setDeferred] = React.useState(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    // Was it dismissed in the last 7 days?
    try {
      const ts = Number(localStorage.getItem('bloxhot.pwaDismissedAt') || 0);
      if (ts && Date.now() - ts < 7 * 24 * 60 * 60 * 1000) return;
    } catch (_) {}
    // Already installed → matchMedia detects display-mode
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  if (!visible || !deferred) return null;

  const install = async () => {
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === 'accepted') {
        try { localStorage.setItem('bloxhot.pwaDismissedAt', String(Date.now())); } catch (_) {}
      }
    } catch (_) {}
    setDeferred(null);
    setVisible(false);
  };
  const dismiss = () => {
    try { localStorage.setItem('bloxhot.pwaDismissedAt', String(Date.now())); } catch (_) {}
    setVisible(false);
  };

  return (
    <div className="pwa-prompt">
      <button className="pwa-close" onClick={dismiss} aria-label="dismiss">
        <i className="fa-solid fa-xmark"/>
      </button>
      <div className="pwa-icon">
        <img src={LOGO_SVG} alt="" onError={(e) => { e.currentTarget.style.display='none'; }}/>
      </div>
      <div className="pwa-body">
        <div className="pwa-title">{tt('pwaTitle', 'Install BLOX.HOT')}</div>
        <div className="pwa-sub">{tt('pwaSub', 'Add to home screen for instant access')}</div>
      </div>
      <button className="pwa-install" onClick={install}>{tt('pwaInstall', 'Install')}</button>
    </div>
  );
}

// Toggles the body--ref-banner class while the welcome banner is mounted
// — older CSS used :has() which has spotty support in some webviews.
function ReferralBannerBodyClass({ active }) {
  React.useEffect(() => {
    if (!active) return;
    document.body.classList.add('body--ref-banner');
    return () => document.body.classList.remove('body--ref-banner');
  }, [active]);
  return null;
}

// ============================================
// ReferralWelcomeBanner — top strip shown to ANONYMOUS visitors who
// arrived via /r/{username}. Displays the inviter's avatar + handle so
// the user knows the link worked, and gives them a 1-click path to
// register. After successful register/login the existing referral-bind
// effect auto-applies the code in the background — banner self-destructs.
// ============================================
function ReferralWelcomeBanner({ profile, onRegister, onDismiss, t }) {
  if (!profile) return null;
  const initials = (profile.username || '?').slice(0, 2).toUpperCase();
  return (
    <div className="ref-welcome-banner" role="status" aria-live="polite">
      <div className="ref-welcome-av">
        {profile.avatar_url
          ? <img src={profile.avatar_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
          : <span className="ref-welcome-av-fb">{initials}</span>}
      </div>
      <div className="ref-welcome-text">
        <div className="ref-welcome-line1">
          🎁 {t?.refInvitedBy || 'You were invited by'} <b>@{profile.username}</b>
        </div>
        <div className="ref-welcome-line2">
          {t?.refSignupBonus || 'Sign up to claim +100 XP bonus — both of you get it.'}
        </div>
      </div>
      <div className="ref-welcome-cta">
        <button className="ref-welcome-btn" onClick={onRegister}>
          {t?.refSignUp || 'Sign up'} <i className="fa-solid fa-arrow-right"/>
        </button>
        <button className="ref-welcome-x" onClick={onDismiss} aria-label="dismiss">
          <i className="fa-solid fa-xmark"/>
        </button>
      </div>
    </div>
  );
}

// ============================================
// OfflineBanner — fixed top strip that appears when navigator.onLine flips
// to false. Reappears the moment connection comes back, then auto-hides
// after 2.5s with a "Back online" confirmation pulse.
// ============================================
function OfflineBanner() {
  const [online, setOnline] = React.useState(() => typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [justRestored, setJustRestored] = React.useState(false);
  React.useEffect(() => {
    const goOn  = () => { setOnline(true);  setJustRestored(true); setTimeout(() => setJustRestored(false), 2500); };
    const goOff = () => { setOnline(false); setJustRestored(false); };
    window.addEventListener('online',  goOn);
    window.addEventListener('offline', goOff);
    return () => {
      window.removeEventListener('online',  goOn);
      window.removeEventListener('offline', goOff);
    };
  }, []);
  if (online && !justRestored) return null;
  return (
    <div className={`offline-banner ${online ? 'is-restored' : 'is-offline'}`} role="status" aria-live="polite">
      <i className={`fa-solid ${online ? 'fa-wifi' : 'fa-plug-circle-xmark'}`}/>
      <span>{online
        ? tt('offlineBackOnline', 'Back online')
        : tt('offlineNoConnection', 'No connection — actions will resume when you reconnect')
      }</span>
    </div>
  );
}

// ============================================
// SIDE DRAWER — slides in from the left, used by the header hamburger on
// tablet/small-laptop widths. Mirrors the mobile-account-sheet UX: tap
// outside or swipe-left to dismiss, esc to close.
// ============================================
function SideDrawer({ open, onClose, activeNav, onNav, t, currentUser, onFairness, onAuthOpen, theme, onThemeToggle }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Prevent body scroll while drawer is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  const go = (page) => { onNav && onNav(page); onClose(); };
  const items = [
    { id: 'coinflip',    label: t.coinflipTab || 'Coinflip',    icon: 'fa-coins' },
    { id: 'dice',        label: t.diceTab     || 'Dice',        icon: 'fa-dice' },
    { id: 'rewards',     label: t.events      || 'Events',      icon: 'fa-trophy' },
    { id: 'leaderboard', label: t.leaderboard || 'Leaderboard', icon: 'fa-medal' },
  ];
  return ReactDOM.createPortal(
    <>
      <div className="hdr-drawer-backdrop is-open" onClick={onClose} aria-hidden="true"/>
      <aside className="hdr-drawer is-open" role="dialog" aria-modal="true" aria-label="Menu">
        <div className="hdr-drawer-head">
          <div className="hdr-drawer-brand">
            <span className="brandmark-icon" aria-hidden="true"/>
            <span className="brandmark-text">BLOX<span className="brandmark-dot">.</span><span className="brandmark-hot">HOT</span></span>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="close menu">
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        <div className="hdr-drawer-body">
          <div className="hdr-drawer-section">
            {items.map(it => (
              <button
                key={it.id}
                className={`hdr-drawer-link ${activeNav === it.id ? 'is-active' : ''}`}
                onClick={() => go(it.id)}
              >
                <i className={`fa-solid ${it.icon}`}/>
                <span>{it.label}</span>
                {activeNav === it.id && <i className="fa-solid fa-chevron-right hdr-drawer-chev"/>}
              </button>
            ))}
          </div>

          <div className="hdr-drawer-divider"/>

          <div className="hdr-drawer-section">
            <button className="hdr-drawer-link" onClick={() => {
              // Close the drawer first, then open the fairness modal on the
              // NEXT animation frame. If we did both at once React batches
              // them, but the drawer's slide-out timing + the modal's mount
              // racing on the same frame caused the modal to render at the
              // exact moment the drawer was still in the DOM at z-index
              // 9200 — and the modal at z-index 200 was hidden under it.
              // Deferring ensures the drawer is gone before the modal mounts.
              onClose();
              requestAnimationFrame(() => { onFairness && onFairness(); });
            }}>
              <i className="fa-solid fa-shield-halved"/>
              <span>{t.fairTitle || 'Provably fair'}</span>
            </button>
            {currentUser && (
              <button className="hdr-drawer-link" onClick={() => go('profile')}>
                <i className="fa-solid fa-user"/>
                <span>{t.masProfile || 'My profile'}</span>
              </button>
            )}
            {currentUser && (
              <button className="hdr-drawer-link" onClick={() => go('affiliates')}>
                <i className="fa-solid fa-users"/>
                <span>{t.pmAffiliates || 'Affiliates'}</span>
              </button>
            )}
            {currentUser && (
              <button className="hdr-drawer-link" onClick={() => go('rakeback')}>
                <i className="fa-solid fa-rotate-left"/>
                <span>{t.pmRakeback || 'Rakeback'}</span>
              </button>
            )}
            {currentUser && (
              <button className="hdr-drawer-link" onClick={() => go('settings')}>
                <i className="fa-solid fa-cog"/>
                <span>{t.masSettings || 'Settings'}</span>
              </button>
            )}
            <button className="hdr-drawer-link" onClick={() => { onThemeToggle && onThemeToggle(); }}>
              <i className={`fa-solid fa-${theme === 'dark' ? 'sun' : 'moon'}`}/>
              <span>{theme === 'dark' ? (t.pmLightTheme || 'Light theme') : (t.pmDarkTheme || 'Dark theme')}</span>
            </button>
          </div>

          {!currentUser && (
            <div className="hdr-drawer-cta">
              <button className="hdr-drawer-login" onClick={() => { onAuthOpen && onAuthOpen('login'); onClose(); }}>
                {t.loginTabLogin || 'Login'}
                <i className="fa-solid fa-arrow-right"/>
              </button>
            </div>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
}

// ============================================
// FAIRNESS MODAL — wraps the existing FairnessPage in a popup.
//
// Previous version closed immediately on open: when the opening click
// reached the just-mounted .modal-overlay (e.target === e.currentTarget
// happened to be true because the click bubbled past the now-existing
// overlay), useOverlayClose triggered close. Replaced with explicit
// click handlers that only close when both events land on the overlay
// itself, and the inner modal stops propagation so internal clicks
// never reach the overlay.
//
// Also: the inner FairnessPage already renders its own H1 "Provably
// fair" title, so the modal's own H3 was a duplicate. Removed.
// ============================================
function FairnessModal({ open, onClose, t }) {
  // ESC to close.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  // Track whether the click STARTED on the overlay itself. We only close
  // when down+up both happened on the overlay (not when the user clicked
  // inside the modal and released outside, and not when the click that
  // OPENED the modal happens to land here because of event bubbling).
  const downOnOverlay = React.useRef(false);
  // Suppress the opening click — when the modal mounts, the click that
  // triggered open is still bubbling. Ignore one mouseup right after mount.
  const ignoreFirstMouseUp = React.useRef(true);
  React.useEffect(() => {
    if (!open) return;
    ignoreFirstMouseUp.current = true;
    const id = setTimeout(() => { ignoreFirstMouseUp.current = false; }, 60);
    return () => clearTimeout(id);
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      style={{ zIndex: 9400 }}
      onMouseDown={(e) => {
        downOnOverlay.current = (e.target === e.currentTarget);
      }}
      onMouseUp={(e) => {
        const both = downOnOverlay.current && e.target === e.currentTarget;
        downOnOverlay.current = false;
        if (ignoreFirstMouseUp.current) { ignoreFirstMouseUp.current = false; return; }
        if (both) onClose();
      }}
    >
      <div
        className="modal fairness-modal-v2"
        style={{ width: 720, maxWidth: '100%' }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        <div className="modal-head fairness-modal-head">
          {/* No title here — FairnessPage already renders an H1.
              Just keep the close button anchored to the corner. */}
          <span/>
          <button className="modal-close" onClick={onClose} aria-label="close">
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>
        <div className="modal-body fairness-modal-body">
          <FairnessPage t={t}/>
        </div>
      </div>
    </div>,
    document.body
  );
}

function App() {
  // Lazy init reads the user's saved accent so a returning visitor sees
  // their chosen color (Emerald/Magenta/Blue) instantly — no orange flash
  // of unstyled brand. Falls back to TWEAKS_DEFAULTS if nothing saved or
  // localStorage is unavailable (private mode etc.).
  const [tweaks, setTweaks] = React.useState(() => {
    try {
      const saved = localStorage.getItem('bloxhot.accent');
      if (saved && SWATCHES.some(s => s.hex === saved)) {
        return { ...TWEAKS_DEFAULTS, accent: saved };
      }
    } catch (_) {}
    return TWEAKS_DEFAULTS;
  });
  const setTweak = (k, v) => {
    const next = { ...tweaks, [k]: v };
    setTweaks(next);
    // Persist accent picks so they survive reloads. We intentionally
    // do NOT persist showFeatured / showHero (those are dev-only knobs
    // toggled from the edit-mode iframe, not user-facing settings).
    if (k === 'accent') {
      try { localStorage.setItem('bloxhot.accent', String(v)); } catch (_) {}
    }
    window.parent?.postMessage({ type: '__edit_mode_set_keys', edits: { [k]: v } }, '*');
  };
  const [tweaksOpen, setTweaksOpen] = React.useState(false);

  const [activeNav, setActiveNavState] = React.useState(() => {
    // Honour the page user landed on (index.php / leaderboard.php / rewards.php)
    return window.BLOXHOT_INITIAL_PAGE || 'coinflip';
  });
  // setActiveNav also updates the URL so deep-links + back/forward work.
  // .htaccess rewrites /leaderboard → leaderboard.php and 301-redirects .php → clean.
  const setActiveNav = React.useCallback((p) => {
    setActiveNavState(p);
    try {
      const clean = p === 'coinflip' || p === 'index' ? '/' : `/${p}`;
      if (window.location.pathname !== clean) {
        window.history.pushState({ page: p }, '', clean);
      }
    } catch (_) {}
    // Force inventory refresh when switching between game tabs so items
    // locked in coinflip don't appear available in dice (and vice-versa).
    // Without this, the dice create modal would show items the user
    // already wagered in a coinflip until the next 45s heartbeat. The
    // `locked_flip_id` is set server-side immediately on flip create —
    // we just need the client to re-fetch /api/me.
    if (p === 'dice' || p === 'coinflip') {
      // refreshMe defined below — guard for hoisting via ref pattern
      try { refreshMeRef.current && refreshMeRef.current(); } catch (_) {}
    }
  }, []);
  // Ref so setActiveNav (defined before refreshMe) can call the latest one.
  // Wired up via useEffect after refreshMe is defined further down.
  const refreshMeRef = React.useRef(null);
  // Same pattern for refreshFlips — the SSE handler must call the LATEST
  // refreshFlips (it closes over currentUser); calling a stale first-render
  // copy meant after login the "is this my flip / auto-open modal" logic ran
  // with currentUser=null until a reconnect. The ref always holds the current one.
  const refreshFlipsRef = React.useRef(null);
  // Mobile swipe-between-tabs — horizontal swipe on the main content
  // navigates between Coinflip ↔ Dice ↔ Events ↔ Leaderboard. Threshold
  // 80px / 250ms ignores scroll-style drags so vertical scrolling still
  // works normally. Pages with their OWN horizontal interactions (modal,
  // chat side, banner carousel) call e.stopPropagation on touchstart so
  // they aren't accidentally swiped over.
  React.useEffect(() => {
    const SWIPE_ORDER = ['coinflip', 'dice', 'rewards', 'leaderboard'];
    let touchX0 = 0, touchY0 = 0, touchT0 = 0;
    const onStart = (e) => {
      if (!e.touches?.[0]) return;
      // Ignore swipes that originate from interactive horizontal elements
      const blocked = e.target.closest('.banner-carousel, .subnav, .modal-overlay, .chat-side, .flip-arena, .inv-grid, .flip-grid');
      if (blocked) { touchT0 = 0; return; }
      touchX0 = e.touches[0].clientX;
      touchY0 = e.touches[0].clientY;
      touchT0 = Date.now();
    };
    const onEnd = (e) => {
      if (!touchT0) return;
      const dt = Date.now() - touchT0;
      const touch = e.changedTouches?.[0];
      if (!touch) return;
      const dx = touch.clientX - touchX0;
      const dy = touch.clientY - touchY0;
      touchT0 = 0;
      // Must be a quick mostly-horizontal swipe of decent distance
      if (Math.abs(dx) < 80 || Math.abs(dy) > Math.abs(dx) || dt > 500) return;
      const idx = SWIPE_ORDER.indexOf(activeNav);
      if (idx < 0) return;
      const next = dx < 0 ? idx + 1 : idx - 1;
      if (next >= 0 && next < SWIPE_ORDER.length) {
        setActiveNav(SWIPE_ORDER[next]);
      }
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend',   onEnd);
    };
  }, [activeNav, setActiveNav]);

  React.useEffect(() => {
    // Map URL path → activeNav. Runs on first load AND on back/forward
    // browser navigation. New SPA routes (rakeback, affiliates, dice…) are
    // listed here AND in .htaccess; both lists must stay in sync.
    const pathToNav = () => {
      const path = window.location.pathname || '';
      if (path.includes('leaderboard')) return 'leaderboard';
      if (path.includes('rewards') || path.includes('events')) return 'rewards';
      if (path.includes('history'))    return 'profile';
      if (path.includes('profile'))    return 'profile';
      if (path.includes('fairness'))   return 'fairness';
      if (path.includes('settings'))   return 'settings';
      if (path.includes('admin'))      return 'admin';
      if (path.includes('rakeback'))   return 'rakeback';
      if (path.includes('affiliates')) return 'affiliates';
      if (path.includes('dice'))       return 'dice';
      return 'coinflip';
    };
    // Initial load — set activeNav from URL so /rakeback (etc.) refresh
    // doesn't drop the user back on the coinflip lobby.
    setActiveNavState(pathToNav());
    const onPop = () => setActiveNavState(pathToNav());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [lang, setLang]   = React.useState(() => {
    try {
      // 1) User-set preference always wins
      const saved = localStorage.getItem('flipline.lang');
      if (saved) return saved;
      // 2) Browser language is a free second-best
      const nav = (navigator.language || navigator.userLanguage || '').toLowerCase().slice(0, 2);
      const SUPPORTED = ['tr','en','ru','es','it','fr','de','ph','zh','ja','uk','ar','ur'];
      if (SUPPORTED.includes(nav)) return nav;
    } catch (_) {}
    return 'en';
  });
  // First-load IP geolocation fallback — fires only when the user has NO
  // saved language AND browser language wasn't recognized. Uses Cloudflare's
  // free public endpoint (no API key, no CORS issues). Result is cached so
  // subsequent loads use it without a network roundtrip.
  React.useEffect(() => {
    try {
      if (localStorage.getItem('flipline.lang')) return; // user has chosen
      if (localStorage.getItem('flipline.langGeoTried')) return; // already tried this device
      const COUNTRY_TO_LANG = {
        TR: 'tr', US: 'en', GB: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en',
        RU: 'ru', BY: 'ru', KZ: 'ru', UA: 'uk',
        ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
        IT: 'it', FR: 'fr', BE: 'fr', CH: 'fr',
        DE: 'de', AT: 'de',
        PH: 'ph',
        CN: 'zh', TW: 'zh', HK: 'zh',
        JP: 'ja',
        SA: 'ar', AE: 'ar', EG: 'ar', IQ: 'ar', JO: 'ar', KW: 'ar', LB: 'ar', OM: 'ar', QA: 'ar', SY: 'ar', YE: 'ar',
        PK: 'ur',
      };
      // Cloudflare's trace endpoint returns plain text including `loc=XX`.
      // No API key, no quota, instant on most pages already proxied via CF.
      fetch('https://www.cloudflare.com/cdn-cgi/trace', { cache: 'no-store' })
        .then(r => r.text())
        .then(text => {
          const m = /loc=([A-Z]{2})/.exec(text);
          const cc = m && m[1];
          const next = cc && COUNTRY_TO_LANG[cc];
          if (next) setLang(next);
          try { localStorage.setItem('flipline.langGeoTried', '1'); } catch (_) {}
        })
        .catch(() => {
          try { localStorage.setItem('flipline.langGeoTried', '1'); } catch (_) {}
        });
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Chat is always open by default on every page load (no localStorage memory).
  // User can still toggle within the session via the header chat button.
  const [chatOpen, setChatOpen] = React.useState(() => {
    // Auto-open only on real desktop (≥1451px). On tablet (981-1450px) the
    // chat sidebar would eat too much horizontal room next to the lobby
    // grid, so users open it explicitly via the header chat icon and it
    // appears as an overlay (same UX as mobile). On phones (≤980px) it's
    // closed too — opened from the bottom mobile-nav chat button.
    try { return window.innerWidth >= 1451; } catch (_) { return true; }
  });
  // Memoize translations so FlipCard's React.memo can actually skip re-renders.
  // Without this, every parent render creates a new `t` reference → all cards re-render.
  const t = React.useMemo(() => ({ ...TRANSLATIONS.en, ...(TRANSLATIONS[lang] || {}) }), [lang]);
  const [flips, setFlips] = React.useState([]);
  const [chat, setChat]   = React.useState([]);
  const [userInventory, setUserInventory] = React.useState([]);
  const [currentUser, setCurrentUser] = React.useState(null);
  const [bootLoading, setBootLoading] = React.useState(true);
  const [showAuth, setShowAuth] = React.useState(null); // null | 'login' | 'register'
  const [notifications, setNotifications] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('bloxhot.notif') || '[]'); } catch (_) { return []; }
  });
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [flipCtx, setFlipCtx] = React.useState(null); // { flip, pos }
  const [showLevelRewards, setShowLevelRewards] = React.useState(false);
  const [showBigWin, setShowBigWin] = React.useState(false);
  const [bigWinData, setBigWinData] = React.useState(null); // { total, items: [...] }
  // Quests removed per user request — replaced by Level Rewards
  const showQuests = false;
  const setShowQuests = () => {};
  const [showFriends, setShowFriends] = React.useState(false);
  const [showMobileAccount, setShowMobileAccount] = React.useState(false);
  const [userPop, setUserPop] = React.useState(null); // { username, pos }
  const [tipTarget, setTipTarget] = React.useState(null); // user object
  const [tipReceived, setTipReceived] = React.useState(null); // {sender_username, item_name, ...}
  const [siteConfig, setSiteConfig] = React.useState({
    maintenance_mode: false, maintenance_message: '',
    broadcast_message: '', broadcast_active: false,
    xp_multiplier: 1.0, house_edge_pct: 0.05,
  });
  React.useEffect(() => {
    const load = () => apiFetch('/api/config').then(setSiteConfig).catch(() => {});
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    // Allow any code (e.g. the admin Pin Message action) to ask for an
    // immediate site-config refresh — that way the pinning admin sees the
    // pin land in the chat strip before the next 60s tick.
    const onForce = () => load();
    window.addEventListener('blox:config-refresh', onForce);
    return () => { clearInterval(t); window.removeEventListener('blox:config-refresh', onForce); };
  }, []);
  const openUserPop = React.useCallback((username, pos) => {
    if (!username) return;
    setUserPop({ username, pos: pos || { x: window.innerWidth/2, y: window.innerHeight/2 } });
  }, []);

  // Public profile URL routing — when user lands on /u/{username} the
  // .htaccess rewrites to index.php; here we detect the path and open the
  // matching user popover automatically so it works as a vanity share link.
  React.useEffect(() => {
    try {
      const m = window.location.pathname.match(/^\/u\/([A-Za-z0-9_]{3,20})\/?$/);
      if (m && m[1]) {
        openUserPop(m[1]);
        // Strip the path so back-button doesn't re-trigger on history nav
        window.history.replaceState({}, '', '/');
      }
    } catch (_) {}
  }, [openUserPop]);
  const navToProfile = React.useCallback((username) => {
    try { window.history.pushState({ page: 'profile' }, '', `/profile?u=${encodeURIComponent(username)}`); } catch (_) {}
    setActiveNav('profile');
    // pushState doesn't fire popstate; tell ProfilePage to re-read the URL
    try { window.dispatchEvent(new Event('bloxhot:navProfile')); } catch (_) {}
  }, [setActiveNav]);
  const pushNotif = React.useCallback((n) => {
    setNotifications(prev => {
      const next = [{ id: Date.now() + Math.random(), ts: Date.now(), read: false, ...n }, ...prev].slice(0, 30);
      try { localStorage.setItem('bloxhot.notif', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);
  const markNotifsRead = React.useCallback(() => {
    setNotifications(prev => {
      const next = prev.map(n => ({ ...n, read: true }));
      try { localStorage.setItem('bloxhot.notif', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);
  const clearNotifs = React.useCallback(() => {
    setNotifications([]);
    try { localStorage.removeItem('bloxhot.notif'); } catch (_) {}
  }, []);
  const [filter, setFilter] = React.useState('all');
  const [side, setSide] = React.useState('any');
  const [sort, setSort] = React.useState('newest');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showCreate, setShowCreate] = React.useState(false);
  const [showInventory, setShowInventory] = React.useState(false);
  const [showGiveawayCreator, setShowGiveawayCreator] = React.useState(false);
  // Hamburger drawer (tablet-only via CSS) + Fairness popup — both lift to
  // App level so any sub-page can open them.
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [fairnessOpen, setFairnessOpen] = React.useState(false);
  // Footer & drawer fire window-level events to open the fairness modal
  // and to navigate to specific pages. Listen here at App level.
  React.useEffect(() => {
    const openFair = () => setFairnessOpen(true);
    const navigate = (e) => { const page = e?.detail?.page; if (page) setActiveNav(page); };
    window.addEventListener('blox:open-fairness', openFair);
    window.addEventListener('blox:nav', navigate);
    return () => {
      window.removeEventListener('blox:open-fairness', openFair);
      window.removeEventListener('blox:nav', navigate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The GiveawayStrip dispatches `blox:openGiveawayCreator` when the user
  // clicks the "Host" button. We catch it here at the App level so the
  // modal sits at the correct z-index (above chat).
  React.useEffect(() => {
    const open = () => {
      if (!currentUser) { setShowAuth('login'); return; }
      setShowGiveawayCreator(true);
    };
    window.addEventListener('blox:openGiveawayCreator', open);
    return () => window.removeEventListener('blox:openGiveawayCreator', open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  const [showBotDeposit, setShowBotDeposit] = React.useState(false);
  const [pendingJoin, setPendingJoin] = React.useState(null);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [toasts, setToasts] = React.useState([]);
  const [totalTrades, setTotalTrades] = React.useState(0);
  const [theme, setTheme] = React.useState(() => {
    // Default to LIGHT theme. Dark stays available via the toggle.
    try { return localStorage.getItem('flipline.theme') || 'light'; } catch (_) { return 'light'; }
  });
  const [winStreak, setWinStreak] = React.useState(0);
  // Tilt counter — consecutive losses across sessions. Persisted in
  // localStorage; reset on win. Shown as a chip on the profile + a
  // friendly toast at milestone thresholds (3, 5, 8…).
  const [tiltCount, setTiltCount] = React.useState(() => {
    try { return parseInt(localStorage.getItem('bloxhot.tiltCount') || '0', 10) || 0; } catch (_) { return 0; }
  });
  // Expose for other components (ProfilePage chip etc) via window global.
  React.useEffect(() => { try { window.__bloxhotTilt = tiltCount; } catch (_) {} }, [tiltCount]);
  const [showLucky7, setShowLucky7] = React.useState(false);
  const [showTutorial, setShowTutorial] = React.useState(false);
  const [pendingConfirm, setPendingConfirm] = React.useState(null); // { title, message, onConfirm, askable }
  const [showValueManager, setShowValueManager] = React.useState(false);
  const [viewingFlip, setViewingFlip] = React.useState(null); // flip object
  const [matchingFlip, setMatchingFlip] = React.useState(null); // flip being matched (open MatchModal)
  // Debounced search query (updates lobby filter 200ms after typing stops)
  const [searchInput, setSearchInput] = React.useState('');
  // Forced: always 'list' on desktop. Mobile @media in CSS overrides the
  // grid to a card layout regardless. No toggle, no user choice.
  const flipView = 'list';
  const setFlipView = () => {}; // no-op to avoid breaking call sites
  React.useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Balance is total worth of inventory
  const balance = React.useMemo(() => userInventory.reduce((s, it) => s + (it.price || 0), 0), [userInventory]);

  // Eager-load the custom emoji manifest so chat messages can render
  // :code: tokens as <img> on the very first paint. Without this, the
  // manifest only loaded when the emoji picker was first opened, and
  // any chat msg containing :code: rendered as literal text for the
  // first few seconds — felt like "chat lag" / "emojis don't load".
  // The `_v` bump forces a re-render of all chat msgs once the manifest
  // arrives so any already-rendered text is reprocessed.
  const [, _bumpEmojiTick] = React.useState(0);
  React.useEffect(() => {
    if (_customEmojiCache) return; // already cached this session
    let cancelled = false;
    loadCustomEmojis().then(() => { if (!cancelled) _bumpEmojiTick(t => t + 1); });
    return () => { cancelled = true; };
  }, []);

  // Real online count from /api/online — counts distinct user_ids whose
  // session row was touched in the last 5 minutes. Polls every 20s.
  const [onlineCount, setOnlineCount] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const fetchOnline = () => apiFetch('/api/online')
      .then(r => { if (!cancelled) setOnlineCount(Number(r.online) || 0); })
      .catch(() => {});
    fetchOnline();
    const id = setInterval(fetchOnline, 20_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Dice lobby summary — polled at the App level so the SubNav can show
  // active dice value next to the "Dice" pill (like Coinflip's pill).
  // Refreshed every 4s so the pill value visibly ticks without a manual
  // refresh. The full dice page poll (3s) replaces this when active.
  const [diceTotalValue, setDiceTotalValue] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const fetchDice = () => apiFetch('/api/dice')
      .then(r => {
        if (cancelled) return;
        const open = (r.matches || []).filter(m => m.state === 'open');
        setDiceTotalValue(open.reduce((s, m) => s + (Number(m.creator_value) || 0), 0));
      })
      .catch(() => {});
    fetchDice();
    const id = setInterval(fetchDice, 4_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Live stats — derived from current flips state. Total value + total
  // items count ONLY active flips (open + rolling). Resolved flips stick
  // around in the lobby for context but they're not "in play" anymore, so
  // it would be misleading to include them in the headline pot.
  const stats = React.useMemo(() => {
    const active = flips.filter(f => f.state === 'open' || f.state === 'rolling');
    const totalValue = active.reduce((s, f) => {
      // Include both sides of the pot for in-progress 2v2s etc.
      const items  = (f.items  || []);
      const oItems = (f.oItems || []);
      return s + flipValue(items) + flipValue(oItems);
    }, 0);
    const totalItems = active.reduce((s, f) => s + (f.items?.length || 0) + (f.oItems?.length || 0), 0);
    return {
      online: onlineCount, // real — from /api/online
      totalValue,
      totalItems,
      activeGames: active.length,
      biggestWin: 182,
      diceValue: diceTotalValue,
    };
  }, [flips, onlineCount, diceTotalValue]);

  React.useEffect(() => {
    const sw = SWATCHES.find(s => s.hex === tweaks.accent) || SWATCHES[0];
    document.documentElement.style.setProperty('--orange', sw.hex);
    document.documentElement.style.setProperty('--orange-h', sw.hover);
    document.documentElement.style.setProperty('--orange-soft', sw.soft);
  }, [tweaks.accent]);

  // Mirror current user into window globals so modals rendered outside
  // App's prop tree (DiceResolvedModal etc.) can fall back to them when
  // a match's username/avatar field is missing.
  React.useEffect(() => {
    try {
      window.__currentUserName   = currentUser?.username || null;
      window.__currentUserAvatar = currentUser?.avatar_url || null;
    } catch (_) {}
  }, [currentUser]);

  // Persist language + apply dir/lang on <html>. Also mirror the choice
  // into a `bloxhot_lang` cookie so server-rendered pages (404.php,
  // future error pages) can show the user's language without needing
  // localStorage access.
  React.useEffect(() => {
    try { localStorage.setItem('flipline.lang', lang); } catch (_) {}
    try {
      const oneYear = 60 * 60 * 24 * 365;
      document.cookie = `bloxhot_lang=${encodeURIComponent(lang)}; path=/; max-age=${oneYear}; SameSite=Lax`;
    } catch (_) {}
    const rtl = ['ar', 'ur'].includes(lang);
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  // Chat-open state is intentionally NOT persisted — defaults open on each visit.

  // Bootstrap: hit /api/me to find out if we have a session, populate inventory.
  // `keepUserOnNull` is set immediately after login — if /api/me hasn't seen the
  // brand-new cookie yet, don't clobber the user we just got from /api/auth/verify.
  // Force a `/api/me` refresh whenever ANY giveaway state changes anywhere
  // in the app — this is what makes giveaway-locked items vanish from
  // inventory + create-flip pickers without the user reloading the page.
  // (Hosting a giveaway sets locked_flip_id = -1 server-side; the client
  // needs a fresh fetch to see that update.)
  React.useEffect(() => {
    const handler = () => { refreshMeRef.current && refreshMeRef.current(); };
    window.addEventListener('blox:giveawayChanged', handler);
    return () => window.removeEventListener('blox:giveawayChanged', handler);
  }, []);

  const refreshMe = React.useCallback(async ({ keepUserOnNull = false } = {}) => {
    try {
      let res = await apiFetch('/api/me');
      // MOBILE FIX: if /api/me reports null user but we still have a
      // Bearer token in localStorage, the cookie is the most likely
      // culprit — mobile Safari sometimes sends a stale or empty value
      // that the server treats as "no auth". Retry once with cookies
      // omitted so the server has to use the Bearer header.
      if (!res.user && authToken.get()) {
        const retry = await _apiFetchOnce('/api/me', {}, { omitCookies: true });
        if (retry.res.ok && retry.data && retry.data.user) {
          res = retry.data;
        }
      }
      if (res.user) {
        setCurrentUser(res.user);
        const inv = (res.inventory || []).map(it => ({
          id: it.id,
          name: it.name,
          rarity: it.rarity,
          price: Number(it.price) || 0,
          lockedFlipId: it.locked_flip_id,
        }));
        setUserInventory(inv);
        // Surface tips received in the last 5 minutes as toasts AND fire a
        // big bouncing-item + confetti animation for the most recent one.
        if (Array.isArray(res.recent_tips) && res.recent_tips.length > 0) {
          try {
            const KEY = 'bloxhot.seenTips';
            const seen = new Set(JSON.parse(localStorage.getItem(KEY) || '[]'));
            const fresh = res.recent_tips.filter(t => !seen.has(t.id));
            for (const tip of fresh) {
              const sender = tip.sender_display || tip.sender_username || 'Someone';
              setToasts(ts => [...ts, {
                id: 'tip-' + tip.id + '-' + Date.now(),
                type: 'win',
                text: `💝 ${sender} tipped you ${tip.item_name}!`,
              }]);
              seen.add(tip.id);
            }
            if (fresh.length > 0) {
              localStorage.setItem(KEY, JSON.stringify([...seen].slice(-200)));
              // Trigger the big tip animation for the newest tip
              setTipReceived(fresh[0]);
            }
          } catch (_) {}
        }
      } else if (!keepUserOnNull) {
        // Server explicitly returned {user: null} — confirmed logged out.
        setCurrentUser(null);
      }
    } catch (err) {
      // CRITICAL: only treat an EXPLICIT auth failure as "logged out".
      // Network errors, 5xx server blips, Cloudflare hiccups on mobile,
      // DNS retries on flaky cellular, etc. used to blank currentUser
      // here too — which made phones look randomly logged out every
      // time the user lost signal for a second. Now we leave the
      // last-known user in place so the UI stays stable, and just try
      // again on the next poll tick.
      const isAuthFail = err && err.code === 'not_authenticated' && err.status === 401;
      if (isAuthFail && !keepUserOnNull) setCurrentUser(null);
    }
  }, []);
  // Expose refreshMe to non-React listeners (giveaway-change window event)
  React.useEffect(() => { refreshMeRef.current = refreshMe; }, [refreshMe]);
  React.useEffect(() => {
    (async () => {
      await refreshMe();
      setBootLoading(false);
    })();
  }, [refreshMe]);

  // Cross-component flip viewer: any child can dispatch
  //   window.dispatchEvent(new CustomEvent('blox:viewFlip', { detail: <flip> }))
  // and the App-level FlipDetailModal will open. Used by ProfilePage's
  // recent-activity rows to play any past flip as a popup.
  React.useEffect(() => {
    const handler = (e) => { if (e.detail) setViewingFlip(e.detail); };
    window.addEventListener('blox:viewFlip', handler);
    return () => window.removeEventListener('blox:viewFlip', handler);
  }, []);

  // Deep-link flip replay — if URL has ?flip=N, fetch the public replay and
  // open the FlipDetailModal. Lets users share links like /?flip=1234 to
  // show off a past win or let a friend watch.
  // Referral capture — accepts two URL shapes:
  //   /r/{username}   — preferred share format
  //   /?ref={CODE}    — legacy 6-char code
  // The pending ref (username OR code) is stashed in localStorage; on next
  // login /api/referral consumes it.
  // Pending referral profile data — looked up so the welcome banner can
  // show the referrer's avatar + username. Lives in state, also cached in
  // localStorage so a quick refresh doesn't re-fetch.
  const [pendingRefProfile, setPendingRefProfile] = React.useState(() => {
    try {
      const raw = localStorage.getItem('bloxhot.pendingRefProfile');
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  });

  React.useEffect(() => {
    try {
      // /r/{username}
      const m = /^\/r\/([A-Za-z0-9_]{3,20})\/?$/.exec(window.location.pathname);
      if (m) {
        const u = m[1];
        if (!localStorage.getItem('bloxhot.pendingRef')) {
          localStorage.setItem('bloxhot.pendingRef', '@' + u);
        }
        // Show the welcome banner IMMEDIATELY with a minimal profile (just
        // the username) so the user sees feedback right away — no waiting
        // on an /api/user fetch that could fail and leave the page blank.
        // We THEN async-enrich it with avatar/display_name in the background.
        const minimal = { username: u, display_name: u, avatar_url: '', level: 0 };
        try { localStorage.setItem('bloxhot.pendingRefProfile', JSON.stringify(minimal)); } catch (_) {}
        setPendingRefProfile(minimal);
        // Replace URL FIRST so the user lands at "/" and the rest of the
        // app boot path is fully normal (no /r/ in window.location). This
        // protects against any other code that branches on pathname.
        try { window.history.replaceState({}, '', '/'); } catch (_) {}
        // Best-effort profile enrich — failures are silent, the minimal
        // banner stays. Fire-and-forget, no blocking on this promise.
        setTimeout(() => {
          apiFetch('/api/user?username=' + encodeURIComponent(u))
            .then(r => {
              if (!r || !r.username) return;
              const enriched = {
                username: r.username,
                display_name: r.display_name || r.username,
                avatar_url: r.avatar_url || '',
                level: r.level || 1,
              };
              try { localStorage.setItem('bloxhot.pendingRefProfile', JSON.stringify(enriched)); } catch (_) {}
              setPendingRefProfile(enriched);
            })
            .catch(() => {});
        }, 100);
        return;
      }
      // ?ref=CODE
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').toUpperCase();
      if (ref && /^[A-Z0-9]{6}$/.test(ref)) {
        if (!localStorage.getItem('bloxhot.pendingRef')) {
          localStorage.setItem('bloxhot.pendingRef', ref);
        }
        params.delete('ref');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
      }
    } catch (_) {}
  }, []);

  // Auto-apply pending referral once the user is authenticated. Fires once
  // per session; the backend returns 409 if already referred — silent OK.
  React.useEffect(() => {
    if (!currentUser) return;
    try {
      const pending = localStorage.getItem('bloxhot.pendingRef');
      if (!pending) return;
      // Username-based pending starts with '@' — backend resolves it to code.
      const body = pending.startsWith('@')
        ? { username: pending.slice(1) }
        : { code: pending };
      apiFetch('/api/referral', { method: 'POST', body })
        .then(() => {
          pushToast && pushToast({ type: 'win', text: tt('refApplied', 'Referral applied! +100 XP') });
        })
        .catch(() => { /* already_referred or invalid — silent */ })
        .finally(() => {
          try {
            localStorage.removeItem('bloxhot.pendingRef');
            localStorage.removeItem('bloxhot.pendingRefProfile');
          } catch (_) {}
          setPendingRefProfile(null);
        });
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flipId = parseInt(params.get('flip') || '0', 10);
    if (!flipId) return;
    apiFetch(`/api/flip-public?id=${flipId}`).then(res => {
      const f = res.flip;
      if (!f) return;
      const creator = f.creator ? {
        id: f.creator.id, name: f.creator.username,
        username: f.creator.username, avatarUrl: f.creator.avatar_url || '',
        lvl: f.creator.level || 1, av: synthAvatar(f.creator.username),
        color: deterministicColor(f.creator.id),
      } : null;
      const opponent = f.opponent ? {
        id: f.opponent.id, name: f.opponent.username,
        username: f.opponent.username, avatarUrl: f.opponent.avatar_url || '',
        lvl: f.opponent.level || 1, av: synthAvatar(f.opponent.username),
        color: deterministicColor(f.opponent.id),
      } : null;
      setViewingFlip({
        id: `replay-${f.id}`, srvId: f.id,
        state: f.state, side: f.side, result: f.result,
        c: creator, o: opponent,
        items: f.creator_items || [], oItems: f.opponent_items || [],
        winnerId: f.winner_id,
        serverSeed: f.server_seed, serverSeedHash: f.server_seed_hash,
        creatorSeed: f.creator_seed, joinerSeed: f.joiner_seed,
        nonce: f.nonce, participants: [],
        isReplay: true,
      });
      // Clean URL so refresh doesn't reopen
      try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}
    }).catch((err) => {
      // Surface to console so a stale endpoint surface doesn't fail silently.
      console.warn('[flip-share] failed to open flip ' + flipId, err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-device "you missed a flip" auto-open REMOVED per user request.
  // FlipDetailModal no longer pops up on page load — users can still open
  // any past flip from Profile → Recent Activity or a shared /?flip=N link.

  // Private-flip invite handling removed.

  // Persist & apply theme on <html data-theme>
  React.useEffect(() => {
    try { localStorage.setItem('flipline.theme', theme); } catch (_) {}
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Lucky 7 trigger
  React.useEffect(() => {
    if (winStreak === 7) {
      setShowLucky7(true);
    }
  }, [winStreak]);

  // Mobile keyboard handling: when an input is focused on a small viewport,
  // body.input-focused hides the mobile-nav so the input is reachable above the keyboard.
  React.useEffect(() => {
    const onFocus = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        document.body.classList.add('input-focused');
      }
    };
    const onBlur = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        document.body.classList.remove('input-focused');
      }
    };
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    return () => {
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onBlur);
    };
  }, []);

  React.useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === '__activate_edit_mode') setTweaksOpen(true);
      if (e.data?.type === '__deactivate_edit_mode') setTweaksOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent?.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Pull-to-refresh removed — was conflicting with modal scrolling.

  // Lock body scroll while any modal/sheet is open so background can't move
  // under the user's finger on mobile.
  const anyModalOpen = !!(showAuth || showInventory || showCreate || showBotDeposit ||
    pendingJoin || viewingFlip || matchingFlip || pendingConfirm || showLevelRewards ||
    showMobileAccount || flipCtx || userPop);
  React.useEffect(() => {
    if (!anyModalOpen) return;
    // iOS Safari needs more than `overflow: hidden` on body — touch-scroll
    // still works under modals unless we PIN the body in place with
    // `position: fixed`. Save the current scrollY so we can restore it
    // when the modal closes (otherwise the page jumps to top).
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const root = document.getElementById('root');
    const prev = {
      bodyOverflow:   document.body.style.overflow,
      bodyPosition:   document.body.style.position,
      bodyTop:        document.body.style.top,
      bodyLeft:       document.body.style.left,
      bodyRight:      document.body.style.right,
      bodyWidth:      document.body.style.width,
      htmlOverflow:   document.documentElement.style.overflow,
      rootOverflow:   root ? root.style.overflow : '',
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top      = `-${scrollY}px`;
    document.body.style.left     = '0';
    document.body.style.right    = '0';
    document.body.style.width    = '100%';
    document.documentElement.style.overflow = 'hidden';
    if (root) root.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.position = prev.bodyPosition;
      document.body.style.top      = prev.bodyTop;
      document.body.style.left     = prev.bodyLeft;
      document.body.style.right    = prev.bodyRight;
      document.body.style.width    = prev.bodyWidth;
      document.documentElement.style.overflow = prev.htmlOverflow;
      if (root) root.style.overflow = prev.rootOverflow;
      // Restore scroll position — the `position: fixed` trick scrolls the
      // page to top, so we have to put it back manually.
      window.scrollTo(0, scrollY);
    };
  }, [anyModalOpen]);

  // ===== Lobby polling — /api/flips every 3s while logged in =====
  const flipsRef = React.useRef(flips);
  React.useEffect(() => { flipsRef.current = flips; }, [flips]);

  const [lobbyLoading, setLobbyLoading] = React.useState(true);
  const [newFlipsCount, setNewFlipsCount] = React.useState(0);
  const flipsRefForDiff = React.useRef([]);
  // Map<flipSrvId, revealAtTimestamp> — used by the spectator hold. A flip
  // is rendered as 'rolling' even if the server says 'resolved' until
  // Date.now() >= revealAt. Cleared lazily after expiry.
  const flipRevealMapRef = React.useRef(new Map());
  // Bump counter to force re-render once per second so reveal timers expire.
  const [revealTick, setRevealTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => {
      if (flipRevealMapRef.current.size === 0) return;
      // Clean expired entries + force re-render
      const now = Date.now();
      let changed = false;
      for (const [k, v] of flipRevealMapRef.current.entries()) {
        if (v <= now) { flipRevealMapRef.current.delete(k); changed = true; }
      }
      if (changed) setRevealTick(t => t + 1);
      else setRevealTick(t => t + 1); // still tick so the renderer notices < 1s remaining
    }, 500);
    return () => clearInterval(id);
  }, []);

  const pendingJoinRef = React.useRef(null);
  React.useEffect(() => { pendingJoinRef.current = pendingJoin; }, [pendingJoin]);

  // Track which flip IDs we've already opened the animation for, so we don't
  // re-trigger it on every poll. Persisted across page reloads via
  // sessionStorage so a quick F5 doesn't replay an already-watched flip.
  const shownAnimationsRef = React.useRef(new Set(
    (() => { try { return JSON.parse(sessionStorage.getItem('bloxhot.shownAnims') || '[]'); } catch (_) { return []; } })()
  ));
  const markShown = (id) => {
    shownAnimationsRef.current.add(id);
    try {
      sessionStorage.setItem('bloxhot.shownAnims',
        JSON.stringify(Array.from(shownAnimationsRef.current).slice(-50)));
    } catch (_) {}
  };
  // Have we run refreshFlips at least once? On the FIRST run we seed
  // shownAnimationsRef with all currently-non-open flips that involve the
  // user — so opening the page doesn't blast modals for stuff that already
  // resolved before they arrived. After that, the detector fires only for
  // genuinely new transitions.
  const initialSeedDoneRef = React.useRef(false);
  // If the user logs in AFTER first refresh, we need to seed once more for
  // their identity — otherwise the very first poll after login would open
  // modals for any of their pre-existing rolling/resolved flips.
  React.useEffect(() => {
    initialSeedDoneRef.current = false;
  }, [currentUser?.id]);

  const refreshFlips = React.useCallback(async () => {
    try {
      const res = await apiFetch('/api/flips');
      // Sync the shared server clock FIRST — every reveal-timing calc below
      // depends on serverNow() being aligned with the backend.
      if (res && res.server_now) syncServerClock(res.server_now);
      const mapped = (res.flips || []).map(f => mapApiFlip(f, currentUser?.id ?? null));

      if (currentUser) {
        const myId = currentUser.id;
        const isMyFlip = (f) => {
          if (f.c && f.c.id === myId) return true;
          if (f.o && f.o.id === myId) return true;
          if ((f.participants || []).some(p => Number(p.user_id) === Number(myId))) return true;
          return false;
        };

        if (!initialSeedDoneRef.current) {
          // SEED PASS — on the very first refresh, mark every currently-
          // non-open flip the user is in as already-shown. We never trigger
          // the modal on page-load for pre-existing rolling/resolved flips.
          for (const f of mapped) {
            if (f.state === 'rolling' || f.state === 'resolved') {
              if (isMyFlip(f)) shownAnimationsRef.current.add(f.srvId);
            }
          }
          try {
            sessionStorage.setItem('bloxhot.shownAnims',
              JSON.stringify(Array.from(shownAnimationsRef.current).slice(-50)));
          } catch (_) {}
          initialSeedDoneRef.current = true;
        } else if (!pendingJoinRef.current) {
          // SUBSEQUENT PASSES — open the animation modal the moment we SEE
          // one of our flips in rolling or resolved state for the first time
          // (and we haven't already shown it). No timezone-fragile time
          // checks — we only depend on whether the flip ID was previously
          // seen in our shown set, which is robust.
          for (const f of mapped) {
            if (f.state !== 'rolling' && f.state !== 'resolved') continue;
            if (shownAnimationsRef.current.has(f.srvId)) continue;
            if (!isMyFlip(f)) continue;
            const yourSide = (f.c && f.c.id === myId) ? f.side : (f.side === 'heads' ? 'tails' : 'heads');
            markShown(f.srvId);
            setPendingJoin({ flip: f, yourSide });
            break;
          }
        }
      }

      // SPECTATOR REVEAL HOLD — when a flip first appears as 'resolved' in
      // our poll, schedule a 3-second "reveal at" timestamp. The lobby-
      // grid renderer will treat such flips as still 'rolling' visually
      // until the timestamp passes — matching the 3-second coin animation
      // that the two players see in the modal. Without this, spectators
      // saw the result instantly while players were still watching the
      // coin spin → big-win surprise reveal happened twice with the
      // spectator copy spoiling the modal copy.
      try {
        // FLIP_REVEAL_MS = total time from joined_at to result reveal. MUST
        // equal the modal's reveal (3000ms — the 3-2-1 countdown). We anchor
        // the spectator reveal to the SAME server instant — joined_at + 3000 —
        // converted into THIS device's local clock via the offset. Result:
        // every spectator AND both players reveal the outcome at the same
        // wall-clock moment, instead of each client counting from whenever it
        // happened to poll.
        const FLIP_REVEAL_MS = 3000;
        const prev = flipsRefForDiff.current || [];
        const prevState = new Map(prev.map(f => [f.srvId, f.state]));
        for (const f of mapped) {
          const wasResolved = prevState.get(f.srvId) === 'resolved';
          if (f.state === 'resolved' && !wasResolved && !flipRevealMapRef.current.has(f.srvId)) {
            const anchorServer = parseServerTs(f.joinedAt) || parseServerTs(f.resolvedAt) || serverNow();
            const revealLocal = Date.now() + localDelayUntilServerTime(anchorServer + FLIP_REVEAL_MS);
            // Clamp so a future-dated anchor (clock weirdness) can't hold the
            // card hostage longer than one full animation from right now.
            flipRevealMapRef.current.set(f.srvId, Math.min(revealLocal, Date.now() + FLIP_REVEAL_MS));
          }
        }
      } catch (_) {}

      flipsRefForDiff.current = mapped;
      setFlips(mapped);
    } catch (_) {} finally {
      setLobbyLoading(false);
    }
  }, [currentUser]);
  // Keep the ref pointing at the LATEST refreshFlips so the SSE handler never
  // calls a stale closure (fixes the post-login "currentUser=null in SSE" bug).
  React.useEffect(() => { refreshFlipsRef.current = refreshFlips; }, [refreshFlips]);

  // SSE live updates state — needed before chat polling effect references it
  const [sseAlive, setSseAlive] = React.useState(false);

  // FAST poll when the user has a live participation in some open flip — so
  // when an opponent joins their open flip, the auto-open animation fires
  // within ~2s instead of waiting up to 8s. SSE handles it instantly when
  // alive, but mobile networks often drop the SSE silently — this is the
  // safety net so creator + joiner both see the animation near-simultaneously.
  const hasLiveParticipation = React.useMemo(() => {
    if (!currentUser) return false;
    return flips.some(f =>
      f.state === 'open' && (
        (f.c && f.c.id === currentUser.id) ||
        (f.participants || []).some(p => Number(p.user_id) === Number(currentUser.id))
      )
    );
  }, [flips, currentUser]);

  React.useEffect(() => {
    refreshFlips();
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 720;
    let interval;
    if (hasLiveParticipation) {
      // User has skin in the game — poll AGGRESSIVELY so the auto-animation
      // triggers near-instantly when their opponent joins. SSE pushes within
      // 0.5s already (see stream.php), this is the backup if SSE is dead.
      interval = sseAlive ? 2500 : 1500;
    } else {
      // Idle lobby browsing — relaxed cadence to spare shared-host PHP workers.
      // Cut WAY down (from 45-60s → 6-15s) because the header SubNav now shows
      // the live pot value next to the Coinflip pill; users complained the
      // number felt frozen until they refreshed. SSE alive is still the
      // primary push, this is just the visible-update fallback.
      interval = sseAlive
        ? (isMobile ? 15000 : 12000)
        : (isMobile ? 6000  : 4000);
    }
    const t = setInterval(refreshFlips, interval);
    return () => clearInterval(t);
  }, [refreshFlips, sseAlive, hasLiveParticipation]);

  // Force-refresh flips immediately whenever the tab regains focus or becomes
  // visible — covers the case where SSE silently dropped while the user was
  // away on another tab/window. Without this, they'd come back and stare at
  // a stale lobby until the next interval fires.
  React.useEffect(() => {
    if (!hasLiveParticipation) return;
    const onFocus = () => { if (!document.hidden) refreshFlips(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refreshFlips, hasLiveParticipation]);

  // iOS Safari bfcache fix: when the page is restored from back-forward
  // cache (user opens another app, comes back, or swipes back to this tab),
  // the React state is intact but the network connection / cookies may have
  // shifted. Re-verify auth.
  //
  // CRITICAL: only fire when there's already a logged-in user. Otherwise we
  // would call /api/me during the LOGIN flow (e.g. user tabs away to Roblox
  // to paste the bio code, then comes back), and a transient response could
  // race with the verify-then-onAuth chain and reset state mid-flight.
  const currentUserRef = React.useRef(currentUser);
  React.useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
  React.useEffect(() => {
    const safeRefresh = () => {
      if (currentUserRef.current) refreshMe({ keepUserOnNull: true });
    };
    const onPageShow = (e) => { if (e.persisted) safeRefresh(); };
    const onVis = () => { if (!document.hidden) safeRefresh(); };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshMe]);

  // ===== Chat polling — /api/chat every 2.5s for active channel =====
  const [chatTab, setChatTab] = React.useState('global'); // 'global' | 'lang'
  const activeChannel = chatTab === 'lang' ? lang : 'global';

  // Track the channel that the current chat list is for. Used so we ONLY
  // clear the list when the channel ACTUALLY changes — not when sseAlive
  // flips (which was causing a visible empty→full refresh flash every time
  // SSE reconnected). Channel changes still need the clear so messages from
  // the old channel don't bleed visually into the new one.
  const lastChannelRef = React.useRef(activeChannel);
  React.useEffect(() => {
    let cancelled = false;
    if (lastChannelRef.current !== activeChannel) {
      lastChannelRef.current = activeChannel;
      setChat([]);
    }
    const poll = async () => {
      try {
        const res = await apiFetch(`/api/chat?channel=${encodeURIComponent(activeChannel)}&since=0&limit=50`);
        if (cancelled) return;
        const msgs = (res.messages || []).map(mapApiChat);
        setChat(prev => {
          // Merge in place — dedupe by srvId. NEVER replace wholesale or the
          // user sees a flicker on every poll cycle.
          const incomingIds = new Set(msgs.map(m => m.srvId).filter(Boolean));
          const optimistic = prev.filter(m => m._temp && !msgs.some(s =>
            s.text === m.text && s.userId === m.userId
          ));
          const keptOld = prev.filter(m => !m._temp && m.srvId && !incomingIds.has(m.srvId));
          // If keptOld empty (first load) → just take server data.
          // Otherwise preserve scroll position by appending only new IDs.
          const merged = keptOld.length === 0
            ? [...msgs, ...optimistic]
            : [...keptOld, ...msgs, ...optimistic];
          // Dedupe + cap at 100
          const seen = new Set();
          const out = [];
          for (const m of merged) {
            const k = m.srvId || ('t:' + m.text);
            if (seen.has(k)) continue;
            seen.add(k);
            out.push(m);
          }
          return out.slice(-100);
        });
      } catch (_) {}
    };
    poll();
    // When SSE is alive, server pushes updates → DON'T poll at all (this
    // was causing a visible re-render every 30s the user perceived as
    // "chat reloads itself"). Fall back to 8s polling only when SSE is
    // confirmed dead. Bumped from 6s → 8s to give the SSE reconnect a
    // chance to revive before we spam-poll.
    const intervalMs = sseAlive ? 0 : 8000;
    const t = intervalMs > 0 ? setInterval(poll, intervalMs) : null;
    return () => { cancelled = true; if (t) clearInterval(t); };
  }, [activeChannel, sseAlive]);

  // SSE live updates — defined AFTER activeChannel to avoid TDZ. EventSource
  // streams flip + chat diffs from /api/stream so we don't depend on polling.
  React.useEffect(() => {
    if (typeof window.EventSource !== 'function') return;
    let es; let cancelled = false; let lastChat = 0;
    const connect = () => {
      try {
        es = new EventSource(`/api/stream?channel=${encodeURIComponent(activeChannel)}&since_chat=${lastChat}`);
      } catch (_) { return; }
      es.addEventListener('hello',          () => { if (!cancelled) setSseAlive(true); });
      es.addEventListener('flips_changed',  () => { (refreshFlipsRef.current || refreshFlips)(); });
      // Maintenance / broadcast / XP settings changed → reload /api/config so
      // the maintenance screen toggles INSTANTLY for everyone, no refresh.
      es.addEventListener('config_changed', () => { window.dispatchEvent(new Event('blox:config-refresh')); });
      // Reactions changed somewhere — broadcast to the Chat component which
      // owns reactions state. This makes cross-device + cross-user reaction
      // updates feel instant (within ~500ms of the click on the other end).
      es.addEventListener('reactions_changed', () => {
        try { window.dispatchEvent(new CustomEvent('blox:reactionsChanged')); } catch (_) {}
      });
      es.addEventListener('chat',           (e) => {
        try {
          const d = JSON.parse(e.data);
          if (Array.isArray(d.messages) && d.messages.length > 0) {
            lastChat = Math.max(lastChat, d.since_chat || 0);
            const mapped = d.messages.map(mapApiChat);
            setChat(prev => {
              const seen = new Set(prev.map(m => m.srvId).filter(Boolean));
              const fresh = mapped.filter(m => !seen.has(m.srvId));
              const optimistic = prev.filter(m => m._temp && !mapped.some(s => s.text === m.text && s.userId === m.userId));
              return [...prev.filter(m => !m._temp), ...fresh, ...optimistic].slice(-100);
            });
          }
        } catch (_) {}
      });
      // Delay reconnect so we don't slam Hostinger's PHP-FPM workers — each SSE
      // connection holds one until it exits, and on shared hosting that's a
      // very limited resource. 3s on clean bye, 5s on error.
      es.addEventListener('bye', () => { try { es && es.close(); } catch (_) {} setSseAlive(false); if (!cancelled) setTimeout(connect, 3000); });
      es.onerror = () => { setSseAlive(false); try { es && es.close(); } catch (_) {} if (!cancelled) setTimeout(connect, 5000); };
    };
    connect();
    return () => { cancelled = true; try { es && es.close(); } catch (_) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel]);

  // ===== Heartbeat — refresh /api/me every 45s to keep inventory + stats fresh =====
  // Use keepUserOnNull so a transient network hiccup doesn't log the user out.
  // 45s (was 15s) eases load on Hostinger shared-hosting PHP workers.
  React.useEffect(() => {
    if (!currentUser) return;
    const t = setInterval(() => refreshMe({ keepUserOnNull: true }), 45000);
    return () => clearInterval(t);
  }, [currentUser, refreshMe]);

  // Daily reward removed per user request — replaced by Level Rewards system.

  const visible = React.useMemo(() => {
    // SPECTATOR HOLD — re-map any flip that's resolved but still inside its
    // 3-second reveal window back to 'rolling' so the card animation runs
    // for spectators the same way it does for the players in the modal.
    const now = Date.now();
    const held = flips.map(x => {
      if (x.state !== 'resolved') return x;
      const ra = flipRevealMapRef.current.get(x.srvId);
      if (ra && ra > now) return { ...x, state: 'rolling', _heldReveal: true };
      return x;
    });
    // Cap finished games shown to MAX_RESOLVED — keep the lobby focused on
    // live action. The server also limits this, but capping client-side
    // protects against stale clients still showing 50+ old battles.
    const MAX_RESOLVED = 10;
    const resolvedSorted = held
      .filter(x => x.state === 'resolved')
      .sort((a, b) => new Date(b.resolvedAt || 0) - new Date(a.resolvedAt || 0))
      .slice(0, MAX_RESOLVED);
    const resolvedKeep = new Set(resolvedSorted.map(x => x.srvId));
    let f = held.filter(x => x.state !== 'resolved' || resolvedKeep.has(x.srvId));
    if (filter !== 'all') f = f.filter(x => x.state === filter);
    if (side !== 'any')   f = f.filter(x => x.side === side);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      f = f.filter(x =>
        x.c.name.toLowerCase().includes(q) ||
        (x.items || []).some(it => it.name.toLowerCase().includes(q))
      );
    }
    if (sort === 'high') f = [...f].sort((a,b) => flipValue(b.items||[]) - flipValue(a.items||[]));
    if (sort === 'low')  f = [...f].sort((a,b) => flipValue(a.items||[]) - flipValue(b.items||[]));
    return f;
    // revealTick is a forced-re-render heartbeat (every 500ms) so the
    // spectator hold expires cleanly. Without it the held flips would
    // stay 'rolling' forever between polls.
  }, [flips, filter, side, sort, searchQuery, revealTick]);

  const featuredFlip = React.useMemo(() => [...flips].filter(f => f.state === 'open').sort((a,b) => b.stake - a.stake)[0], [flips]);

  const pushToast = (toast) => {
    const id = Date.now() + Math.random();
    // Max 3 toasts stacked at once. If we already have 3, drop the
    // oldest before adding the new one — prevents notification spam
    // from blanketing the screen on rapid-fire events (e.g. WS
    // reconnect storms or bulk admin actions).
    setToasts(prev => [...prev, { ...toast, id }].slice(-3));
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 4000);
  };
  // Global toast + auth-prompt event bus — any component can dispatch
  // `window.dispatchEvent(new CustomEvent('blox:toast', { detail: {...} }))`
  // or `blox:showAuth` and we'll route it through the App-level state.
  React.useEffect(() => {
    const onToast = (e) => { if (e.detail) pushToast(e.detail); };
    const onAuth  = (e) => { setShowAuth((e.detail && e.detail.mode) || 'login'); };
    // Mod deleted a chat message → drop it from the live list immediately.
    const onChatRemove = (e) => {
      const sid = e.detail && e.detail.srvId;
      if (sid != null) setChat(prev => prev.filter(m => m.srvId !== sid));
    };
    const onOpenVM = () => setShowValueManager(true);
    window.addEventListener('blox:toast', onToast);
    window.addEventListener('blox:showAuth', onAuth);
    window.addEventListener('blox:chat-remove', onChatRemove);
    window.addEventListener('blox:open-value-manager', onOpenVM);
    return () => {
      window.removeEventListener('blox:toast', onToast);
      window.removeEventListener('blox:showAuth', onAuth);
      window.removeEventListener('blox:chat-remove', onChatRemove);
      window.removeEventListener('blox:open-value-manager', onOpenVM);
    };
  }, []);

  const handleCreate = async ({ side, items, matchRange, teamSize, teamSizeB }) => {
    if (!currentUser) { setShowAuth('login'); return; }
    if (!items || items.length === 0) return;
    const itemIds = items.map(it => Number(it.id)).filter(n => Number.isFinite(n));
    if (itemIds.length === 0) {
      pushToast({ type: 'lose', text: tt('noValidItems', 'No valid items selected') });
      return;
    }
    setShowCreate(false);
    try {
      await apiFetch('/api/flips', {
        method: 'POST',
        body: {
          side, item_ids: itemIds,
          client_seed: randomHex(8),
          match_range: typeof matchRange === 'number' ? matchRange : 0.10,
          team_size:   typeof teamSize  === 'number' ? teamSize  : 1,
          team_size_b: typeof teamSizeB === 'number' ? teamSizeB : (typeof teamSize === 'number' ? teamSize : 1),
        },
      });
      pushToast({ type: 'info', text: `Created flip · ${items.length} items · ${fmtVal(flipValue(items))}` });
      pushNotif({ kind: 'info', title: 'Flip created', text: `${items.length} items · ${fmtVal(flipValue(items))} value` });
      refreshMe({ keepUserOnNull: true });
      refreshFlips();
    } catch (err) {
      pushToast({ type: 'lose', text: err.message || 'Could not create flip' });
    }
  };

  const doJoin = async (flip) => {
    if (!flip.srvId) {
      pushToast({ type: 'lose', text: 'This flip is stale, refresh the page.' });
      return;
    }
    // Pick the joiner items: we used a quick-join greedy auto-pick before opening
    // FlipModal (see performInstaJoin), but for manual matches we already stashed
    // the chosen items on flip.__joinItems by handleConfirmMatch.
    const joinItems = flip.__joinItems || [];
    if (joinItems.length === 0) {
      pushToast({ type: 'lose', text: 'No items selected to match' });
      return;
    }
    const yourSide = flip.side === 'heads' ? 'tails' : 'heads';
    const joinerSeed = randomHex(8);
    try {
      const res = await apiFetch('/api/flips/join', {
        method: 'POST',
        body: {
          flip_id: flip.srvId,
          item_ids: joinItems.map(it => Number(it.id)).filter(Number.isFinite),
          joiner_seed: joinerSeed,
          ...(flip.inviteCode ? { invite_code: flip.inviteCode } : {}),
          ...(flip.joinTeam ? { team: flip.joinTeam } : {}),
        },
      });
      const resolved = mapApiFlip(res.flip, currentUser?.id);
      setFlips(prev => prev.map(f => f.srvId === flip.srvId ? resolved : f));
      // ONLY open FlipModal when the flip actually resolved. For partial team
      // joins (2v2 with 2 players in so far) keep the lobby state open.
      if (resolved.state === 'resolved') {
        markShown(resolved.srvId);
        setPendingJoin({ flip: { ...resolved, proof: res.proof }, yourSide });
      } else {
        // No "Joined — waiting" toast per user request — silent join
        refreshFlips();
      }
      // XP + achievements feedback from backend
      const prog = res.progress;
      if (prog) {
        if (prog.gained_xp > 0) {
          pushNotif({ kind: 'info', title: `+${prog.gained_xp} XP`, text: prog.leveled_up ? `Level ${prog.new_level}!` : undefined });
        }
        if (prog.leveled_up) {
          pushToast({ type: 'win', text: `🎉 Level ${prog.new_level}!` });
          // Banner unlock check — fires a toast when crossing a banner threshold
          const BANNER_TIERS = [
            { lvl: 5,   name: 'Forest' },
            { lvl: 15,  name: 'Magenta' },
            { lvl: 30,  name: 'Inferno' },
            { lvl: 50,  name: 'Midnight' },
            { lvl: 75,  name: 'Phoenix' },
            { lvl: 100, name: 'Diamond' },
          ];
          for (const tier of BANNER_TIERS) {
            if (prog.new_level >= tier.lvl && prog.old_level < tier.lvl) {
              pushToast({ type: 'win', text: `🎨 Banner unlocked: ${tier.name}` });
              pushNotif({ kind: 'win', title: 'Banner unlocked!', text: `${tier.name} — pick it from your profile.` });
            }
          }
        }
        if (Array.isArray(prog.unlocked) && prog.unlocked.length > 0) {
          prog.unlocked.forEach(code => {
            pushNotif({ kind: 'win', title: 'Achievement unlocked', text: code.replace(/_/g, ' ') });
          });
        }
      }
    } catch (err) {
      pushToast({ type: 'lose', text: err.message || 'Could not join flip' });
      // Reset matching state on failure
      refreshFlips();
    }
  };

  // Inventory available for joining new flips — excludes items locked into another flip.
  const availableInventory = React.useMemo(
    () => userInventory.filter(it => !it.lockedFlipId),
    [userInventory]
  );

  // New join flow: open MatchModal so user picks items from inventory
  const handleJoin = (flip) => {
    if (!currentUser) { setShowAuth('login'); return; }
    if (flip.mine || (flip.c && flip.c.id === currentUser.id)) {
      pushToast({ type: 'lose', text: 'Cannot join your own flip' });
      return;
    }
    if (!availableInventory || availableInventory.length === 0) {
      pushToast({ type: 'lose', text: 'No items available — deposit more or wait for active flips to finish' });
      return;
    }
    setMatchingFlip(flip);
  };

  // After user picks items in MatchModal — gate high-value flips behind a confirm dialog
  const handleConfirmMatch = (items) => {
    const flip = matchingFlip;
    setMatchingFlip(null);
    if (!flip) return;
    // Stash the chosen items on the flip object so doJoin can send their IDs.
    const flipWithItems = { ...flip, __joinItems: items };
    const value = flipValue(flip.items || []);
    let skipConfirm = false;
    try { skipConfirm = localStorage.getItem('bloxhot.skipBigConfirm') === '1'; } catch (_) {}
    if (value >= 100 && !skipConfirm) {
      setPendingConfirm({
        title: `Match ${fmtVal(value)} flip?`,
        message: `This is a high-value flip. Coin flip is 50/50 — confirm you want to risk ${fmtVal(flipValue(items))} of items against ${flip.c.name}.`,
        confirmLabel: 'Yes, match it',
        cancelLabel: 'Cancel',
        askable: true,
        askLabel: "Don't ask again for high-value flips",
        onConfirm: (dontAsk) => {
          if (dontAsk) { try { localStorage.setItem('bloxhot.skipBigConfirm', '1'); } catch (_) {} }
          setPendingConfirm(null);
          doJoin(flipWithItems);
        },
      });
    } else {
      doJoin(flipWithItems);
    }
  };

  // ==========================================
  // INSTA JOIN — quick auto-pick + immediate join, with 15-min ack
  // ==========================================
  const INSTA_ACK_MS = 15 * 60 * 1000;
  const isInstaAcked = () => {
    try {
      const t = parseInt(localStorage.getItem('bloxhot.instaAck') || '0', 10);
      return t > 0 && (Date.now() - t < INSTA_ACK_MS);
    } catch (_) { return false; }
  };

  const showQuickJoinFail = (kind, flip) => {
    const target = flipValue(flip.items || []);
    const noItems = kind === 'no-items';
    setPendingConfirm({
      variant: 'fail',
      icon: 'close',
      title: noItems ? (t.qjFailNoItems || 'No items') : (t.qjFailTitle || 'Not enough value'),
      message: noItems
        ? (t.qjFailNoItemsBody || 'Deposit items first to use quick join.')
        : `${(t.qjFailBody || 'Pick manually or deposit more items.')} (~${fmtVal(target)} needed)`,
      confirmLabel: noItems ? (t.qjFailDeposit || 'Open wallet') : (t.qjFailManual || 'Pick manually'),
      cancelLabel: t.cancel || 'Close',
      onConfirm: () => {
        setPendingConfirm(null);
        if (noItems) setShowInventory(true);
        else setMatchingFlip(flip);
      },
    });
  };

  const performInstaJoin = (flip) => {
    if (!availableInventory || availableInventory.length === 0) {
      showQuickJoinFail('no-items', flip);
      return;
    }
    // Auto-pick — two-pass to guarantee we cover minV when possible:
    //   Pass 1: greedy high-value, staying ≤ maxV (clean match)
    //   Pass 2: if still short, fall back to smallest-first, allowing
    //           overshoot of maxV rather than giving up entirely
    // Hard cap at 50 items to match backend.
    const target = flipValue(flip.items || []);
    const rangePct = typeof flip.matchRange === 'number' ? flip.matchRange : 0.05;
    const minV = target * (1 - rangePct);
    const maxV = target * (1 + rangePct);
    const MAX_QJ_ITEMS = 50;
    const sortedDesc = [...availableInventory].sort((a, b) => b.price - a.price);
    const sortedAsc  = [...availableInventory].sort((a, b) => a.price - b.price);
    const pickedIds = new Set();
    const picked = [];
    let acc = 0;
    for (const it of sortedDesc) {
      if (picked.length >= MAX_QJ_ITEMS) break;
      if (acc >= minV) break;
      if (acc + it.price > maxV) continue;
      picked.push(it); pickedIds.add(it.id);
      acc += it.price;
    }
    if (acc < minV) {
      for (const it of sortedAsc) {
        if (picked.length >= MAX_QJ_ITEMS) break;
        if (pickedIds.has(it.id)) continue;
        picked.push(it); pickedIds.add(it.id);
        acc += it.price;
        if (acc >= minV) break;
      }
    }
    // Match-range is now 50% on the server, so any cover ≥ minV is acceptable.
    // We allow modest overshoot (pass 2) since the user complained that
    // strict <=maxV caused quick-join to bail when their inventory had a
    // single item bigger than the cap.
    if (picked.length > 0 && acc >= minV) {
      pushToast({ type: 'info', text: `${t.qjTitle || 'Quick join'} · ${picked.length} · ${fmtVal(acc)}` });
      doJoin({ ...flip, __joinItems: picked });
    } else {
      showQuickJoinFail('value', flip);
    }
  };

  const handleInstaJoin = (flip) => {
    if (!currentUser) { setShowAuth('login'); return; }
    // Defensive: never let the user join their own flip via quick-join.
    if (flip.mine || (flip.c && flip.c.id === currentUser.id)) {
      pushToast({ type: 'lose', text: 'Cannot join your own flip' });
      return;
    }
    if (!availableInventory || availableInventory.length === 0) {
      showQuickJoinFail('no-items', flip);
      return;
    }
    if (isInstaAcked()) {
      performInstaJoin(flip);
      return;
    }
    setPendingConfirm({
      icon: 'bolt',
      title: t.qjTitle || 'Quick join',
      message: t.qjBody || 'Auto-picks items and joins instantly.',
      confirmLabel: t.qjConfirm || 'Quick join',
      cancelLabel: t.cancel || 'Cancel',
      askable: true,
      askLabel: t.qjAsk || "Don't show again for 15 minutes",
      onConfirm: (dontAsk) => {
        if (dontAsk) { try { localStorage.setItem('bloxhot.instaAck', String(Date.now())); } catch (_) {} }
        setPendingConfirm(null);
        performInstaJoin(flip);
      },
    });
  };

  // Dice insta-join — mirrors coinflip's quick-join pattern. Auto-picks
  // items matching creator_value within ±5% (server tolerance is 0.05
  // in api/dice/join.php — keep these in sync), gates behind the same
  // 15-min "don't ask again" confirm, then POSTs to /api/dice/join.
  // Fires from DicePage via `blox:dice-insta-join` custom event so the
  // DicePage doesn't need to hold all the picker logic itself.
  const performDiceInstaJoin = React.useCallback((match) => {
    if (!availableInventory || availableInventory.length === 0) {
      pushToast({ type: 'lose', text: 'No items available to match — deposit more.' });
      return;
    }
    const target = Number(match.creator_value) || 0;
    if (target <= 0) {
      pushToast({ type: 'lose', text: 'Match value missing — try opening the full picker.' });
      return;
    }
    const minV = target * 0.95;
    const maxV = target * 1.05;
    const MAX_QJ_ITEMS = 50;
    const sortedDesc = [...availableInventory].sort((a, b) => b.price - a.price);
    const sortedAsc  = [...availableInventory].sort((a, b) => a.price - b.price);
    const pickedIds = new Set();
    const picked = [];
    let acc = 0;
    for (const it of sortedDesc) {
      if (picked.length >= MAX_QJ_ITEMS) break;
      if (acc >= minV) break;
      if (acc + it.price > maxV) continue;
      picked.push(it); pickedIds.add(it.id);
      acc += it.price;
    }
    if (acc < minV) {
      for (const it of sortedAsc) {
        if (picked.length >= MAX_QJ_ITEMS) break;
        if (pickedIds.has(it.id)) continue;
        picked.push(it); pickedIds.add(it.id);
        acc += it.price;
        if (acc >= minV) break;
      }
    }
    if (picked.length === 0 || acc < minV) {
      pushToast({ type: 'lose', text: `Could not auto-pick — target ${fmtVal(target)} ±5%. Use the full picker.` });
      return;
    }
    pushToast({ type: 'info', text: `Quick join · ${picked.length} items · ${fmtVal(acc)}` });
    const clientSeed = randomHex(16); // joiner's provably-fair seed
    // Fire the join. Dispatch a follow-up event so DicePage refreshes
    // its lobby + opens the resolved modal once the server replies.
    // CRITICAL: pass `pickedItems` AND `pickedValue` so the resolved
    // modal can render the joiner's own items + the correct stake pill
    // percentage. Without these, the modal showed 0%/100% with the
    // joiner's column empty (user-reported bug).
    apiFetch('/api/dice/join', { method: 'POST', body: { id: match.id, item_ids: picked.map(it => it.id), client_seed: clientSeed } })
      .then(res => {
        refreshMe({ keepUserOnNull: true });
        window.dispatchEvent(new CustomEvent('blox:dice-joined', {
          detail: {
            match,
            rolls: res.rolls,
            winner_id: res.winner_id,
            seed: res.seed,
            joiner_seed: res.joiner_seed,
            // Joiner's chosen items — flattened to the same shape as the
            // server returns in /api/dice (id+name+price+rarity).
            pickedItems: picked.map(it => ({
              inv_id: it.id,
              name:   it.name,
              price:  Number(it.price) || 0,
              rarity: it.rarity || 'common',
            })),
            pickedValue: acc,
          },
        }));
      })
      .catch(err => pushToast({ type: 'lose', text: err.message || 'Could not join dice match' }));
  }, [availableInventory, pushToast, refreshMe]);

  // Listen for dice insta-join requests fired from the DicePage button.
  // Gate behind the SAME 15-min confirm modal as coinflip — single
  // shared `isInstaAcked` cookie means dismissing once for either game
  // covers both for 15 minutes.
  React.useEffect(() => {
    const onInsta = (e) => {
      const match = e?.detail;
      if (!match || !currentUser) { if (!currentUser) setShowAuth('login'); return; }
      if (match.creator_id === currentUser.id) {
        pushToast({ type: 'lose', text: 'Cannot join your own match' });
        return;
      }
      if (isInstaAcked()) { performDiceInstaJoin(match); return; }
      setPendingConfirm({
        icon: 'bolt',
        title: t.qjTitle || 'Quick join',
        message: t.qjBody || 'Auto-picks items and joins instantly.',
        confirmLabel: t.qjConfirm || 'Quick join',
        cancelLabel: t.cancel || 'Cancel',
        askable: true,
        askLabel: t.qjAsk || "Don't show again for 15 minutes",
        onConfirm: (dontAsk) => {
          if (dontAsk) { try { localStorage.setItem('bloxhot.instaAck', String(Date.now())); } catch (_) {} }
          setPendingConfirm(null);
          performDiceInstaJoin(match);
        },
      });
    };
    window.addEventListener('blox:dice-insta-join', onInsta);
    return () => window.removeEventListener('blox:dice-insta-join', onInsta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, performDiceInstaJoin, t]);

  const handleResult = (won, result) => {
    if (!pendingJoin) return;
    const { flip } = pendingJoin;
    const items = flip.items || [];
    const total = flipValue(items) + flipValue(flip.oItems || []);
    // No win/lose toasts — the FlipModal itself is the result feedback.
    // Toasts on top of the modal were redundant + noisy per user request.
    // Notifications (bell tray) also disabled for flip outcomes — they
    // pile up on every flip and clutter the inbox.
    if (won) {
      setWinStreak(s => s + 1);
      // Reset tilt counter on win — fresh start.
      try { localStorage.setItem('bloxhot.tiltCount', '0'); } catch (_) {}
      setTiltCount(0);
    } else {
      setWinStreak(0);
      // TILT COUNTER — track consecutive losses. After 3+, fire a friendly
      // toast suggesting a break. After 5+, show on the profile chip.
      // Counter stored in localStorage so it survives reloads.
      let next = 0;
      try {
        next = (parseInt(localStorage.getItem('bloxhot.tiltCount') || '0', 10) || 0) + 1;
        localStorage.setItem('bloxhot.tiltCount', String(next));
      } catch (_) {}
      setTiltCount(next);
      if (next === 3) {
        pushToast && pushToast({
          type: 'info',
          text: tt('tilt3', '3 losses in a row 💀 — variance is real, you got this.'),
        });
      } else if (next === 5) {
        pushToast && pushToast({
          type: 'info',
          text: tt('tilt5', '5 in a row… maybe take a quick break? ☕'),
        });
      } else if (next >= 8 && next % 3 === 0) {
        pushToast && pushToast({
          type: 'info',
          text: tt('tiltN', "{n} losses in a row. Variance is brutal but it'll turn — promise.", { n: next }),
        });
      }
    }
    setTotalTrades(t => t + 1);
    // Server already moved items + updated stats — refresh local state.
    // keepUserOnNull so a transient /api/me hiccup right after a win can't
    // boot the user mid-celebration.
    refreshMe({ keepUserOnNull: true });
    refreshFlips();
    // NOTE: do NOT clear pendingJoin here — let the user close the modal manually.
  };

  // Actual cancel request — runs only after the user confirms.
  const doCancel = async (flip) => {
    if (!flip.srvId) return;
    try {
      await apiFetch('/api/flips/cancel', { method: 'POST', body: { flip_id: flip.srvId } });
      pushToast({ type: 'info', text: `Flip cancelled · ${(flip.items?.length||0)} items returned` });
      refreshMe({ keepUserOnNull: true });
      refreshFlips();
    } catch (err) {
      pushToast({ type: 'lose', text: err.message || 'Could not cancel flip' });
    }
  };

  // Cancel now asks "are you sure?" first — accidental taps shouldn't tear
  // down a flip and bounce all the locked items around. Plain strings (no
  // tt()) so this can never throw out of the click handler.
  const handleCancel = (flip) => {
    if (!flip || !flip.srvId) return;
    const n = (flip.items && flip.items.length) || 0;
    setPendingConfirm({
      title: 'Cancel this flip?',
      message: `Your ${n} item${n === 1 ? '' : 's'} will be returned to your inventory and the flip will close.`,
      confirmLabel: 'Yes, cancel',
      cancelLabel: 'Keep it open',
      icon: 'close',
      variant: 'fail',
      onConfirm: () => { setPendingConfirm(null); doCancel(flip); },
    });
  };

  const handleWatch = (flip) => setViewingFlip(flip);

  const handleBotDeposit = async (bot) => {
    if (!currentUser) { setShowBotDeposit(false); setShowAuth('login'); return; }
    setShowBotDeposit(false);
    try {
      const res = await apiFetch('/api/inventory/deposit-demo', { method: 'POST', body: { count: 3 } });
      const n = (res.added || []).length;
      pushToast({ type: 'win', text: `+${n} items received from ${bot.name}` });
      refreshMe({ keepUserOnNull: true });
    } catch (err) {
      pushToast({ type: 'lose', text: err.message || 'Deposit failed' });
    }
  };

  const handleWithdraw = (itemIds) => {
    const count = itemIds.length;
    // Real withdraw uses Roblox trade tickets — not built yet.
    // For now keep it as a toast so the UX flow works.
    pushToast({ type: 'info', text: `Withdrawing ${count} items — bot will trade with you in-game` });
  };

  const handleSendChat = async (text) => {
    if (!currentUser) { setShowAuth('login'); return; }
    const trimmed = (text || '').trim();
    if (!trimmed) return;

    // ===== Slash commands (admin-only) =====
    // `/purge` → wipe all messages in the current channel.  Hidden from
    // non-admins: the command parses but the backend rejects.  We
    // short-circuit the normal send path so the command itself never
    // appears as a chat message.
    if (trimmed.startsWith('/')) {
      const [cmd] = trimmed.slice(1).split(/\s+/);
      if (cmd.toLowerCase() === 'purge') {
        if (!currentUser.is_admin) {
          pushToast({ type: 'lose', text: 'Admin only.' });
          return;
        }
        try {
          const res = await apiFetch('/api/admin/chat-purge', { method: 'POST', body: { channel: activeChannel } });
          setChat([]);
          pushToast({ type: 'win', text: `Purged ${res?.purged ?? 0} messages.` });
        } catch (err) {
          pushToast({ type: 'lose', text: err.message || 'Could not purge' });
        }
        return;
      }
      // Future slash commands (e.g. /pin, /clearuser) can branch here.
      // Unknown command → fall through to send as a normal chat message
      // (matches Discord's behaviour with non-existent commands).
    }

    const tempId = 'tmp-' + Date.now();
    // Mirror the server's payload shape EXACTLY so the optimistic bubble
    // looks identical to the eventual server-echoed message — same
    // is_admin badge, same level role color, same display name. Without
    // is_admin here the "MOD" tag popped in 200ms later (was the
    // "late-loading badge" the user reported).
    setChat(prev => [...prev, {
      id: tempId, type: 'user',
      name: currentUser.username || currentUser.username,
      username: currentUser.username,
      avatarUrl: currentUser.avatar_url || '',
      lvl: Math.max(1, currentUser.level || 1),
      is_admin: !!currentUser.is_admin,
      color: deterministicColor(currentUser.id),
      av: synthAvatar(currentUser.username || currentUser.username),
      text: trimmed,
      userId: currentUser.id,
      _temp: true,
    }].slice(-50));
    try {
      await apiFetch('/api/chat', { method: 'POST', body: { channel: activeChannel, text: trimmed } });
    } catch (err) {
      pushToast({ type: 'lose', text: err.message || 'Could not send message' });
      setChat(prev => prev.filter(m => m.id !== tempId));
    }
  };

  const handleLogout = async () => {
    try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch (_) {}
    authToken.clear();
    setCurrentUser(null);
    setUserInventory([]);
    setFlips([]);
    setChat([]);
    setProfileOpen(false);
  };

  // Helper: gate any action that needs a logged-in user behind the auth modal.
  const requireAuth = (mode = 'login') => {
    if (currentUser) return true;
    setShowAuth(mode);
    return false;
  };

  // Global Esc handler — close the topmost open modal/sheet.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (userPop)            { setUserPop(null); return; }
      if (flipCtx)            { setFlipCtx(null); return; }
      if (pendingConfirm)     { setPendingConfirm(null); return; }
      if (matchingFlip)       { setMatchingFlip(null); return; }
      if (viewingFlip)        { setViewingFlip(null); return; }
      if (showLevelRewards)   { setShowLevelRewards(false); return; }
      if (showMobileAccount)  { setShowMobileAccount(false); return; }
      if (showInventory)      { setShowInventory(false); return; }
      if (showCreate)         { setShowCreate(false); return; }
      if (showBotDeposit)     { setShowBotDeposit(false); return; }
      if (showAuth)           { setShowAuth(null); return; }
      if (notifOpen)          { setNotifOpen(false); return; }
      if (profileOpen)        { setProfileOpen(false); return; }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [userPop, flipCtx, pendingConfirm, matchingFlip, viewingFlip, showLevelRewards, showMobileAccount, showInventory, showCreate, showBotDeposit, showAuth, notifOpen, profileOpen]);

  // ===== Site-wide banners (admin-controlled) =====
  const broadcastKey = `bloxhot.broadcast.${siteConfig.broadcast_message?.slice(0,40)}`;
  const [broadcastDismissed, setBroadcastDismissed] = React.useState(() => {
    try { return localStorage.getItem(broadcastKey) === '1'; } catch (_) { return false; }
  });
  React.useEffect(() => {
    try { setBroadcastDismissed(localStorage.getItem(broadcastKey) === '1'); } catch (_) {}
  }, [broadcastKey]);
  const isAdminUser = !!currentUser?.is_admin;
  const showMaintenance = siteConfig.maintenance_mode && !isAdminUser;

  if (showMaintenance) {
    return (
      <div className="maintenance-screen">
        <div className="maintenance-card">
          <img src={LOGO_SVG} alt="BLOX.HOT" className="maint-logo" draggable={false} onError={(e) => { e.currentTarget.style.display='none'; }}/>
          <div className="maint-icon" aria-hidden="true">
            <i className="fa-solid fa-screwdriver-wrench"/>
          </div>
          <h1>Under maintenance</h1>
          <p>{siteConfig.maintenance_message || "We're tuning things up — back online shortly."}</p>
          <div className="maint-status">
            <span className="maint-dot"/>
            <span>Be right back</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {siteConfig.broadcast_active && siteConfig.broadcast_message && !broadcastDismissed && (
        <div className="site-broadcast">
          <i className="fa-solid fa-bullhorn"/>
          <span>{siteConfig.broadcast_message}</span>
          <button onClick={() => {
            setBroadcastDismissed(true);
            try { localStorage.setItem(broadcastKey, '1'); } catch (_) {}
          }} aria-label="dismiss"><i className="fa-solid fa-xmark"/></button>
        </div>
      )}
      {siteConfig.xp_multiplier > 1.0 && (
        <div className="site-xp-event">
          <i className="fa-solid fa-bolt"/> <b>{siteConfig.xp_multiplier}x XP event active!</b> Earn more XP from every flip.
        </div>
      )}
      <OfflineBanner/>
      <GlobalModalFocusTrap/>
      {!currentUser && pendingRefProfile && (
        <ReferralWelcomeBanner
          profile={pendingRefProfile}
          onRegister={() => setShowAuth('register')}
          onDismiss={() => {
            try { localStorage.removeItem('bloxhot.pendingRefProfile'); } catch (_) {}
            setPendingRefProfile(null);
          }}
          t={t}
        />
      )}
      <ReferralBannerBodyClass active={!currentUser && !!pendingRefProfile}/>
      <Header
        balance={balance}
        bootLoading={bootLoading}
        onWallet={() => requireAuth() && setShowInventory(true)}
        lang={lang} onLang={setLang} t={t}
        chatOpen={chatOpen}
        onChatToggle={() => setChatOpen(o => !o)}
        theme={theme}
        onThemeToggle={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
        onMenu={() => setDrawerOpen(true)}
        onFairness={() => setFairnessOpen(true)}
        onHome={() => setActiveNav('coinflip')}
        currentUser={currentUser}
        onAuthOpen={(mode) => setShowAuth(mode)}
        notifications={notifications}
        notifOpen={notifOpen}
        onNotifToggle={() => {
          setNotifOpen(o => {
            const next = !o;
            if (next) markNotifsRead();
            return next;
          });
        }}
        onClearNotifs={clearNotifs}
        profileOpen={profileOpen}
        onProfileToggle={() => setProfileOpen(o => !o)}
        profileMenu={
          <ProfileMenu
            balance={balance}
            onWallet={() => setShowInventory(true)}
            onPage={(p) => setActiveNav(p)}
            onClose={() => setProfileOpen(false)}
            theme={theme}
            onThemeToggle={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
            t={t}
            currentUser={currentUser}
            onLogout={handleLogout}
            onQuests={() => setShowLevelRewards(true)}
            onFriends={() => setShowFriends(true)}
          />
        }
        subNav={<SubNav active={activeNav} onChange={setActiveNav} t={t} stats={stats} variant="header"/>}
      />

      <Chat
        messages={chat}
        online={stats.online}
        onSend={handleSendChat}
        t={t} lang={lang}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        tab={chatTab}
        onTabChange={setChatTab}
        onUserClick={openUserPop}
        pinned={siteConfig.broadcast_active ? siteConfig.broadcast_message : null}
        currentUser={currentUser}
      />
      <div className={`chat-backdrop ${chatOpen ? 'is-visible' : ''}`} onClick={() => setChatOpen(false)}/>

      <div className={`page ${chatOpen ? 'with-chat' : ''}`}>
        <SubNav active={activeNav} onChange={setActiveNav} t={t} stats={stats} />
        <div className="app-layout">
          <main className="layout-main">
            {activeNav === 'leaderboard' ? (
              <LeaderboardPage t={t}/>
            ) : activeNav === 'dice' ? (
              <DicePage t={t} currentUser={currentUser} onNav={setActiveNav} requireAuth={requireAuth} userInventory={userInventory} refreshInventory={() => refreshMe()} showHero={tweaks.showHero}/>
            ) : activeNav === 'rewards' ? (
              <RewardsPage t={t} onGo={setActiveNav}/>
            ) : activeNav === 'profile' ? (
              <ProfilePage t={t} currentUser={currentUser} onNav={setActiveNav}/>
            ) : activeNav === 'fairness' ? (
              <FairnessPage t={t}/>
            ) : activeNav === 'settings' ? (
              <SettingsPage t={t} lang={lang} onLang={setLang} theme={theme}
                onThemeToggle={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
                onLogout={handleLogout}
                currentUser={currentUser}
                accent={tweaks.accent}
                onAccentChange={(hex) => setTweak('accent', hex)}/>
            ) : activeNav === 'affiliates' ? (
              <AffiliatesPage t={t} currentUser={currentUser}/>
            ) : activeNav === 'rakeback' ? (
              <RakebackPage t={t} currentUser={currentUser} refreshMe={refreshMe}/>
            ) : activeNav === 'admin' ? (
              <AdminPage t={t} currentUser={currentUser}/>
            ) : (
              <>
                {tweaks.showHero && <BannerCarousel onGo={(page) => setActiveNav(page)}/>}
                <Tickers stats={stats} t={t} loading={bootLoading || (lobbyLoading && flips.length === 0)}/>

                <section className="section" data-screen-label="Lobby">
                  <div className="section-head">
                    {/* Count only OPEN + ROLLING flips — resolved ones are shown
                        for context but they're not "active" anymore. */}
                    <h2>{t.activeCoinflips} <span className="count">{flips.filter(f => f.state === 'open' || f.state === 'rolling').length}</span></h2>
                    {/* History moved into Profile → Recent activity */}
                  </div>

                  <FilterBar t={t} filter={filter} onFilter={setFilter} side={side} onSide={setSide} sort={sort}
                    onSortToggle={() => setSort(s => s==='newest'?'high':s==='high'?'low':'newest')}
                    openN={flips.filter(f=>f.state==='open').length}
                    rollN={flips.filter(f=>f.state==='rolling').length}
                    onCreate={() => { if (requireAuth()) setShowCreate(true); }}
                    flipView={flipView} onFlipView={setFlipView}
                    searchValue={searchInput} onSearchChange={setSearchInput} />


                  <div className={`flip-grid ${flipView === 'card' ? 'is-card' : ''}`}>
                    {lobbyLoading && flips.length === 0 ? (
                      <>
                        <FlipCardSkeleton/><FlipCardSkeleton/><FlipCardSkeleton/>
                      </>
                    ) : visible.length === 0 ? (
                      <div className="empty-state" style={{gridColumn:'1/-1'}}>
                        {/* Hand-rolled SVG illustration — two coins
                            facing each other with a subtle motion arc.
                            Stays on-brand (orange + grey) and doesn't
                            need an external image asset. */}
                        <svg className="empty-state-svg" viewBox="0 0 200 120" fill="none" aria-hidden="true">
                          <defs>
                            <linearGradient id="esCoinA" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0" stopColor="#FFE89A"/>
                              <stop offset="0.55" stopColor="#FE9920"/>
                              <stop offset="1" stopColor="#A14D08"/>
                            </linearGradient>
                            <linearGradient id="esCoinB" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0" stopColor="#E5E7EB"/>
                              <stop offset="0.55" stopColor="#9CA3AF"/>
                              <stop offset="1" stopColor="#4B5563"/>
                            </linearGradient>
                          </defs>
                          {/* Motion arc */}
                          <path d="M 50 95 Q 100 30 150 95" stroke="currentColor" strokeWidth="2" strokeDasharray="4 5" fill="none" opacity="0.25"/>
                          {/* Left coin (heads) */}
                          <circle cx="50" cy="95" r="22" fill="url(#esCoinA)"/>
                          <text x="50" y="101" textAnchor="middle" fontSize="18" fontWeight="800" fill="rgba(0,0,0,0.45)">H</text>
                          {/* Right coin (tails) */}
                          <circle cx="150" cy="95" r="22" fill="url(#esCoinB)"/>
                          <text x="150" y="101" textAnchor="middle" fontSize="18" fontWeight="800" fill="rgba(0,0,0,0.45)">T</text>
                        </svg>
                        <h3>{t.noFlipsMatch || 'No flips here yet'}</h3>
                        <p>Be the first to start the action — create a flip and others will join.</p>
                        <button className="empty-cta" onClick={() => { if (requireAuth()) setShowCreate(true); }}>
                          <i className="fa-solid fa-plus"/> Create the first flip
                        </button>
                      </div>
                    ) : visible.map(f => <FlipCard key={f.id} flip={f} onJoin={handleJoin} onInstaJoin={handleInstaJoin} onWatch={handleWatch} onCancel={handleCancel} t={t} onContext={(flip, pos) => setFlipCtx({ flip, pos })} onUserClick={openUserPop} currentUserId={currentUser?.id ?? null}/>)}
                  </div>
                </section>
              </>
            )}
          </main>
        </div>

        <Footer/>
      </div>

      <PwaInstallPrompt/>

      <MobileNav
        onWallet={() => { if (requireAuth()) setShowInventory(true); }}
        chatOpen={chatOpen}
        onChatToggle={() => setChatOpen(o => !o)}
        onCreate={() => { if (requireAuth()) setShowCreate(true); }}
        onHome={() => setActiveNav('coinflip')}
        onProfile={() => {
          if (currentUser) setShowMobileAccount(true);
          else setShowAuth('login');
        }}
        activeNav={activeNav}
        t={t}
        currentUser={currentUser}
      />

      <SideDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeNav={activeNav}
        onNav={setActiveNav}
        t={t}
        currentUser={currentUser}
        onFairness={() => setFairnessOpen(true)}
        onAuthOpen={(mode) => setShowAuth(mode)}
        theme={theme}
        onThemeToggle={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
      />
      <FairnessModal open={fairnessOpen} onClose={() => setFairnessOpen(false)} t={t}/>

      {showCreate    && <CreateModal t={t} userInventory={userInventory} onClose={() => setShowCreate(false)} onCreate={handleCreate} onOpenWallet={() => { setShowCreate(false); setShowInventory(true); }}/>}
      {showInventory && <InventoryModal items={userInventory} onClose={() => setShowInventory(false)} onOpenDeposit={() => { setShowInventory(false); setShowBotDeposit(true); }} onWithdraw={handleWithdraw}/>}
      {showGiveawayCreator && (
        <GiveawayCreatorModal
          items={userInventory}
          onClose={() => setShowGiveawayCreator(false)}
          onCreated={() => { setShowGiveawayCreator(false); refreshMe({ keepUserOnNull: true }); pushToast({ type: 'win', text: 'Giveaway is live!' }); }}
          pushToast={pushToast}
        />
      )}
      {showBotDeposit && <BotDepositModal onClose={() => setShowBotDeposit(false)} onSelectBot={handleBotDeposit}/>}
      {pendingJoin   && <FlipModal flip={pendingJoin.flip} yourSide={pendingJoin.yourSide} onClose={() => { setPendingJoin(null); }} onResult={handleResult} currentUser={currentUser}/>}
      {viewingFlip && <FlipDetailModal flip={viewingFlip} onClose={() => setViewingFlip(null)} onJoin={(f) => { setViewingFlip(null); handleJoin(f); }} currentUser={currentUser}/>}
      {matchingFlip && <MatchModal flip={matchingFlip} userInventory={userInventory} onClose={() => setMatchingFlip(null)} onConfirm={handleConfirmMatch}/>}
      {pendingConfirm && <ConfirmDialog {...pendingConfirm} onCancel={() => setPendingConfirm(null)}/>}
      {showValueManager && <ValueManagerModal onClose={() => setShowValueManager(false)}/>}
      {showAuth && (
        <Login
          t={t}
          onClose={() => setShowAuth(null)}
          onAuth={(u) => {
            setShowAuth(null);
            setCurrentUser(u);
            // Don't let refreshMe override the freshly-set user if /api/me
            // happens to miss the brand-new session cookie.
            refreshMe({ keepUserOnNull: true });
            // Tutorial overlay removed per user request — first-time users
            // no longer get the auto-popup walkthrough.
          }}
        />
      )}
      {/* Daily reward modal removed — replaced by Level Rewards */}
      {flipCtx && (
        <FlipContextMenu
          flip={flipCtx.flip}
          pos={flipCtx.pos}
          t={t}
          onClose={() => setFlipCtx(null)}
          onWatch={handleWatch}
          onViewProfile={(username) => {
            try { window.history.pushState({ page: 'profile' }, '', `/profile?u=${encodeURIComponent(username)}`); } catch (_) {}
            setActiveNav('profile');
          }}
          onReport={() => pushToast({ type: 'info', text: 'Reported — admins notified.' })}
        />
      )}
      {/* Tutorial removed per user request. The TutorialOverlay component
          itself stays defined (in case we add it back), but it's no longer
          rendered anywhere. The `showTutorial` state lingers for the same
          reason — it's wired but never set to true. */}
      {showLucky7 && <LuckyBurst onEnd={() => setShowLucky7(false)}/>}
      {showBigWin && bigWinData && (
        <BigWinCinematic data={bigWinData} onEnd={() => { setShowBigWin(false); setBigWinData(null); }}/>
      )}
      {showLevelRewards && <LevelRewardsModal t={t} currentUser={currentUser} onClose={() => setShowLevelRewards(false)}/>}
      {showMobileAccount && currentUser && (
        <MobileAccountSheet
          t={t}
          currentUser={currentUser}
          balance={balance}
          theme={theme}
          onThemeToggle={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
          lang={lang}
          onLang={setLang}
          onClose={() => setShowMobileAccount(false)}
          onWallet={() => setShowInventory(true)}
          onPage={(p) => setActiveNav(p)}
          onLogout={handleLogout}
        />
      )}
      {userPop && (
        <UserPopover
          username={userPop.username}
          pos={userPop.pos}
          t={t}
          onClose={() => setUserPop(null)}
          onOpenProfile={(username) => { setUserPop(null); navToProfile(username); }}
          onTip={currentUser ? (u) => setTipTarget(u) : null}
        />
      )}
      {tipTarget && (
        <TipModal
          receiver={tipTarget}
          userInventory={userInventory}
          t={t}
          onClose={() => setTipTarget(null)}
          onDone={(res, sentInvId) => {
            pushToast({ type: 'win', text: `🎁 Tipped ${res.tip.item_name} to @${res.tip.receiver.username}` });
            if (res.wished) {
              pushNotif({ kind: 'win', title: 'Wishlist hit!', text: `${res.tip.receiver.username} wanted that — they got bonus XP.` });
            }
            // Session tip counter — fires a "🔥 Tip Spree" toast at 3+ tips
            try {
              const KEY = 'bloxhot.sessionTipCount';
              const count = (Number(sessionStorage.getItem(KEY)) || 0) + 1;
              sessionStorage.setItem(KEY, String(count));
              if (count === 3) {
                pushToast({ type: 'win', text: '🔥 Tip Spree! You\'ve tipped 3 times this session — generous!' });
                pushNotif({ kind: 'win', title: 'Tip Spree 🔥', text: '3 tips this session — keep it up' });
              } else if (count === 10) {
                pushToast({ type: 'win', text: '⭐ Big Heart! 10 tips this session!' });
              }
            } catch (_) {}
            // Backend returns the authoritative fresh inventory in `res.inventory`.
            // Use that directly so we don't depend on /api/me round-trip timing.
            if (Array.isArray(res.inventory)) {
              setUserInventory(res.inventory.map(it => ({
                id: it.id,
                name: it.name,
                rarity: it.rarity,
                price: Number(it.price) || 0,
                lockedFlipId: it.locked_flip_id,
              })));
            } else if (sentInvId) {
              // Fallback: optimistic filter if backend didn't send inventory.
              setUserInventory(inv => inv.filter(it => it.id !== sentInvId));
            }
            refreshMe({ keepUserOnNull: true });
          }}
        />
      )}

      {/* Big bouncing tip-received animation with confetti */}
      {tipReceived && (
        <TipReceivedAnimation
          tip={tipReceived}
          onDone={() => setTipReceived(null)}
        />
      )}

      <div className="toast-stack">
        {toasts.map(ts => (
          <div key={ts.id} className={`toast ${ts.type}`}>
            {ts.type === 'win'  && <span className="toast-icon"><I name="trophy" size={20} color="white"/></span>}
            {ts.type === 'lose' && <span className="toast-icon"><I name="close" size={20} color="white"/></span>}
            {ts.type === 'info' && <span className="toast-icon"><I name="bolt" size={20} color="white"/></span>}
            <span>{ts.text}</span>
          </div>
        ))}
      </div>

      <div className={`tweaks ${tweaksOpen ? 'show' : ''}`}>
        <div className="tweaks-head">
          <h4>Tweaks</h4>
          <button className="tweaks-close" onClick={() => { setTweaksOpen(false); window.parent?.postMessage({type:'__edit_mode_dismissed'},'*'); }}><I name="close" size={14}/></button>
        </div>
        <div className="tw-section">
          <div className="tw-label">Accent color</div>
          <div className="tw-swatches">
            {SWATCHES.map(s => (
              <div key={s.hex} className={`tw-swatch ${tweaks.accent===s.hex?'active':''}`} style={{background: s.hex}} onClick={() => setTweak('accent', s.hex)}/>
            ))}
          </div>
        </div>
        <div className="tw-section">
          <div className="tw-label">Layout</div>
          <div className="tw-row">
            <span>Hero banner</span>
            <div className={`tw-toggle ${tweaks.showHero?'on':''}`} onClick={() => setTweak('showHero', !tweaks.showHero)}/>
          </div>
          <div className="tw-row">
            <span>Whale flip card</span>
            <div className={`tw-toggle ${tweaks.showFeatured?'on':''}`} onClick={() => setTweak('showFeatured', !tweaks.showFeatured)}/>
          </div>
        </div>
        <div className="tw-section">
          <div className="tw-label">Demo</div>
          <button className="tw-btn" onClick={async () => {
            try {
              const res = await apiFetch('/api/inventory/deposit-demo', { method: 'POST', body: { count: 5 } });
              pushToast({ type: 'win', text: `+${(res.added||[]).length} demo items` });
              refreshMe({ keepUserOnNull: true });
            } catch (err) {
              pushToast({ type: 'lose', text: err.message || 'Demo deposit failed' });
            }
          }}>+5 demo items</button>
          {/* "Show tutorial tour" dev button removed with the tutorial feature. */}
        </div>
      </div>
    </>
  );
}

// ============================================
// ERROR BOUNDARY — catches render-time crashes anywhere below it. Without
// this, a single bad render in any child component renders the WHOLE page
// blank (the white-screen-of-death the user kept hitting). Now we show a
// recoverable error card with a refresh button so users can recover.
// ============================================
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error: error?.message || String(error) }; }
  componentDidCatch(error, info) {
    try { console.error('AppErrorBoundary caught:', error, info); } catch (_) {}
    // Best-effort report — fire-and-forget. Don't await.
    try {
      fetch('/api/admin/audit.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'react_crash',
          message: String(error?.message || error).slice(0, 500),
          stack: String(error?.stack || '').slice(0, 2000),
          path: window.location.pathname,
          ua: navigator.userAgent.slice(0, 200),
        }),
      }).catch(() => {});
    } catch (_) {}
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        padding: 24, background: 'var(--white)',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <div style={{
          maxWidth: 480, textAlign: 'center',
          background: 'var(--g-100)',
          padding: '32px 28px', borderRadius: 16,
          border: '1px solid var(--g-200)',
        }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
          <h2 style={{ margin: '0 0 8px', color: 'var(--g-900)', fontSize: 20 }}>Something went wrong</h2>
          <p style={{ margin: '0 0 18px', color: 'var(--g-600)', fontSize: 13.5, lineHeight: 1.5 }}>
            The page hit an unexpected error and couldn't finish loading. This is on us — try refreshing.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 22px', borderRadius: 10,
              background: 'var(--orange)', color: 'white',
              border: 0, cursor: 'pointer',
              fontSize: 14, fontWeight: 700,
            }}
          >Refresh page</button>
          <details style={{ marginTop: 18, textAlign: 'left' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--g-500)', fontSize: 11 }}>Technical details</summary>
            <pre style={{ marginTop: 8, padding: 10, background: 'var(--g-50)', borderRadius: 6, fontSize: 10.5, color: 'var(--g-700)', overflow: 'auto', maxHeight: 240 }}>{this.state.error}</pre>
          </details>
        </div>
      </div>
    );
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(<AppErrorBoundary><App/></AppErrorBoundary>);

// Service worker registration — caches static assets for instant repeat
// visits and provides an offline fallback. PWA "Install" prompt component
// is rendered inside <App/>.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[sw] registration failed', err);
    });
  });
}
